export default {
  quarter: 1,
  phase: '1',
  theme: 'Sounds and Senses',
  weeks: 13,
  description: 'Sound play — animal noises, rhythm claps, name sounds; Object naming and matching; Rhyming exposure',
  lessons: [
    {
      // ========= LESSON 1 (V2) =========
  id: 1,
  phase: '1-2',
  quarter: 1,
  week: 1,
  weekIndex: 0,
  dayInWeek: 1,
  title: 'Lesson 1: Animal Sounds — The Noises Animals Make!',
  theme: 'Sound Recognition & Imitation',
  skillRecurrenceTags: ['sound-awareness', 'listening', 'imitation'],
  relatedToPreviousSkills: [],
  duration: '10-15 min',
  version: 2,
  banner: 'Grab any toy animal, stuffed animal, or picture of an animal — and your voice. That’s it.',
  steps: [
    {
      stepNumber: 1,
      name: 'Wake Up Your Voice',
      duration: '2 min',
      hook: 'Your baby’s brain is wired to lock onto your voice before any other sound on earth.',
      sayThis: [
        '“Hello, little one! Mama/Dada is here. Listen to this...”',
        '“Mmmmm... mmmmmm... can you feel that?”',
        '“Oooooh! Aaaaaah! Weeee!”'
      ],
      thenDoThis: [
        'Hum softly while making eye contact — let them study your mouth',
        'Change your pitch: go high, go low, go silly',
        'Pause after each sound for 5 full seconds — silence is where imitation starts',
        'Repeat 3–4 times — try different sounds each round (hums, vowels, raspberries)',
        'If they make ANY sound back, mirror it right back to them — that’s a conversation'
      ],
      watchFor: 'Their eyes locking onto your lips, or their mouth opening slightly — that’s the motor planning that becomes speech.',
      whyThisWorks: 'Kuhl et al. (2003) showed babies 6–24 months map mouth movements to sounds — your exaggerated face is their phonics textbook.',
      transition: 'Your baby is tuned in. Now let’s give them a sound worth copying.'
    },
    {
      stepNumber: 2,
      name: 'Your First Animal Sound',
      duration: '3 min',
      hook: 'Imitation is how babies learn to talk. This is their first chance to try.',
      sayThis: [
        '“Look! Cow says MOOO! Can you moo? Mooooo!”',
        '“Yes! You made a sound! That’s YOUR moo!”',
        '(Use whatever animal you have — dog says WOOF, cat says MEOW, duck says QUACK. The animal doesn’t matter, the sound does.)'
      ],
      thenDoThis: [
        'Pick one animal your child knows — a stuffed dog, a picture of a cat, anything',
        'Hold it up, point to it, then point to your mouth',
        'Make the sound slowly with your mouth exaggerated so they can see the shape',
        'Wait 5 full seconds. Watch their face. Any sound, smile, or mouth movement counts.',
        'Cheer big — then do the SAME animal again. 2–3x with the same sound before moving on.',
        'Repetition is the point — don’t rush to a new animal'
      ],
      watchFor: 'Mouth changing shape — opening, rounding, or any vocalization at all, even a grunt. That’s imitation happening.',
      whyThisWorks: 'Hart & Risley (1995) found that enthusiastic verbal interaction directly accelerates vocabulary growth, starting from the very first sounds.',
      transition: 'One animal down. Let’s hear more sounds.'
    },
    {
      stepNumber: 3,
      name: 'More Sounds',
      duration: '3 min',
      hook: 'More animals, more sounds. Variety keeps them listening.',
      sayThis: [
        '“Dog says WOOF WOOF!”',
        '“Cat says MEOW!”',
        '“Duck says QUACK QUACK!”',
        '(Pick 2–3 animals. Use toys, pictures, or just your voice — no props required.)'
      ],
      thenDoThis: [
        'One animal at a time — make the sound first, then name the animal',
        'Exaggerate your mouth for each sound so they can see the difference between them',
        'Pause 5 seconds after each sound and lean in — make it a game of waiting',
        'Do each animal 2x before moving to the next one',
        'Repeat whichever sound gets the biggest reaction — follow their interest, not a script'
      ],
      watchFor: 'Eyes tracking between your mouth and the toy (or your face if no toy) — that’s them connecting sound to source.',
      whyThisWorks: 'Fernald et al. (1989) showed infant-directed speech (high pitch, slow pace, exaggerated tone) doubles attention span and sound recognition.',
      transition: 'They’ve been listening. Now let’s get their body in on it.'
    },
    {
      stepNumber: 4,
      name: 'Clap and Sound',
      duration: '2 min',
      hook: 'Adding a clap or stomp before the sound turns it into a game they’ll want to repeat.',
      sayThis: [
        '“Clap, clap, clap — MOOO!”',
        '“Stomp, stomp — WOOF!”',
        '“Pat, pat, pat — QUACK!”',
        '(Swap in whichever sounds your baby liked most.)'
      ],
      thenDoThis: [
        'Clap 2–3 times, then immediately say an animal sound',
        'If they’re sitting, gently pat their hands together with yours',
        'Do each combo 2–3x — the repetition IS the game, not a warmup for something else',
        'Speed up or slow down based on their energy — match them, don’t direct them'
      ],
      watchFor: 'Any rhythmic movement — bouncing, hand waving, kicking — even off-beat. They’re feeling the pattern.',
      whyThisWorks: 'Trainor et al. (2002) found early rhythm play builds the auditory processing that children later use to hear syllables and phonemes.',
      transition: 'You just did something real. Let’s close this one out together.'
    },
    {
      stepNumber: 5,
      name: 'Your First Activity',
      duration: '1 min',
      hook: 'You just ran a research-backed language session. Your baby’s brain changed shape in the last 10 minutes.',
      sayThis: [
        '“You made so many sounds today! MOOO! WOOF! QUACK!”',
        '“I love making sounds with you.”',
        '“We’re going to do this again tomorrow!”'
      ],
      thenDoThis: [
        'Let your baby hold the toy animal (if you used one) — ownership anchors memory',
        'Make one more sound together, whichever was their favorite',
        'Big hug, big praise — tell them they did something important, because they did'
      ],
      watchFor: 'If they reach for the toy or vocalize after the activity ends — that’s the learning sticking.',
      whyThisWorks: 'Positive emotional association with learning activities increases the likelihood of skill retention and future engagement (Hirsh-Pasek et al., 2020).',
      transition: null
    }
  ]
},

    // ---------- LESSON 2 ----------
    {
      id: 2,
  phase: '1-2',
  quarter: 1,
  week: 1,
  weekIndex: 0,
  dayInWeek: 2,
  title: 'Lesson 2: The Drum and the Duck — Rhythm Play',
  theme: 'Rhythm & Listening',
  skillRecurrenceTags: ['sound-awareness', 'rhythm', 'attention'],
  relatedToPreviousSkills: ['listening', 'sound-awareness'],
  duration: '10-15 min',
  steps: [
    {
      id: 0,
      type: 'intro',
      title: 'Welcome to Rhythm Time!',
      duration: '1 min',
      content: {
        goal: 'Encourage your baby to feel beats, notice patterns, and link rhythm with fun sounds.',
        parentGuide: [
          'This builds auditory timing and focus, both essential for speech rhythm.',
          'Babies naturally love rhythmic repetition — it soothes and teaches simultaneously.',
          'Let your baby explore sounds freely; perfection isn’t the goal.',
          'Music and rhythm develop listening focus and sound anticipation.'
        ],
        materials: ['Plastic bowl or small drum', 'Spoon or hand to tap', 'Your voice'],
        tip: 'Keep beats simple and repetitive; toddlers thrive on predictability.',
        researchNote: 'Gerry et al. (2012) - Early rhythmic movement improves later language timing and coordination.'
      }
    },
    {
      id: 1,
      type: 'activity',
      title: 'Step 1: Tap, Tap, Quack!',
      duration: '3-4 min',
      focus: 'Auditory Coordination',
      researchBasis: 'Trainor et al. (2002) - Early rhythm exposure strengthens speech rhythm processing.',
      content: {
        goal: 'Combine rhythm and sound recognition by matching beats with animal sounds.',
        whatToSay: [
          '"Tap tap tap! The duck says quack!"',
          '"Let’s tap for the cow — tap tap MOO!"',
          '"Listen to the sound of your drum!"'
        ],
        whatToDo: [
          'Gently tap the bowl or drum three times.',
          'After each tap, make an animal sound.',
          'Encourage your baby to tap along or vocalize.',
          'Repeat rhythm sequences for each animal.'
        ],
        watchFor: [
          'Child tapping or watching closely.',
          'Response to rhythmic pattern.',
          'Any attempt to join in with sound.'
        ],
        parentTip: 'Children feel rhythm before they master imitation — keep it fun and flowing.'
      }
    },
    {
      id: 2,
      type: 'activity',
      title: 'Step 2: Fast and Slow!',
      duration: '3-4 min',
      focus: 'Auditory Discrimination',
      researchBasis: 'Trehub et al. (1997) - Infants detect tempo and pitch changes, enhancing sound sensitivity.',
      content: {
        goal: 'Help your baby notice differences in rhythm and speed.',
        whatToSay: [
          '"Listen — tap, tap, tap — fast!"',
          '"Now… sloooow… tap… tap…"'
        ],
        whatToDo: [
          'Alternate between fast and slow tapping.',
          'Say “fast” and “slow” each time.',
          'Encourage your baby to move or bounce with tempo.'
        ],
        watchFor: [
          'Child moving faster/slower with rhythm.',
          'Smiling or reacting when pace changes.'
        ],
        parentTip: 'Tempo play develops self-regulation — learning to pause and wait between sounds.'
      }
    },
    {
      id: 3,
      type: 'activity',
      title: 'Step 3: Sound and Silence',
      duration: '3 min',
      focus: 'Listening Focus',
      researchBasis: 'Fernald & Kuhl (1987) - Attention to pauses in speech supports comprehension.',
      content: {
        goal: 'Build listening skills by pausing and waiting for anticipation.',
        whatToSay: [
          '"Tap tap… and stop!"',
          '"Shhh… listen… what comes next?"'
        ],
        whatToDo: [
          'Tap three times, then pause.',
          'Wait silently, letting your baby anticipate the next tap.',
          'Repeat several times with joyful pauses.'
        ],
        watchFor: [
          'Anticipation facial expressions.',
          'Attention focused during pauses.'
        ],
        parentTip: 'Pauses teach listening — silence is as powerful as sound.'
      }
    },
    {
      id: 5,
      type: 'conclusion',
      title: 'Activity Complete!',
      duration: '1 min',
      content: {
        celebration: [
          '🎶 Fantastic rhythm fun!',
          'You practiced:',
          '✓ Fast and slow tapping',
          '✓ Listening and waiting',
          '✓ Matching rhythm to animal sounds'
        ],
        reviewQuestions: [
          'Did your baby prefer fast or slow beats?',
          'How did they react to pauses?',
          'Did they copy any rhythms or sounds?'
        ],
        parentTakeaway: {
          title: 'Why This Matters',
          content: 'Rhythm sharpens focus and self-regulation — key skills for reading readiness and attention control.'
        },
        nextSteps: [
          'Use rhythm during daily routines (clap for snack time).',
          'Play with tempo words during music or songs.',
          'Encourage gentle tapping with household objects.'
        ]
      }
    }
  ]
},

    // ---------- LESSON 3 ----------
    {
      id: 3,
  phase: '1-2',
  quarter: 1,
  week: 1,
  weekIndex: 0,
  dayInWeek: 3,
  title: 'Lesson 3: Name Sounds — "That’s Me!"',
  theme: 'Name Recognition & Listening',
  skillRecurrenceTags: ['sound-awareness', 'name-recognition', 'attention'],
  relatedToPreviousSkills: ['listening', 'sound-awareness', 'rhythm'],
  duration: '10-15 min',
  steps: [
    {
      id: 0,
      type: 'intro',
      title: 'Welcome to Name Play!',
      duration: '1 min',
      content: {
        goal: 'Help your baby recognize their name as a unique sound pattern.',
        parentGuide: [
          'This lesson builds personal connection to language through name recognition.',
          'your baby’s name is often their first understood “word.”',
          'Repetition and joyful tone help reinforce meaning.',
          'Linking their name to positive emotion builds confidence.'
        ],
        materials: ['Your voice', 'Family photos (optional)', 'Small mirror (optional)'],
        tip: 'Always say your baby’s name with warmth and eye contact — connection drives attention.',
        researchNote: 'Mandel et al. (1995) - Infants recognize their own names by 4–6 months, showing early word detection ability.'
      }
    },
    {
      id: 1,
      type: 'activity',
      title: 'Step 1: Say My Name!',
      duration: '3-4 min',
      focus: 'Name Recognition',
      researchBasis: 'Newman et al. (2006) - Name repetition activates social and linguistic attention in infants.',
      content: {
        goal: 'Build name awareness through repetition, tone, and play.',
        whatToSay: [
          '"Babys Name! That’s your name!"',
          '"Who is Babys name? That’s YOU!"',
          '"Babys Name, where are you?"'
        ],
        whatToDo: [
          'Say your baby’s name in different tones (high, low, sing-song).',
          'Pause for reactions — smiles, head turns, vocalizations.',
          'Celebrate any acknowledgment with hugs or claps.'
        ],
        watchFor: [
          'Child turning toward their name.',
          'Smiling or vocalizing in response.',
          'Attempting to imitate sounds from their name.'
        ],
        parentTip: 'Turn this into peekaboo — say the name before you appear!'
      }
    },
    {
      id: 2,
      type: 'activity',
      title: 'Step 2: Family Names',
      duration: '3-4 min',
      focus: 'Auditory Differentiation',
      researchBasis: 'Snow (1983) - Family name recognition builds contextual understanding and belonging.',
      content: {
        goal: 'Introduce familiar family names to strengthen auditory memory and connection.',
        whatToSay: [
          '"Mama!"',
          '"Dada!"',
          '"Nana!"'
        ],
        whatToDo: [
          'Say each name clearly.',
          'Show a picture or point to the person if present.',
          'Wait for any response or repetition.'
        ],
        watchFor: [
          'Child looking toward the named person.',
          'Attempting to say a name sound.',
          'Smiling or gesturing toward family.'
        ],
        parentTip: 'Keep it short and sweet — repetition of loved ones’ names reinforces trust and belonging.'
      }
    },
    {
      id: 3,
      type: 'activity',
      title: 'Step 3: Name Rhythm Chant',
      duration: '3-4 min',
      focus: 'Phonological Awareness',
      researchBasis: 'Whitehurst & Lonigan (1998) - Early syllable awareness predicts reading readiness.',
      content: {
        goal: 'Develop sound rhythm awareness through chanting names.',
        whatToSay: [
          '"Lu-ci-a-no! Clap, clap, clap!"',
          '"Mama! Clap, clap!"'
        ],
        whatToDo: [
          'Clap syllables as you say names slowly.',
          'Encourage your baby to join in or bounce to rhythm.',
          'End with laughter or tickles.'
        ],
        watchFor: [
          'Child attempting claps or bounces.',
          'Recognition or vocal response during chant.'
        ],
        parentTip: 'Clapping syllables makes sound units visible and fun!'
      }
    },
    {
      id: 5,
      type: 'conclusion',
      title: 'Activity Complete!',
      duration: '1 min',
      content: {
        celebration: [
          '🎉 Wonderful name fun!',
          'You practiced:',
          '✓ Listening for your name',
          '✓ Saying and hearing family names',
          '✓ Clapping to name rhythm'
        ],
        reviewQuestions: [
          'Did your baby turn toward their name?',
          'Which name made them most excited?',
          'Did they try any clapping or sound imitation?'
        ],
        parentTakeaway: {
          title: 'Why This Matters',
          content: 'Recognizing one’s name is a powerful milestone — it builds self-awareness and phonological memory, both crucial for later reading.'
        },
        nextSteps: [
          'Use your baby’s name in songs and daily routines.',
          'Play name peekaboo during transitions.',
          'Clap names of friends and family regularly.'
        ]
      }
    }
  ]
},

    // ---------- LESSON 4 ----------
    {
      "id": 4,
  "phase": "1-2",
  "quarter": 1,
  "week": 4,
  "weekIndex": 3,
  "dayInWeek": 1,
  "title": "Lesson 4: Big and Small Talk",
  "theme": "Size & Comparison",
  "skillRecurrenceTags": ["descriptive-language", "vocabulary-growth", "observation"],
  "relatedToPreviousSkills": ["object-recognition", "labeling"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Big and Small!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce your baby to size concepts through real objects and play-based comparison.",
        "parentGuide": [
          "Understanding 'big' and 'small' helps your baby begin to categorize and describe their world.",
          "Early exposure to descriptive words builds rich vocabulary foundations.",
          "Don’t expect immediate understanding—this is about exposure and fun!",
          "Let your baby touch and explore everything freely."
        ],
        "materials": ["One big ball or stuffed toy", "One small ball or toy", "Safe basket or box"],
        "tip": "Use exaggerated tone and gestures to emphasize differences in size—it helps language stick.",
        "researchNote": "Gentner (2003) – Comparative play supports concept learning and word meaning formation."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Big vs. Small Playtime",
      "duration": "4 min",
      "focus": "Vocabulary & Concept Awareness",
      "researchBasis": "Gleitman (1990) – Repetition and context-rich speech accelerate early word learning.",
      "content": {
        "goal": "Help your baby begin recognizing and hearing the contrast between 'big' and 'small'.",
        "whatToSay": [
          "\"Look, big ball!\" (hold up big ball)",
          "\"Now small ball! So tiny!\"",
          "\"Big ball rolls far! Small ball rolls close!\"",
          "\"Can your baby find the small one? Yay!\""
        ],
        "whatToDo": [
          "Hold up both objects and label them clearly with tone and expression.",
          "Show the difference by rolling or stacking them.",
          "Ask your baby to hand you one when named.",
          "Switch objects and repeat several times with smiles and praise."
        ],
        "examples": ["Big teddy vs. small teddy", "Large spoon vs. baby spoon"],
        "watchFor": ["Pointing or reaching for named object", "Mimicking your tone", "Excited responses"],
        "parentTip": "Don’t over-correct—just keep repeating the contrast naturally.",
        "funTwist": "End with both toys saying hello to each other: 'Big ball says hi! Small ball says hi!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Size Sort Surprise",
      "duration": "3-4 min",
      "focus": "Categorization & Fine Motor Practice",
      "researchBasis": "Smith et al. (2002) – Sorting and classification support cognitive and linguistic development.",
      "content": {
        "goal": "Encourage your baby to group objects by size and build understanding of categories.",
        "whatToSay": [
          "\"Let’s put all the big ones here!\"",
          "\"Where does the small one go?\"",
          "\"Wow, your baby is sorting like a scientist!\""
        ],
        "whatToDo": [
          "Place 4–5 mixed items in front of your baby (big and small).",
          "Model sorting them into two piles, labeling as you go.",
          "Encourage your baby to try placing one or two objects.",
          "Celebrate every effort—no wrong answers!"
        ],
        "watchFor": ["Attempts to copy sorting motion", "Interest in size differences", "Naming or babbling"],
        "parentTip": "It’s okay if your baby doesn’t sort correctly—focus on attention and participation.",
        "funTwist": "Make funny sounds for each pile: ‘Boom!’ for big, ‘Beep!’ for small."
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Big Hugs, Small Hugs",
      "duration": "2-3 min",
      "focus": "Emotional Connection & Reinforcement",
      "researchBasis": "Tamis-LeMonda et al. (2014) – Warm, responsive play strengthens emotional learning and language uptake.",
      "content": {
        "goal": "Reinforce understanding through loving movement and imitation.",
        "whatToSay": [
          "\"Big hug!\" (spread arms wide)",
          "\"Small hug!\" (small circle with hands)",
          "\"Aww, your baby gives small hug!\"",
          "\"Let’s do another big hug! Yay!\""
        ],
        "whatToDo": [
          "Model each type of hug dramatically.",
          "Encourage your baby to copy your movements.",
          "Alternate between big and small with silly voices.",
          "End with a joyful final big hug!"
        ],
        "watchFor": ["Imitating motions", "Laughter or smiles", "Physical engagement"],
        "parentTip": "Movement helps cement abstract ideas—keep it playful!",
        "funTwist": "Add silly sound effects for each hug size!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Great job exploring sizes!",
          "You practiced:",
          "✓ Big and small comparisons",
          "✓ Using descriptive words",
          "✓ Sorting and matching"
        ],
        "reviewQuestions": [
          "Did your baby notice which object was big or small?",
          "Which activity made them most excited?",
          "Did they respond to your voice or gestures?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Early exposure to descriptive words like 'big' and 'small' builds conceptual thinking and vocabulary depth—core skills for reading comprehension later on."
        },
        "nextSteps": [
          "Use size words throughout the day: 'big chair,' 'small spoon.'",
          "Compare objects at mealtime or bath time.",
          "Encourage your baby to choose between two sizes often."
        ]
      }
    }
  ]
},

    // ---------- LESSON 5 ----------
    {
  "id": 5,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 1,
  "title": "Lesson 5: Touch and Tell",
  "theme": "Sensory & Vocabulary",
  "skillRecurrenceTags": ["tactile-awareness", "word-mapping", "exploration"],
  "relatedToPreviousSkills": ["object-recognition", "naming"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Touch and Tell!",
      "duration": "1 min",
      "content": {
        "goal": "Help your baby connect textures and touch sensations to new words.",
        "parentGuide": [
          "Touch-based exploration supports brain connections for both language and sensory development.",
          "Describing textures out loud links words to experiences.",
          "Let your baby explore independently—don’t rush!",
          "Be animated and expressive when labeling textures."
        ],
        "materials": ["Soft blanket", "Wooden spoon", "Rubber toy", "Paper towel"],
        "tip": "Let your baby touch first before labeling—it increases focus and curiosity.",
        "researchNote": "Lobo & Galloway (2013) – Sensorimotor play drives cognitive and linguistic development."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Texture Tour",
      "duration": "4 min",
      "focus": "Sensory Vocabulary",
      "researchBasis": "Snow (1999) – Labeling experiences aloud helps infants build receptive vocabulary.",
      "content": {
        "goal": "Introduce texture words like soft, hard, smooth, and bumpy.",
        "whatToSay": [
          "\"Ooh, soft blanket!\"",
          "\"Hard spoon—tap tap!\"",
          "\"Smooth paper—feel that!\"",
          "\"your baby, can you touch bumpy toy?\""
        ],
        "whatToDo": [
          "Lay out 3–4 textured items.",
          "Let your baby touch and explore one at a time.",
          "Label each texture clearly and repeat twice.",
          "Encourage imitation with tone and enthusiasm."
        ],
        "watchFor": ["Touching and exploring each item", "Vocalizing or smiling", "Looking back at you after labeling"],
        "parentTip": "Repeat the same words several times—this builds familiarity and retention.",
        "funTwist": "Play ‘peek-touch’—hide one texture under a cloth and let your baby find it!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Touch and Match",
      "duration": "3 min",
      "focus": "Memory & Association",
      "researchBasis": "Rowe (2012) – Parents’ use of descriptive vocabulary predicts later expressive language growth.",
      "content": {
        "goal": "Encourage matching items by feel or sight.",
        "whatToSay": [
          "\"Find the soft one!\"",
          "\"Can your baby find another smooth thing?\"",
          "\"Yay, that’s the same!\""
        ],
        "whatToDo": [
          "Present two similar textures and one different one.",
          "Ask your baby to find the one that feels the same.",
          "Guide hand gently to help compare.",
          "Celebrate every attempt with claps and smiles."
        ],
        "watchFor": ["Reaching for correct object", "Repeating texture sounds", "Focused curiosity"],
        "funTwist": "Use a blindfold (light scarf) for you, not your baby—let them ‘teach’ you which is which!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Texture Dance",
      "duration": "2-3 min",
      "focus": "Integration through Movement",
      "researchBasis": "Iverson (2010) – Physical exploration is directly tied to cognitive and linguistic development.",
      "content": {
        "goal": "Link textures with body movements to reinforce memory and joy.",
        "whatToSay": [
          "\"Soft touch—gentle hands!\"",
          "\"Hard touch—tap tap tap!\"",
          "\"Smooth dance—slide your hands!\""
        ],
        "whatToDo": [
          "Turn on gentle music and call out texture actions.",
          "Demonstrate while your baby imitates or watches.",
          "Repeat several cycles and cheer after each one!"
        ],
        "watchFor": ["Copying actions", "Smiling or vocalizing", "Reaching for materials again"],
        "parentTip": "Movement helps strengthen word retention through multisensory input."
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Sensory superstar!",
          "You practiced:",
          "✓ Feeling textures",
          "✓ Learning new words",
          "✓ Matching and comparing"
        ],
        "reviewQuestions": [
          "Which texture made your baby smile or laugh?",
          "Did your baby respond to your labels?",
          "Which item was their favorite?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Sensory exploration helps wire the brain for descriptive language and builds the foundation for later reading comprehension and expressive speech."
        },
        "nextSteps": [
          "Describe textures throughout the day—'soft towel,' 'bumpy road.'",
          "Let your baby explore textures during bath or mealtime.",
          "Continue labeling and praising curiosity."
        ]
      }
    }
  ]
},


    // ---------- LESSON 6 (Week 2 Day 1) ----------
    {
  "id": 6,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 1,
  "title": "Lesson 6: Peekaboo Patterns",
  "theme": "Memory & Anticipation",
  "skillRecurrenceTags": ["object-permanence", "turn-taking", "predictive-thinking"],
  "relatedToPreviousSkills": ["attention", "observation"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Peekaboo Patterns!",
      "duration": "1 min",
      "content": {
        "goal": "Help your baby practice prediction and memory through familiar peekaboo play.",
        "parentGuide": [
          "Peekaboo teaches that people and objects still exist even when hidden.",
          "Predictable play builds memory and anticipation skills critical for reading comprehension later.",
          "Let your baby take turns hiding and revealing.",
          "Use exaggerated expressions and voice tone."
        ],
        "materials": ["Light scarf or blanket", "Favorite toy", "Your smile!"],
        "tip": "Keep rhythm consistent—it helps your baby predict what’s next.",
        "researchNote": "Piaget (1952) – Object permanence development begins near age 1 and is vital for cognitive reasoning."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Classic Peekaboo",
      "duration": "4 min",
      "focus": "Object Permanence",
      "researchBasis": "Meltzoff & Brooks (2008) – Infants learn social patterns through turn-taking games like peekaboo.",
      "content": {
        "goal": "Reinforce the idea that something hidden can return.",
        "whatToSay": [
          "\"Where’s your baby? Peekaboo!\"",
          "\"Mommy’s gone… and back!\"",
          "\"You knew I’d come back!\""
        ],
        "whatToDo": [
          "Cover your face gently with a cloth.",
          "Pause briefly, then reveal with excitement.",
          "Encourage your baby to pull down the scarf themselves.",
          "Repeat rhythmically with smiles and laughter."
        ],
        "watchFor": ["Excited movement", "Anticipatory giggles", "Trying to copy the motion"],
        "funTwist": "Switch roles—let your baby hide *you* behind the blanket!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Toy Hide & Seek",
      "duration": "3 min",
      "focus": "Visual Memory",
      "researchBasis": "Diamond (1990) – Simple memory games strengthen prefrontal brain circuits for attention and working memory.",
      "content": {
        "goal": "Encourage tracking and recall of hidden objects.",
        "whatToSay": [
          "\"Where did teddy go?\"",
          "\"Can your baby find it?\"",
          "\"You remembered! There it is!\""
        ],
        "whatToDo": [
          "Hide toy under blanket halfway so it’s visible.",
          "Encourage your baby to pull blanket off.",
          "Repeat with full cover and slight location change.",
          "Cheer and label every discovery."
        ],
        "watchFor": ["Looking under blanket", "Smiling or giggling at reveal", "Pointing or babbling excitedly"],
        "parentTip": "Hide toys slowly at first—anticipation is the key to engagement."
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Peekaboo Pattern Game",
      "duration": "2-3 min",
      "focus": "Predictive Thinking & Pattern Recognition",
      "researchBasis": "Hirsh-Pasek et al. (2015) – Predictable social play fosters early pattern recognition, aiding reading readiness.",
      "content": {
        "goal": "Build recognition of rhythmic and predictable play patterns.",
        "whatToSay": [
          "\"Up, down, peekaboo!\"",
          "\"One, two, hide again!\"",
          "\"Peekaboo—your baby knew it!\""
        ],
        "whatToDo": [
          "Add rhythm or song to peekaboo (“Peekaboo… where are you?”).",
          "Repeat 3–4 times in a predictable pattern.",
          "Encourage anticipation and imitation.",
          "Celebrate your baby joining in the rhythm or sound."
        ],
        "watchFor": ["Joining the rhythm", "Imitating ‘boo!’", "Anticipating reveal moments"],
        "funTwist": "Use a favorite song beat and hide to the rhythm!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Peekaboo pro!",
          "You practiced:",
          "✓ Memory and prediction",
          "✓ Turn-taking",
          "✓ Understanding object permanence"
        ],
        "reviewQuestions": [
          "Did your baby try to take their turn hiding or revealing?",
          "Did they anticipate your reveal?",
          "Which version made them giggle most?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Predictable social games strengthen your baby’s ability to anticipate, remember, and communicate—skills that later support sequencing and story comprehension."
        },
        "nextSteps": [
          "Play peekaboo during daily routines—changing, feeding, bedtime.",
          "Add variety: hide toys or your voice.",
          "Keep the rhythm fun and interactive!"
        ]
      }
    }
  ]
},

    // ---------- LESSON 7 (Week 2 Day 2) ----------
    {
  "id": 7,
  "phase": "1-2",
  "quarter": 1,
  "week": 2,
  "weekIndex": 1,
  "dayInWeek": 1,
  "title": "Lesson 7: Sounds Around Us",
  "theme": "Environmental Sound Awareness",
  "skillRecurrenceTags": ["listening", "auditory discrimination", "sound imitation"],
  "relatedToPreviousSkills": ["turn-taking", "sound play"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Listening Time!",
      "duration": "1 min",
      "content": {
        "goal": "Help your baby notice and identify everyday sounds in their environment.",
        "parentGuide": [
          "Listening to sounds helps your baby build auditory memory and attention.",
          "These skills prepare for sound-to-word mapping used later in speech and reading.",
          "Encourage curiosity and response to new sounds.",
          "Stay calm and positive, even if your baby doesn’t react right away."
        ],
        "materials": [
          "Quiet space near window or outdoors",
          "Soft toy or small bell",
          "Your voice"
        ],
        "tip": "Pause after each sound to let your baby process before you respond.",
        "researchNote": "Werker & Tees (1984) – Infants are highly attuned to subtle sound contrasts, forming the foundation of phonological awareness."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: What Do You Hear?",
      "duration": "3-4 min",
      "focus": "Auditory Attention",
      "researchBasis": "Kuhl (2004) – Repetition and labeling strengthen neural sound mapping.",
      "content": {
        "goal": "Guide your baby to listen and identify familiar household sounds.",
        "whatToSay": [
          "\"Listen… what do you hear? That’s the clock! Tick-tock!\"",
          "\"I hear the dog barking—woof, woof!\"",
          "\"Do you hear the water? Shhh, it’s running!\""
        ],
        "whatToDo": [
          "Sit quietly together and listen to nearby sounds.",
          "Label each sound with exaggerated tone and gesture.",
          "Pause 3–5 seconds between each sound.",
          "Encourage your baby to point or react when they notice a sound."
        ],
        "examples": ["Fan hums softly", "Door creaks open", "Bird sings outside"],
        "watchFor": [
          "your baby turns toward sound source.",
          "Smiles or babbles after you label a sound."
        ],
        "parentTip": "Model curiosity — say 'Hmm, what’s that sound?' even if your baby doesn’t yet respond.",
        "funTwist": "Make a ‘sound hunt’ game indoors—find where each noise is coming from!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Copy That Sound!",
      "duration": "3-4 min",
      "focus": "Sound Imitation",
      "researchBasis": "Hart & Risley (1995) – Children who hear more varied language develop stronger expressive vocabularies.",
      "content": {
        "goal": "Encourage imitation of environmental sounds to strengthen auditory memory.",
        "whatToSay": [
          "\"Can you say woof-woof like the dog?\"",
          "\"Brrr—listen to the blender! Can you make that sound?\"",
          "\"Tick-tock! That’s the clock!\""
        ],
        "whatToDo": [
          "Model a sound with excitement.",
          "Wait for your baby to respond (any noise counts!).",
          "Repeat and praise all attempts.",
          "Add gestures—clap, wave, or smile with the sound."
        ],
        "watchFor": [
          "your baby vocalizes or imitates tone.",
          "Giggling or movement paired with sound attempts."
        ],
        "parentTip": "Respond to every attempt with praise, not correction.",
        "funTwist": "Take turns making sounds and guessing—repeat favorites for fun!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Sound Parade",
      "duration": "2-3 min",
      "focus": "Integration and Rhythm",
      "researchBasis": "Patel (2008) – Rhythm perception supports later reading and speech fluency.",
      "content": {
        "goal": "Integrate listening and movement for full-body engagement.",
        "whatToSay": [
          "\"Let’s march—boom, boom!\"",
          "\"Can you shake-shake-shake with the rattle?\"",
          "\"We’re a noisy parade!\""
        ],
        "whatToDo": [
          "Play simple rhythmic sounds (clap, tap, shake).",
          "Encourage your baby to join in movement.",
          "Match movements to sounds.",
          "End with quiet listening to slow heartbeat or silence."
        ],
        "watchFor": [
          "Synchronizing movements to rhythm.",
          "Excited anticipation before next sound."
        ],
        "parentTip": "Vary between loud and quiet—it teaches auditory contrast.",
        "funTwist": "Create a parade line through the house with musical toys!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Great listening today!",
          "You practiced:",
          "✓ Listening closely to sounds",
          "✓ Naming what you heard",
          "✓ Making fun sound effects",
          "✓ Marching with rhythm!"
        ],
        "reviewQuestions": [
          "Did your baby notice and turn toward sounds?",
          "Which sound made them giggle most?",
          "Did they try to copy any noises?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Recognizing and reproducing sounds helps your baby map the world through auditory cues, a foundation for phonemic awareness and later reading readiness."
        },
        "nextSteps": [
          "Narrate sounds throughout the day—cars, birds, doors.",
          "Play ‘guess the sound’ at home.",
          "Keep sound play brief, frequent, and joyful!"
        ]
      }
    }
  ]
},


    // ---------- LESSON 8 (Week 2 Day 3) ----------
    {
  "id": 8,
  "phase": "1-2",
  "quarter": 1,
  "week": 2,
  "weekIndex": 1,
  "dayInWeek": 2,
  "title": "Lesson 8: Touch and Texture",
  "theme": "Sensory Exploration",
  "skillRecurrenceTags": ["sensory awareness", "language pairing", "fine motor"],
  "relatedToPreviousSkills": ["listening", "object interaction"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Texture Time!",
      "duration": "1 min",
      "content": {
        "goal": "Encourage your baby to explore textures and begin pairing touch sensations with descriptive words.",
        "parentGuide": [
          "Early sensory play helps wire brain pathways for memory and attention.",
          "Naming textures builds foundational vocabulary for adjectives and descriptions.",
          "Follow your baby’s lead—don’t rush their curiosity.",
          "Focus on the process, not the product!"
        ],
        "materials": [
          "Soft blanket or towel",
          "Plastic spoon",
          "Piece of paper",
          "Stuffed animal"
        ],
        "tip": "Avoid overstimulation—present one item at a time.",
        "researchNote": "Gibson (1988) – Sensory exploration enhances perceptual learning and cognitive development in infants."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Feel and Name",
      "duration": "3-4 min",
      "focus": "Tactile Exploration and Vocabulary",
      "researchBasis": "Whitehurst & Lonigan (1998) – Descriptive language exposure supports early vocabulary growth.",
      "content": {
        "goal": "Introduce texture words like soft, smooth, rough, bumpy.",
        "whatToSay": [
          "\"Ooh, this blanket is so soft! Can you feel it?\"",
          "\"This spoon is smooth and cool.\"",
          "\"Let’s touch the paper—crinkly!\""
        ],
        "whatToDo": [
          "Lay items in front of your baby.",
          "Say texture aloud as you touch each item together.",
          "Encourage reaching, patting, or grabbing.",
          "Respond positively to all exploration."
        ],
        "examples": ["Soft blanket", "Bumpy toy", "Smooth spoon"],
        "watchFor": [
          "Facial expressions of curiosity or surprise.",
          "Repeated touching of favorite textures."
        ],
        "parentTip": "Repeat texture words naturally throughout the day—during bath time, meals, and dressing.",
        "funTwist": "Hide textures in a small bag and let your baby reach inside to guess!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Texture Hunt",
      "duration": "3-4 min",
      "focus": "Sensory Categorization",
      "researchBasis": "Piaget (1952) – Infants form cognitive schemas by comparing and contrasting sensory input.",
      "content": {
        "goal": "Encourage your baby to notice differences and similarities between textures.",
        "whatToSay": [
          "\"Can you find something soft like your bear?\"",
          "\"Hmm, what else feels smooth like your spoon?\"",
          "\"This one is rough—can you find another rough thing?\""
        ],
        "whatToDo": [
          "Hold two items and describe how they differ.",
          "Encourage your baby to touch both.",
          "Offer choices and verbal comparison words.",
          "Praise attempts to choose or respond."
        ],
        "watchFor": [
          "Looking between items when you compare.",
          "Attempting to match or imitate touch."
        ],
        "parentTip": "Keep tone light—this is about discovery, not correctness.",
        "funTwist": "Do a ‘texture walk’—touch walls, floor, couch, and describe them aloud."
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Texture and Emotion",
      "duration": "2-3 min",
      "focus": "Emotional Connection Through Sensory Play",
      "researchBasis": "Greenspan (1992) – Emotional labeling linked to sensory experiences strengthens attachment and communication.",
      "content": {
        "goal": "Link textures with feelings to build emotional vocabulary.",
        "whatToSay": [
          "\"This soft blanket feels cozy! Cozy makes us happy.\"",
          "\"This cold spoon feels silly!\"",
          "\"We smile when something feels nice.\""
        ],
        "whatToDo": [
          "Describe your own emotional response to each texture.",
          "Encourage your baby to mimic facial expressions.",
          "Give hugs or laughter during soft/pleasant textures.",
          "End with favorite texture cuddles."
        ],
        "watchFor": [
          "Smiling, laughing, or mirroring expressions.",
          "Preference for certain textures."
        ],
        "parentTip": "Labeling feelings alongside sensory experiences builds self-regulation early.",
        "funTwist": "Wrap favorite soft object in a blanket and play peekaboo!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Great job exploring today!",
          "You practiced:",
          "✓ Touching and naming textures",
          "✓ Comparing smooth and rough",
          "✓ Connecting feelings to sensations"
        ],
        "reviewQuestions": [
          "Which texture made your baby giggle?",
          "Did your baby show a favorite texture?",
          "How did they react when you described soft vs. rough?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Touch-based learning connects physical sensations with language, emotions, and memory—foundations for descriptive vocabulary and emotional awareness."
        },
        "nextSteps": [
          "Use texture words during daily routines.",
          "Encourage safe exploration with new household items.",
          "Revisit soft and rough play during bath or bedtime."
        ]
      }
    }
  ]
},


    // ---------- LESSON 9 (Week 2 Day 4) ----------
    {
  "id": 9,
  "phase": "1-2",
  "quarter": 1,
  "week": 2,
  "weekIndex": 1,
  "dayInWeek": 3,
  "title": "Lesson 9: Naming Familiar Faces",
  "theme": "Social Recognition and Naming",
  "skillRecurrenceTags": ["object recognition", "social language", "naming"],
  "relatedToPreviousSkills": ["attention", "labeling", "memory"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Face Time!",
      "duration": "1 min",
      "content": {
        "goal": "Help your baby recognize and name familiar faces, supporting social bonding and word recall.",
        "parentGuide": [
          "Naming familiar faces strengthens word-object association.",
          "Facial recognition activates emotional and memory pathways.",
          "Repetition helps your baby store and retrieve names faster.",
          "Use gentle enthusiasm and clear pronunciation."
        ],
        "materials": [
          "Family photos (printed or in a photo album)",
          "Mirror",
          "Favorite stuffed toy"
        ],
        "tip": "Keep emotional tone positive—smiling faces create stronger learning links.",
        "researchNote": "Nelson (1987) – Early memory for familiar faces supports later social-emotional development and language growth."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Name That Face!",
      "duration": "3-4 min",
      "focus": "Name Recognition and Word Mapping",
      "researchBasis": "Hart & Risley (1995) – Frequent naming builds expressive vocabulary through repetition.",
      "content": {
        "goal": "Link faces to names using photos or real people.",
        "whatToSay": [
          "\"Who’s that? That’s Grandma!\"",
          "\"There’s Daddy—hi Daddy!\"",
          "\"That’s you, your baby!\""
        ],
        "whatToDo": [
          "Show photos one by one.",
          "Say the name clearly and smile.",
          "Wait for reaction—smile, babble, or point.",
          "Repeat 2–3 times per photo."
        ],
        "watchFor": [
          "Smiling or cooing when seeing familiar faces.",
          "Reaching or pointing toward photos."
        ],
        "parentTip": "Use real family members often—babies learn best with emotionally meaningful people.",
        "funTwist": "Make a simple photo book and point to names daily."
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Mirror Talk",
      "duration": "3-4 min",
      "focus": "Self-Recognition and Pronoun Awareness",
      "researchBasis": "Amsterdam (1972) – Mirror recognition marks a key stage in self-awareness around 18 months.",
      "content": {
        "goal": "Introduce self-recognition and the word 'me' or 'I'.",
        "whatToSay": [
          "\"Who’s that in the mirror? It’s your baby!\"",
          "\"Hi there—you’re smiling!\"",
          "\"Can you touch your nose in the mirror?\""
        ],
        "whatToDo": [
          "Sit with mirror and point to features.",
          "Encourage touching face parts (nose, mouth, eyes).",
          "Name features as you go.",
          "Celebrate every attempt."
        ],
        "watchFor": [
          "Smiling or laughing at reflection.",
          "Touching or recognizing own features."
        ],
        "parentTip": "Say 'you' and 'me' naturally—it supports pronoun understanding.",
        "funTwist": "Add silly faces and mimic together!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Find the Face",
      "duration": "2-3 min",
      "focus": "Memory Recall and Attention",
      "researchBasis": "Fagan (1973) – Familiar face recognition predicts higher later IQ and attention control.",
      "content": {
        "goal": "Encourage recognition and recall by name.",
        "whatToSay": [
          "\"Where’s Grandma? Can you find her picture?\"",
          "\"Where’s Daddy?\"",
          "\"You found Mommy! Yay!\""
        ],
        "whatToDo": [
          "Lay out 2–3 photos.",
          "Ask for a specific person.",
          "Praise attempts even if incorrect.",
          "Mix up photo order and repeat."
        ],
        "watchFor": [
          "Eye contact or pointing toward named person.",
          "Smiles when correct face found."
        ],
        "parentTip": "End on success—always finish with a familiar, loved face.",
        "funTwist": "Include pets or favorite toys to keep engagement high."
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You did amazing, your baby!",
          "You practiced:",
          "✓ Naming faces you love",
          "✓ Looking in the mirror",
          "✓ Remembering special people"
        ],
        "reviewQuestions": [
          "Which face made your baby most excited?",
          "Did they respond to their own reflection?",
          "Are they starting to say or babble any names?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Recognizing and naming people builds emotional bonds and strengthens memory and word retrieval—crucial for language and empathy."
        },
        "nextSteps": [
          "Name family members when looking at photos together.",
          "Keep photo book accessible for daily review.",
          "Use names often during routines—'Daddy’s helping,' 'Grandma’s calling!'"
        ]
      }
    }
  ]
},

    // ---------- LESSON 10 (Week 2 Day 5) ----------
{
  "id": 10,
  "phase": "1-2",
  "quarter": 1,
  "week": 2,
  "weekIndex": 1,
  "dayInWeek": 4,
  "title": "Lesson 10: Quiet Time Echoes — Copy My Sound",
  "theme": "Everyday Sound Explorers",
  "skillRecurrenceTags": ["imitation", "auditory-memory", "turn-taking"],
  "relatedToPreviousSkills": ["listening", "sound-awareness", "rhythm"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Echo Time!",
      "duration": "1 min",
      "content": {
        "goal": "Develop listening focus and sound imitation through gentle echo games.",
        "parentGuide": [
          "Echo games build auditory memory — the ability to hold and repeat sounds.",
          "This skill directly supports speech development and later phonics learning.",
          "Keep sounds simple and playful — success builds confidence.",
          "Silence between sounds teaches patience and focus."
        ],
        "materials": ["Your voice", "Quiet comfortable space", "Optional: soft toy or puppet"],
        "tip": "Wait 3-5 seconds after each sound — giving your baby time to process and respond.",
        "researchNote": "Kuhl (2010) - Early sound imitation during the sensitive period builds neural pathways for language and social learning."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Simple Sound Copies",
      "duration": "3-4 min",
      "focus": "Basic Imitation",
      "researchBasis": "Kuhl et al. (2003) - Vocal imitation strengthens phonetic mapping in infant brains.",
      "content": {
        "goal": "Encourage your baby to copy simple sounds through playful repetition.",
        "whatToSay": [
          "\"Listen to this sound: Ahhh. Can you say ahhh?\"",
          "\"Now try this one: Oooo!\"",
          "\"You did it! You made the same sound!\""
        ],
        "whatToDo": [
          "Make a clear vowel sound (ahhh, oooo, eee).",
          "Keep your face close and animated.",
          "Wait patiently for any vocalization attempt.",
          "Celebrate every effort with smiles and praise.",
          "Repeat 3-4 different sounds."
        ],
        "examples": ["Ahhh (long and gentle)", "Oooo (round lips)", "Mmm (closed mouth hum)"],
        "watchFor": [
          "your baby watching your mouth closely.",
          "Any attempt to vocalize, even if different.",
          "Smiling or giggling during play."
        ],
        "parentTip": "Perfect copying isn't the goal — any sound attempt shows active listening!",
        "funTwist": "Use a puppet to 'echo' too — make it a three-way game!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Rhythm Echo Game",
      "duration": "3-4 min",
      "focus": "Pattern Recognition",
      "researchBasis": "Trainor (2005) - Rhythm perception supports language timing and speech flow.",
      "content": {
        "goal": "Combine sound with rhythm to strengthen memory and attention.",
        "whatToSay": [
          "\"Clap, clap, clap! Can you clap like me?\"",
          "\"Tap, pause, tap! Your turn!\"",
          "\"Great job copying the pattern!\""
        ],
        "whatToDo": [
          "Create simple rhythmic patterns (clap-clap-pause).",
          "Model clearly with exaggerated movements.",
          "Encourage your baby to copy the pattern.",
          "Alternate between fast and slow rhythms.",
          "Add sound to rhythm (clap + 'bah!')."
        ],
        "watchFor": [
          "Attempting to clap or move with rhythm.",
          "Anticipating the pattern after repetition.",
          "Vocalizing along with movements."
        ],
        "parentTip": "If your baby doesn't copy exactly, match their rhythm instead — it builds turn-taking!",
        "funTwist": "Add body parts: tap knees, pat head, clap hands in sequence!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Quiet Listening Moments",
      "duration": "2-3 min",
      "focus": "Attention & Self-Regulation",
      "researchBasis": "Fernald & Kuhl (1987) - Pauses in communication strengthen anticipation and focus.",
      "content": {
        "goal": "Build the ability to wait, listen, and respond thoughtfully.",
        "whatToSay": [
          "\"Shhh... listen... what comes next?\"",
          "\"Quiet time... then we sing!\"",
          "\"You waited so nicely — here comes the sound!\""
        ],
        "whatToDo": [
          "Create a quiet moment together (5-10 seconds).",
          "Make a gentle sound after the pause.",
          "Wait for your baby to react or imitate.",
          "Repeat pattern: quiet → sound → imitation.",
          "End with cuddles and praise."
        ],
        "watchFor": [
          "Staying calm during quiet moments.",
          "Eyes focused on you expectantly.",
          "Immediate response after sound appears."
        ],
        "parentTip": "Silence teaches listening just as much as sound — embrace the pause!",
        "funTwist": "Play 'sound surprise' — make funny sounds after each quiet moment!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Amazing listening, your baby!",
          "You practiced:",
          "✓ Copying sounds",
          "✓ Following rhythms",
          "✓ Waiting and listening quietly"
        ],
        "reviewQuestions": [
          "Which sound did your baby copy best?",
          "Did they enjoy the rhythm or vocal sounds more?",
          "How did they handle the quiet moments?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Echo games develop auditory memory, self-control, and imitation — three key skills that form the foundation for speech clarity and reading fluency."
        },
        "nextSteps": [
          "Use echo games during transitions (before meals, bathtime).",
          "Copy your baby's sounds too — it builds confidence!",
          "Add echo play to songs and nursery rhymes."
        ]
      }
    }
  ]
},

// ---------- LESSON 11 (Week 3 Day 1) ----------
{
  "id": 11,
  "phase": "1-2",
  "quarter": 1,
  "week": 3,
  "weekIndex": 2,
  "dayInWeek": 0,
  "title": "Lesson 11: Hello Echo! — Parent-Child Sound Call-and-Response",
  "theme": "Voice & Echo Play",
  "skillRecurrenceTags": ["turn-taking", "vocal-play", "social-communication"],
  "relatedToPreviousSkills": ["imitation", "listening", "sound-awareness"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Call-and-Response!",
      "duration": "1 min",
      "content": {
        "goal": "Strengthen conversational turn-taking through vocal play and echo games.",
        "parentGuide": [
          "Call-and-response teaches the rhythm of conversation — foundational for all communication.",
          "These games help your baby understand that communication is a two-way exchange.",
          "Respond to every sound your baby makes — it reinforces their voice matters.",
          "Keep it playful and joyful — no pressure for 'correct' responses."
        ],
        "materials": ["Your voice", "Comfortable sitting space", "Optional: mirror"],
        "tip": "Exaggerate your expressions — big smiles and animated faces keep engagement high.",
        "researchNote": "Bruner (1983) - Turn-taking games form the scaffold for language pragmatics and social communication."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Hello and Answer Game",
      "duration": "3-4 min",
      "focus": "Turn-Taking Foundation",
      "researchBasis": "Snow (1977) - Turn-taking in vocal play directly predicts later conversational competence.",
      "content": {
        "goal": "Establish simple call-and-response patterns through greeting sounds.",
        "whatToSay": [
          "\"Hello!\" (wait for response)",
          "\"Hi there!\" (pause and smile)",
          "\"Can you say hello back?\"",
          "\"You said hi! I heard you!\""
        ],
        "whatToDo": [
          "Say 'hello' clearly and wait 5 seconds.",
          "Accept any vocalization as a response.",
          "Reply with enthusiasm to every sound.",
          "Vary your tone (high, low, sing-song).",
          "Take turns 4-5 times."
        ],
        "examples": ["Hello → baby babbles → 'You said hello!'", "Hi → baby coos → 'Hi to you too!'"],
        "watchFor": [
          "your baby vocalizing after your greeting.",
          "Eye contact during exchanges.",
          "Smiling or excited movements when you respond."
        ],
        "parentTip": "If your baby doesn't respond immediately, model both sides: 'Hello! (pause) Hi mama!'",
        "funTwist": "Use different languages: 'Hola!' 'Bonjour!' 'Ciao!' for variety!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sound Copy Exchange",
      "duration": "3-4 min",
      "focus": "Vocal Imitation",
      "researchBasis": "Goldstein & Schwade (2008) - Contingent vocal responses accelerate babbling development.",
      "content": {
        "goal": "Create a playful back-and-forth sound exchange to strengthen vocal confidence.",
        "whatToSay": [
          "\"I'll make a sound, then you make one!\"",
          "\"Bah! Your turn!\"",
          "\"I love your sound! Now mine: Dah!\"",
          "\"We're having a sound conversation!\""
        ],
        "whatToDo": [
          "Make a simple consonant-vowel sound (ba, da, ma).",
          "Wait expectantly for your baby to respond.",
          "Copy whatever sound they make back to them.",
          "Alternate: you speak, they speak, repeat.",
          "End with laughter and praise."
        ],
        "watchFor": [
          "your baby trying new sounds.",
          "Increased vocalization frequency.",
          "Anticipating their 'turn' to speak."
        ],
        "parentTip": "Copying your baby's sounds shows them their voice has power — it encourages more talking!",
        "funTwist": "Use animal sounds in call-and-response: 'Moo!' (pause) 'Woof!' (pause)"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Name Call Game",
      "duration": "2-3 min",
      "focus": "Name Recognition & Response",
      "researchBasis": "Mandel et al. (1995) - Name recognition is among the earliest word comprehension milestones.",
      "content": {
        "goal": "Strengthen response to name through playful vocal games.",
        "whatToSay": [
          "\"your baby!\" (pause)",
          "\"Yes! You looked at me!\"",
          "\"your baby, where are you?\"",
          "\"There you are! You answered!\""
        ],
        "whatToDo": [
          "Call your baby from across the room or while hidden.",
          "Wait for them to turn, look, or vocalize.",
          "Celebrate with claps and smiles.",
          "Repeat from different positions.",
          "End with a big hug when they 'find' you."
        ],
        "watchFor": [
          "Turning head toward voice.",
          "Smiling or vocalizing when name is called.",
          "Searching for you with eyes."
        ],
        "parentTip": "This game builds auditory attention and social responsiveness — key for classroom readiness!",
        "funTwist": "Play 'Marco Polo' style: you call name, they make a sound back!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 What a wonderful conversation, your baby!",
          "You practiced:",
          "✓ Taking turns with sounds",
          "✓ Responding to greetings",
          "✓ Recognizing your name"
        ],
        "reviewQuestions": [
          "Did your baby respond to their name consistently?",
          "Which sounds did they enjoy making most?",
          "Did they show excitement during turn-taking?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Call-and-response play teaches the fundamental rhythm of conversation — waiting, listening, and responding — skills essential for all social communication and reading comprehension."
        },
        "nextSteps": [
          "Use name-calling during hide-and-seek games.",
          "Practice greetings with family members.",
          "Continue sound exchanges throughout the day casually."
        ]
      }
    }
  ]
},

// ---------- LESSON 12 (Week 3 Day 2) ----------
{
  "id": 12,
  "phase": "1-2",
  "quarter": 1,
  "week": 3,
  "weekIndex": 2,
  "dayInWeek": 1,
  "title": "Lesson 12: High Voice, Low Voice — Pitch Exploration",
  "theme": "Voice & Echo Play",
  "skillRecurrenceTags": ["pitch-awareness", "vocal-variety", "auditory-discrimination"],
  "relatedToPreviousSkills": ["sound-awareness", "imitation", "listening"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Voice Play!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce pitch differences to expand vocal range and auditory awareness.",
        "parentGuide": [
          "Exploring pitch helps your baby develop vocal control and expressiveness.",
          "High and low voices teach auditory discrimination — hearing differences in sound.",
          "This skill supports later intonation, reading fluency, and emotional expression.",
          "Make it silly and fun — laughter enhances learning!"
        ],
        "materials": ["Your voice", "Optional: toy animals (big and small)", "Optional: musical toy"],
        "tip": "Use exaggerated pitch changes — it helps your baby hear the contrast clearly.",
        "researchNote": "Fernald (1989) - Infants prefer and respond more to varied pitch in infant-directed speech."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: High and Low Discovery",
      "duration": "3-4 min",
      "focus": "Pitch Contrast",
      "researchBasis": "Trehub et al. (1997) - Infants detect pitch changes, forming foundation for musical and linguistic perception.",
      "content": {
        "goal": "Help your baby distinguish between high and low voices through demonstration.",
        "whatToSay": [
          "\"This is my high voice!\" (speak in high pitch)",
          "\"Now my low voice!\" (speak in low pitch)",
          "\"Can you hear the difference?\"",
          "\"High, high, high! Low, low, low!\""
        ],
        "whatToDo": [
          "Demonstrate high voice with raised eyebrows and smile.",
          "Switch to low voice with serious or funny face.",
          "Alternate several times slowly.",
          "Encourage your baby to watch your face and mouth.",
          "Pair with gestures (reach up high, crouch down low)."
        ],
        "examples": ["'Hello!' (high) vs 'Hello!' (low)", "'your baby!' (high) vs 'your baby!' (low)"],
        "watchFor": [
          "your baby smiling or laughing at pitch changes.",
          "Attempting to vocalize in different pitches.",
          "Following your gestures (looking up/down)."
        ],
        "parentTip": "Pitch play is naturally engaging — most babies respond immediately to silly voices!",
        "funTwist": "Use stuffed animals: 'Mouse talks high! Bear talks low!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Pitch Imitation Game",
      "duration": "3-4 min",
      "focus": "Vocal Exploration",
      "researchBasis": "Kuhl (2004) - Vocal imitation with varied prosody strengthens speech perception.",
      "content": {
        "goal": "Encourage your baby to experiment with their own voice pitch.",
        "whatToSay": [
          "\"Can you make a high sound? Eeeee!\"",
          "\"Now a low sound! Ohhhhh!\"",
          "\"You're using your voice in new ways!\"",
          "\"Let's go up and down: Eeee-Ohhh!\""
        ],
        "whatToDo": [
          "Model high pitch with animated expression.",
          "Wait for your baby to attempt any sound.",
          "Praise all vocal attempts enthusiastically.",
          "Switch between high and low multiple times.",
          "Add movement: jump for high, squat for low."
        ],
        "watchFor": [
          "your baby attempting different vocal tones.",
          "Giggling during pitch changes.",
          "Matching movements to pitch (bouncing, bending)."
        ],
        "parentTip": "Don't worry if your baby can't match pitch exactly — any vocal experimentation is progress!",
        "funTwist": "Sing a familiar song in high voice, then low voice — see which they prefer!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: High-Low Story Time",
      "duration": "2-3 min",
      "focus": "Pitch in Context",
      "researchBasis": "Kitamura & Burnham (2003) - Varied prosody in speech increases infant attention and comprehension.",
      "content": {
        "goal": "Integrate pitch awareness into storytelling for emotional expression.",
        "whatToSay": [
          "\"The little mouse said (high voice) 'Help me!'\"",
          "\"The big bear said (low voice) 'I'll help you!'\"",
          "\"Can you talk like the mouse? Like the bear?\""
        ],
        "whatToDo": [
          "Tell a simple story using high and low character voices.",
          "Use toys or your hands as characters.",
          "Pause and encourage your baby to make sounds.",
          "Repeat with different animals or characters.",
          "End with everyone using silly voices together."
        ],
        "watchFor": [
          "Increased attention during story with varied voices.",
          "Attempting to imitate character voices.",
          "Pointing or reaching toward characters."
        ],
        "parentTip": "Varied voices in stories boost engagement and teach emotional tone — both key for reading!",
        "funTwist": "Let your baby choose who talks high or low by pointing!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Your voice is amazing, your baby!",
          "You practiced:",
          "✓ Hearing high and low sounds",
          "✓ Using different voice pitches",
          "✓ Playing with story voices"
        ],
        "reviewQuestions": [
          "Did your baby try making high or low sounds?",
          "Which pitch made them laugh more?",
          "Did they show interest in character voices?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Pitch exploration develops vocal flexibility, auditory discrimination, and emotional expressiveness — all essential for clear speech, reading with expression, and social communication."
        },
        "nextSteps": [
          "Use high and low voices during playtime.",
          "Read books with character voices regularly.",
          "Sing songs with pitch variations."
        ]
      }
    }
  ]
},

// ---------- LESSON 13 (Week 3 Day 3) ----------
{
  "id": 13,
  "phase": "1-2",
  "quarter": 1,
  "week": 3,
  "weekIndex": 2,
  "dayInWeek": 2,
  "title": "Lesson 13: Funny Faces, Funny Sounds — Mouth Movement Awareness",
  "theme": "Voice & Echo Play",
  "skillRecurrenceTags": ["oral-motor", "articulation-prep", "imitation"],
  "relatedToPreviousSkills": ["sound-awareness", "imitation", "facial-recognition"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Face Fun!",
      "duration": "1 min",
      "content": {
        "goal": "Build awareness of how mouth movements create different sounds — the foundation of articulation.",
        "parentGuide": [
          "Watching and imitating mouth movements helps your baby understand how speech is made.",
          "These exercises strengthen oral motor skills needed for clear pronunciation.",
          "Silly faces make learning fun and memorable.",
          "Don't expect perfect imitation — any attempt strengthens the connection."
        ],
        "materials": ["Mirror (optional)", "Your animated face", "Silly enthusiasm"],
        "tip": "Get close to your baby's face and exaggerate all movements — visibility is key!",
        "researchNote": "Kuhl & Meltzoff (1996) - Watching mouth movements enhances phonetic learning in infants."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Silly Face Parade",
      "duration": "3-4 min",
      "focus": "Facial Imitation",
      "researchBasis": "Meltzoff & Moore (1977) - Infants imitate facial movements from birth, building motor-visual integration.",
      "content": {
        "goal": "Encourage your baby to watch and copy different facial expressions and mouth shapes.",
        "whatToSay": [
          "\"Look at my silly face!\" (stick out tongue)",
          "\"Can you do this?\" (puff cheeks)",
          "\"Now this!\" (open mouth wide)",
          "\"You're making funny faces too!\""
        ],
        "whatToDo": [
          "Make exaggerated facial expressions one at a time.",
          "Hold each face for 3-5 seconds.",
          "Encourage your baby to copy.",
          "Praise any attempt — smiles, tongue movements, sounds.",
          "Cycle through 4-5 different faces."
        ],
        "examples": ["Stick tongue out", "Puff cheeks big", "Kiss face (pucker lips)", "Wide open mouth", "Scrunch nose"],
        "watchFor": [
          "your baby watching your face intently.",
          "Attempting to move their mouth or tongue.",
          "Smiling or laughing at silly faces."
        ],
        "parentTip": "Use a mirror so your baby can see their own face — it doubles the learning!",
        "funTwist": "Take turns: you make a face, then your baby 'teaches' you one!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sound and Face Match",
      "duration": "3-4 min",
      "focus": "Articulation Awareness",
      "researchBasis": "Vihman et al. (1986) - Visual-motor imitation of speech sounds accelerates babbling development.",
      "content": {
        "goal": "Connect specific mouth movements with their corresponding sounds.",
        "whatToSay": [
          "\"When I say 'Mmmm,' my lips come together — see?\"",
          "\"'Bah!' — watch my lips pop open!\"",
          "\"Can you make 'Puh' like this?\" (demonstrate)",
          "\"Your mouth is making sounds!\""
        ],
        "whatToDo": [
          "Show clear mouth position for each sound (m, b, p, d).",
          "Say sound slowly while your baby watches.",
          "Pause and invite imitation.",
          "Celebrate any vocalization or mouth movement.",
          "Repeat each sound 2-3 times."
        ],
        "examples": ["Mmmm (lips together, hum)", "Bah (lips pop)", "Puh (puff of air)", "Dah (tongue to top)"],
        "watchFor": [
          "your baby focusing on your mouth.",
          "Attempting to move lips or tongue similarly.",
          "Making sounds while exploring mouth positions."
        ],
        "parentTip": "These are the building blocks of first words — 'mama,' 'baba,' 'dada' all start here!",
        "funTwist": "Play 'mirror match' — both make the same face and sound together!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Animal Sound Faces",
      "duration": "2-3 min",
      "focus": "Integration & Play",
      "researchBasis": "Hart & Risley (1995) - Playful repetition in varied contexts strengthens language retention.",
      "content": {
        "goal": "Combine facial movements with familiar animal sounds for joyful practice.",
        "whatToSay": [
          "\"Fish face — open and close!\" (demonstrate)",
          "\"Lion roar!\" (open wide and growl)",
          "\"Snake tongue!\" (stick tongue in and out)",
          "\"You're a silly animal!\""
        ],
        "whatToDo": [
          "Act out each animal with face and sound.",
          "Encourage your baby to copy movements.",
          "Add body movements (crawl like snake, stretch like lion).",
          "Repeat favorite animals multiple times.",
          "End with giggles and animal parade."
        ],
        "watchFor": [
          "Attempting animal faces or sounds.",
          "Laughing and engaging enthusiastically.",
          "Requesting favorite animals again (pointing, vocalizing)."
        ],
        "parentTip": "Animal play makes oral motor practice feel like a game, not a lesson!",
        "funTwist": "Let your baby 'teach' you their version of each animal!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Such silly, wonderful faces, your baby!",
          "You practiced:",
          "✓ Making funny faces",
          "✓ Watching mouth movements",
          "✓ Connecting sounds to faces"
        ],
        "reviewQuestions": [
          "Which silly face made your baby laugh most?",
          "Did they try imitating any mouth movements?",
          "Which animal sound was their favorite?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Mouth movement awareness builds the physical foundation for speech clarity. Watching and imitating helps your baby understand how to form sounds — essential for first words and clear articulation."
        },
        "nextSteps": [
          "Make silly faces during diaper changes or meals.",
          "Point out your mouth when you talk to your baby.",
          "Play mirror games regularly for self-awareness."
        ]
      }
    }
  ]
},

// ---------- LESSON 14 (Week 3 Day 4) ----------
{
  "id": 14,
  "phase": "1-2",
  "quarter": 1,
  "week": 3,
  "weekIndex": 2,
  "dayInWeek": 3,
  "title": "Lesson 14: Sound Hide-and-Seek — Guess the Noise!",
  "theme": "Voice & Echo Play",
  "skillRecurrenceTags": ["auditory-discrimination", "sound-identification", "attention"],
  "relatedToPreviousSkills": ["listening", "sound-awareness", "object-recognition"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sound Detective!",
      "duration": "1 min",
      "content": {
        "goal": "Develop auditory discrimination by identifying and locating hidden sounds.",
        "parentGuide": [
          "Sound identification strengthens listening focus and auditory memory.",
          "These skills help your baby differentiate between similar sounds — crucial for phonics later.",
          "Hide-and-seek adds problem-solving and spatial awareness.",
          "Keep it playful — discovery is the reward!"
        ],
        "materials": ["Small bell or rattle", "Crinkly paper", "Musical toy", "Lightweight blanket or cloth"],
        "tip": "Start with obvious hiding spots, then gradually make it more challenging.",
        "researchNote": "Aslin et al. (1998) - Sound localization skills strengthen auditory processing and attention control."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: What's That Sound?",
      "duration": "3-4 min",
      "focus": "Sound Recognition",
      "researchBasis": "Jusczyk & Aslin (1995) - Infants build mental catalogs of familiar sounds through repeated exposure.",
      "content": {
        "goal": "Help your baby identify common sounds before hiding them.",
        "whatToSay": [
          "\"Listen! Shake, shake, shake — that's the rattle!\"",
          "\"Hear the bell? Ding, ding, ding!\"",
          "\"This paper goes crinkle, crinkle!\"",
          "\"Can you remember these sounds?\""
        ],
        "whatToDo": [
          "Show each sound-making object clearly.",
          "Make its sound 2-3 times.",
          "Let your baby touch and explore each item.",
          "Name the sound each time ('bell sound!', 'rattle sound!').",
          "Review all three sounds before hiding."
        ],
        "examples": ["Bell: 'ding-ding'", "Rattle: 'shake-shake'", "Paper: 'crinkle-crinkle'"],
        "watchFor": [
          "your baby watching and listening intently.",
          "Reaching for objects that make sounds.",
          "Attempting to make sounds themselves."
        ],
        "parentTip": "Familiarity first — your baby needs to know the sounds before finding them!",
        "funTwist": "Let your baby make each sound first, then you hide it!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Find the Hidden Sound",
      "duration": "3-4 min",
      "focus": "Auditory Localization",
      "researchBasis": "Morrongiello et al. (1994) - Sound localization games enhance spatial hearing and attention.",
      "content": {
        "goal": "Encourage your baby to locate sounds hidden under cloths or behind objects.",
        "whatToSay": [
          "\"Where's the bell? Listen... ding ding!\"",
          "\"Can you find it?\"",
          "\"You found the sound! Great listening!\"",
          "\"Let's hide another one!\""
        ],
        "whatToDo": [
          "Hide one object under a light cloth while your baby watches.",
          "Make the sound from under the cloth.",
          "Encourage your baby to lift cloth or reach for it.",
          "Celebrate discovery enthusiastically.",
          "Repeat with different objects and locations."
        ],
        "watchFor": [
          "Turning head toward sound source.",
          "Reaching or crawling toward hidden object.",
          "Smiling or excited when finding sound."
        ],
        "parentTip": "Start easy with partially visible objects, then progress to fully hidden!",
        "funTwist": "Hide sounds in different rooms and go on a 'sound safari'!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Guess the Mystery Sound",
      "duration": "2-3 min",
      "focus": "Auditory Memory",
      "researchBasis": "Saffran et al. (1996) - Auditory pattern recognition supports language learning and memory.",
      "content": {
        "goal": "Build sound memory by identifying sounds without seeing the object.",
        "whatToSay": [
          "\"Close your eyes — what sound is this?\"",
          "\"Is it the bell or the rattle?\"",
          "\"You knew it! That's the crinkly paper!\"",
          "\"Your ears are so smart!\""
        ],
        "whatToDo": [
          "Make a sound behind your back or under blanket.",
          "Ask your baby to identify it.",
          "Reveal object to confirm.",
          "Accept pointing, vocalizing, or reaching as answers.",
          "Repeat with all three sounds randomly."
        ],
        "watchFor": [
          "Recognition responses (smiling, reaching correctly).",
          "Attempting to vocalize sound names.",
          "Anticipation before reveal."
        ],
        "parentTip": "Memory games like this build auditory processing — essential for phonics awareness!",
        "funTwist": "Let your baby hide sounds for you to find — role reversal builds confidence!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Amazing detective work, your baby!",
          "You practiced:",
          "✓ Listening to different sounds",
          "✓ Finding hidden noises",
          "✓ Remembering sounds"
        ],
        "reviewQuestions": [
          "Which sound did your baby find first?",
          "Did they enjoy the hide-and-seek element?",
          "Could they identify sounds without seeing objects?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Sound identification and localization strengthen auditory discrimination — the ability to hear differences between sounds. This skill is fundamental for distinguishing letter sounds in reading."
        },
        "nextSteps": [
          "Play sound guessing games with household items.",
          "Hide musical toys during playtime.",
          "Name sounds throughout the day ('I hear the phone!', 'That's the door!')."
        ]
      }
    }
  ]
},

// ---------- LESSON 15 (Week 3 Day 5) ----------
{
  "id": 15,
  "phase": "1-2",
  "quarter": 1,
  "week": 3,
  "weekIndex": 2,
  "dayInWeek": 4,
  "title": "Lesson 15: Mix and Match Sounds — Identify Two Together",
  "theme": "Voice & Echo Play",
  "skillRecurrenceTags": ["auditory-discrimination", "multi-sensory", "attention"],
  "relatedToPreviousSkills": ["sound-awareness", "listening", "auditory-memory"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sound Mixing!",
      "duration": "1 min",
      "content": {
        "goal": "Develop the ability to distinguish and identify multiple sounds occurring together.",
        "parentGuide": [
          "Distinguishing overlapping sounds builds auditory processing — key for noisy classrooms later.",
          "This skill helps your baby filter important sounds from background noise.",
          "Multi-sound awareness supports phoneme blending (combining sounds into words).",
          "Keep combinations simple — two sounds maximum at this age."
        ],
        "materials": ["Two different sound toys (bell + rattle)", "Drum or pot", "Shaker or maraca", "Your voice"],
        "tip": "Present sounds separately first, then combine — it helps your baby recognize each component.",
        "researchNote": "Werner & Marean (1996) - Infants develop complex auditory scene analysis gradually through exposure."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Two Sound Introduction",
      "duration": "3-4 min",
      "focus": "Sound Differentiation",
      "researchBasis": "Trainor & Trehub (1992) - Infants can distinguish simultaneous sounds with clear acoustic differences.",
      "content": {
        "goal": "Help your baby recognize two distinct sounds before hearing them together.",
        "whatToSay": [
          "\"This is the bell — ding ding!\"",
          "\"This is the drum — boom boom!\"",
          "\"Two different sounds! Can you hear both?\"",
          "\"Which one do you like better?\""
        ],
        "whatToDo": [
          "Present first sound clearly (bell).",
          "Let your baby explore it for 30 seconds.",
          "Present second sound (drum).",
          "Let your baby explore it too.",
          "Alternate between sounds several times.",
          "Name each sound clearly when played."
        ],
        "examples": ["Bell + drum", "Rattle + shaker", "Clap + stomp"],
        "watchFor": [
          "your baby looking between both sound sources.",
          "Showing preference for one sound.",
          "Attempting to make both sounds themselves."
        ],
        "parentTip": "Choose sounds with very different qualities — high vs. low, soft vs. loud.",
        "funTwist": "Play 'which hand?' — hide a sound toy in each hand and shake one at a time!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sound Together Time",
      "duration": "3-4 min",
      "focus": "Simultaneous Processing",
      "researchBasis": "Bahrick & Lickliter (2000) - Multi-sensory experiences enhance attention and learning in infants.",
      "content": {
        "goal": "Introduce overlapping sounds and help your baby identify both components.",
        "whatToSay": [
          "\"Now I'll play them together! Bell AND drum!\"",
          "\"Can you hear both sounds?\"",
          "\"Which one is louder? Which one is quieter?\"",
          "\"You're listening to two things at once!\""
        ],
        "whatToDo": [
          "Play both sounds simultaneously but distinctly.",
          "Pause and ask 'what did you hear?'",
          "Demonstrate each sound separately again.",
          "Repeat combined sounds 2-3 times.",
          "Encourage your baby to point or reach for each source."
        ],
        "watchFor": [
          "Eyes moving between both sound sources.",
          "Attempting to vocalize or imitate either sound.",
          "Increased focus during combined sounds."
        ],
        "parentTip": "If your baby seems confused, separate sounds more clearly in space (left hand vs. right hand).",
        "funTwist": "Create a 'band' — you play one, your baby plays the other together!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Matching Game",
      "duration": "2-3 min",
      "focus": "Recognition & Memory",
      "researchBasis": "Saffran et al. (1996) - Pattern recognition through varied exposure strengthens auditory memory.",
      "content": {
        "goal": "Help your baby identify which sounds were in the combination they just heard.",
        "whatToSay": [
          "\"Which sounds did we hear? Was it the bell?\"",
          "\"Yes! And what else? The drum!\"",
          "\"You remembered both sounds!\"",
          "\"Let's try a new mix!\""
        ],
        "whatToDo": [
          "Play combination of two sounds.",
          "Show 3-4 objects and ask your baby to identify which were used.",
          "Accept pointing, reaching, or vocalizing.",
          "Confirm by playing each sound individually.",
          "Celebrate correct identifications enthusiastically.",
          "Try different combinations (rattle + voice, clap + shake)."
        ],
        "watchFor": [
          "Correctly identifying one or both sounds.",
          "Processing time before responding.",
          "Growing confidence with practice."
        ],
        "parentTip": "This is advanced listening — any correct identification shows real progress!",
        "funTwist": "Make 'sound recipes' — 'one drum plus one bell makes music!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Incredible listening, your baby!",
          "You practiced:",
          "✓ Hearing two sounds separately",
          "✓ Listening to sounds together",
          "✓ Identifying multiple sounds"
        ],
        "reviewQuestions": [
          "Could your baby distinguish between the two sounds?",
          "Did they show interest when sounds played together?",
          "Which sound combination engaged them most?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Processing multiple sounds simultaneously builds auditory filtering skills — essential for understanding speech in noisy environments and later for blending phonemes into words during reading."
        },
        "nextSteps": [
          "Play background music while talking to your baby.",
          "Name multiple sounds in busy environments ('car, birds, wind').",
          "Create simple sound combinations during bath or play."
        ]
      }
    }
  ]
},

    // ---------- LESSON 16 (Week 4 Day 1) ----------
    {
      "id": 16,
    "phase": "1-2",
    "quarter": 1,
    "week": 4,
    "weekIndex": 3,
    "dayInWeek": 0,
    "title": "Lesson 16: Sound Band Day — Homemade Instruments",
    "theme": "Review Through Music & Echo Games",
    "skillRecurrenceTags": ["sound-exploration", "rhythm-awareness", "creative-play"],
    "relatedToPreviousSkills": ["animal-sounds-week1", "rhythm-clapping-week1", "everyday-sounds-week2"],
    "duration": "10-15 min",
    "steps": [
      {
        "id": 0,
        "type": "intro",
        "title": "Welcome to Sound Band Day!",
        "duration": "1 min",
        "content": {
          "goal": "Create and play simple homemade instruments to review sound recognition, rhythm patterns, and turn-taking from Weeks 1-3",
          "parentGuide": [
            "This review lesson brings together everything your baby has explored: recognizing different sounds, keeping a beat, and responding to patterns",
            "Making music together strengthens auditory discrimination—the ability to hear differences between sounds, which is essential for distinguishing letter sounds later",
            "At this age, your baby is building neural pathways every time they hear rhythm and repetition, even if they don't create sounds perfectly yet",
            "Celebrate all attempts—shaking, banging, or just listening are all valuable forms of musical engagement",
            "This playful review helps consolidate learning without feeling like a test"
          ],
          "materials": ["empty plastic containers with lids", "rice or dried beans", "wooden spoons", "pots or plastic bowls", "paper towel tubes", "tape"],
          "tip": "Let your baby explore each instrument freely for 30 seconds before you demonstrate—curiosity drives learning better than instruction",
          "researchNote": "Trainor et al. (2002) - Rhythmic engagement enhances infants' phonological awareness and auditory pattern recognition"
        }
      },
      {
        "id": 1,
        "type": "activity",
        "title": "Step 1: Build Our Band",
        "duration": "3-4 min",
        "focus": "Sensory exploration and cause-effect understanding",
        "researchBasis": "Piaget (1952) - Hands-on manipulation develops cognitive schemas for understanding the physical world",
        "content": {
          "goal": "Create 2-3 simple instruments together, naming sounds as you go",
          "whatToSay": [
            "\"Let's make a shaker! We'll put rice inside—listen to this sound!\"",
            "\"Shake, shake, shake! Do you hear that? That's OUR music!\"",
            "\"This spoon and pot make a loud BANG! Can you try?\"",
            "\"Gentle tap, tap, tap. Now a BIG bang! Which sound do you like?\""
          ],
          "whatToDo": [
            "Fill a plastic container halfway with rice or beans and seal it securely with tape",
            "Hand it to your baby and demonstrate shaking: \"Shake, shake, shake!\"",
            "Set out a pot and wooden spoon; tap it gently, then let your baby try",
            "Show your baby how to hold and tap, but don't correct their grip—exploration matters most",
            "Create a third instrument: paper towel tube to tap on the floor or hum through",
            "Name each sound as your baby plays: \"That's a jingle!\" \"I hear a boom!\""
          ],
          "examples": [
            "Rice shaker = \"jingle-jingle sound\"",
            "Wooden spoon on pot = \"boom-boom sound\"",
            "Paper tube on floor = \"thump-thump sound\""
          ],
          "watchFor": [
            "your baby looking at the instrument after making a sound (understanding cause-effect)",
            "Attempting to imitate your shaking or tapping motion",
            "Switching between instruments—showing sound discrimination",
            "Smiling or laughing when sounds are made"
          ],
          "parentTip": "If your baby only wants to mouth the instruments, that's developmentally perfect! Oral exploration teaches them about texture and safety",
          "funTwist": "Add colorful stickers or ribbons to each instrument so your baby can also identify them visually"
        }
      },
      {
        "id": 2,
        "type": "activity",
        "title": "Step 2: Play the Rhythm Game",
        "duration": "4-5 min",
        "focus": "Pattern recognition and imitation skills",
        "researchBasis": "Saffran et al. (1996) - Infants recognize auditory patterns after brief exposure, building prediction skills crucial for language",
        "content": {
          "goal": "Establish simple rhythm patterns and invite your baby to copy or respond",
          "whatToSay": [
            "\"Watch me! Tap-tap-pause-tap-tap. Now you try!\"",
            "\"Shake-shake-STOP! Can you do shake-shake-STOP?\"",
            "\"Let's go SLOW... tap... tap... tap. Now FAST! TapTapTap!\"",
            "\"I hear your baby making music! You're a musician!\""
          ],
          "whatToDo": [
            "Choose one instrument and create a very simple pattern: two taps, pause, two taps",
            "Repeat the pattern 3 times while saying \"tap-tap-pause-tap-tap\"",
            "Hand the instrument to your baby and say \"Your turn!\"",
            "Celebrate any response—even one tap counts as participation",
            "Try a different pattern with the shaker: \"Shake-shake-STOP!\" with exaggerated pauses",
            "Switch roles: let your baby create sounds while you imitate THEIR rhythm",
            "Add your voice: hum or sing \"la-la-la\" while tapping to connect sound and rhythm"
          ],
          "examples": [
            "Two quick taps + long pause = your baby learns to anticipate the pause",
            "Loud-soft-loud pattern = introduces dynamic variation",
            "You tap, baby taps, you tap = early turn-taking structure"
          ],
          "watchFor": [
            "your baby watching your hands intently before their turn",
            "Any attempt to match your rhythm, even if imperfect",
            "Stopping when you stop—showing they're tracking the pattern",
            "Smiling during the pause (anticipation of what comes next)"
          ],
          "parentTip": "Don't worry if your baby doesn't copy exactly—they're learning the CONCEPT of patterns, which is the real goal",
          "funTwist": "Add animal sounds from Week 1: \"Tap like a bunny—fast! Now moo like a cow—slow and loud!\""
        }
      },
      {
        "id": 3,
        "type": "activity",
        "title": "Step 3: Sound Band Parade",
        "duration": "3-4 min",
        "focus": "Gross motor integration and joyful review",
        "researchBasis": "Bruner (1983) - Movement combined with language creates stronger memory encoding and engagement",
        "content": {
          "goal": "Move around the space while making music, integrating sound recognition from all previous weeks",
          "whatToSay": [
            "\"Let's march like we're in a parade! Can you shake your shaker while we walk?\"",
            "\"Stop! Freeze like a statue! Now—BANG the drum!\"",
            "\"Let's tiptoe like little mice... tap-tap-tap so quietly!\"",
            "\"Now stomp like elephants! BOOM-BOOM-BOOM! So loud!\""
          ],
          "whatToDo": [
            "Pick up instruments and start walking slowly around the room together",
            "Play your instrument while moving, encouraging your baby to do the same",
            "Every 10-15 seconds, call out \"Freeze!\" and stop completely—make it silly and fun",
            "During each freeze, try one sound: shake, tap, or bang together",
            "Change the movement style: march, tiptoe, stomp, sway",
            "Connect movements to animal sounds from Week 1: \"Hop like a bunny while you shake!\"",
            "End with a big finale: everyone plays all instruments at once, then BOW together"
          ],
          "examples": [
            "Marching with shaker = steady beat + gross motor coordination",
            "Freezing on command = listening comprehension and impulse control",
            "Tiptoeing quietly = volume control and body awareness"
          ],
          "watchFor": [
            "your baby stopping when you say \"freeze\"—even if delayed",
            "Attempting to walk while holding an instrument",
            "Laughing during movement changes",
            "Making sounds in rhythm with their steps"
          ],
          "parentTip": "Hold your baby's hand if they need walking support—the goal is joyful participation, not independent movement",
          "funTwist": "Invite a sibling, other parent, or stuffed animal to join the parade—social learning amplifies engagement!"
        }
      },
      {
        "id": 5,
        "type": "conclusion",
        "title": "Activity Complete!",
        "duration": "1 min",
        "content": {
          "celebration": [
            "🎉 Amazing work, musician! You created a whole sound band today!",
            "You practiced:",
            "✓ Making different sounds with instruments",
            "✓ Following rhythm patterns with stops and starts",
            "✓ Moving and making music at the same time"
          ],
          "reviewQuestions": [
            "Which instrument made your baby smile the most?",
            "Did your baby try to copy any of your rhythm patterns?",
            "What surprised you about how your baby explored the sounds?"
          ],
          "parentTakeaway": {
            "title": "Why This Matters",
            "content": "Today's rhythm work builds the same pattern-recognition skills your baby will use to break words into sounds when learning to read. Every tap, shake, and march strengthens their brain's ability to detect patterns—the foundation of phonics."
          },
          "nextSteps": [
            "Keep your homemade instruments accessible for free play throughout the week",
            "Add rhythm to daily routines: tap the table during meals, shake toys during diaper changes",
            "Notice when your baby creates their own rhythms and imitate them—you're validating their musical experiments"
          ]
        }
      }
    ]
  },

    // ---------- LESSON 17 (Week 4 Day 2) ----------
    {
      "id": 17,
      "phase": "1-2",
      "quarter": 1,
      "week": 4,
      "weekIndex": 3,
      "dayInWeek": 1,
      "title": "Lesson 17: Echo Parade — March and Repeat Rhythms",
      "theme": "Review Through Music & Echo Games",
      "skillRecurrenceTags": ["echo-patterns", "imitation", "auditory-memory"],
      "relatedToPreviousSkills": ["voice-echo-week3", "rhythm-clapping-week1", "turn-taking"],
      "duration": "10-15 min",
      "steps": [
        {
          "id": 0,
          "type": "intro",
          "title": "Welcome to Echo Parade!",
          "duration": "1 min",
          "content": {
            "goal": "Practice call-and-response patterns through movement and voice, reviewing echo skills from Week 3 with added rhythm",
            "parentGuide": [
              "Echo games teach your baby to listen carefully and hold sounds in memory—the same skill they'll need to sound out words later",
              "Repetition doesn't bore babies; it builds neural pathways. Each echo strengthens the connection between hearing and producing sounds",
              "This lesson combines auditory memory with physical movement, engaging multiple brain regions simultaneously for deeper learning",
              "Even if your baby doesn't echo back perfectly, they're learning the conversational structure of language: I speak, you respond, I speak again",
              "Today's parade format makes review feel fresh and exciting rather than repetitive"
            ],
            "materials": ["none required", "optional: scarves or ribbons for waving"],
            "tip": "Use exaggerated facial expressions and gestures when you call—babies learn from visual cues as much as auditory ones",
            "researchNote": "Kuhl (2010) - Infant speech perception and imitation activate motor planning areas, building pathways for future speech production"
          }
        },
        {
          "id": 1,
          "type": "activity",
          "title": "Step 1: The Call and Echo Warm-Up",
          "duration": "3-4 min",
          "focus": "Vocal imitation and auditory attention",
          "researchBasis": "Fernald (1989) - Infant-directed speech with exaggerated intonation captures attention and enhances vocal learning",
          "content": {
            "goal": "Establish the echo pattern with simple sounds before adding movement",
            "whatToSay": [
              "\"I'm going to make a sound, and you can echo it back! Ready?\"",
              "\"Ahhhhh!\" (pause) \"Your turn—Ahhhhh!\"",
              "\"Oooooh!\" (pause) \"Echo—Oooooh!\"",
              "\"Bah-bah-bah!\" (pause) \"Can you say bah-bah-bah?\"",
              "\"I hear your baby making sounds! That's an echo!\""
            ],
            "whatToDo": [
              "Sit face-to-face with your baby at their eye level",
              "Make a simple vowel sound (\"ahhh\") for 2 seconds, then pause for 3-4 seconds",
              "Wait expectantly, smiling, to see if your baby responds with any sound",
              "Celebrate ANY vocalization: \"Yes! That's your echo!\"",
              "Try 3-4 different sounds: \"oooh,\" \"eee,\" \"bah-bah,\" \"mmm\"",
              "If your baby doesn't vocalize, echo their natural sounds instead—turn-taking works both ways",
              "Gradually increase the pause time to give your baby more processing time"
            ],
            "examples": [
              "You: \"Ahhhh\" → Baby makes any sound → You: \"Perfect echo!\"",
              "You: \"Bah-bah\" → Baby says \"ba\" → You: \"Yes! Bah!\"",
              "Baby babbles → You echo them exactly → Baby learns reciprocity"
            ],
            "watchFor": [
              "your baby making eye contact during the pause—they're thinking!",
              "Mouth movements even without sound (pre-vocalization)",
              "Any vocalization within 5 seconds of your sound",
              "Smiling or excitement when you respond to their sounds"
            ],
            "parentTip": "The pause is the most important part—resist filling the silence! your baby needs processing time to form their response",
            "funTwist": "Add hand motions to each sound: arms up for \"ahhh,\" hands out for \"oooh,\" pat knees for \"bah-bah-bah\""
          }
        },
        {
          "id": 2,
          "type": "activity",
          "title": "Step 2: March and Echo",
          "duration": "4-5 min",
          "focus": "Combining movement with vocal patterns",
          "researchBasis": "Trainor et al. (2005) - Movement synchronized with rhythm enhances infants' ability to encode and predict auditory patterns",
          "content": {
            "goal": "Add marching movements to echo patterns, integrating gross motor and language skills",
            "whatToSay": [
              "\"Let's march around the room! When I say a sound, you echo it back!\"",
              "\"March, march, march—Hup-hup-hup!\" (pause for echo)",
              "\"Big steps now—Boom-boom-boom!\" (pause for echo)",
              "\"Tiptoe time—Shhhhh-shhhhh-shhhhh!\" (pause for echo)",
              "\"Stomp like this—THUMP!\" (pause for echo)"
            ],
            "whatToDo": [
              "Stand up and hold your baby's hands if they need support, or let them crawl beside you",
              "Start marching in place or around the room at a steady pace",
              "Every 4-5 steps, stop and make a marching sound: \"Hup-hup-hup!\"",
              "Pause and wait for your baby to make any sound back",
              "Resume marching and repeat with a new sound",
              "Vary the movement style: big stomps, tiptoes, sideways steps, spinning in place",
              "Match your sound to the movement: loud for stomps, quiet for tiptoes",
              "End with a big \"STOP!\" and freeze together, then laugh and celebrate"
            ],
            "examples": [
              "March + \"Hup-hup-hup\" = connecting sound to steady beat",
              "Tiptoe + \"Shhh-shhh-shhh\" = volume control awareness",
              "Stomp + \"THUMP!\" = single loud sound vs. repeated pattern"
            ],
            "watchFor": [
              "your baby stopping when you stop (understanding the pattern)",
              "Attempting to vocalize during the pause, even if it's just breathing sounds",
              "Matching their movement energy to yours (stomping harder, tiptoeing lighter)",
              "Anticipating the stop—slowing down before you freeze"
            ],
            "parentTip": "If your baby is crawling, get down and crawl too! Matching their level builds connection and makes echoing more natural",
            "funTwist": "Add animal sounds from Week 1: march and roar like a lion, tiptoe and squeak like a mouse, stomp and trumpet like an elephant"
          }
        },
        {
          "id": 3,
          "type": "activity",
          "title": "Step 3: The Echo Parade Finale",
          "duration": "3-4 min",
          "focus": "Sequencing multiple echo patterns and celebrating review",
          "researchBasis": "Saffran et al. (1996) - Repeated exposure to sound sequences strengthens pattern memory and anticipation skills",
          "content": {
            "goal": "String together all the echo patterns from today into one continuous parade with celebration",
            "whatToSay": [
              "\"We're going to do ALL our echoes in one big parade! Can you keep up?\"",
              "\"March-march-march—Hup-hup-hup!\" (pause)",
              "\"Tiptoe-tiptoe—Shhh-shhh!\" (pause)",
              "\"Big stomp—BOOM!\" (pause)",
              "\"Spin around—Wheee!\" (pause)",
              "\"And... FREEZE! We did it!\""
            ],
            "whatToDo": [
              "Start the parade with a big announcement: \"Here comes our echo parade!\"",
              "Move through 4-5 different movement-sound combinations without stopping between them",
              "Keep each echo opportunity short (2-3 second pause) to maintain momentum",
              "Add visual excitement: wave your arms, jump, twirl, clap overhead",
              "Let your baby lead for 10-15 seconds—march behind them echoing THEIR sounds",
              "Build to a grand finale: \"One more time—EVERYONE ECHO: Yay!\"",
              "End with applause, high-fives, or a group hug"
            ],
            "examples": [
              "March → stomp → tiptoe → spin = varied movement sequence",
              "Loud → soft → loud = dynamic pattern",
              "You lead, baby leads, you lead = turn-taking practice"
            ],
            "watchFor": [
              "your baby attempting to switch movements when you do",
              "More consistent vocalization as the parade progresses (confidence building)",
              "Showing excitement or anticipation for the next pattern",
              "Laughing or smiling during the celebration"
            ],
            "parentTip": "Make note of which sounds your baby can do—you'll be amazed at their progress in a few weeks",
            "funTwist": "Invite siblings or other family members to join the parade—each person adds their own echo sound!"
          }
        },
        {
          "id": 5,
          "type": "conclusion",
          "title": "Activity Complete!",
          "duration": "1 min",
          "content": {
            "celebration": [
              "🎉 What an amazing echo parade! You're becoming such a great listener!",
              "You practiced:",
              "✓ Listening to sounds and echoing them back",
              "✓ Marching and making sounds at the same time",
              "✓ Following a sequence of different movements and sounds"
            ],
            "reviewQuestions": [
              "Which echo sound did your baby respond to most clearly?",
              "Did your baby start anticipating when to echo based on the pattern?",
              "How did movement seem to affect your baby's willingness to vocalize?"
            ],
            "parentTakeaway": {
              "title": "Why This Matters",
              "content": "Echo games teach your baby the fundamental structure of conversation and reading aloud: listen, process, respond. This back-and-forth pattern is the same skill they'll use when sounding out words—hear the sound, hold it in memory, reproduce it."
            },
            "nextSteps": [
              "Create echo moments throughout the day: during meals (\"Yum-yum!\" echo), diaper changes (\"All clean!\" echo), or bathtime",
              "Echo your baby's spontaneous sounds back to them—this validates their communication attempts",
              "Notice if your baby starts echoing environmental sounds (doors closing, phones ringing) on their own"
            ]
          }
        }
      ]
    },

    // ---------- LESSON 18 (Week 4 Day 3) ----------
    {
      "id": 18,
      "phase": "1-2",
      "quarter": 1,
      "week": 4,
      "weekIndex": 3,
      "dayInWeek": 2,
      "title": "Lesson 18: Guess That Sound Challenge",
      "theme": "Review Through Music & Echo Games",
      "skillRecurrenceTags": ["sound-discrimination", "auditory-memory", "problem-solving"],
      "relatedToPreviousSkills": ["sounds-around-us-week2", "sound-hide-and-seek-week3", "animal-sounds-week1"],
      "duration": "10-15 min",
      "steps": [
        {
          "id": 0,
          "type": "intro",
          "title": "Welcome to Guess That Sound!",
          "duration": "1 min",
          "content": {
            "goal": "Review sound recognition from Weeks 1-3 through playful guessing games that test auditory discrimination and memory",
            "parentGuide": [
              "Identifying sounds without seeing the source builds auditory discrimination—a critical pre-reading skill that helps children distinguish between similar letter sounds later",
              "This game reviews all the sounds your baby has learned while adding the cognitive challenge of prediction and memory",
              "At this age, babies are developing object permanence—understanding that things exist even when they can't see them. This game extends that to sounds",
              "Guessing games encourage problem-solving and hypothesis-testing, even at 12-24 months",
              "Celebrating all guesses (even wrong ones) builds confidence and persistence—essential learning dispositions"
            ],
            "materials": ["toys or objects that make sounds", "blanket or large towel", "items from Weeks 1-3 (toy animals, kitchen items, noisy toys)"],
            "tip": "Start with very familiar sounds and gradually increase difficulty—success builds motivation for trickier challenges",
            "researchNote": "Kuhl et al. (2003) - Infants' ability to discriminate sounds between 6-24 months directly predicts later language development"
          }
        },
        {
          "id": 1,
          "type": "activity",
          "title": "Step 1: Mystery Sound Box",
          "duration": "3-4 min",
          "focus": "Auditory recognition without visual cues",
          "researchBasis": "Diamond (1990) - Games requiring auditory memory and prediction strengthen prefrontal cortex development",
          "content": {
            "goal": "Hide familiar sound-making objects and let your baby identify them by sound alone",
            "whatToSay": [
              "\"I'm going to hide something under this blanket and make its sound. Can you guess what it is?\"",
              "\"Listen carefully... Moo-moo! What animal makes that sound?\"",
              "\"You think it's a cow? Let's peek and see! You were right!\"",
              "\"Here's a tricky one... Ring-ring! What makes that sound?\"",
              "\"Should we look? YES! It's the bell from Week 2!\""
            ],
            "whatToDo": [
              "Gather 4-5 familiar objects: toy animals, bells, rattles, toy phones, anything from previous lessons",
              "Show all the objects to your baby first, naming each one",
              "Hide one object under a blanket while your baby watches",
              "Make the sound of that object (animal sound, rattle noise, ringing)",
              "Ask \"What is it?\" and wait for your baby to respond with a point, sound, or gesture",
              "Lift the blanket dramatically: \"Let's see!\" and celebrate the reveal",
              "Repeat with different objects, gradually hiding them before your baby sees which one you chose"
            ],
            "examples": [
              "Toy cow under blanket → \"Moo!\" → Baby points → Reveal and celebrate",
              "Shaker under blanket → \"Shake-shake!\" → Baby reaches for blanket → \"You want to check?\"",
              "Toy phone → \"Ring-ring!\" → Baby says any sound → \"Yes! It's the phone!\""
            ],
            "watchFor": [
              "your baby looking at the correct object (even before you hide it) when you make the sound",
              "Attempting to lift the blanket themselves to check",
              "Making a related sound or gesture (like mooing back when they hear cow)",
              "Recognition speed increasing with repeated rounds"
            ],
            "parentTip": "If your baby seems stuck, offer two choices: \"Is it the cow or the duck?\" and point to each—narrowing options supports success",
            "funTwist": "Let your baby hide an object and YOU guess—then guess wrong on purpose and make it silly! \"Is it an elephant? No? Help me!\""
          }
        },
        {
          "id": 2,
          "type": "activity",
          "title": "Step 2: Sound Matching Game",
          "duration": "4-5 min",
          "focus": "Comparing and matching similar sounds",
          "researchBasis": "Anthony & Francis (2005) - Early sound discrimination activities enhance phonological awareness development",
          "content": {
            "goal": "Present two sounds and help your baby identify which objects make matching sounds",
            "whatToSay": [
              "\"Listen to these two sounds. Are they the SAME or DIFFERENT?\"",
              "\"Shake-shake-shake... and... jingle-jingle! Same or different?\"",
              "\"You're right—different! This one shakes, this one jingles!\"",
              "\"Now try this: Moo-moo... and... Moo-moo! Same or different?\"",
              "\"SAME! Both cows say moo!\""
            ],
            "whatToDo": [
              "Choose two objects that make similar sounds (two rattles, two bells)",
              "Make the sound of each one, one after the other, behind your back or under blanket",
              "Ask \"Same or different?\" using simple gestures (hands together for same, apart for different)",
              "Reveal both objects and let your baby explore them",
              "Repeat with two objects that make obviously different sounds (bell vs. drum)",
              "Celebrate all responses: \"You noticed they sound different! Good listening!\"",
              "Try matching game: lay out 3 objects, make one sound, ask \"Which one made that sound?\""
            ],
            "examples": [
              "Two shakers (same) vs. shaker and bell (different)",
              "High-pitched toy vs. low-pitched toy = different",
              "Two animal toys that both squeak = same"
            ],
            "watchFor": [
              "your baby looking between the two objects while sounds are made",
              "Reaching for one object over another (showing preference or recognition)",
              "Facial expressions changing between same and different sounds",
              "Attempting to test the objects themselves by shaking or tapping"
            ],
            "parentTip": "Same/different is an abstract concept—your baby may not grasp it fully yet, but they're building the foundation for categorization",
            "funTwist": "Use your voice: make high and low sounds, then ask \"Same or different?\" Point to your throat to show the sound comes from you!"
          }
        },
        {
          "id": 3,
          "type": "activity",
          "title": "Step 3: The Grand Sound Challenge",
          "duration": "3-4 min",
          "focus": "Sequencing multiple sound memories",
          "researchBasis": "Saffran et al. (1996) - Pattern recognition and sequence memory form the basis for understanding language structure",
          "content": {
            "goal": "Present a sequence of 2-3 sounds and see if your baby can identify all of them",
            "whatToSay": [
              "\"This is the big challenge! I'm going to make THREE sounds. Can you find all three things?\"",
              "\"Ready? Listen: Moo-moo! Ring-ring! Shake-shake-shake!\"",
              "\"That's the cow, the bell, and the shaker! Can you find the cow?\"",
              "\"You found it! Now where's the bell? Yes! One more—the shaker!\"",
              "\"You remembered all three sounds! You're a sound expert!\""
            ],
            "whatToDo": [
              "Set out 4-5 familiar objects in a row where your baby can see them",
              "Make the sounds of 2-3 objects in sequence, keeping them hidden",
              "Ask \"Can you find the things that made those sounds?\"",
              "Help your baby point to or pick up each one, naming them as you go",
              "Repeat the sounds if your baby seems unsure—memory is still developing",
              "Gradually increase difficulty: 2 sounds, then 3, then 4 if your baby is thriving",
              "End with a victory celebration: clap, cheer, and high-five for being such good listeners"
            ],
            "examples": [
              "Moo + ring + shake = 3-item auditory sequence",
              "Baby finds 2 out of 3 = still celebrate! \"You found two! Amazing memory!\"",
              "Baby picks up wrong item = \"That's the duck! We're looking for the cow. Moo-moo—hear it?\""
            ],
            "watchFor": [
              "your baby scanning the objects while you make sounds",
              "Picking up objects in order or picking up multiple objects showing they remember sequence",
              "Self-correcting—putting down one object and choosing another",
              "Confidence growing with each successful round"
            ],
            "parentTip": "If 3 sounds feels overwhelming, drop back to 2—your baby will show you when they're ready for more complexity",
            "funTwist": "Create a silly sound story: \"The cow said moo, then rang the bell, then shook the shaker—what a silly day!\""
          }
        },
        {
          "id": 5,
          "type": "conclusion",
          "title": "Activity Complete!",
          "duration": "1 min",
          "content": {
            "celebration": [
              "🎉 Incredible sound detective work! You recognized so many sounds today!",
              "You practiced:",
              "✓ Identifying sounds without seeing what made them",
              "✓ Comparing sounds to tell if they're the same or different",
              "✓ Remembering multiple sounds in a row"
            ],
            "reviewQuestions": [
              "Which sound did your baby recognize the fastest?",
              "Did your baby show more confidence as the game progressed?",
              "Were there any sounds that seemed particularly interesting or challenging?"
            ],
            "parentTakeaway": {
              "title": "Why This Matters",
              "content": "Sound discrimination—telling sounds apart—is the exact skill your baby will need to distinguish between letter sounds like 'b' and 'd' or 'p' and 't'. Today's guessing games build the auditory precision that makes phonics easier later."
            },
            "nextSteps": [
              "Play \"guess that sound\" during daily routines: close your eyes and identify kitchen sounds, outdoor noises, or toy sounds",
              "Narrate mysterious sounds: \"I hear something! What could it be? Oh—it's the washing machine!\"",
              "Let your baby be the sound-maker while you guess—role reversal builds confidence"
            ]
          }
        }
      ]
    },

    // ---------- LESSON 19 (Week 4 Day 4) ----------
{
  "id": 19,
  "phase": "1-2",
  "quarter": 1,
  "week": 4,
  "weekIndex": 3,
  "dayInWeek": 3,
  "title": "Lesson 19: Sing-Along Favorites — Integrating Weeks 1–3",
  "theme": "Review Through Music & Echo Games",
  "skillRecurrenceTags": ["song-memory", "vocabulary-review", "rhythm-integration"],
  "relatedToPreviousSkills": ["animal-sounds-week1", "rhythm-clapping-week1", "voice-echo-week3", "name-recognition-week1"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sing-Along Time!",
      "duration": "1 min",
      "content": {
        "goal": "Review all learned skills from Weeks 1-3 through familiar songs and musical play.",
        "parentGuide": [
          "Songs are powerful memory anchors—they help your baby consolidate multiple skills at once through melody and repetition.",
          "Musical integration combines rhythm, vocabulary, turn-taking, and sound awareness in one joyful package.",
          "Repetition through song doesn't feel like review—it feels like fun, making learning stick naturally.",
          "At this age, babies are beginning to anticipate familiar song patterns, building prediction skills crucial for reading.",
          "Singing together strengthens emotional bonds while teaching language structure through natural rhythm and rhyme."
        ],
        "materials": ["Your voice", "Optional: simple instruments from Lesson 16", "Optional: toy animals or photos from earlier lessons"],
        "tip": "Don't worry about singing perfectly—babies respond to enthusiasm and warmth, not pitch accuracy!",
        "researchNote": "Trainor & Corrigall (2010) - Musical engagement enhances language development, auditory processing, and social bonding in infants."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Animal Sound Songs",
      "duration": "3-4 min",
      "focus": "Vocabulary Review Through Music",
      "researchBasis": "Thiessen & Saffran (2009) - Musical structure helps infants segment and remember word patterns.",
      "content": {
        "goal": "Review animal sounds from Week 1 by incorporating them into simple, repetitive songs.",
        "whatToSay": [
          "\"Let's sing about our animal friends! Ready?\"",
          "\"Old MacDonald had a farm, E-I-E-I-O! And on that farm he had a COW, E-I-E-I-O!\"",
          "\"With a moo-moo here and a moo-moo there!\"",
          "\"What animal should we sing about next? The dog? Duck? You choose!\"",
          "\"Great choice! Let's do the duck—quack-quack!\""
        ],
        "whatToDo": [
          "Sing 'Old MacDonald' or create a simple tune: 'The [animal] says [sound], the [animal] says [sound], all day long!'",
          "Pause before each animal sound and wait for your baby to fill it in or make any vocalization.",
          "Use toy animals or point to pictures while singing to reinforce visual-sound connection.",
          "Let your baby choose which animal to sing about next by pointing or vocalizing.",
          "Cycle through 4-5 animals from Week 1: cow, dog, cat, duck, and any others you practiced.",
          "Add actions: flap arms for duck, pant for dog, curl up small for cat."
        ],
        "examples": [
          "Sing 'moo-moo' then pause—baby makes any sound—celebrate: 'Yes! The cow says moo!'",
          "Point to dog toy—sing 'And on that farm he had a...'—baby says 'woof' or points—you complete: 'DOG!'"
        ],
        "watchFor": [
          "your baby anticipating the animal sound and vocalizing before you do.",
          "Pointing to animals when you name them in song.",
          "Moving or dancing during familiar parts of the song.",
          "Attempting to imitate the melody, not just the animal sounds."
        ],
        "parentTip": "If your baby doesn't vocalize, exaggerate your pauses and facial expressions—sometimes anticipation shows in smiles before sounds!",
        "funTwist": "Create a 'silly farm' where animals make wrong sounds: 'The cow says quack!' then laugh and correct it together!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Name Song Time",
      "duration": "3-4 min",
      "focus": "Name Recognition and Rhythm",
      "researchBasis": "Mandel et al. (1995) - Name recognition strengthened through musical repetition enhances self-awareness and identity formation.",
      "content": {
        "goal": "Reinforce name recognition from Week 1 by creating personalized songs that celebrate your baby.",
        "whatToSay": [
          "\"Let's sing a song about YOU! This is your baby's special song!\"",
          "\"your baby, your baby, where are you? Here I am, here I am, how do you do!\"",
          "\"Clap for your baby—clap, clap, clap!\"",
          "\"your baby can jump! your baby can dance! your baby is wonderful!\""
        ],
        "whatToDo": [
          "Sing a simple name song to the tune of 'Where Is Thumbkin?' or 'Twinkle Twinkle'.",
          "Replace key words with your baby: 'Twinkle twinkle little [name], how I wonder where you are!'",
          "Clap out the syllables in your baby while singing: 'Lu-ci-a-no' gets four claps.",
          "Add actions: wave when singing 'here I am', jump or bounce for 'jump', sway for 'dance'.",
          "Invite family members' names into the song: 'Mama, Dada, and your baby!'",
          "Pause dramatically before saying your baby each time—build anticipation."
        ],
        "examples": [
          "Sing slowly: 'B-a-b-y-'-s n-a-m-e' with one clap per syllable",
          "Hide behind hands during 'where are you?' then pop out on 'here I am!'",
          "Sing in different voices: high voice, low voice, whisper voice, loud voice"
        ],
        "watchFor": [
          "your baby responding to their name by looking at you, smiling, or vocalizing.",
          "Attempting to clap along with syllables.",
          "Moving their body during action words (jump, dance).",
          "Recognition when other family members' names are sung."
        ],
        "parentTip": "Singing name songs during transitions—car rides, diaper changes, or wind-down time—reinforces learning naturally.",
        "funTwist": "Let your baby 'conduct' the song—when they wave their arms, you sing; when they stop, you stop!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Echo Song Parade",
      "duration": "3-4 min",
      "focus": "Call-and-Response Integration",
      "researchBasis": "Gerry et al. (2012) - Active musical participation strengthens rhythm processing and language timing.",
      "content": {
        "goal": "Combine echo patterns from Week 3 with movement and song for a full-body language experience.",
        "whatToSay": [
          "\"I'm going to sing a line, and you echo it back! Ready?\"",
          "\"If you're happy and you know it, clap your hands!\" (pause for clap)",
          "\"Can you clap? Yes! If you're happy and you know it...\"",
          "\"Stomp your feet!\" (pause for stomp)",
          "\"Say 'hooray!'\" (pause for 'hooray!')"
        ],
        "whatToDo": [
          "Choose a simple action song: 'If You're Happy', 'Head Shoulders Knees and Toes', or make up your own.",
          "Sing one line, then pause and gesture for your baby to do the action or echo a sound.",
          "Use call-and-response format: You sing 'clap your hands'—baby claps—you celebrate and continue.",
          "Add rhythm instruments from Lesson 16 during pause moments—shake, tap, or bang together.",
          "Vary tempo: sing slowly for gentle movements, quickly for energetic ones.",
          "Create a 'parade' around the room while singing—movement + music = maximum engagement."
        ],
        "examples": [
          "Sing 'If you're happy' + pause + baby claps + you sing 'clap your hands!'",
          "Echo version: 'Say moo!' (pause) baby says 'moo' (or any sound) 'Good job!'",
          "Parade version: march while singing, stop and freeze during pauses, resume when baby makes sound"
        ],
        "watchFor": [
          "your baby participating in actions even if echo sounds aren't clear yet.",
          "Anticipating pauses and getting ready to respond.",
          "Smiling or laughing during familiar song parts.",
          "Initiating actions before you finish singing the instruction."
        ],
        "parentTip": "your baby may join in with movement before sounds—this is still communication! All participation counts.",
        "funTwist": "Let your baby pick the action by demonstrating: they clap, you sing 'clap your hands'; they stomp, you sing 'stomp your feet'!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 What a wonderful concert! You sang so many songs today!",
          "You practiced:",
          "✓ Animal sound songs from Week 1",
          "✓ Name songs with clapping rhythms",
          "✓ Echo songs with actions and sounds",
          "✓ Moving and singing together!"
        ],
        "reviewQuestions": [
          "Which song made your baby most excited?",
          "Did your baby start singing or humming any parts independently?",
          "Which actions or sounds did they participate in most actively?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Songs consolidate multiple literacy skills at once—vocabulary, rhythm, memory, and social interaction. The patterns in music mirror the patterns in language, making songs one of the most powerful early literacy tools available."
        },
        "nextSteps": [
          "Create a 'song rotation' for daily routines—wake-up songs, meal songs, cleanup songs, bedtime songs.",
          "Teach other caregivers your baby's favorite songs so they can maintain consistency.",
          "Notice which songs your baby starts 'requesting' through gestures or sounds—these become anchor points for learning."
        ]
      }
    }
  ]
},

    // ---------- LESSON 20 (Week 4 Day 5) ----------
{
  "id": 20,
  "phase": "1-2",
  "quarter": 1,
  "week": 4,
  "weekIndex": 3,
  "dayInWeek": 4,
  "title": "Lesson 20: \"Sound Show!\" — Family Mini Performance",
  "theme": "Review Through Music & Echo Games",
  "skillRecurrenceTags": ["performance", "confidence-building", "skill-integration", "social-sharing"],
  "relatedToPreviousSkills": ["all-weeks-1-3", "sound-awareness", "rhythm", "imitation", "vocabulary"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sound Show Day!",
      "duration": "1 min",
      "content": {
        "goal": "Celebrate Week 4 review by creating a fun 'performance' that showcases all the skills your baby has learned from Weeks 1-3.",
        "parentGuide": [
          "Performance play—even for an audience of stuffed animals—builds confidence and validates your baby's hard work.",
          "Showcasing learned skills helps consolidate memory through active demonstration rather than passive review.",
          "Social sharing (even pretend) introduces your baby to the idea that learning is something to be proud of and shared.",
          "This lesson transforms review into celebration, reinforcing positive associations with learning activities.",
          "Including family members (or even just talking about them) strengthens social awareness and communication purpose."
        ],
        "materials": ["Items from Weeks 1-3 (toy animals, instruments, photos)", "Blanket or sheet for 'stage area'", "Stuffed animals or family members as 'audience'"],
        "tip": "Keep it playful, not perfect—the goal is joyful sharing, not flawless performance. Celebrate every attempt!",
        "researchNote": "Vygotsky (1978) - Social performance of learned skills strengthens internalization and self-regulation."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Setting the Stage",
      "duration": "2-3 min",
      "focus": "Creating Performance Environment",
      "researchBasis": "Bruner (1986) - Symbolic play and role-taking enhance cognitive and social development.",
      "content": {
        "goal": "Create a simple 'stage' area and build excitement about showing what your baby can do.",
        "whatToSay": [
          "\"Today is special—we're going to put on a Sound Show!\"",
          "\"Let's make a stage right here! This is where you'll show everyone what you've learned!\"",
          "\"Look—Teddy and Bunny want to watch! They're your audience!\"",
          "\"Should we invite Daddy/Mommy/Grandma to watch too?\"",
          "\"This is going to be SO exciting!\""
        ],
        "whatToDo": [
          "Lay out a blanket or sheet to define the 'stage' area—this creates a sense of special space.",
          "Arrange stuffed animals, family members, or even just chairs in a semi-circle as 'audience'.",
          "Gather all the props from Weeks 1-3: toy animals, homemade instruments, photos, texture items.",
          "Let your baby help 'set up' by carrying items to the stage—involvement builds ownership.",
          "Practice a 'bow' or wave to the audience—make it silly and fun.",
          "Announce dramatically: 'Ladies and gentlemen, animals and toys, welcome to your baby's Sound Show!'"
        ],
        "examples": [
          "Place teddy bears in 'seats' and have them 'clap' (you clap for them)",
          "Put on background 'audience sounds'—gentle clapping or cheering",
          "Give your baby a 'microphone' (wooden spoon or toy) to hold"
        ],
        "watchFor": [
          "your baby showing excitement about the setup—smiling, moving items, engaging with props.",
          "Recognition that this is a special activity—different from regular play.",
          "Responding to 'audience' presence—looking at stuffed animals or family members.",
          "Increased energy and anticipation."
        ],
        "parentTip": "If your baby seems shy or uncertain, you go first! Model a mini-performance, then invite them to join: 'Now it's your turn!'",
        "funTwist": "Let your baby be the 'announcer' too—hand them the mic and say 'Introduce the show!' (celebrate any sound they make)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: The Sound Show Performance",
      "duration": "5-6 min",
      "focus": "Demonstrating Learned Skills",
      "researchBasis": "Bandura (1977) - Observational learning and self-efficacy strengthen through performance opportunities.",
      "content": {
        "goal": "Guide your baby through a 3-part 'show' featuring animal sounds, rhythm play, and echo songs.",
        "whatToSay": [
          "\"Act 1: Animal Sounds! Can you show everyone the cow sound? Moo!\"",
          "\"Let's hear the dog! Woof-woof! The audience loves it!\"",
          "\"Act 2: Rhythm Time! Grab your instruments and let's make music!\"",
          "\"Shake-shake-shake! Tap-tap-tap! You're amazing!\"",
          "\"Act 3: Echo Fun! I'll say something, and you echo back!\"",
          "\"Hello!\" (pause) \"You said hello! The audience is clapping!\""
        ],
        "whatToDo": [
          "ACT 1 (2 min): Hold up toy animals one by one, name them, and encourage your baby to make their sounds.",
          "Celebrate each attempt with audience applause (you clap, or tap stuffed animals' paws together).",
          "ACT 2 (2 min): Use homemade instruments from Lesson 16. Create a simple pattern (tap-tap-shake) and perform it together.",
          "Add marching or dancing. End with a big finale drumroll.",
          "ACT 3 (1-2 min): Sing a favorite song from Lesson 19 with call-and-response.",
          "You sing a line, your baby does the action or echo. Include their name song as the grand finale.",
          "Between each 'act', do a mini-bow and accept applause from the audience."
        ],
        "examples": [
          "Act 1: Show cow, make moo sound together, bow, audience claps",
          "Act 2: Both shake instruments while marching in circle, freeze and bow, applause",
          "Act 3: Sing name song, your baby claps on their name, everyone cheers"
        ],
        "watchFor": [
          "your baby engaging with each activity, even if briefly.",
          "Looking toward 'audience' for reactions—developing social awareness.",
          "Showing pride or pleasure when applause happens.",
          "Gaining confidence as the show progresses—more vocal, more animated."
        ],
        "parentTip": "If your baby gets overwhelmed, simplify: just do one act, or shorten each section. The goal is joy, not endurance!",
        "funTwist": "Celebrate the performance together—clapping and cheering builds confidence and makes it a memory!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Encore and Celebration",
      "duration": "2-3 min",
      "focus": "Positive Reinforcement and Closure",
      "researchBasis": "Dweck (2006) - Celebrating effort rather than perfection builds growth mindset and persistence.",
      "content": {
        "goal": "End with enthusiastic celebration, reinforcing that your baby should be proud of what they've learned.",
        "whatToSay": [
          "\"The audience wants an encore! One more song? One more sound?\"",
          "\"You were INCREDIBLE! Let's take a big bow together!\"",
          "\"The audience is giving you a standing ovation! Listen to them cheer!\"",
          "\"You showed everyone so many things you learned—animal sounds, rhythms, echoes, and songs!\"",
          "\"I'm so proud of you! You're such a wonderful learner!\""
        ],
        "whatToDo": [
          "Ask your baby which part they want to do again as an 'encore'—respect their choice.",
          "Perform the encore together with extra enthusiasm and energy.",
          "Do a final, exaggerated bow—hold hands and bow deeply to the audience.",
          "Have the 'audience' give standing ovation—make stuffed animals jump and clap wildly, family members cheer.",
          "Give your baby a special 'certificate' or hug: 'You are officially a Sound Show Star!'",
          "If you recorded it, watch a snippet together: 'Look! That's YOU! You were amazing!'",
          "Clean up the stage together, narrating: 'What a successful show! Time to pack up our theater.'"
        ],
        "examples": [
          "Baby chooses animal sounds for encore—perform 3 favorites with big gestures",
          "Final bow with dramatic flair—arms wide, bow low, pop up with jazz hands",
          "Watch recording: 'See you shaking the shaker! Hear your moo sound! You're a star!'"
        ],
        "watchFor": [
          "your baby showing satisfaction or pride—smiling, clapping for themselves, seeking praise.",
          "Wanting to share the performance with others who weren't there.",
          "Requesting to do it again—sign of positive association with learning activities.",
          "Calm-down readiness—some babies get very excited and need help transitioning to quieter play."
        ],
        "parentTip": "Save any recordings or photos—these become powerful tools for reviewing skills later and showing your baby their growth over time.",
        "funTwist": "Create a 'poster' for the show—draw or print pictures of the activities and post it on the fridge: 'Coming Soon: your baby's NEXT Sound Show!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 BRAVO! What an amazing Sound Show! You're a superstar!",
          "You practiced:",
          "✓ Performing animal sounds for an audience",
          "✓ Playing rhythm instruments with confidence",
          "✓ Singing and echoing in front of others",
          "✓ Taking bows and celebrating your learning!"
        ],
        "reviewQuestions": [
          "Which part of the show did your baby seem most confident about?",
          "Did your baby show pride or enjoyment during the performance?",
          "How did your baby respond to the audience (real or pretend)?",
          "Which skill has grown the most visibly since Week 1?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Performance—even playful, informal performance—consolidates learning by requiring your baby to actively recall and demonstrate skills rather than passively respond. It also builds confidence and teaches that learning is something to be shared and celebrated, not hidden or feared."
        },
        "nextSteps": [
          "Next week begins Week 5: Object Naming and Pointing—new skills building on this strong foundation!",
          "Consider making 'Sound Show Day' a monthly tradition to review and celebrate progress.",
          "Share recordings or stories about the show with family members—social reinforcement extends learning.",
          "Notice which skills your baby gravitates toward independently—these are emerging strengths to nurture."
        ]
      }
    }
  ]
},

// ---------- LESSON 21 (Week 5 Day 1) ----------
{
  "id": 21,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 0,
  "title": "Lesson 21: \"What's This?\" — Name Common Toys",
  "theme": "Object Naming and Pointing",
  "skillRecurrenceTags": ["object-labeling", "vocabulary-building", "pointing-gesture"],
  "relatedToPreviousSkills": ["listening", "attention", "sound-awareness"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Naming Time!",
      "duration": "1 min",
      "content": {
        "goal": "Begin systematic object naming, linking words to concrete items through repetition and pointing.",
        "parentGuide": [
          "Object naming is the foundation of vocabulary development—each labeled item becomes a building block for language.",
          "Pointing gestures emerge around 12 months and are one of the strongest predictors of later language ability.",
          "Repetition is essential: babies need to hear a word 20-30 times in varied contexts before it becomes part of their vocabulary.",
          "Keep objects in view during naming—visual connection strengthens word-object association.",
          "Celebrate all attempts at pointing or vocalizing, even if your baby doesn't say the word clearly yet."
        ],
        "materials": ["3-5 familiar toys (ball, car, stuffed animal, block, cup)", "Container or basket to hold toys"],
        "tip": "Start with toys your baby already loves—emotional connection makes new words stick faster.",
        "researchNote": "Rowe (2012) - Parents' use of specific object labels and descriptive vocabulary directly predicts children's expressive language growth."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Slow Introduction",
      "duration": "3-4 min",
      "focus": "Clear Labeling and Visual Connection",
      "researchBasis": "Snow (1983) - Early labeling with clear articulation and repetition builds receptive vocabulary foundation.",
      "content": {
        "goal": "Introduce 3 toys one at a time, saying each name clearly and slowly while your baby watches.",
        "whatToSay": [
          "\"Look! This is a ball. Ball. Can you see the ball?\"",
          "\"Ball—round and bouncy. Ball.\"",
          "\"Now this one—car. Car. Vroom-vroom, it's a car!\"",
          "\"This is bear. Bear. Soft, fuzzy bear.\"",
          "\"Let's say them again: ball, car, bear.\""
        ],
        "whatToDo": [
          "Hold up the first toy (ball) at your baby's eye level, about 12 inches away.",
          "Say the name clearly three times: 'Ball. Ball. Ball.'",
          "Let your baby touch or hold the ball for 10-15 seconds while you repeat the name.",
          "Set it down in view and pick up the second toy (car).",
          "Repeat the process: name it three times, let your baby explore it, set it down.",
          "Do the same with the third toy (bear).",
          "Review all three: hold each up briefly and name it once more."
        ],
        "examples": [
          "Ball: 'Ball. Feel the ball. Round ball. Ball.'",
          "Car: 'Car. The car has wheels. Vroom! Car.'",
          "Bear: 'Bear. Soft bear. Hug the bear. Bear.'"
        ],
        "watchFor": [
          "your baby making eye contact with each toy as you name it.",
          "Reaching for or touching toys when you say their names.",
          "Vocalizing or babbling in response to your labeling.",
          "Visual tracking between your face and the toy—joint attention."
        ],
        "parentTip": "Don't rush! Slow, clear repetition is more effective than introducing many words quickly.",
        "funTwist": "Add one silly descriptor: 'This is a ball—a BLUE ball!' or 'This is bear—a SLEEPY bear!' (yawn together)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Point and Name Game",
      "duration": "4-5 min",
      "focus": "Encouraging Pointing Gesture",
      "researchBasis": "Goldin-Meadow et al. (2007) - Pointing gestures predict vocabulary size and are a critical bridge to spoken language.",
      "content": {
        "goal": "Encourage your baby to point to named toys, strengthening word recognition and gesture communication.",
        "whatToSay": [
          "\"Can you point to the ball? Where's the ball?\"",
          "\"Yes! There's the ball! You pointed to the ball!\"",
          "\"Now can you find the car? Where's the car?\"",
          "\"Great job! You found the car!\"",
          "\"Let's try bear. Point to bear. Show me bear!\"",
          "\"You did it! That's the bear!\""
        ],
        "whatToDo": [
          "Lay all three toys in a row in front of your baby, slightly spaced apart.",
          "Ask 'Where's the ball?' and model pointing to it clearly.",
          "Wait 3-5 seconds for your baby to point, look at, or reach toward the ball.",
          "Celebrate ANY response—even just looking at the correct toy: 'Yes! That's the ball!'",
          "If your baby doesn't respond, gently guide their hand to point: 'Let's point together! There's the ball!'",
          "Repeat with each toy, mixing up the order randomly.",
          "As your baby gains confidence, reduce your modeling and let them lead."
        ],
        "examples": [
          "Ask for 'car'—baby looks at car—you celebrate: 'You're looking at the car! Good listening!'",
          "Ask for 'bear'—baby reaches toward bear—you affirm: 'You want the bear! Yes!'",
          "Ask for 'ball'—baby points—you give them the ball immediately as reward"
        ],
        "watchFor": [
          "your baby consistently looking at or reaching for the correct toy.",
          "Attempting to point with index finger (fine motor milestone).",
          "Vocalizing the word or part of the word ('bah' for ball, 'cah' for car).",
          "Getting faster at identifying toys over multiple rounds—recognition speed is increasing."
        ],
        "parentTip": "If pointing hasn't emerged yet, accept reaching, looking, or touching as valid responses—gesture develops gradually.",
        "funTwist": "Play 'hide one toy' under a cloth—ask 'Where's the car?'—lift cloth together and celebrate the 'find'!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Toy Box Surprise",
      "duration": "3-4 min",
      "focus": "Retrieval and Active Participation",
      "researchBasis": "Whitehurst & Lonigan (1998) - Active participation in naming activities strengthens word learning more than passive listening.",
      "content": {
        "goal": "Build retrieval skills by having your baby find and hand you named toys from a container.",
        "whatToSay": [
          "\"Let's play Toy Box! I'm going to ask for a toy, and you find it!\"",
          "\"Can you find the ball in the box? Get the ball for me!\"",
          "\"You found it! You got the ball! Thank you!\"",
          "\"Now find the car! Where's the car?\"",
          "\"There it is! You found the car! You're so good at finding things!\""
        ],
        "whatToDo": [
          "Place all three toys in a basket or box where your baby can see them.",
          "Ask for one specific toy: 'Can you get the ball?'",
          "Wait patiently while your baby searches and retrieves it.",
          "Accept the toy with enthusiasm: 'Thank you! This IS the ball!'",
          "If your baby hands you the wrong toy, gently redirect: 'That's the bear! I love bear! Now can you find the ball?'",
          "Repeat with each toy, praising retrieval efforts.",
          "Add a challenge: 'Can you get ball AND car?' (two items)",
          "Let your baby put toys back in the box, naming them as they go: 'In goes the ball! In goes the car!'"
        ],
        "examples": [
          "Baby reaches in, pulls out ball, hands it to you—immediate praise and labeling",
          "Baby pulls out wrong toy first—you name it, then repeat request for correct one",
          "Baby successfully retrieves two toys in sequence—celebrate problem-solving!"
        ],
        "watchFor": [
          "your baby searching visually before reaching—cognitive planning.",
          "Correctly identifying toys more often than randomly grabbing.",
          "Handing you toys (social exchange and joint attention).",
          "Saying or approximating toy names while retrieving ('bah!' for ball)."
        ],
        "parentTip": "If your baby gets frustrated, simplify: use only two toys, or let them hand you ANY toy and you name it together.",
        "funTwist": "Play 'delivery service'—your baby retrieves toys and 'delivers' them to different locations: 'Take the ball to Daddy!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Amazing naming work today! You learned so many toy names!",
          "You practiced:",
          "✓ Listening to toy names (ball, car, bear)",
          "✓ Pointing to toys when asked",
          "✓ Finding and getting specific toys",
          "✓ Saying or trying toy names"
        ],
        "reviewQuestions": [
          "Which toy name did your baby seem to recognize most consistently?",
          "Is your baby pointing with intention, or still using whole-hand reaching?",
          "Did your baby attempt to say any of the toy names?",
          "How did your baby respond to the retrieval game—excited, frustrated, engaged?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Object naming is the cornerstone of vocabulary development. Each concrete noun your baby learns becomes a reference point for understanding categories, descriptions, and eventually abstract concepts. Pointing connects gesture and language, a critical step toward spoken communication."
        },
        "nextSteps": [
          "Use these three toy names repeatedly throughout the day in natural contexts.",
          "Add one new toy name tomorrow, keeping ball, car, and bear as familiar anchors.",
          "Encourage pointing during book reading: 'Where's the ball in this picture?'",
          "Keep toys visible and accessible—your baby will practice naming independently during play."
        ]
      }
    }
  ]
},

// ---------- LESSON 22 (Week 5 Day 2) ----------
{
  "id": 22,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 1,
  "title": "Lesson 22: \"My Toy Box!\" — Find and Say It",
  "theme": "Object Naming and Pointing",
  "skillRecurrenceTags": ["vocabulary-expansion", "object-recognition", "descriptive-language"],
  "relatedToPreviousSkills": ["object-labeling", "pointing-gesture", "retrieval"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Find and Say!",
      "duration": "1 min",
      "content": {
        "goal": "Expand vocabulary by adding descriptors to yesterday's toy names (color, size, texture) while reinforcing retrieval skills.",
        "parentGuide": [
          "Adding descriptive words (adjectives) transforms simple nouns into richer language experiences.",
          "Descriptive vocabulary predicts reading comprehension—children who hear varied language understand complex texts better.",
          "At this age, your baby may not reproduce descriptors yet, but receptive understanding is building rapidly.",
          "Pairing descriptors with concrete objects makes abstract concepts (like 'blue' or 'soft') tangible.",
          "Repetition with variation—same toys, new words—deepens learning without overwhelming."
        ],
        "materials": ["Same 3 toys from Lesson 21 (ball, car, bear)", "Optional: 1-2 additional toys with clear descriptors", "Toy box or basket"],
        "tip": "Choose descriptors your baby can SEE or FEEL—color, size, texture work better than abstract qualities at this age.",
        "researchNote": "Rowe (2012) - Descriptive vocabulary input (adjectives, spatial words) predicts later expressive language complexity."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Add the Details",
      "duration": "3-4 min",
      "focus": "Introducing Descriptive Language",
      "researchBasis": "Weisleder & Fernald (2013) - Quantity AND quality of language input (including descriptors) accelerates word learning.",
      "content": {
        "goal": "Reintroduce yesterday's toys with added descriptive words, emphasizing the new information.",
        "whatToSay": [
          "\"Remember our ball? This is the BLUE ball. Blue ball.\"",
          "\"Look at your car—it's a RED car. Red car, zoom-zoom!\"",
          "\"Here's your bear. This is a SOFT bear. Feel how soft? Soft bear.\"",
          "\"Blue ball, red car, soft bear. Three special toys!\""
        ],
        "whatToDo": [
          "Hold up each toy from yesterday, saying the basic name first: 'Ball.'",
          "Then add the descriptor with emphasis: 'BLUE ball.'",
          "Let your baby touch or explore the toy while you repeat the full phrase: 'Blue ball, blue ball.'",
          "Point to or demonstrate the descriptor: touch the ball and say 'blue,' run hand over bear and say 'soft.'",
          "Cycle through all three toys with their descriptors 2-3 times.",
          "Keep the basic name primary, descriptor secondary: 'ball... blue ball' not just 'blue.'"
        ],
        "examples": [
          "Ball: 'Ball. Blue ball. See the blue? Blue ball.'",
          "Car: 'Car. Red car. Red like an apple! Red car.'",
          "Bear: 'Bear. Soft bear. Pet the soft bear. So soft!'"
        ],
        "watchFor": [
          "your baby looking at the specific feature you name (color, texture).",
          "Increased attention when descriptors are added—novelty captures interest.",
          "Attempting to touch or explore the described feature.",
          "Any vocal approximation of the descriptor or fuller phrase."
        ],
        "parentTip": "Don't expect your baby to say 'blue ball' yet—focus on receptive understanding. Production will follow in time.",
        "funTwist": "Find matching colors in the room: 'Blue ball—look, blue pillow too! Both blue!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Find the Right One",
      "duration": "4-5 min",
      "focus": "Receptive Descriptor Recognition",
      "researchBasis": "Booth & Waxman (2002) - Toddlers use adjectives to differentiate between similar objects, showing early categorical thinking.",
      "content": {
        "goal": "Test comprehension by asking your baby to identify toys using descriptors.",
        "whatToSay": [
          "\"Can you find the BLUE ball? Which one is blue?\"",
          "\"Yes! You found the blue ball!\"",
          "\"Now where's the RED car? Show me the red car!\"",
          "\"Great! That's the red car—vroom!\"",
          "\"Can you get the SOFT bear? Feel for the soft one!\"",
          "\"Perfect! You found the soft bear!\""
        ],
        "whatToDo": [
          "Place all three toys in front of your baby.",
          "Ask for a toy using ONLY the descriptor first: 'Find the blue one!'",
          "If your baby seems confused, add the noun: 'Find the blue ball.'",
          "Wait for your baby to point, look at, or retrieve the correct toy.",
          "Celebrate correct choices enthusiastically, reinforcing both words: 'Yes! Blue ball!'",
          "For 'incorrect' choices, gently correct: 'That's the red car! Let's find the blue ball together.'",
          "Repeat 3-4 times, mixing up the order.",
          "Gradually rely more on descriptor alone: 'Get the soft one' instead of 'soft bear.'"
        ],
        "examples": [
          "Place toys in new arrangement—ask for 'red car'—baby points correctly—celebrate!",
          "Ask 'Which is soft?'—baby touches bear—affirm: 'Yes! Soft bear!'",
          "Mix in a distractor: 'Get the blue one!' when blue ball and red car are present"
        ],
        "watchFor": [
          "your baby using the descriptor as a selection cue, not just the noun.",
          "Hesitation or processing time before responding—cognitive work is happening!",
          "Touching or pointing to the described feature (color, texture) on the toy.",
          "Consistency improving over multiple rounds."
        ],
        "parentTip": "If descriptors alone are too hard, pair them with nouns every time—your baby will separate them naturally as understanding grows.",
        "funTwist": "Play 'descriptor detective': hide toys around the room and give clues: 'Find something soft!' 'Get the blue one!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: My Toy Box Sort",
      "duration": "3-4 min",
      "focus": "Categorization and Descriptor Use",
      "researchBasis": "Smith et al. (2002) - Early sorting activities based on attributes strengthen categorical reasoning and vocabulary retention.",
      "content": {
        "goal": "Introduce simple sorting by descriptors to deepen understanding and add playful challenge.",
        "whatToSay": [
          "\"Let's sort our toys! All the soft things go HERE.\"",
          "\"Is the ball soft? No! The ball is hard.\"",
          "\"Is the bear soft? YES! Soft bear goes here!\"",
          "\"What about the car? Is it soft or hard?\"",
          "\"Hard car! The car goes with the ball!\""
        ],
        "whatToDo": [
          "Set up two areas (could be two sides of a blanket, two baskets, or just two spots on the floor).",
          "Label one area with a descriptor: 'This is the SOFT pile.'",
          "Hold up each toy and ask your baby: 'Is this soft or hard?'",
          "Let your baby touch and explore before deciding (or you decide together).",
          "Place the toy in the appropriate area, narrating: 'Soft bear goes in the soft pile!'",
          "Try with different descriptors if time allows: big/little, smooth/bumpy, quiet/loud.",
          "Celebrate all sorting attempts, even if 'incorrect'—the process matters more than accuracy.",
          "Mix toys back up and sort again if your baby is engaged."
        ],
        "examples": [
          "Soft vs. hard: bear and blanket in one pile, ball and car in another",
          "Big vs. little: if you have varied sizes, sort by size",
          "Color sort: 'All blue things here, all red things there!'"
        ],
        "watchFor": [
          "your baby actively testing toys (squeezing, touching) before sorting.",
          "Attempting to place toys in categories, even if not perfectly accurate.",
          "Saying or approximating descriptor words ('sah' for soft, 'buh' for blue).",
          "Showing preference or opinion about where toys belong."
        ],
        "parentTip": "Sorting is abstract for this age—think of it as playful exploration, not a test. Any engagement with the concept is progress!",
        "funTwist": "Let your baby create a 'surprise pile' where anything can go—validates their agency and creativity!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're learning so many new words! Blue, red, soft—amazing!",
          "You practiced:",
          "✓ Hearing descriptive words (blue ball, red car, soft bear)",
          "✓ Finding toys using descriptors",
          "✓ Sorting toys by their features",
          "✓ Touching and exploring to understand words"
        ],
        "reviewQuestions": [
          "Did your baby seem to understand the descriptors when finding toys?",
          "Which descriptor seemed easiest for your baby—color, texture, or size?",
          "Did your baby attempt to say any of the new words?",
          "How did your baby respond to sorting—curious, frustrated, playful?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Descriptive language transforms simple vocabulary into rich, nuanced communication. Children who hear varied descriptors develop stronger reading comprehension because they understand that words can modify and specify meaning—a critical skill for understanding complex sentences later."
        },
        "nextSteps": [
          "Use descriptors naturally throughout the day: 'Get your blue cup,' 'Where's your soft blanket?'",
          "Point out descriptors during book reading: 'Look at the BIG dog! See the little cat?'",
          "Add new descriptors gradually to familiar objects—don't rush vocabulary expansion.",
          "Tomorrow we'll explore kitchen helpers—naming safe household items in context!"
        ]
      }
    }
  ]
},

// ---------- LESSON 23 (Week 5 Day 3) ----------
{
  "id": 23,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 2,
  "title": "Lesson 23: \"Kitchen Helpers\" — Utensil Name Play",
  "theme": "Object Naming and Pointing",
  "skillRecurrenceTags": ["household-vocabulary", "functional-naming", "routine-integration"],
  "relatedToPreviousSkills": ["object-labeling", "descriptive-language", "pointing"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Kitchen Time!",
      "duration": "1 min",
      "content": {
        "goal": "Expand vocabulary into everyday household items, specifically safe kitchen utensils, through hands-on exploration.",
        "parentGuide": [
          "Naming everyday objects builds functional vocabulary—words your baby will use daily in real contexts.",
          "Kitchen items offer rich sensory experiences: different sounds (tap spoon on pot), textures (smooth bowl, rough sponge), and functions.",
          "Learning vocabulary during routines (like meal prep) helps your baby connect words to meaningful activities.",
          "This lesson integrates language learning into daily life, making literacy development natural and sustainable.",
          "Safety first: use only child-safe, non-breakable items with no sharp edges."
        ],
        "materials": ["3-4 safe kitchen items: plastic/wooden spoon, bowl, cup, pot lid", "Kitchen towel or mat for play area"],
        "tip": "Let your baby explore items fully before naming—curiosity drives engagement, then language labels the experience.",
        "researchNote": "Tomasello (2003) - Vocabulary learned in functional, social contexts (like meal routines) transfers better to spontaneous use than isolated word drills."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Kitchen Friends",
      "duration": "3-4 min",
      "focus": "Initial Labeling and Exploration",
      "researchBasis": "Hart & Risley (1995) - Rich, varied vocabulary exposure during daily routines predicts later language and academic success.",
      "content": {
        "goal": "Introduce 3-4 kitchen items with clear, repeated naming and sensory exploration.",
        "whatToSay": [
          "\"This is a spoon! Spoon. We eat with a spoon!\"",
          "\"Feel the spoon—smooth spoon!\"",
          "\"Here's a bowl. Bowl. We put food in a bowl!\"",
          "\"Big round bowl!\"",
          "\"This is a cup. Cup. We drink from a cup!\"",
          "\"Can you hold the cup? Cup!\""
        ],
        "whatToDo": [
          "Sit with your baby in the kitchen or at a table with items spread out.",
          "Pick up the first item (spoon) and name it clearly 3 times.",
          "Demonstrate its function briefly: pretend to stir or eat.",
          "Hand it to your baby and let them explore for 15-20 seconds while you repeat the name.",
          "Move to the next item (bowl), repeat the same process.",
          "Continue with cup and any additional safe items.",
          "After introducing all items, point to each and name them in sequence: 'Spoon, bowl, cup.'"
        ],
        "examples": [
          "Spoon: 'Spoon. Stir-stir with the spoon. Tap the spoon. Spoon!'",
          "Bowl: 'Bowl. Big bowl. Put things in the bowl. Bowl!'",
          "Cup: 'Cup. Drink from the cup. Tip the cup. Cup!'"
        ],
        "watchFor": [
          "your baby picking up each item to explore independently.",
          "Imitating your functional actions (stirring, tipping, tapping).",
          "Making sounds or babbles when handling items.",
          "Looking at you when you name items—checking for social reference."
        ],
        "parentTip": "Kitchen items naturally invite action—let your baby bang, shake, or nest them. Motor play reinforces word learning!",
        "funTwist": "Add sound effects for each item: 'Spoon goes tap-tap-tap! Bowl goes bonk! Cup goes glug-glug!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Kitchen Band Rhythm",
      "duration": "4-5 min",
      "focus": "Reinforcement Through Sound Play",
      "researchBasis": "Trainor et al. (2002) - Combining rhythm activities with vocabulary learning strengthens memory encoding through multisensory engagement.",
      "content": {
        "goal": "Use kitchen items to create sounds and rhythms while reinforcing their names.",
        "whatToSay": [
          "\"Let's make music with our kitchen friends!\"",
          "\"Tap the SPOON on the bowl—tap-tap-tap!\"",
          "\"Bang the spoon on the POT—boom-boom-boom!\"",
          "\"Shake the CUP with rice—shake-shake-shake!\"",
          "\"You're making kitchen music!\""
        ],
        "whatToDo": [
          "Show your baby how to tap the spoon on different surfaces (bowl, pot lid, table).",
          "Name each item as you use it: 'Spoon on bowl makes a tapping sound!'",
          "Create a simple rhythm pattern: tap-tap-pause-tap-tap.",
          "Encourage your baby to copy your pattern.",
          "If you have a cup with dried beans/rice (sealed safely), let your baby shake it while you say 'cup!'",
          "Alternate turns: you tap with spoon, your baby taps with spoon.",
          "Narrate constantly: 'your baby is tapping the spoon! Listen to that spoon!'"
        ],
        "examples": [
          "Parent: 'Spoon tap-tap!' → Baby copies → Parent: 'Yes! Spoon makes tap-tap!'",
          "Roll bowl back and forth while saying 'bowl, bowl, bowl' in rhythm",
          "Stack cup on bowl, knock down, rebuild—naming each item repeatedly"
        ],
        "watchFor": [
          "your baby attempting to create sounds intentionally with items.",
          "Responding to item names by looking at or reaching for the correct utensil.",
          "Coordinating rhythm with naming (tapping while you say 'spoon').",
          "Showing pride in their 'music-making'—smiling, looking for your reaction."
        ],
        "parentTip": "Rhythm play isn't about perfection—it's about joyful repetition that makes words memorable through action!",
        "funTwist": "Create a 'kitchen concert': family members are the audience, your baby performs with their kitchen instruments!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Helper Time Game",
      "duration": "3-4 min",
      "focus": "Functional Use and Following Directions",
      "researchBasis": "Bruner (1983) - Joint activity routines (like meal preparation) provide natural scaffolding for language development.",
      "content": {
        "goal": "Practice following simple directions using kitchen vocabulary in a pretend-play or real meal prep context.",
        "whatToSay": [
          "\"Can you help me? I need the SPOON!\"",
          "\"Thank you! Now can you put the spoon IN the bowl?\"",
          "\"Great! Now give me the CUP please!\"",
          "\"You're such a good helper! Can you stir with the spoon?\"",
          "\"Stir-stir-stir! You're cooking!\""
        ],
        "whatToDo": [
          "Set up a simple pretend-play scenario: making pretend soup or meal.",
          "Give your baby specific requests: 'Get the spoon,' 'Put cup here,' 'Stir the bowl.'",
          "Demonstrate each action first if needed, then let your baby try.",
          "Accept approximations—if your baby grabs the wrong item, gently correct: 'That's the bowl! Now let's find the spoon.'",
          "Praise all helping attempts enthusiastically: 'You're helping! You got the spoon!'",
          "Add simple two-step directions if your baby is ready: 'Get the spoon AND the cup.'",
          "End by 'serving' the pretend food together, naming items as you put them away."
        ],
        "examples": [
          "Ask for spoon → baby retrieves spoon → 'Thank you! That's the spoon!'",
          "Say 'Put the cup on the bowl' → baby places cup → celebrate spatial understanding!",
          "Pretend to taste from bowl → 'Mmm! Delicious! You stirred with the spoon!'"
        ],
        "watchFor": [
          "your baby responding correctly to verbal requests without visual cues.",
          "Following two-step directions (get X and put it Y).",
          "Using items functionally (stirring in bowl, drinking from cup).",
          "Initiating their own 'helping' actions—showing understanding of routines."
        ],
        "parentTip": "Real meal prep is even better if safe—let your baby hand you items during actual cooking when possible!",
        "funTwist": "Let your baby 'serve' you pretend food and YOU name the items: 'Is that the spoon? Thank you!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're a kitchen helper superstar!",
          "You practiced:",
          "✓ Learning kitchen words (spoon, bowl, cup)",
          "✓ Making music with kitchen items",
          "✓ Following directions to help",
          "✓ Using items the way grown-ups do!"
        ],
        "reviewQuestions": [
          "Which kitchen item was your baby most interested in?",
          "Could your baby retrieve items when named?",
          "Did your baby use items functionally (stirring, drinking motions)?",
          "How did your baby respond to being a 'helper'—proud, engaged, playful?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Functional vocabulary learned in real contexts (like kitchen routines) becomes part of your baby's everyday language faster than isolated word learning. Connecting words to actions and purposes makes language meaningful and useful, not just memorized."
        },
        "nextSteps": [
          "Use these kitchen words during real meals: 'Get your spoon,' 'Put the cup here.'",
          "Let your baby help with safe tasks during meal prep—hands-on learning reinforces vocabulary.",
          "Point out kitchen items during book reading or outside home: 'Look, a spoon like ours!'",
          "Tomorrow we move to bath time vocabulary—water, splash, pour, wash!"
        ]
      }
    }
  ]
},

// ---------- LESSON 24 (Week 5 Day 4) ----------
{
  "id": 24,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 3,
  "title": "Lesson 24: \"Bath Time Talk\" — Label and Splash",
  "theme": "Object Naming and Pointing",
  "skillRecurrenceTags": ["routine-vocabulary", "action-words", "sensory-language"],
  "relatedToPreviousSkills": ["object-labeling", "functional-naming", "routine-integration"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Bath Talk!",
      "duration": "1 min",
      "content": {
        "goal": "Build bath-time vocabulary by naming water toys, body parts, and action words during play and washing routines.",
        "parentGuide": [
          "Bath time is a rich sensory environment perfect for language learning—water play naturally engages attention.",
          "Action words (verbs) are as important as object names (nouns) for sentence development.",
          "Repetitive routines like bathing provide predictable contexts where your baby can anticipate and learn vocabulary.",
          "Pairing words with immediate actions (saying 'splash' while splashing) creates strong word-meaning connections.",
          "This lesson can be done in an actual bath OR as pretend play with a basin of water and toys."
        ],
        "materials": ["Bath toys (rubber duck, cup, boat)", "Washcloth", "Basin of water or bathtub", "Towel"],
        "tip": "Narrate constantly during bath time—every action is a language opportunity: 'pouring water,' 'washing arms,' 'splashing feet!'",
        "researchNote": "Snow (1983) - Language learning accelerates when new words are embedded in predictable, emotionally positive routines like bathing and eating."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Bath Friends",
      "duration": "3-4 min",
      "focus": "Object Naming in Context",
      "researchBasis": "Rowe (2012) - Diverse vocabulary input, including less common words like bath-specific items, predicts language growth.",
      "content": {
        "goal": "Introduce and name 3-4 bath-related objects with hands-on water play.",
        "whatToSay": [
          "\"Look! It's our duck! Duck says quack-quack in the bath!\"",
          "\"Here's a cup. Cup! We pour water with the cup.\"",
          "\"This is a boat! Boat floats on the water!\"",
          "\"And here's our washcloth. Washcloth—soft and wet!\"",
          "\"Duck, cup, boat, washcloth—our bath friends!\""
        ],
        "whatToDo": [
          "Place your baby near water (in bath or beside basin) with toys visible.",
          "Pick up first toy (duck) and name it clearly 3 times.",
          "Demonstrate what it does: make it float, squeeze it to squeak, make it swim.",
          "Hand it to your baby and let them explore in water while you repeat: 'Duck! Duck!'",
          "Move to next item (cup), show how to scoop and pour water, name it repeatedly.",
          "Continue with boat and washcloth, emphasizing texture and function.",
          "Review all items quickly: point to each and name it."
        ],
        "examples": [
          "Duck: 'Duck! The duck swims! Quack-quack! See the duck float!'",
          "Cup: 'Cup! Fill the cup. Pour the cup. Water in the cup!'",
          "Boat: 'Boat! The boat sails! Push the boat! Boat!'"
        ],
        "watchFor": [
          "your baby reaching for specific toys when named.",
          "Imitating your actions (squeezing duck, pouring from cup).",
          "Vocalizing or babbling while playing with water toys.",
          "Showing excitement about water play—motivation enhances learning."
        ],
        "parentTip": "Water play is naturally captivating—use that attention to repeat names many times without your baby noticing the repetition!",
        "funTwist": "Make toys 'talk': 'Duck says: I love splashing! Cup says: Pour me! Boat says: Let's sail!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Action Words in Water",
      "duration": "4-5 min",
      "focus": "Verb Learning Through Movement",
      "researchBasis": "Tomasello & Kruger (1992) - Children learn verbs most effectively when they perform the action simultaneously with hearing the word.",
      "content": {
        "goal": "Introduce action words (splash, pour, wash, float) by doing the actions together while narrating.",
        "whatToSay": [
          "\"Let's SPLASH! Splash-splash-splash!\"",
          "\"Now POUR the water! Pour, pour, pour!\"",
          "\"Time to WASH! Wash your arm—clean, clean, clean!\"",
          "\"Watch the boat FLOAT! It's floating on the water!\"",
          "\"You're splashing, pouring, washing, and floating!\""
        ],
        "whatToDo": [
          "Model each action clearly while saying the verb: splash water with your hand.",
          "Say the word 3-4 times while continuing the action: 'Splash! Splash! Splash!'",
          "Encourage your baby to do the action with you.",
          "Use toys to demonstrate: pour water from cup, make boat float, use washcloth to wash.",
          "Pair actions with exaggerated movements and sound effects.",
          "Alternate actions: splash a few times, then pour, then wash, then float.",
          "Ask your baby: 'Can you splash?' 'Can you pour?' and celebrate all attempts."
        ],
        "examples": [
          "Splash: Hit water with hand, say 'SPLASH!' with each hit, invite baby to join",
          "Pour: Lift cup high, tip slowly, watch water fall, say 'Pouring!' while it pours",
          "Wash: Rub washcloth on baby's arm gently, say 'Wash-wash-wash!'"
        ],
        "watchFor": [
          "your baby imitating actions immediately or shortly after you demonstrate.",
          "Attempting to say action words or making related sounds ('pah' for splash).",
          "Initiating actions independently—showing they've internalized the meaning.",
          "Looking at you for encouragement or to share the joy of the action."
        ],
        "parentTip": "Verbs are harder than nouns—your baby may understand 'splash' long before saying it. Keep modeling with patience!",
        "funTwist": "Create a 'splash contest': who can make the biggest splash? (Controlled and safe, of course!)"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Bath Time Song",
      "duration": "3-4 min",
      "focus": "Integration Through Music",
      "researchBasis": "Gerry et al. (2012) - Songs that combine vocabulary with actions strengthen word retention and retrieval.",
      "content": {
        "goal": "Create a simple bath-time song that incorporates all the new vocabulary into a memorable routine.",
        "whatToSay": [
          "\"Let's sing our Bath Time Song!\"",
          "\"This is the way we SPLASH the water, splash the water, splash the water...\"",
          "\"This is the way we POUR from the cup, pour from the cup, early in the morning!\"",
          "\"This is the way we WASH our arms, wash our arms...\"",
          "\"This is the way the BOAT floats by, floats by...\""
        ],
        "whatToDo": [
          "Sing to the tune of 'Here We Go Round the Mulberry Bush' or make up a simple melody.",
          "Do each action while singing: splash during 'splash the water,' pour during 'pour from cup.'",
          "Encourage your baby to do the actions with you.",
          "Repeat each verse 2-3 times for reinforcement.",
          "Point to or hold up the relevant toy during each verse: cup for pouring, boat for floating.",
          "End with a celebratory verse: 'Now we're clean and we had fun, had fun, had fun!'",
          "Make it a daily bath routine—repetition builds mastery."
        ],
        "examples": [
          "Verse 1: Splash hands in water while singing 'splash the water'",
          "Verse 2: Pour from cup while singing 'pour from the cup'",
          "Verse 3: Wash with cloth while singing 'wash our arms'",
          "Verse 4: Push boat while singing 'boat floats by'"
        ],
        "watchFor": [
          "your baby anticipating actions when you start singing certain verses.",
          "Attempting actions before you finish singing the instruction.",
          "Vocalizing along with the song—even humming counts!",
          "Requesting the song again (through gestures, sounds, or excitement)."
        ],
        "parentTip": "Routine songs become powerful memory cues—your baby will eventually know 'splash' comes next just from hearing the melody!",
        "funTwist": "Let your baby pick which action to do next—point to toys and see which they choose, then sing that verse!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Bath time was so fun! You learned so many water words!",
          "You practiced:",
          "✓ Naming bath toys (duck, cup, boat, washcloth)",
          "✓ Doing action words (splash, pour, wash, float)",
          "✓ Singing a bath time song",
          "✓ Playing and learning in the water!"
        ],
        "reviewQuestions": [
          "Which bath toy did your baby engage with most?",
          "Did your baby attempt any of the action words or movements?",
          "How did your baby respond to the bath song—engaged, excited, calm?",
          "Is your baby starting to anticipate routine vocabulary (knowing 'splash' means fun is coming)?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Learning vocabulary in functional routines like bath time ensures your baby encounters words repeatedly in meaningful contexts. Action words (verbs) are essential for sentence building—'duck splash!' becomes your baby's first two-word phrase soon!"
        },
        "nextSteps": [
          "Make this a daily bath routine—sing the song, use the vocabulary every time.",
          "Add new bath words gradually: 'bubbles,' 'wet,' 'dry,' 'towel.'",
          "Point out water and washing in other contexts: washing hands, watering plants, rain outside.",
          "Tomorrow is cleanup vocabulary—naming and sorting during tidying routines!"
        ]
      }
    }
  ]
},

// ---------- LESSON 25 (Week 5 Day 5) ----------
{
  "id": 25,
  "phase": "1-2",
  "quarter": 1,
  "week": 5,
  "weekIndex": 4,
  "dayInWeek": 4,
  "title": "Lesson 25: \"Let's Clean Up\" — Name and Sort Together",
  "theme": "Object Naming and Pointing",
  "skillRecurrenceTags": ["routine-vocabulary", "categorization", "spatial-language"],
  "relatedToPreviousSkills": ["object-labeling", "functional-naming", "sorting"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Cleanup Time!",
      "duration": "1 min",
      "content": {
        "goal": "Transform cleanup routines into vocabulary-building opportunities by naming items and using spatial/action words during tidying.",
        "parentGuide": [
          "Cleanup routines offer natural repetition—your baby hears the same words in the same context daily, accelerating learning.",
          "Spatial words (in, on, under) are crucial for reading comprehension and math concepts later.",
          "Making cleanup playful and language-rich prevents resistance and builds positive associations with helping.",
          "Sorting during cleanup develops categorization skills—foundational for organizing information in reading.",
          "This lesson turns a necessary routine into a powerful learning opportunity that happens every day."
        ],
        "materials": ["Toys from recent lessons", "2-3 containers/baskets for sorting", "Optional: labels or pictures for bins"],
        "tip": "Keep cleanup short and positive—5-10 minutes maximum. The goal is joyful vocabulary practice, not perfect tidying!",
        "researchNote": "Casasola (2008) - Spatial language exposure during everyday activities predicts later spatial reasoning and math readiness."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Name Before Cleanup",
      "duration": "3-4 min",
      "focus": "Object Review and Labeling",
      "researchBasis": "Booth et al. (2005) - Naming objects before categorizing them strengthens word-category associations.",
      "content": {
        "goal": "Review toy names from the week before putting them away, reinforcing vocabulary through repetition.",
        "whatToSay": [
          "\"Before we clean up, let's say goodbye to our toys!\"",
          "\"What's this? It's the BALL! Bye-bye, ball!\"",
          "\"Here's the CAR! Vroom-vroom, car!\"",
          "\"And our BEAR! Soft bear, time to sleep!\"",
          "\"Let's put each friend away and say their name!\""
        ],
        "whatToDo": [
          "Sit with your baby surrounded by toys from this week's lessons.",
          "Pick up each toy one at a time.",
          "Name it clearly while showing it to your baby: 'This is the cup!'",
          "Wait for your baby to acknowledge (look, point, vocalize).",
          "Hand the toy to your baby briefly before putting it away.",
          "Encourage your baby to name or approximate the word.",
          "Cycle through 5-8 toys, naming each one 2-3 times.",
          "Make it conversational: 'Can you say ball? Ball! Yes, ball!'"
        ],
        "examples": [
          "Hold up ball: 'Ball!' → baby says 'bah' → 'Yes! Ball!' → hand to baby → retrieve for cleanup",
          "Show duck: 'Duck! Quack-quack!' → baby squeezes it → 'Duck says quack!' → into bin",
          "Present spoon: 'Spoon! We cook with the spoon!' → baby taps it → 'Tap-tap, spoon!' → away it goes"
        ],
        "watchFor": [
          "your baby attempting to name toys before you do—showing retrieval.",
          "Consistent recognition across repeated naming sessions.",
          "Vocalizing approximations that are getting clearer over time.",
          "Engaging with each toy briefly as a 'goodbye' ritual."
        ],
        "parentTip": "This pre-cleanup naming session makes tidying feel less abrupt and more like a learning game!",
        "funTwist": "Give toys personalities: 'Ball is tired! Ball says, take me home to my basket!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sort and Say",
      "duration": "4-5 min",
      "focus": "Categorization and Spatial Language",
      "researchBasis": "Waxman & Markow (1995) - Sorting activities paired with labeling accelerate category learning and vocabulary organization.",
      "content": {
        "goal": "Sort toys into categories while using spatial and category words (in, together, toys, kitchen items).",
        "whatToSay": [
          "\"Let's put all the TOYS in this basket!\"",
          "\"Ball goes IN the toy basket. IN!\"",
          "\"Where does the car go? Yes! IN with the ball!\"",
          "\"Now the KITCHEN things go in this basket!\"",
          "\"Spoon goes IN the kitchen basket. Spoon and cup TOGETHER!\"",
          "\"All done! Toys together, kitchen things together!\""
        ],
        "whatToDo": [
          "Set up 2-3 labeled areas: 'Toys,' 'Kitchen Items,' 'Bath Toys' (or whatever categories fit your items).",
          "Model sorting: pick up ball, say 'Ball is a TOY,' walk to toy basket, say 'Ball goes IN,' drop it in.",
          "Emphasize spatial words with gestures: point IN the basket, show items going TOGETHER.",
          "Ask your baby: 'Where does the car go?' and guide them to the correct basket.",
          "If your baby chooses wrong basket, gently correct: 'Car is a toy! Let's put it with the toys!'",
          "Celebrate all sorting attempts: 'You put the cup IN! Great job!'",
          "Narrate constantly: 'IN the basket, ON the shelf, WITH the other toys.'"
        ],
        "examples": [
          "Baby puts duck in bath basket → 'Yes! Duck goes IN the bath basket! Duck is a bath toy!'",
          "Baby reaches for car → 'Car! Where does car go?' → baby points to toy basket → 'Right! Car goes there!'",
          "Stack items: 'Put the bowl ON TOP of the spoon! On top!'"
        ],
        "watchFor": [
          "your baby sorting items into correct categories more consistently.",
          "Understanding spatial words—putting things IN vs. ON vs. UNDER.",
          "Following two-step directions: 'Get the ball and put it in the basket.'",
          "Initiating cleanup actions independently—internalized routine."
        ],
        "parentTip": "Don't obsess over perfect sorting—the language practice matters more than category accuracy at this age!",
        "funTwist": "Make it a race: 'How fast can we put toys IN? Ready, set, GO!' (keep it playful, not stressful)"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Cleanup Song Celebration",
      "duration": "2-3 min",
      "focus": "Routine Integration Through Music",
      "researchBasis": "Thiessen & Saffran (2009) - Consistent cleanup songs create predictable routines that support vocabulary retention and transition management.",
      "content": {
        "goal": "End cleanup with a celebratory song that reinforces vocabulary and makes tidying fun.",
        "whatToSay": [
          "\"Time for our Cleanup Song!\"",
          "\"Clean up, clean up, everybody everywhere!\"",
          "\"Clean up, clean up, everybody do your share!\"",
          "\"Put the TOYS away, put the TOYS away!\"",
          "\"IN the basket, IN the basket, we're all done today!\""
        ],
        "whatToDo": [
          "Sing a simple cleanup song (traditional or make your own melody).",
          "Do cleanup actions while singing: put items away, close lids, push baskets to storage.",
          "Encourage your baby to move and clean while singing.",
          "Add toy names to the song: 'Put the BALL away, put the CAR away!'",
          "Use hand motions: mime putting things IN, patting baskets closed.",
          "End with a celebration: 'All clean! We did it! High five!'",
          "Make this your consistent cleanup song for every tidying session."
        ],
        "examples": [
          "Sing while both putting toys in basket—teamwork!",
          "Stop singing mid-song and freeze—baby notices and waits—resume and they engage again",
          "Change lyrics daily: 'Put the cup away, put the spoon away!'"
        ],
        "watchFor": [
          "your baby beginning to anticipate cleanup when song starts.",
          "Joining in movements even if not singing words yet.",
          "Continuing to clean while song plays—music motivates action.",
          "Showing satisfaction when cleanup is complete—pride in helping."
        ],
        "parentTip": "Consistent cleanup songs become powerful cues—eventually your baby will start tidying when they hear the first note!",
        "funTwist": "Let your baby 'conduct' the song—when they wave arms, you all clean faster; when they stop, everyone freezes!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 Amazing cleanup work! You're such a great helper!",
          "You practiced:",
          "✓ Naming toys before putting them away",
          "✓ Sorting toys into categories",
          "✓ Using words like IN, ON, TOGETHER",
          "✓ Singing our cleanup song!"
        ],
        "reviewQuestions": [
          "Did your baby recognize where different items belong?",
          "Is your baby understanding spatial words like 'in' and 'on'?",
          "How did your baby respond to the cleanup song—helpful, engaged?",
          "Which toys can your baby now name consistently?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Embedding vocabulary in daily routines like cleanup ensures your baby practices words multiple times per day naturally. Spatial language and categorization skills developed during cleanup directly support later reading comprehension and logical thinking."
        },
        "nextSteps": [
          "Use this cleanup routine every day—consistency builds mastery.",
          "Next week begins Colors and Textures—building on this week's object naming foundation!",
          "Add new spatial words gradually: 'under the table,' 'next to the chair,' 'behind the door.'",
          "Celebrate cleanup as a team effort—'We clean together!' builds social vocabulary too."
        ]
      }
    }
  ]
},

// ---------- LESSON 26 (Week 6 Day 1) ----------
{
  "id": 26,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 0,
  "title": "Lesson 26: \"Soft and Rough\" — Touch Exploration",
  "theme": "Colors and Textures",
  "skillRecurrenceTags": ["sensory-vocabulary", "texture-discrimination", "descriptive-language"],
  "relatedToPreviousSkills": ["object-labeling", "descriptive-language", "tactile-exploration"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Texture Discovery!",
      "duration": "1 min",
      "content": {
        "goal": "Begin Week 6's focus on sensory vocabulary by exploring contrasting textures (soft vs. rough) through hands-on play.",
        "parentGuide": [
          "Texture words are abstract descriptors that become concrete through touch—sensory experience makes language stick.",
          "Comparing opposites (soft/rough, smooth/bumpy) helps your baby understand that words describe qualities, not just name objects.",
          "Rich sensory vocabulary predicts stronger reading comprehension—descriptive language helps children visualize stories.",
          "This week builds on Week 5's object naming by adding layers of description to familiar items.",
          "Multi-sensory learning (touch + language) creates stronger neural pathways than words alone."
        ],
        "materials": ["Soft items: blanket, stuffed animal, cotton ball, pillow", "Rough items: sandpaper, brush, textured ball, sponge", "Basket or bag for exploration"],
        "tip": "Let your baby touch BEFORE you label—the sensory experience primes them to understand the word.",
        "researchNote": "Lobo & Galloway (2013) - Tactile exploration combined with language labeling enhances both sensory processing and vocabulary acquisition."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Soft Touch Time",
      "duration": "3-4 min",
      "focus": "Introducing 'Soft' Concept",
      "researchBasis": "Gentner (2003) - Learning adjectives requires comparison and contrast; introducing one property first provides baseline for comparison.",
      "content": {
        "goal": "Introduce the concept of 'soft' through exploration of multiple soft items.",
        "whatToSay": [
          "\"Feel this blanket. So SOFT! Soft blanket!\"",
          "\"Touch your bear. Soft! Soft bear!\"",
          "\"This cotton ball is soft too! Everything is SOFT!\"",
          "\"Soft, soft, soft! Can you say soft?\"",
          "\"All these things feel SOFT when we touch them!\""
        ],
        "whatToDo": [
          "Gather 3-4 clearly soft items and place them in front of your baby.",
          "Pick up the blanket, feel it yourself dramatically: 'Ohhh, SOFT!'",
          "Hand it to your baby and guide their hand to stroke it gently.",
          "Say 'soft' 3-4 times while your baby explores it.",
          "Move to next soft item (bear), repeat the process.",
          "After exploring all soft items, review: 'They're ALL soft! Soft, soft, soft!'",
          "Encourage your baby to touch each item again as you name them.",
          "Make exaggerated happy faces when touching soft things—emotional connection enhances memory."
        ],
        "examples": [
          "Blanket: Rub it on baby's cheek: 'Soft on your face! Soft blanket!'",
          "Bear: Hug it: 'Soft hugs! Bear is soft!'",
          "Cotton ball: Let baby squish it: 'Squeeze the soft cotton! So soft!'"
        ],
        "watchFor": [
          "your baby exploring items tactilely—squeezing, stroking, patting.",
          "Facial expressions showing pleasure or interest in soft textures.",
          "Attempting to say 'soft' or making related sounds ('sah,' 'soff').",
          "Independently reaching for soft items after initial introduction."
        ],
        "parentTip": "Use the word 'soft' constantly during this activity—repetition in a single context builds strong associations!",
        "funTwist": "Play 'soft treasure hunt': hide soft items around the room and search for 'soft things' together!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Rough Discovery",
      "duration": "3-4 min",
      "focus": "Introducing 'Rough' as Contrast",
      "researchBasis": "Smith & Sera (1992) - Children learn adjectives more effectively when presented with clear perceptual contrasts.",
      "content": {
        "goal": "Introduce 'rough' texture and begin contrasting it with 'soft' to build understanding of opposites.",
        "whatToSay": [
          "\"Now feel THIS. It's different! This is ROUGH!\"",
          "\"Rough sandpaper—not soft! ROUGH!\"",
          "\"Touch the brush. Rough! Different from soft!\"",
          "\"Soft is smooth. Rough is bumpy and scratchy!\"",
          "\"Can you feel the difference? Soft... rough!\""
        ],
        "whatToDo": [
          "Introduce first rough item (sandpaper or brush) by letting your baby touch it.",
          "Say 'rough' with slightly different intonation than 'soft'—vocal contrast helps differentiation.",
          "Show gentle concern face (not scary, just different) when touching rough items.",
          "Let your baby explore 2-3 rough items, naming each one 'rough.'",
          "After introducing rough items, bring back one soft item for direct comparison.",
          "Have your baby touch soft, then rough, then soft again: 'Soft... rough... soft!'",
          "Emphasize the contrast: 'The blanket is SOFT. The sandpaper is ROUGH. Different!'"
        ],
        "examples": [
          "Sandpaper: Gently rub baby's hand on it: 'Feel that? Rough! Bumpy rough!'",
          "Brush: Stroke bristles gently on their palm: 'Rough brush! Not smooth!'",
          "Compare: 'Touch the bear—soft! Now touch the brush—rough! See?'"
        ],
        "watchFor": [
          "your baby showing different reactions to rough vs. soft textures.",
          "Pulling hand away from rough items (showing sensory discrimination).",
          "Looking between soft and rough items when you name them—making comparisons.",
          "Attempting to imitate 'rough' (harder to say than 'soft')—celebrate attempts!"
        ],
        "parentTip": "Some babies dislike rough textures—respect their comfort! If they resist, use milder 'rough' items like textured fabric.",
        "funTwist": "Make silly faces for each texture: happy smile for soft, surprised face for rough!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Texture Sort Challenge",
      "duration": "3-4 min",
      "focus": "Active Categorization",
      "researchBasis": "Waxman & Klibanoff (2000) - Categorization tasks with perceptual properties strengthen conceptual understanding of adjectives.",
      "content": {
        "goal": "Have your baby actively sort items into 'soft' and 'rough' categories to demonstrate understanding.",
        "whatToSay": [
          "\"Let's sort! Soft things go HERE. Rough things go HERE.\"",
          "\"Feel this blanket. Is it soft or rough?\"",
          "\"SOFT! Put it in the soft pile!\"",
          "\"Now touch the sandpaper. Soft or rough?\"",
          "\"ROUGH! It goes in the rough pile!\"",
          "\"You're sorting! You know soft and rough!\""
        ],
        "whatToDo": [
          "Create two distinct areas: one side for 'soft,' one side for 'rough' (use baskets or just floor spaces).",
          "Label each area clearly: 'This is the SOFT side!' (place blanket as marker).",
          "Hold up items one at a time and ask: 'Is this soft or rough?'",
          "Let your baby touch it first before choosing.",
          "Guide them to place it in the correct pile.",
          "If they choose wrong, let them feel both piles: 'Feel the soft pile—all soft! Feel the rough pile—all rough! Where does this go?'",
          "Celebrate all attempts: 'You're thinking about it! That's wonderful!'",
          "Mix items and sort again if your baby is engaged."
        ],
        "examples": [
          "Baby touches cotton ball → you ask 'soft or rough?' → baby puts in soft pile → celebrate!",
          "Baby grabs brush → feels it → looks unsure → you model: 'Feel it—rough! Rough side!'",
          "Baby correctly sorts 3 items in a row → big celebration: 'You KNOW textures!'"
        ],
        "watchFor": [
          "your baby pausing to feel items before sorting—showing intentional discrimination.",
          "Correctly sorting items more often than randomly placing them.",
          "Self-correcting—picking up item, feeling it again, moving it to other pile.",
          "Showing confidence growing—faster sorting with repeated rounds."
        ],
        "parentTip": "At this age, even 50% accuracy shows understanding! The process of comparing and deciding matters most.",
        "funTwist": "Play 'texture spy': 'I spy something SOFT in this room—can you find it?'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're a texture expert! You know soft and rough!",
          "You practiced:",
          "✓ Feeling soft things (blanket, bear, cotton)",
          "✓ Feeling rough things (sandpaper, brush)",
          "✓ Telling the difference between soft and rough",
          "✓ Sorting textures into groups!"
        ],
        "reviewQuestions": [
          "Could your baby differentiate between soft and rough textures?",
          "Which texture seemed to engage your baby more?",
          "Did your baby attempt to say 'soft' or 'rough'?",
          "How accurately could your baby sort items by texture?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Texture vocabulary builds descriptive language skills that help your baby communicate experiences and preferences. Understanding adjectives through sensory experience creates a foundation for comprehending descriptive passages in books—'the soft bunny' or 'the rough tree bark' will have concrete meaning."
        },
        "nextSteps": [
          "Point out soft and rough textures throughout the day: 'soft towel,' 'rough sidewalk.'",
          "Let your baby choose between textures: 'Do you want the soft blanket or rough one?'",
          "Tomorrow we add COLOR to our learning—visual descriptors joining tactile ones!",
          "Read books with texture words and let your baby touch the pictures while you describe them."
        ]
      }
    }
  ]
},

// ---------- LESSON 27 (Week 6 Day 2) ----------
{
  "id": 27,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 1,
  "title": "Lesson 27: \"The Color Song\" — Name and Point",
  "theme": "Colors and Textures",
  "skillRecurrenceTags": ["color-recognition", "pointing", "music-integration"],
  "relatedToPreviousSkills": ["object-labeling", "pointing-gesture", "song-learning"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Color Time!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce basic color vocabulary (red, blue, yellow) through song, pointing, and object association.",
        "parentGuide": [
          "Color recognition typically develops between 18-36 months, but early exposure builds neural pathways for later mastery.",
          "Pairing colors with songs makes abstract concepts more memorable through melody and rhythm.",
          "At this age, focus on receptive understanding (identifying colors you name) rather than expressive naming.",
          "Colors are everywhere—this lesson opens doors to vocabulary-rich conversations throughout the day.",
          "Repetition across many contexts (toys, clothes, food, books) strengthens color word learning."
        ],
        "materials": ["Objects in 3 primary colors: red ball/toy, blue block/cup, yellow duck/item", "Optional: colored scarves or fabric squares"],
        "tip": "Start with just 2-3 colors maximum—too many choices overwhelm and dilute learning at this age.",
        "researchNote": "Sandhofer & Smith (1999) - Color word learning requires many exposures across varied contexts; children learn color names gradually through repeated association."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Colors",
      "duration": "3-4 min",
      "focus": "Initial Color Introduction",
      "researchBasis": "Wagner et al. (2013) - Labeling colors with consistent objects helps toddlers form initial color-word associations.",
      "content": {
        "goal": "Introduce red, blue, and yellow by pairing each color word with a specific object.",
        "whatToSay": [
          "\"Look! This ball is RED. Red ball!\"",
          "\"See the red? Red like an apple! Red!\"",
          "\"Now look—BLUE block! Blue!\"",
          "\"Blue like the sky! Blue block!\"",
          "\"And here's YELLOW! Yellow duck! Bright yellow!\"",
          "\"Red, blue, yellow—three beautiful colors!\""
        ],
        "whatToDo": [
          "Hold up the red object first, very close to your baby at eye level.",
          "Say 'red' clearly 4-5 times while your baby looks at it.",
          "Let your baby touch and explore the red object for 15-20 seconds.",
          "Put red object down (but keep it visible).",
          "Introduce blue object the same way: hold, name repeatedly, let explore.",
          "Then introduce yellow object with same process.",
          "Line up all three objects and review: point to each, saying its color.",
          "Keep your tone enthusiastic and varied—engagement drives attention."
        ],
        "examples": [
          "Red ball: 'Red! This is red! Red ball! Touch the red!'",
          "Blue block: 'Blue! See blue? Blue block! Hold the blue!'",
          "Yellow duck: 'Yellow! Bright yellow! Yellow duck! Squeeze yellow!'"
        ],
        "watchFor": [
          "your baby making eye contact with each object when you name its color.",
          "Reaching for or touching objects as you introduce them.",
          "Visual tracking between objects as you review colors.",
          "Any vocalization attempts—'boo' for blue, 'yeh' for yellow count!"
        ],
        "parentTip": "Don't expect your baby to say color words yet—we're building receptive vocabulary that will support later production.",
        "funTwist": "Make each color 'talk': 'Red ball says: I'm red!' 'Blue block says: Look at my blue!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: The Color Song",
      "duration": "4-5 min",
      "focus": "Musical Reinforcement",
      "researchBasis": "Thiessen & Saffran (2009) - Musical structure supports word segmentation and memory; songs make vocabulary sticky.",
      "content": {
        "goal": "Teach a simple color song that pairs each color with an action and object.",
        "whatToSay": [
          "\"Let's sing the Color Song!\"",
          "\"Red, red, red—find something red!\"",
          "\"(Point to red ball) There! Red, red, red!\"",
          "\"Blue, blue, blue—find something blue!\"",
          "\"(Point to blue block) Yes! Blue, blue, blue!\"",
          "\"Yellow, yellow, yellow—find something yellow!\"",
          "\"(Point to yellow duck) Hooray! Yellow, yellow, yellow!\""
        ],
        "whatToDo": [
          "Create a simple repetitive tune (or use a familiar melody like 'Twinkle Twinkle').",
          "Sing each color name three times with rhythm: 'Red, red, red!'",
          "After singing the color, pause and wait for your baby to point to or look at that colored object.",
          "When they identify it (or you guide them), celebrate: 'YES! Red!'",
          "Repeat for all three colors.",
          "Do 2-3 rounds of the song, mixing up the order of colors.",
          "Encourage your baby to point or reach during the pause after each color.",
          "Add clapping or swaying to make it physically engaging."
        ],
        "examples": [
          "Sing: 'Red, red, red—find something red!' → baby looks at ball → you cheer and point!",
          "Sing: 'Blue, blue, blue!' → you model pointing to blue block → baby copies",
          "Sing all three colors in sequence, then ask: 'Which color should we sing first?'"
        ],
        "watchFor": [
          "your baby anticipating which object to look at when you sing a color.",
          "Attempting to point during the pause.",
          "Moving or bouncing to the rhythm of the song.",
          "Beginning to associate the color word sound with the correct object."
        ],
        "parentTip": "Singing the same color song daily creates a ritual—your baby will start anticipating colors before you name them!",
        "funTwist": "Let your baby hold each colored object while you sing about it—multisensory learning!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Color Hunt Challenge",
      "duration": "3-4 min",
      "focus": "Active Color Recognition",
      "researchBasis": "Booth et al. (2005) - Active search tasks requiring color identification strengthen color-word mappings.",
      "content": {
        "goal": "Encourage your baby to find and retrieve objects based on color names alone.",
        "whatToSay": [
          "\"Can you find something RED? Look for red!\"",
          "\"Yes! You found the red ball! Red!\"",
          "\"Now let's find BLUE! Where's something blue?\"",
          "\"Great! You found blue! That's the blue block!\"",
          "\"Last one—find YELLOW! Can you get yellow?\"",
          "\"You did it! Yellow duck! You found all the colors!\""
        ],
        "whatToDo": [
          "Place the three colored objects in different spots (but within easy reach).",
          "Ask your baby to find one specific color: 'Get the red one!'",
          "Wait 5-7 seconds for your baby to process and respond.",
          "Celebrate ANY attempt—even looking at the right object counts as success.",
          "If your baby grabs the wrong one, gently redirect: 'That's blue! Let's find red together!'",
          "Guide their hand if needed: 'Here's red! You found red!'",
          "Repeat with each color 2-3 times, mixing up locations.",
          "Gradually reduce your pointing/guiding as your baby gains confidence."
        ],
        "examples": [
          "Say 'find red' → baby looks at red ball → you celebrate and hand it to them",
          "Say 'get blue' → baby grabs yellow duck → you say 'That's yellow! Blue is here!'",
          "Say 'where's yellow?' → baby successfully retrieves yellow duck → big celebration!"
        ],
        "watchFor": [
          "your baby scanning objects before reaching—cognitive processing visible.",
          "Correctly identifying colors more than 50% of the time (chance would be 33%).",
          "Processing time decreasing—faster recognition with practice.",
          "Showing excitement when they get it right—internal motivation building."
        ],
        "parentTip": "Color recognition is complex—expect inconsistency! Some days your baby will 'know' colors, other days not. Keep practicing!",
        "funTwist": "Play 'color delivery': 'Take the red ball to Daddy! Bring the blue block to the couch!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're learning colors! Red, blue, yellow—amazing!",
          "You practiced:",
          "✓ Looking at red, blue, and yellow objects",
          "✓ Singing the Color Song",
          "✓ Pointing to colors when named",
          "✓ Finding colored objects!"
        ],
        "reviewQuestions": [
          "Which color did your baby seem to recognize most consistently?",
          "Did your baby enjoy the Color Song—singing, moving, participating?",
          "Could your baby find the correct color when asked?",
          "Did your baby attempt to say any color words?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Color vocabulary is a gateway to descriptive language. As your baby learns colors, they gain tools to describe their world with more precision: 'I want the red cup' is more specific than 'I want that.' Color words also appear constantly in books, making reading comprehension richer."
        },
        "nextSteps": [
          "Point out these three colors throughout the day: 'Your red shirt!' 'Blue sky!' 'Yellow banana!'",
          "Sing the Color Song during transitions—car rides, diaper changes, waiting times.",
          "Tomorrow we'll go on a color hunt around the house!",
          "Read books with bold colors and name them as you point to pictures."
        ]
      }
    }
  ]
},

// ---------- LESSON 28 (Week 6 Day 3) ----------
{
  "id": 28,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 2,
  "title": "Lesson 28: \"Find Something Blue!\" — Object Hunt",
  "theme": "Colors and Textures",
  "skillRecurrenceTags": ["color-recognition", "search-skills", "environmental-vocabulary"],
  "relatedToPreviousSkills": ["color-recognition", "pointing", "retrieval"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Color Hunt!",
      "duration": "1 min",
      "content": {
        "goal": "Strengthen color recognition by finding colored objects throughout the environment, transferring learning to new contexts.",
        "parentGuide": [
          "Transferring color knowledge from toys to everyday objects shows true understanding—not just memorization.",
          "Movement-based learning (hunting, walking, searching) engages multiple brain regions, strengthening memory.",
          "This lesson expands your baby's world—suddenly EVERYTHING has a color to notice and name.",
          "Finding colors in various contexts (clothes, furniture, food) generalizes the vocabulary.",
          "Active searching builds problem-solving skills alongside color recognition."
        ],
        "materials": ["Items from yesterday's lesson (red, blue, yellow)", "Your home environment with various colored objects", "Optional: basket to collect found items"],
        "tip": "Start in one room to avoid overwhelming—you can expand to other spaces as your baby gains confidence.",
        "researchNote": "O'Hanlon & Roberson (2006) - Color word learning requires exposure to colors across many different objects and contexts to build abstract color categories."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Color Review Walk",
      "duration": "3-4 min",
      "focus": "Warm-Up and Confidence Building",
      "researchBasis": "Sandhofer & Smith (1999) - Reviewing learned color words immediately before extension tasks improves transfer success.",
      "content": {
        "goal": "Quickly review yesterday's three colors using the same objects before expanding to new items.",
        "whatToSay": [
          "\"Remember our colors? Let's check!\"",
          "\"Here's RED! Touch the red ball!\"",
          "\"And BLUE! Where's our blue block?\"",
          "\"And YELLOW! Show me yellow duck!\"",
          "\"Great! Now let's find MORE red, blue, and yellow things!\""
        ],
        "whatToDo": [
          "Bring out the three familiar colored objects from Lesson 27.",
          "Quickly review each color: hold up, name, let your baby touch.",
          "Sing one round of yesterday's Color Song for reinforcement.",
          "Then announce: 'Now we're going on a COLOR HUNT!'",
          "Build excitement: 'Let's walk around and find colors everywhere!'",
          "Take your baby's hand and begin walking slowly around the room.",
          "Point out one example: 'Look! I see something red! The pillow is red!'"
        ],
        "examples": [
          "Point to red ball: 'Red!' → baby touches it → 'Yes, red ball!'",
          "Hold up blue block: 'What color?' → baby looks or points → 'Blue! Blue block!'",
          "Quick color test: 'Find yellow!' → baby gets duck → 'Perfect! Now let's find MORE yellow!'"
        ],
        "watchFor": [
          "your baby quickly recognizing the three review colors.",
          "Showing excitement about the upcoming 'hunt' activity.",
          "Making connections: looking around room when you say 'find more colors.'",
          "Physical readiness—willing to walk or be carried to explore."
        ],
        "parentTip": "This quick review primes your baby's brain—they'll be more successful finding new colored objects after reviewing familiar ones.",
        "funTwist": "Give your baby a 'collection basket' to put found colors in—makes them feel like a serious treasure hunter!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: The Great Color Hunt",
      "duration": "5-6 min",
      "focus": "Color Transfer and Generalization",
      "researchBasis": "Smith et al. (2002) - Learning to categorize across different exemplars (many blue things) builds robust category knowledge.",
      "content": {
        "goal": "Search the environment for objects matching target colors, expanding color vocabulary to varied contexts.",
        "whatToSay": [
          "\"Let's find something BLUE! Look around—where's blue?\"",
          "\"I see blue! The towel is blue! Touch the blue towel!\"",
          "\"You found it! Blue towel! Just like our blue block!\"",
          "\"Now find RED! Let's look for red things!\"",
          "\"Red cup! You found red! That's red like our red ball!\"",
          "\"Last color—find YELLOW! Where's yellow?\""
        ],
        "whatToDo": [
          "Choose one color to hunt for first (start with their strongest color from yesterday).",
          "Walk slowly through the room, scanning for that color together.",
          "When you spot something, point dramatically: 'I see blue!'",
          "Guide your baby to touch or point to the blue item.",
          "Name it with color emphasis: 'Blue towel! Blue like our blue block!'",
          "Let your baby lead sometimes—if they point to something, check its color together.",
          "Switch to next color after finding 2-3 items in first color.",
          "Create a pile or basket of found items if possible—visual collection of learning.",
          "Make connections explicit: 'This blue cup and this blue block—both BLUE!'"
        ],
        "examples": [
          "Hunt for blue → find blue book → 'Blue book! Just like blue block! Both blue!'",
          "Baby points to something → you check: 'Is that red? Yes! Red pillow! You found red!'",
          "Find yellow banana in kitchen → 'Yellow banana! Yellow like our yellow duck!'"
        ],
        "watchFor": [
          "your baby independently pointing to or reaching for correctly colored items.",
          "Showing recognition when you name colors—looking around for that color.",
          "Making connections between different objects of same color.",
          "Growing confidence—pointing more quickly, scanning more efficiently."
        ],
        "parentTip": "If your baby points to wrong color, celebrate their looking: 'Good looking! That's green! Let's find blue!' (Don't introduce new colors yet—stay focused on the three)",
        "funTwist": "Take photos of each found color—create a 'color book' your baby can look at later to review!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Color Sort Celebration",
      "duration": "2-3 min",
      "focus": "Categorization Review",
      "researchBasis": "Waxman & Markow (1995) - Sorting collected items by shared properties reinforces category boundaries and labels.",
      "content": {
        "goal": "Review all found objects by sorting them into color groups, reinforcing that different objects can share color.",
        "whatToSay": [
          "\"Look at all the colors we found! Let's sort them!\"",
          "\"All the RED things go here. Red pillow, red cup—together!\"",
          "\"BLUE things go here. Blue towel, blue block—all blue!\"",
          "\"YELLOW things here! Yellow duck, yellow toy—yellow!\"",
          "\"Wow! So many reds! So many blues! So many yellows!\""
        ],
        "whatToDo": [
          "Gather all the colored items you found during the hunt.",
          "Create three areas on the floor or table, one for each color.",
          "Pick up first item: 'The red cup—where does it go? With the other red things!'",
          "Place it in the red pile.",
          "Hand your baby the next item: 'Where does the blue towel go?'",
          "Guide them to put it in the correct pile if needed.",
          "Continue sorting all found items by color.",
          "Review each pile: 'ALL red! Even though they're different things, they're all RED!'",
          "Let your baby mess up piles and re-sort—repetition solidifies learning."
        ],
        "examples": [
          "Red pile: cup, ball, pillow, toy → all different but all red",
          "Baby puts blue block with blue towel → celebrate: 'Yes! Both blue! Together!'",
          "Baby unsure about yellow item → feel it together: 'Is this yellow? Yes! Yellow pile!'"
        ],
        "watchFor": [
          "your baby correctly sorting items more often than randomly placing.",
          "Understanding that different objects (cup, towel, block) can share a color property.",
          "Self-correcting—moving items between piles when reconsidering.",
          "Showing satisfaction when piles are organized—early sense of order."
        ],
        "parentTip": "Sorting is abstract thinking—your baby is learning that 'color' is a property that exists across different objects, not tied to one thing.",
        "funTwist": "Count items in each pile: 'One red, two red, three red!' (early math integration with colors)"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're a color detective! You found colors everywhere!",
          "You practiced:",
          "✓ Hunting for red, blue, and yellow objects",
          "✓ Finding colors in different things",
          "✓ Sorting colors into groups",
          "✓ Seeing that MANY things can be the same color!"
        ],
        "reviewQuestions": [
          "Could your baby find colors in new objects beyond the original toys?",
          "Which color was easiest for your baby to identify in the environment?",
          "Did your baby show excitement during the hunt—engaged searching?",
          "Was your baby able to sort items by color with some accuracy?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Transfer of learning—applying color knowledge to new objects—shows true conceptual understanding, not just memorization. This cognitive skill is exactly what your baby will need for reading: understanding that the letter 'B' represents the same sound whether it's in 'ball,' 'book,' or 'baby.'"
        },
        "nextSteps": [
          "Continue color hunts daily: 'Find something blue before we leave!' makes transitions educational.",
          "Point out colors during all activities: dressing, eating, playing, reading.",
          "Tomorrow we explore TEXTURE again—smooth versus bumpy!",
          "Praise color observations: if your baby points at something, name its color even if they didn't ask."
        ]
      }
    }
  ]
},

// ---------- LESSON 29 (Week 6 Day 4) ----------
{
  "id": 29,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 3,
  "title": "Lesson 29: \"Texture Trail\" — Smooth vs. Bumpy",
  "theme": "Colors and Textures",
  "skillRecurrenceTags": ["texture-discrimination", "sensory-vocabulary", "movement-learning"],
  "relatedToPreviousSkills": ["soft-rough", "tactile-exploration", "descriptive-language"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Texture Trail!",
      "duration": "1 min",
      "content": {
        "goal": "Expand texture vocabulary by introducing smooth and bumpy, creating a physical path for exploration.",
        "parentGuide": [
          "Building on soft/rough from Lesson 26, this lesson adds a second texture pair to expand descriptive vocabulary.",
          "Creating a physical 'trail' integrates gross motor movement with sensory learning—embodied cognition strengthens memory.",
          "Smooth and bumpy are slightly more nuanced than soft/rough, offering progression in perceptual discrimination.",
          "Texture words help your baby describe their experiences: 'I don't like bumpy socks' or 'I want the smooth cup.'",
          "Multiple texture words build a richer descriptive vocabulary foundation for later reading comprehension."
        ],
        "materials": ["Smooth items: paper, plastic plate, book cover, smooth fabric", "Bumpy items: bubble wrap, textured mat, bumpy ball, corrugated cardboard", "Tape to create floor trail"],
        "tip": "Create the trail while your baby watches—the setup process builds anticipation and attention!",
        "researchNote": "Bushnell & Boudreau (1993) - Haptic (touch-based) exploration combined with verbal labeling accelerates perceptual category development in toddlers."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet Smooth and Bumpy",
      "duration": "3-4 min",
      "focus": "Texture Introduction and Contrast",
      "researchBasis": "Gentner & Namy (1999) - Comparison facilitates learning—presenting smooth and bumpy together clarifies the contrast.",
      "content": {
        "goal": "Introduce smooth and bumpy textures through hands-on exploration and direct comparison.",
        "whatToSay": [
          "\"Feel this paper. So SMOOTH! Smooth like glass!\"",
          "\"Smooth, smooth, smooth! Run your hand on it!\"",
          "\"Now feel THIS! It's BUMPY! Bumpy, bumpy!\"",
          "\"Bumpy has lumps! Not smooth—BUMPY!\"",
          "\"Smooth is flat. Bumpy has bumps! Feel the difference!\""
        ],
        "whatToDo": [
          "Sit with your baby and present a clearly smooth item (paper or plastic plate).",
          "Run your hand across it slowly with exaggerated smoothness: 'Smooooooth!'",
          "Guide your baby's hand across it several times while repeating 'smooth.'",
          "Set smooth item aside (but visible).",
          "Present bumpy item (bubble wrap or textured ball).",
          "Touch it with different expression—more tentative: 'Bumpy! Feel the bumps!'",
          "Let your baby explore the bumpy texture.",
          "Bring both items back for direct comparison: 'Smooth... bumpy... smooth... bumpy!'",
          "Have your baby alternate touching each while you name them."
        ],
        "examples": [
          "Smooth paper: Glide hand across: 'Smooth! No bumps! Flat smooth!'",
          "Bumpy bubble wrap: Poke at bubbles: 'Bumpy! Feel bumps! Pop, pop—bumpy!'",
          "Compare: 'Touch smooth paper, now touch bumpy wrap—different!'"
        ],
        "watchFor": [
          "your baby exploring both textures with hands, not just looking.",
          "Different exploratory behaviors for each texture—smoothing vs. poking.",
          "Facial expressions showing recognition of differences.",
          "Attempting to say 'smooth' or 'bumpy' or approximations ('moo,' 'buh-pee')."
        ],
        "parentTip": "Smooth and bumpy are more subtle than soft/rough—give your baby extra time to feel the differences!",
        "funTwist": "Make sound effects: 'Smooth goes shhhhhh' (gliding sound), 'Bumpy goes buh-buh-buh!' (staccato)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Build the Texture Trail",
      "duration": "4-5 min",
      "focus": "Multi-Sensory Exploration Through Movement",
      "researchBasis": "Thelen & Smith (1994) - Motor activity and perception are deeply integrated; movement enhances sensory learning.",
      "content": {
        "goal": "Create a path of alternating smooth and bumpy textures for your baby to walk, crawl, or touch along.",
        "whatToSay": [
          "\"Let's make a Texture Trail! Watch!\"",
          "\"First, SMOOTH paper! Step on smooth!\"",
          "\"Next, BUMPY bubble wrap! Feel the bumps!\"",
          "\"Smooth, then bumpy, smooth, then bumpy!\"",
          "\"Let's walk the trail and feel everything!\""
        ],
        "whatToDo": [
          "Tape smooth items to the floor in a line (paper, smooth fabric, plastic).",
          "Alternate with bumpy items (bubble wrap, textured mat, cardboard).",
          "Create a trail of 6-8 items: smooth-bumpy-smooth-bumpy pattern.",
          "Walk the trail yourself first, modeling: step on each, say its texture loudly.",
          "Invite your baby to walk/crawl the trail with you.",
          "Hold their hand if needed for balance.",
          "At each section, stop and feel with hands AND feet: 'Smooth under your feet!'",
          "Name the texture every time your baby steps/touches: 'Bumpy! Smooth! Bumpy!'",
          "Walk the trail 2-3 times, each time naming textures clearly.",
          "Let your baby explore off-trail too—return to items that interest them most."
        ],
        "examples": [
          "Step on paper: 'Smooth! Feel smooth under your feet!'",
          "Step on bubble wrap: 'Bumpy! Pop-pop-bumpy!'",
          "Crawl over textured mat: 'Bumpy on your hands! Bumpy knees!'"
        ],
        "watchFor": [
          "your baby responding differently to smooth vs. bumpy sections—slowing down, reacting.",
          "Wanting to touch items with hands while standing/crawling on them.",
          "Anticipating texture changes—looking ahead on the trail.",
          "Choosing to revisit favorite textures—showing preference development."
        ],
        "parentTip": "If your baby is hesitant about the trail, sit beside it and explore with hands first—walking can come later!",
        "funTwist": "Add a 'magic' element: 'When we step on smooth, we freeze like ice! When we step on bumpy, we bounce!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Texture Trail Challenge",
      "duration": "3-4 min",
      "focus": "Active Recognition and Choice",
      "researchBasis": "Clearfield (2004) - Self-directed exploration with verbal scaffolding promotes faster concept learning than passive exposure.",
      "content": {
        "goal": "Ask your baby to find and move to specific textures on the trail, testing comprehension.",
        "whatToSay": [
          "\"Can you find something SMOOTH on our trail?\"",
          "\"Yes! You found smooth paper! Stand on the smooth!\"",
          "\"Now find BUMPY! Where's bumpy?\"",
          "\"Great! You're standing on bumpy bubble wrap!\"",
          "\"Can you jump from smooth to bumpy? Smooth to bumpy!\""
        ],
        "whatToDo": [
          "Stand at one end of the trail with your baby.",
          "Ask them to find a specific texture: 'Find smooth!'",
          "Let your baby walk/crawl to a smooth section.",
          "Celebrate when they arrive: 'You found smooth! You're standing on smooth!'",
          "If they choose wrong, gently guide: 'That's bumpy! Let's find smooth together!'",
          "Ask them to move between textures: 'Go from smooth to bumpy!'",
          "Repeat several times, mixing up instructions.",
          "End with free exploration: 'Show me your favorite texture!'",
          "Let your baby lead you to sections they prefer."
        ],
        "examples": [
          "Say 'find bumpy' → baby walks to bubble wrap → celebrate: 'Bumpy! You found it!'",
          "Say 'step on smooth' → baby stands on paper → 'Yes! Smooth under your feet!'",
          "Baby chooses to sit on textured mat → 'You like bumpy! That's bumpy!'"
        ],
        "watchFor": [
          "your baby correctly identifying textures more often than randomly choosing.",
          "Moving confidently along the trail—comfort with the activity.",
          "Using hands to verify texture before committing—checking their answer.",
          "Showing clear preferences—spending more time on certain textures."
        ],
        "parentTip": "If the trail is too long or complex, simplify: just 2-3 sections is enough for meaningful learning!",
        "funTwist": "Turn it into a game: 'The smooth sections are rivers! The bumpy sections are mountains! Let's adventure!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You walked the Texture Trail! You know smooth and bumpy!",
          "You practiced:",
          "✓ Feeling smooth textures (paper, plastic)",
          "✓ Feeling bumpy textures (bubble wrap, textured mat)",
          "✓ Walking and touching the Texture Trail",
          "✓ Finding smooth and bumpy when asked!"
        ],
        "reviewQuestions": [
          "Could your baby differentiate between smooth and bumpy?",
          "Did your baby enjoy the movement aspect—walking the trail?",
          "Which texture seemed to engage your baby more?",
          "Was your baby able to find requested textures with some consistency?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Now your baby has FOUR texture words: soft, rough, smooth, bumpy. This descriptive vocabulary expansion allows for more precise communication and richer story comprehension. When books describe 'the smooth stone' or 'bumpy road,' your baby will have concrete sensory memories to connect with those words."
        },
        "nextSteps": [
          "Leave the trail up for free exploration throughout the day if possible.",
          "Point out smooth and bumpy textures during daily life: 'smooth table,' 'bumpy sidewalk.'",
          "Tomorrow we integrate colors and textures: 'Find the smooth red thing!'",
          "Read books with texture descriptions and let your baby touch pictures while you describe."
        ]
      }
    }
  ]
},

// ---------- LESSON 30 (Week 6 Day 5) ----------
{
  "id": 30,
  "phase": "1-2",
  "quarter": 1,
  "week": 6,
  "weekIndex": 5,
  "dayInWeek": 4,
  "title": "Lesson 30: \"Colorful Snacks\" — Talk About What You See",
  "theme": "Colors and Textures",
  "skillRecurrenceTags": ["color-in-context", "functional-vocabulary", "mealtime-language"],
  "relatedToPreviousSkills": ["color-recognition", "descriptive-language", "routine-integration"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Colorful Snack Time!",
      "duration": "1 min",
      "content": {
        "goal": "Integrate color vocabulary into mealtime by naming food colors, building functional language use in daily routines.",
        "parentGuide": [
          "Mealtime provides 3+ daily opportunities for vocabulary practice—natural, frequent, and meaningful contexts.",
          "Food colors are vivid and varied, offering excellent color discrimination practice.",
          "Naming foods with color descriptors builds compound phrases: 'orange carrot,' 'green peas.'",
          "This lesson shows your baby that colors aren't just in toys—they're everywhere, including what we eat.",
          "Talking during meals models that language and eating happen together, setting up future mealtime conversations."
        ],
        "materials": ["Colorful snacks: orange (carrots, oranges), green (peas, cucumber), red (strawberries, tomatoes)", "Yellow (banana, cheese)", "Blue (blueberries)", "Plate or tray", "High chair or eating area"],
        "tip": "Choose 3-4 colors of foods your baby already eats—familiarity with taste lets them focus on color learning.",
        "researchNote": "Rowe (2012) - Mealtime language input, especially descriptive talk about food, strongly predicts vocabulary growth and later literacy skills."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: The Colorful Plate",
      "duration": "3-4 min",
      "focus": "Food and Color Association",
      "researchBasis": "Clerkin & Smith (2022) - Naming objects during hands-on interaction (like eating) creates stronger word-object mappings than passive viewing.",
      "content": {
        "goal": "Arrange colorful foods on a plate and name each one with its color before eating.",
        "whatToSay": [
          "\"Look at our colorful snack! So many colors!\"",
          "\"Here's an ORANGE carrot! Orange!\"",
          "\"And GREEN peas! Green like grass!\"",
          "\"RED strawberry! Bright red!\"",
          "\"YELLOW banana! Yummy yellow!\"",
          "\"Let's name the colors before we eat!\""
        ],
        "whatToDo": [
          "Arrange 3-4 different colored foods on your baby's plate in separate sections.",
          "Before your baby eats, point to each food item.",
          "Name it with color emphasis: 'This is an ORANGE carrot.'",
          "Let your baby touch or hold each food while you name its color.",
          "Go through all foods 1-2 times, naming colors clearly.",
          "Ask your baby to point: 'Where's the orange carrot?'",
          "Then give permission to eat: 'Now we can eat our colorful snack!'",
          "Continue naming colors as your baby eats: 'You're eating the green peas!'"
        ],
        "examples": [
          "Hold up carrot stick: 'Orange carrot! See the orange? Orange!' → hand to baby",
          "Point to strawberry: 'Red strawberry! Red! Touch the red!' → baby touches",
          "Show banana: 'Yellow banana! Yellow like our yellow duck! Yellow!'"
        ],
        "watchFor": [
          "your baby pointing to correct food when you name a color.",
          "Looking at foods while you name colors—making visual connections.",
          "Attempting to say color words or food names.",
          "Choosing which color to eat first—showing color recognition through choice."
        ],
        "parentTip": "Make it routine: name food colors at EVERY meal—repetition in context is incredibly powerful for vocabulary!",
        "funTwist": "Play 'guess which color I'll eat first!'—builds anticipation and engagement with colors."
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Color Taste Game",
      "duration": "4-5 min",
      "focus": "Multi-Sensory Integration",
      "researchBasis": "Bahrick & Lickliter (2000) - Multi-sensory experiences (sight, touch, taste) enhance learning and memory in infants.",
      "content": {
        "goal": "Combine color naming with tasting, creating rich sensory-language experiences.",
        "whatToSay": [
          "\"Let's taste the ORANGE carrot! Take a bite of orange!\"",
          "\"Mmm! How does orange taste? Crunchy orange!\"",
          "\"Now try GREEN! Taste the green peas!\"",
          "\"Green peas are soft! Soft green!\"",
          "\"What color should we taste next? Red or yellow?\""
        ],
        "whatToDo": [
          "Offer one colored food at a time with color emphasis.",
          "As your baby takes a bite, narrate: 'You're eating orange!'",
          "Add texture or taste descriptors: 'Crunchy orange carrot! Sweet red strawberry!'",
          "Let your baby choose which color to eat next—honoring preferences.",
          "Continue narrating colors as they eat different items.",
          "Compare: 'The orange carrot is crunchy. The yellow banana is soft!'",
          "Ask questions: 'Do you like the red strawberry? More green?'",
          "Make eating playful but focused on color vocabulary.",
          "End by reviewing: 'You ate orange, green, red, AND yellow! Colorful!'"
        ],
        "examples": [
          "Baby bites carrot → 'Crunching orange! Orange carrot is crunchy!'",
          "Baby reaches for strawberry → 'You want red! Here's the red strawberry!'",
          "Baby finishes banana → 'All gone yellow! The yellow banana is all gone!'"
        ],
        "watchFor": [
          "your baby responding to color names by reaching for correct foods.",
          "Making choices based on color: 'You picked green!'",
          "Multi-sensory engagement—looking, touching, tasting while hearing colors.",
          "Building positive associations with colors through pleasant eating experience."
        ],
        "parentTip": "Don't pressure eating—if your baby just plays with food while you name colors, that's still valuable language learning!",
        "funTwist": "Take tiny 'color bites': 'One small bite of red! One small bite of orange!' Makes it playful and controlled."
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Color Cleanup Chat",
      "duration": "2-3 min",
      "focus": "Review and Routine Integration",
      "researchBasis": "Snow (1983) - Extending vocabulary conversations beyond the main activity reinforces learning and shows language's functional purpose.",
      "content": {
        "goal": "Review colors while cleaning up after snack, reinforcing vocabulary in a routine transition.",
        "whatToSay": [
          "\"Snack time is over! Let's clean up our colors!\"",
          "\"What color is on your hands? Orange from the carrots!\"",
          "\"Let's wipe the red from the strawberries!\"",
          "\"Put the yellow banana peel in the trash!\"",
          "\"We ate so many colors today!\""
        ],
        "whatToDo": [
          "Begin cleanup while continuing color talk.",
          "Point out colors on your baby's hands, face, bib: 'I see orange on your fingers!'",
          "Name colors as you wipe or clean: 'Wiping away the red!'",
          "Ask your baby to help: 'Can you put the green peas bowl here?'",
          "Review what was eaten: 'We ate orange carrots, green peas, red strawberries, and yellow banana!'",
          "Connect to earlier lessons: 'The carrots were orange like the orange toy!'",
          "Transition smoothly: 'Now our hands are clean! Let's go play with our blue block!'",
          "Make cleanup quick and positive—still a learning moment!"
        ],
        "examples": [
          "Wipe baby's hands: 'Orange carrot juice on your hands! Orange!'",
          "Show dirty bib: 'Look—green and red on your bib! Green peas and red strawberry!'",
          "Throw away scraps: 'Bye-bye yellow banana peel! Bye-bye orange!'"
        ],
        "watchFor": [
          "your baby noticing colors in unexpected places (on hands, bib, face).",
          "Helping with cleanup when color words are used as directions.",
          "Making connections between food colors and toy colors from earlier lessons.",
          "Anticipating cleanup routine—learning that meals have predictable endings."
        ],
        "parentTip": "Cleanup is part of the meal routine—continuing language through transition prevents abrupt endings!",
        "funTwist": "Play 'spot the colors': 'I spy orange on your nose! I spy red on your bib!' while cleaning up."
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 What a colorful snack! You know so many food colors!",
          "You practiced:",
          "✓ Naming food colors (orange carrot, green peas, red strawberry, yellow banana)",
          "✓ Choosing colors to eat",
          "✓ Tasting and describing colors",
          "✓ Finding colors during cleanup!"
        ],
        "reviewQuestions": [
          "Could your baby identify food colors when asked?",
          "Did your baby show preferences for certain colored foods?",
          "Was your baby able to connect food colors to toy colors?",
          "How engaged was your baby with color talk during eating?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Integrating vocabulary into functional routines like eating ensures your baby practices words multiple times daily in meaningful contexts. Colors learned through food become personally relevant—your baby can now request 'the red one' or 'more green,' giving them more precise communication tools for expressing wants and needs."
        },
        "nextSteps": [
          "Name food colors at every meal from now on—make it a consistent habit.",
          "Next week begins Sorting and Matching—we'll compare same/different and sizes!",
          "Ask your baby to choose between colors: 'Do you want the orange carrot or green peas first?'",
          "Take photos of colorful meals and create a 'food color book' for review during non-meal times."
        ]
      }
    }
  ]
},

// ---------- LESSON 31 (Week 7 Day 1) ----------
{
  "id": 31,
  "phase": "1-2",
  "quarter": 1,
  "week": 7,
  "weekIndex": 6,
  "dayInWeek": 0,
  "title": "Lesson 31: \"Same or Different?\" — Object Pairs",
  "theme": "Sorting and Matching",
  "skillRecurrenceTags": ["comparison", "categorization", "cognitive-reasoning"],
  "relatedToPreviousSkills": ["object-recognition", "sorting", "descriptive-language"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Same and Different!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce the concepts of 'same' and 'different' through comparing pairs of objects, building classification skills.",
        "parentGuide": [
          "Same/different is a foundational cognitive skill—it's how we categorize everything in our world.",
          "This concept underlies reading: recognizing that 'B' and 'b' are the same letter despite looking different, or that 'b' and 'd' are different despite looking similar.",
          "At this age, your baby is building the neural pathways for comparison—don't expect mastery, just exposure and practice.",
          "Visual discrimination (seeing differences) develops before verbal expression of those differences.",
          "This week's sorting and matching theme strengthens logical thinking and organizational skills crucial for all learning."
        ],
        "materials": ["Pairs of identical objects (2 red balls, 2 spoons, 2 blocks)", "Pairs of different objects (ball and block, spoon and cup)", "Basket or tray"],
        "tip": "Start with VERY obvious differences—color, size, or type—before introducing subtle variations.",
        "researchNote": "Smith (1989) - Comparison tasks accelerate category learning; toddlers learn concepts faster when given examples and non-examples side by side."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Same Twins",
      "duration": "3-4 min",
      "focus": "Introducing 'Same' Concept",
      "researchBasis": "Gentner & Namy (1999) - Children learn relational concepts like 'same' through explicit comparison and labeling.",
      "content": {
        "goal": "Show your baby pairs of identical objects and introduce the word 'same' with enthusiasm.",
        "whatToSay": [
          "\"Look! Two red balls! Are they the same?\"",
          "\"YES! SAME! Both red balls—SAME!\"",
          "\"Here are two spoons. Same or different?\"",
          "\"SAME! Two spoons—exactly the SAME!\"",
          "\"When things match, they're SAME!\""
        ],
        "whatToDo": [
          "Hold up two identical objects (two red balls) side by side at your baby's eye level.",
          "Say 'same' with big expression and nod your head enthusiastically.",
          "Let your baby hold both objects, one in each hand.",
          "Bring them together: 'Look—same! Same! Match!'",
          "Repeat with 2-3 more identical pairs (two spoons, two blocks, two cups).",
          "Use exaggerated gestures: bring items together, nod vigorously, smile big.",
          "Make 'same' sound positive and exciting—emotional connection helps memory.",
          "After showing 3 pairs, review: 'All the same! These match, these match, these match!'"
        ],
        "examples": [
          "Two red balls: Hold them touching: 'Same! Both red! Both balls! SAME!'",
          "Two spoons: Tap them together: 'Same! Matching spoons! Same!'",
          "Two yellow blocks: Stack them: 'Same blocks! Same color! Same size! SAME!'"
        ],
        "watchFor": [
          "your baby looking back and forth between identical items—visual comparison.",
          "Attempting to bring items together—showing understanding of matching.",
          "Smiling or showing interest when you say 'same'—positive association.",
          "Attempting to say 'same' or nodding head in imitation."
        ],
        "parentTip": "'Same' is abstract—your baby may not fully grasp it yet, but repeated exposure builds the foundation!",
        "funTwist": "Make items 'kiss' when they're the same: 'The balls are the same—they give each other kisses!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Different Discovery",
      "duration": "4-5 min",
      "focus": "Introducing 'Different' as Contrast",
      "researchBasis": "Oakes & Ribar (2005) - Infants develop categorization through detecting both similarities and differences; contrasting examples clarify boundaries.",
      "content": {
        "goal": "Present pairs of different objects and introduce 'different' with contrasting tone from 'same.'",
        "whatToSay": [
          "\"Now look at these. A ball and a block. Same or different?\"",
          "\"DIFFERENT! Not the same—DIFFERENT!\"",
          "\"This is a spoon, this is a cup. Different!\"",
          "\"Different shapes! Different things!\"",
          "\"When things don't match, they're DIFFERENT!\""
        ],
        "whatToDo": [
          "Hold up two clearly different objects (ball and block).",
          "Say 'different' with a contrasting expression—shake head, look surprised.",
          "Separate the objects apart from each other (unlike bringing 'same' items together).",
          "Let your baby hold both, one in each hand.",
          "Point out differences: 'This is round, this is square—different!'",
          "Repeat with 2-3 more different pairs.",
          "Use consistent gestures: pull items apart, shake head, use lower tone for 'different.'",
          "After several examples, bring back a 'same' pair to contrast: 'These are same! These are different!'",
          "Alternate showing same and different pairs to reinforce the contrast."
        ],
        "examples": [
          "Ball and block: Hold apart: 'Different! Ball is round, block is square! Different!'",
          "Spoon and cup: 'Different! Spoon is long, cup is round! Different!'",
          "Red ball and blue block: 'Different colors! Different shapes! Different!'"
        ],
        "watchFor": [
          "your baby looking between different items—noting contrasts.",
          "Different handling of 'different' items than 'same' items.",
          "Facial expressions showing recognition that items don't match.",
          "Attempting to separate items or push them apart (mimicking your gesture)."
        ],
        "parentTip": "Use different voice tones for 'same' (happy, high) vs 'different' (neutral, lower)—auditory cues help differentiate concepts!",
        "funTwist": "Make 'different' items argue playfully: 'Ball says I'm round! Block says I'm square! They're different!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Same or Different Game",
      "duration": "3-4 min",
      "focus": "Active Discrimination Practice",
      "researchBasis": "Booth (2006) - Active judgment tasks (asking children to decide) enhance conceptual learning more than passive observation.",
      "content": {
        "goal": "Present various pairs and ask your baby to help determine if they're same or different.",
        "whatToSay": [
          "\"Let's play a game! I'll show you two things. You help me!\"",
          "\"Two red balls—same or different?\"",
          "\"(wait for response) YES! SAME! Great job!\"",
          "\"Ball and spoon—same or different?\"",
          "\"(wait) DIFFERENT! You're right! Different!\""
        ],
        "whatToDo": [
          "Present one pair of objects at a time.",
          "Ask 'Same or different?' in a questioning tone.",
          "Wait 3-5 seconds for your baby to respond (looking, pointing, vocalizing, or gesturing).",
          "Accept ANY response as engagement—even uncertain looks.",
          "Provide the answer with enthusiasm: 'YES! Same!' or 'Different!'",
          "Model the appropriate gesture: bring together or pull apart.",
          "Encourage your baby to help with the gesture.",
          "Mix up pairs: same, different, same, different in random order.",
          "Repeat 5-6 pairs, celebrating all participation.",
          "Gradually let your baby lead—if they bring items together or apart, affirm their choice."
        ],
        "examples": [
          "Show two spoons → ask 'same or different?' → baby brings them together → 'Yes! SAME!'",
          "Show ball and cup → ask question → baby looks confused → you answer: 'Different! Different things!'",
          "Show two blue blocks → baby nods → 'You think same? YES! Same blue blocks!'"
        ],
        "watchFor": [
          "your baby showing thinking behaviors—pausing, looking carefully, comparing visually.",
          "Attempting gestures—bringing items together or pushing apart.",
          "Correct responses increasing with practice (even 40-50% shows learning).",
          "Confidence growing—responding faster, with less hesitation."
        ],
        "parentTip": "This is HARD cognitively for this age—celebrate all attempts, not just correct answers! The thinking process is the goal.",
        "funTwist": "Let your baby be the teacher: 'You show ME same or different!' and you respond to their choices."
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're learning same and different! Such smart thinking!",
          "You practiced:",
          "✓ Looking at pairs of objects",
          "✓ Learning the word 'same' for matching things",
          "✓ Learning the word 'different' for things that don't match",
          "✓ Helping decide: same or different!"
        ],
        "reviewQuestions": [
          "Did your baby show recognition of matching (same) items?",
          "Could your baby detect obvious differences between objects?",
          "Did your baby attempt gestures (bringing together or separating items)?",
          "How did your baby respond to the question format—engaged, confused, thoughtful?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Same/different is the foundation of all categorization and pattern recognition—skills essential for reading. Recognizing that letters have consistent features (all 'A's are the same despite font changes) while different letters have distinct features ('b' vs 'd') requires this exact cognitive skill your baby is building today."
        },
        "nextSteps": [
          "Point out same and different throughout the day: 'Your socks are the same!' 'Mommy's cup is different from yours!'",
          "Tomorrow we explore animal families—matching baby animals to parent animals!",
          "Play same/different with everyday objects: shoes, toys, food items.",
          "Use same/different during book reading: 'Are these two dogs the same or different?'"
        ]
      }
    }
  ]
},

// ---------- LESSON 32 (Week 7 Day 2) ----------
{
  "id": 32,
  "phase": "1-2",
  "quarter": 1,
  "week": 7,
  "weekIndex": 6,
  "dayInWeek": 1,
  "title": "Lesson 32: \"Animal Families\" — Match Baby to Parent",
  "theme": "Sorting and Matching",
  "skillRecurrenceTags": ["matching", "animal-vocabulary", "relationships"],
  "relatedToPreviousSkills": ["animal-sounds-week1", "same-different", "object-matching"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Animal Families!",
      "duration": "1 min",
      "content": {
        "goal": "Build matching skills by pairing baby animals with their parents, reinforcing same/different concepts through meaningful relationships.",
        "parentGuide": [
          "Matching exercises strengthen visual discrimination and categorical thinking—foundational for letter recognition.",
          "Animal families provide meaningful context for matching—your baby can relate to parent-child relationships.",
          "This lesson spirals back to Week 1's animal sounds while adding new complexity: relationships and matching.",
          "Vocabulary expands beyond object names to relationship words: baby, mama, family, together.",
          "Matching by category (both are cows) rather than identical appearance is more advanced cognitive work."
        ],
        "materials": ["Toy animals in parent-baby pairs (cow-calf, dog-puppy, cat-kitten, duck-duckling)", "Pictures of animal families (optional)", "Basket or sorting area"],
        "tip": "Use toy animals if possible—3D objects are easier to manipulate and match than pictures at this age.",
        "researchNote": "Gelman & Coley (1990) - Children develop conceptual categories through understanding relationships and family resemblance rather than just perceptual similarity."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Animal Families",
      "duration": "3-4 min",
      "focus": "Introduction to Parent-Baby Relationships",
      "researchBasis": "Booth & Waxman (2002) - Relational language ('baby,' 'mama') helps toddlers understand categorical relationships beyond simple perceptual matching.",
      "content": {
        "goal": "Introduce animal families by showing parent-baby pairs together and naming the relationships.",
        "whatToSay": [
          "\"Look! This is Mama Cow and her Baby Calf!\"",
          "\"Mama Cow is BIG. Baby Calf is little!\"",
          "\"They're a family! Mama and baby together!\"",
          "\"Here's Mama Dog and Baby Puppy! A dog family!\"",
          "\"Mama Cat and Baby Kitten! They match—both cats!\""
        ],
        "whatToDo": [
          "Present first pair (mama cow and baby calf) together, touching or close.",
          "Name each animal: 'Mama Cow' and 'Baby Calf.'",
          "Point out relationship: 'Mama and baby! Family!'",
          "Make their sounds together: 'Both say moo! Moo-moo!'",
          "Let your baby hold and explore both animals.",
          "Repeat with 2-3 more animal families.",
          "Emphasize 'together' and 'family'—bringing pairs physically close.",
          "After introducing all families, review: 'Cow family, dog family, cat family—all together!'"
        ],
        "examples": [
          "Cow pair: 'Big Mama Cow—moo! Little Baby Calf—tiny moo! Together!'",
          "Dog pair: 'Mama Dog—woof! Baby Puppy—little woof! Dog family!'",
          "Cat pair: 'Mama Cat—meow! Baby Kitten—soft meow! Cat family!'"
        ],
        "watchFor": [
          "your baby looking between parent and baby animals—noting size differences.",
          "Attempting to bring pairs together—understanding 'family' concept.",
          "Making animal sounds for both parent and baby.",
          "Showing interest in smaller baby animals—they're often more appealing to toddlers."
        ],
        "parentTip": "Connect to your baby's own family: 'Just like you and Mommy! You're baby, Mommy is mama!'",
        "funTwist": "Make baby animals search for mamas: 'Where's your mama, Baby Puppy? Let's find her!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Match the Babies",
      "duration": "4-5 min",
      "focus": "Active Matching Practice",
      "researchBasis": "Mandler (2004) - Categorical understanding develops when children actively sort and match items based on conceptual relationships.",
      "content": {
        "goal": "Separate parent and baby animals, then help your baby match each baby to the correct parent.",
        "whatToSay": [
          "\"Oh no! The babies are lost! Can you help them find their mamas?\"",
          "\"Here's Baby Calf. Where's Mama Cow?\"",
          "\"YES! Baby Calf goes with Mama Cow! Moo-moo!\"",
          "\"Now Baby Puppy needs his mama! Where's Mama Dog?\"",
          "\"You found her! Together again—dog family!\""
        ],
        "whatToDo": [
          "Separate all the parent animals into one group and baby animals into another.",
          "Place parents in a row in front of your baby.",
          "Hold up one baby animal: 'Who's the mama?'",
          "Wait for your baby to point to or pick up the matching parent.",
          "If correct, celebrate enthusiastically: 'Yes! They're both cows! Family!'",
          "If incorrect, gently guide: 'That's Mama Cat. Baby Calf needs Mama COW. Where's the cow?'",
          "Place matched pairs together after each success.",
          "Repeat until all families are reunited.",
          "Make sounds together when pairs are matched: 'Moo-moo! The cow family!'"
        ],
        "examples": [
          "Hold up baby calf → your baby points to mama cow → bring them together: 'Cow family reunited!'",
          "Hold up baby puppy → your baby grabs mama cat → redirect: 'Mama Cat says meow, but puppy says woof! Find the woof mama!'",
          "Baby successfully matches 3 pairs → big celebration: 'All the families are together! You did it!'"
        ],
        "watchFor": [
          "your baby successfully matching pairs more often than randomly guessing.",
          "Using sounds as clues—making moo sound to identify cow pair.",
          "Processing time visible—looking carefully between options before choosing.",
          "Self-correcting—picking up one parent, reconsidering, choosing another."
        ],
        "parentTip": "This is conceptual matching (both are dogs), not identical matching—much harder! Celebrate all attempts.",
        "funTwist": "Make baby animals cry for mamas: 'Wah-wah! Baby Puppy wants his mama!' Add urgency and drama!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Family Sort Game",
      "duration": "3-4 min",
      "focus": "Categorization and Reinforcement",
      "researchBasis": "Waxman & Markow (1995) - Sorting activities with verbal labeling strengthen category knowledge and vocabulary connections.",
      "content": {
        "goal": "Sort all animals into family groups, creating separate areas for each animal family.",
        "whatToSay": [
          "\"Let's put each family in their own home!\"",
          "\"Cow family lives HERE. Mama Cow and Baby Calf together!\"",
          "\"Dog family lives HERE. Can you put the dogs in their home?\"",
          "\"Cat family HERE! Where do the cats go?\"",
          "\"Look—each family is together in their own home!\""
        ],
        "whatToDo": [
          "Create 3-4 distinct areas (corners of blanket, different baskets, or spots on floor).",
          "Label first area: 'This is the cow home!' and place cow family there.",
          "Hand your baby a dog family member: 'Where do dogs live?'",
          "Guide them to place it in a new area: 'HERE is the dog home!'",
          "Continue sorting all animals by family.",
          "Let your baby help decide where each new family lives.",
          "After sorting, visit each family: 'Let's visit the cow family! Moo-moo!'",
          "Make families greet each other: 'The cow family says hi to the dog family!'",
          "End with celebration: 'All families are home and together!'"
        ],
        "examples": [
          "Baby places mama cat with baby kitten in cat area → 'Perfect! Cat family together!'",
          "Baby puts puppy in cow area → redirect playfully: 'Puppy says woof! Cows say moo! Let's find the woof family!'",
          "All families sorted → parade them: 'The cow family walks by! The dog family walks by!'"
        ],
        "watchFor": [
          "your baby placing animals in correct family groups.",
          "Understanding that each family needs its own space—conceptual organization.",
          "Making sounds or gestures showing recognition of which animal goes where.",
          "Showing satisfaction when organization is complete—enjoying order."
        ],
        "parentTip": "This activity builds categorization AND spatial organization—both key pre-math and pre-reading skills!",
        "funTwist": "Make it a bedtime story: 'Now all the families go to sleep in their homes. Goodnight cow family! Goodnight dog family!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You matched all the animal families! What a smart helper!",
          "You practiced:",
          "✓ Learning about mamas and babies",
          "✓ Matching baby animals to parent animals",
          "✓ Sorting families into groups",
          "✓ Using animal sounds to help match!"
        ],
        "reviewQuestions": [
          "Could your baby successfully match most baby-parent pairs?",
          "Did your baby use sounds or other cues to help identify animals?",
          "How did your baby respond to the 'family' concept—connections to own family?",
          "Was your baby able to sort animals by family groups?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Matching by category (all cows belong together even though mama and baby look different) is more sophisticated than matching identical objects. This conceptual matching is exactly the skill needed for reading—understanding that 'A,' 'a,' and different fonts of 'a' all represent the same letter because they share essential features."
        },
        "nextSteps": [
          "Point out parent-child pairs in books: 'Look, mama bird and baby bird—like our toy birds!'",
          "Use family language throughout the day: 'We're a family just like the cow family!'",
          "Tomorrow we explore BIG vs. LITTLE more deeply—size comparisons!",
          "Keep animal families together for pretend play—your baby will practice matching independently."
        ]
      }
    }
  ]
},

// ---------- LESSON 33 (Week 7 Day 3) ----------
{
  "id": 33,
  "phase": "1-2",
  "quarter": 1,
  "week": 7,
  "weekIndex": 6,
  "dayInWeek": 2,
  "title": "Lesson 33: \"Big vs. Little\" — Size Comparison",
  "theme": "Sorting and Matching",
  "skillRecurrenceTags": ["comparison", "size-concepts", "descriptive-language"],
  "relatedToPreviousSkills": ["same-different", "matching", "object-recognition"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Big and Little!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce size comparison through concrete examples, building measurement and comparison vocabulary essential for mathematical and spatial reasoning.",
        "parentGuide": [
          "Size words (big/little) are relational concepts—an object can be big compared to one thing and little compared to another.",
          "Understanding relative size is foundational for measurement, spatial reasoning, and eventually mathematical comparison.",
          "This lesson builds on yesterday's same/different work—now we're comparing a specific attribute (size).",
          "Visual comparison strengthens attention to detail—the same skill needed to distinguish letters like 'c' and 'o' or 'n' and 'h'.",
          "Expect your baby to understand big/little receptively (can point to big vs. little) before using the words expressively."
        ],
        "materials": ["Pairs of same objects in different sizes (big ball and small ball, big block and little block, big spoon and tiny spoon)", "Nesting toys or stacking cups (optional)", "Basket for collection"],
        "tip": "Use extreme size differences at first—a basketball vs. a ping pong ball is clearer than two similar-sized balls.",
        "researchNote": "Ebeling & Gelman (1994) - Children acquire dimensional adjectives like 'big' and 'small' through direct comparison and contrastive labeling."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet Big and Little",
      "duration": "3-4 min",
      "focus": "Introducing Size Vocabulary with Contrast",
      "researchBasis": "Sandhofer & Smith (1999) - Children learn dimensional terms faster when adults provide direct comparisons and use exaggerated gestures.",
      "content": {
        "goal": "Present pairs of objects that differ only in size, explicitly labeling 'big' and 'little' with contrasting tones and gestures.",
        "whatToSay": [
          "\"Look! Two balls! This one is BIG! This one is little.\"",
          "\"BIG ball! LITTLE ball!\"",
          "\"Here's a BIG spoon and a little tiny spoon!\"",
          "\"BIG block—so big! Little block—so tiny!\"",
          "\"Can you find the BIG one? Can you find the little one?\""
        ],
        "whatToDo": [
          "Hold up a big ball in one hand, dramatically high and away from your body.",
          "Say 'BIG' with a loud, expansive voice and wide arms.",
          "Hold up a little ball in your other hand, close to your body.",
          "Say 'little' with a softer, higher voice and bring hands close together.",
          "Let your baby hold both objects, one in each hand, to feel the size difference.",
          "Emphasize the contrast: 'This one is BIG, this one is little!'",
          "Repeat with 2-3 more pairs, always using exaggerated gestures.",
          "Use body language: stretch tall for 'big,' crouch down for 'little.'",
          "After showing several pairs, review: 'All these are big! All these are little!'"
        ],
        "examples": [
          "Big ball vs. small ball: Hold big one overhead: 'BIG!' Hold small one low: 'little!'",
          "Big spoon vs. baby spoon: 'Mama uses the BIG spoon! Baby uses the little spoon!'",
          "Big block vs. small block: Stack big one: 'BIG block at the bottom!' Small one on top: 'little block on top!'"
        ],
        "watchFor": [
          "your baby looking between the two sizes—visual comparison happening.",
          "Attempting to hold both objects—exploring physical size difference through touch.",
          "Responding differently to 'big' vs. 'little'—reaching for the correct one when asked.",
          "Smiling or laughing at exaggerated gestures—emotional engagement helps memory."
        ],
        "parentTip": "Make 'big' and 'little' SOUND different—loud/expansive vs. soft/contracted. Auditory cues reinforce the concept!",
        "funTwist": "Make big items say 'BIG voice'—'I'M BIG!'—and little items say tiny voices: 'I'm so little!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Big and Little Hunt",
      "duration": "4-5 min",
      "focus": "Active Size Identification",
      "researchBasis": "Gelman & Ebeling (1998) - Active search tasks requiring children to identify and categorize by dimensions strengthen conceptual understanding.",
      "content": {
        "goal": "Ask your baby to find and identify big vs. little objects, building receptive understanding and active participation.",
        "whatToSay": [
          "\"Can you find something BIG?\"",
          "\"YES! That's BIG! Great job!\"",
          "\"Now find something little. Where's something little?\"",
          "\"You found it! That's little!\"",
          "\"Let's find the BIG ball. Where's the BIG ball?\""
        ],
        "whatToDo": [
          "Spread out 4-6 objects of varying sizes on the floor or table.",
          "Ask your baby to find 'something big.'",
          "Wait 3-5 seconds for your baby to respond (pointing, grabbing, or looking).",
          "Celebrate any size-appropriate choice enthusiastically: 'Yes! That IS big!'",
          "If your baby chooses incorrectly, gently redirect: 'That one is little! Can you find BIG?'",
          "Alternate requests: 'Find little,' 'Find big,' 'Show me big,' 'Point to little.'",
          "Increase difficulty: 'Find the BIGGEST one!' or 'Find the littlest one!'",
          "Let your baby compare objects side by side to confirm size.",
          "Make it a game: 'I spy with my little eye something BIG and red!'",
          "Repeat 6-8 times with different objects and sizes."
        ],
        "examples": [
          "Baby picks up big block when asked for 'big' → 'YES! Big block! So big!'",
          "Baby grabs little ball when asked for 'little' → 'Perfect! Little ball! Tiny!'",
          "Baby hesitates between two sizes → compare side by side: 'This one big, this one little. Which is big?'"
        ],
        "watchFor": [
          "your baby correctly identifying big vs. little more often than chance (60%+ success shows learning).",
          "Processing time—pausing to think and compare before choosing.",
          "Self-correcting—reaching for one object, reconsidering, choosing another.",
          "Comparing objects directly—holding two items side by side to decide."
        ],
        "parentTip": "If your baby struggles, reduce choices to just TWO objects with dramatic size difference—success builds confidence!",
        "funTwist": "Hide big and little items around the room: 'Big treasure hunt! Find the BIG teddy bear hiding!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Big and Little Stacking",
      "duration": "3-4 min",
      "focus": "Hands-On Size Exploration",
      "researchBasis": "Piaget (1952) - Physical manipulation of size-graded materials allows children to construct understanding of seriation and measurement.",
      "content": {
        "goal": "Use stacking or nesting activities to explore size relationships through physical play—big holds little, big goes on bottom, etc.",
        "whatToSay": [
          "\"Let's build a tower! BIG block on bottom!\"",
          "\"Now little block on top! Big bottom, little top!\"",
          "\"Can you put the little cup INSIDE the big cup?\"",
          "\"It fits! Little goes inside big!\"",
          "\"Let's make a BIG pile and a little pile!\""
        ],
        "whatToDo": [
          "Start with stacking blocks in size order—big on bottom, little on top.",
          "Narrate as you build: 'Big first! Now medium! Now little on top!'",
          "Let your baby try stacking—assist if needed.",
          "Use nesting cups or boxes: demonstrate how little fits inside big.",
          "Hand your baby a small cup: 'Put it in the BIG one!'",
          "Celebrate when little disappears inside big: 'It fits! Hidden inside!'",
          "Create sorting game: 'All big things in this pile, all little things over there!'",
          "Let your baby sort objects by size into two groups.",
          "Build a 'big tower' and a 'little tower' side by side—compare heights.",
          "Knock them down together: 'The BIG tower makes a BIG crash! The little tower makes a little crash!'"
        ],
        "examples": [
          "Baby stacks big block, then small block on top → 'Perfect! Big holds up little!'",
          "Baby nests small cup inside large cup → 'Yes! Little hides in big!'",
          "Baby creates big pile and little pile → 'Look! You sorted by size! So smart!'"
        ],
        "watchFor": [
          "your baby understanding that big items can hold or support little items.",
          "Attempting to stack in size order—showing spatial reasoning.",
          "Enjoying nesting activities—physical satisfaction of fitting things together.",
          "Sorting objects by size independently—showing internalized concept."
        ],
        "parentTip": "Stacking and nesting are inherently rewarding—your baby learns size through natural play consequences!",
        "funTwist": "Make it a story: 'Papa Bear's BIG chair, Mama Bear's medium chair, Baby Bear's LITTLE chair!' Act it out!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned big and little! Such smart comparing!",
          "You practiced:",
          "✓ Seeing the difference between big and little",
          "✓ Finding big and little objects",
          "✓ Stacking and sorting by size",
          "✓ Using size words: BIG and little!"
        ],
        "reviewQuestions": [
          "Could your baby identify big vs. little when asked?",
          "Did your baby show understanding through gestures or choices?",
          "How did your baby respond to stacking/nesting activities—engaged, frustrated, successful?",
          "Did your baby attempt to say 'big' or 'little'?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Size comparison is a foundational measurement concept that extends to reading—understanding that letters have consistent sizes and proportions (lowercase 'b' vs. uppercase 'B'), that spaces are small gaps between words, and that some words are physically longer than others on the page."
        },
        "nextSteps": [
          "Point out big and little throughout the day: 'Big truck! Little car!' 'Daddy's big shoes, your little shoes!'",
          "Tomorrow we explore color sorting—organizing by attributes!",
          "Use size words during dressing: 'Big shirt!' 'Little socks!'",
          "Read books with size themes like 'Big Dog, Little Dog' or 'The Very Hungry Caterpillar' (tiny caterpillar to BIG butterfly)."
        ]
      }
    }
  ]
},

// ---------- LESSON 34 (Week 7 Day 4) ----------
{
  "id": 34,
  "phase": "1-2",
  "quarter": 1,
  "week": 7,
  "weekIndex": 6,
  "dayInWeek": 3,
  "title": "Lesson 34: \"Color Sorting Game\" — Sort by Color",
  "theme": "Sorting and Matching",
  "skillRecurrenceTags": ["color-recognition", "sorting", "categorization"],
  "relatedToPreviousSkills": ["color-naming-week6", "same-different", "matching"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Color Sorting!",
      "duration": "1 min",
      "content": {
        "goal": "Practice categorization by sorting objects into color groups, strengthening visual discrimination and organizational thinking.",
        "parentGuide": [
          "Sorting by color is more abstract than sorting by type (all balls together)—it requires attending to one specific attribute among many.",
          "This selective attention skill is crucial for reading—noticing that 'p,' 'b,' 'd,' and 'q' differ only in the direction of one line.",
          "Color recognition typically develops between 18-24 months, but exposure starts much earlier.",
          "This lesson spirals back to Week 6's color exploration, now adding the complexity of sorting and organizing.",
          "Don't expect perfection—the goal is practice with categorization thinking, not mastery of all colors."
        ],
        "materials": ["Colored objects (balls, blocks, toys, pom-poms) in 3-4 distinct colors (red, blue, yellow, green)", "Sorting containers (bowls, baskets, or plates—one per color)", "Optional: colored paper or mats to mark each sorting area"],
        "tip": "Start with primary colors (red, blue, yellow) and high-contrast choices—avoid pastels or similar shades initially.",
        "researchNote": "Sandhofer & Smith (2001) - Sorting tasks with verbal labeling accelerate children's color vocabulary and categorical perception."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Color Home Introduction",
      "duration": "3-4 min",
      "focus": "Setting Up Sorting Categories",
      "researchBasis": "Waxman & Markow (1995) - Establishing clear category labels before sorting helps children understand the organizing principle.",
      "content": {
        "goal": "Introduce sorting containers as 'color homes' and demonstrate how objects go with their matching color family.",
        "whatToSay": [
          "\"Let's sort by color! Each color gets its own home!\"",
          "\"This is the RED home! All red things go here!\"",
          "\"This is the BLUE home! Blue things live here!\"",
          "\"Watch me! Red ball goes in the RED home!\"",
          "\"Where does the blue ball go? The BLUE home!\""
        ],
        "whatToDo": [
          "Set out 3 bowls or containers in front of your baby.",
          "Place a colored paper or toy in each bowl to mark it: red paper in bowl 1, blue paper in bowl 2, yellow paper in bowl 3.",
          "Point to each bowl and name it: 'Red home! Blue home! Yellow home!'",
          "Hold up a red object: 'This is red. Where does it go?'",
          "Place it dramatically in the red bowl: 'In the RED home!'",
          "Clap and celebrate: 'Red with red! Perfect!'",
          "Repeat demonstration with 2-3 objects, placing each in correct bowl.",
          "After demonstrations, encourage: 'Now YOU try! Where does this blue block go?'",
          "Guide your baby's hand to the correct bowl if needed: 'Blue home! Yes!'"
        ],
        "examples": [
          "Red ball → red bowl: 'Red ball in the red home! Now all the reds can play together!'",
          "Blue block → blue bowl: 'Blue block! It matches the blue paper! Perfect!'",
          "Yellow pom-pom → yellow bowl: 'Yellow goes with yellow! You're sorting!'"
        ],
        "watchFor": [
          "your baby watching the demonstration carefully—tracking where objects go.",
          "Reaching toward the correct bowl—showing understanding of the task.",
          "Looking at the object, then looking at the bowls—active comparison happening.",
          "Attempting to place objects in bowls—motor engagement showing comprehension."
        ],
        "parentTip": "Keep the color markers visible in each bowl throughout sorting—visual cues help your baby remember which home is which!",
        "funTwist": "Make color homes talk: 'Red home says: Send me all the red things! I'm lonely without them!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sort Together",
      "duration": "4-5 min",
      "focus": "Guided Sorting Practice",
      "researchBasis": "Gopnik & Meltzoff (1987) - Joint attention during sorting tasks enhances category learning; shared focus guides correct categorization.",
      "content": {
        "goal": "Work together to sort a collection of colored objects, providing support and feedback as your baby practices.",
        "whatToSay": [
          "\"Let's sort all these toys! You pick one!\"",
          "\"What color is it? Red!\"",
          "\"Where does red go? In the red home!\"",
          "\"You did it! Red with red!\"",
          "\"Uh-oh, this one is tricky. Is it red or blue?\""
        ],
        "whatToDo": [
          "Place 9-12 colored objects in a pile between you and your baby.",
          "Pick up one object: 'You pick a toy!'",
          "Wait for your baby to choose and hold an object.",
          "Name the color enthusiastically: 'That's BLUE! You picked blue!'",
          "Ask: 'Where does blue go?' and point to the options.",
          "If your baby places it correctly, celebrate: 'YES! Blue home! Perfect match!'",
          "If your baby places it incorrectly, gently redirect: 'Hmm, that's the yellow home. This is blue—let's find the blue home!'",
          "Physically guide if needed, but let your baby do the placing action.",
          "Alternate turns: 'My turn! I'll pick a red one!'",
          "Continue until all objects are sorted, maintaining energy and encouragement.",
          "If your baby loses interest, reduce the pile to 4-6 objects."
        ],
        "examples": [
          "Baby picks red ball → successfully places in red bowl → 'Amazing! You sorted red!'",
          "Baby picks blue block → puts it in yellow bowl → redirect: 'That's yellow home. Blue needs blue home!' → guide to correct bowl.",
          "Baby independently sorts 3 items correctly in a row → big celebration: 'You're a sorting expert!'"
        ],
        "watchFor": [
          "your baby successfully placing objects more often than chance (33% with 3 colors).",
          "Hesitation before choosing a bowl—thinking and comparing is good!",
          "Self-correcting—starting to put object in wrong bowl, then switching.",
          "Speed increasing with practice—building confidence and automaticity."
        ],
        "parentTip": "If your baby consistently chooses one bowl (e.g., always picks blue home), that's fine—they're learning that 'blue' is consistent!",
        "funTwist": "Count as you sort: 'One red, two reds, three reds in the red home! How many blues can we find?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Color Family Celebration",
      "duration": "3-4 min",
      "focus": "Review and Reinforcement",
      "researchBasis": "Booth & Waxman (2002) - Reviewing and labeling sorted categories strengthens conceptual understanding and memory consolidation.",
      "content": {
        "goal": "Review each sorted color group, celebrate the organized result, and reinforce color vocabulary.",
        "whatToSay": [
          "\"Look at all your hard work! Everything is sorted!\"",
          "\"Let's visit the red family! One, two, three red things!\"",
          "\"Now the blue family! All the blues together!\"",
          "\"Yellow family! They all match!\"",
          "\"You sorted by COLOR! So smart!\""
        ],
        "whatToDo": [
          "Point to the red bowl: 'Let's see the red family!'",
          "Pick up each red object one at a time: 'Red ball! Red block! Red car!'",
          "Count how many reds: 'One, two, three reds! Three red things!'",
          "Put them back in the red bowl together.",
          "Repeat with each color family.",
          "Make the colors talk to each other: 'The reds say hi to the blues!'",
          "Mix all objects back together: 'Uh-oh! All mixed up again!'",
          "If your baby is engaged, invite a quick re-sort: 'Can you put one red back?'",
          "End with big celebration: 'You sorted all the colors! Amazing!'",
          "Let your baby dump and remix the bowls for sensory fun."
        ],
        "examples": [
          "Red bowl has 4 items → count them: 'One, two, three, four reds! So many!'",
          "Blue bowl has 2 items → 'Two blues! They're friends!'",
          "Baby wants to re-sort immediately → 'Yes! Let's sort again! You love sorting!'"
        ],
        "watchFor": [
          "your baby showing pride in the organized result—smiling, clapping, looking pleased.",
          "Attempting to name colors when reviewing—'boo' for blue, 'wed' for red.",
          "Wanting to sort again—intrinsic motivation shows enjoyment.",
          "Playing with sorted groups—lining them up, stacking, exploring the organized set."
        ],
        "parentTip": "Sorting is often so satisfying that babies want to do it repeatedly—this is excellent learning through joyful repetition!",
        "funTwist": "Make it a parade: March each color family across the table: 'Here comes the red parade! Here comes the blue parade!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You sorted by color! What amazing organizing!",
          "You practiced:",
          "✓ Recognizing different colors",
          "✓ Sorting objects into color groups",
          "✓ Putting things where they belong",
          "✓ Thinking about how things are the same!"
        ],
        "reviewQuestions": [
          "Could your baby successfully place objects in the correct color bowls?",
          "Did your baby show understanding of the sorting concept (items go with matching items)?",
          "Which color(s) seemed easiest for your baby to recognize?",
          "Did your baby enjoy the organizing process—want to repeat it?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Sorting by attributes like color strengthens categorical thinking and selective attention—the exact skills needed to distinguish letters that share many features but differ in one critical way (like 'E' and 'F,' or 'm' and 'n')."
        },
        "nextSteps": [
          "Sort everyday items by color: 'Let's put all blue toys in this basket!'",
          "Tomorrow we explore matching by SOUND—a new sorting dimension!",
          "During meals, sort foods by color: 'Carrots are orange, broccoli is green!'",
          "Read color-themed books like 'Brown Bear, Brown Bear' and point out color sorting in illustrations."
        ]
      }
    }
  ]
},

// ---------- LESSON 35 (Week 7 Day 5) ----------
{
  "id": 35,
  "phase": "1-2",
  "quarter": 1,
  "week": 7,
  "weekIndex": 6,
  "dayInWeek": 4,
  "title": "Lesson 35: \"Match by Sound\" — Shake and Find",
  "theme": "Sorting and Matching",
  "skillRecurrenceTags": ["auditory-discrimination", "matching", "sound-awareness"],
  "relatedToPreviousSkills": ["sound-exploration-week2", "same-different", "sorting"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sound Matching!",
      "duration": "1 min",
      "content": {
        "goal": "Learn to match objects by the sounds they make instead of how they look. This builds listening skills and helps your baby understand that things can be sorted in different ways.",
        "parentGuide": [
          "Up until now, your baby has been sorting things they can SEE (colors, sizes, types of objects). Today we're sorting by what we HEAR—a totally different skill!",
          "This is harder than visual sorting because your baby has to remember sounds and compare them in their mind.",
          "Being able to match sounds is super important for reading—it's the same skill they'll use to match letter sounds to words.",
          "Don't worry if your baby doesn't 'get it' right away. Just shaking and listening to different sounds is valuable practice.",
          "This week wraps up sorting and matching with the most challenging type: sorting by sound alone!"
        ],
        "materials": ["6-8 small containers with lids (plastic bottles, film canisters, small jars)", "Fillings that make different sounds: rice, dried beans, bells, sand, water, coins", "Make 2 containers of each sound (2 with rice, 2 with beans, 2 with bells)"],
        "tip": "Use containers your baby can't see through. The whole point is matching by sound, not by sight!",
        "researchNote": "Jusczyk & Aslin (1995) - Babies who practice listening to and matching sounds develop stronger language skills because they're training their brain to hear small differences."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Listen to Each Sound",
      "duration": "3-4 min",
      "focus": "Learning What Each Shaker Sounds Like",
      "researchBasis": "Fernald & Kuhl (1987) - Children learn to tell sounds apart by hearing clear examples of each one and focusing on the differences.",
      "content": {
        "goal": "Shake each type of container and help your baby learn what rice sounds like, what beans sound like, and what bells sound like.",
        "whatToSay": [
          "\"Let's shake this one! Listen—shake, shake, shake!\"",
          "\"What does it sound like? Sssshhh—that's RICE!\"",
          "\"Rice sounds soft and quiet—shhhhh!\"",
          "\"Now this one! Shake it! Clink-clink—those are BELLS!\"",
          "\"Bells sound loud and jingly!\""
        ],
        "whatToDo": [
          "Pick up one rice shaker and shake it near your ear.",
          "Make a focused, listening face—show your baby you're really paying attention to the sound.",
          "Say what's inside: 'This is rice! Hear that soft sound?'",
          "Hand it to your baby and let them shake it several times.",
          "Pick up the second rice shaker: 'Here's another one. Let's see what IT sounds like!'",
          "Shake it and say: 'Rice again! The same sound—soft and quiet!'",
          "Put both rice shakers side by side: 'Both rice! They match—same sound!'",
          "Repeat this process with beans (louder rattling) and bells (jingling).",
          "Keep describing the sounds: 'Beans are loud and bumpy! Bells are ringy and jingly!'"
        ],
        "examples": [
          "Rice shakers: Shake both, hold them up together: 'Both sound like shhhhh! Rice twins!'",
          "Bell shakers: 'Ring-ring! Jingle-jingle! Both bells—same sound!'",
          "Bean shakers: 'Rattle-rattle! Loud beans! These two match!'"
        ],
        "watchFor": [
          "your baby shaking the containers—they're actively making the sounds.",
          "Your baby holding the shaker near their ear—they're trying to listen closely.",
          "Your baby shaking two containers back and forth—they're comparing the sounds.",
          "Your baby showing excitement when they hear sounds they like—bells often get big reactions!"
        ],
        "parentTip": "Say the sound out loud (shhh, rattle, jingle) while shaking. This helps your baby connect the sound to words!",
        "funTwist": "Make silly faces for each sound: sleepy face for quiet rice, surprised face for loud beans, happy face for jingly bells!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Find the Matching Sound",
      "duration": "4-5 min",
      "focus": "Playing the Matching Game",
      "researchBasis": "Saffran et al. (1996) - When babies practice matching sounds, they build the brain connections they need to learn language and reading.",
      "content": {
        "goal": "Shake one container and help your baby find the other container that makes the same sound.",
        "whatToSay": [
          "\"I'm going to shake this one! Listen carefully!\"",
          "\"Shhhhh—soft rice sound! Can you find another one that sounds like this?\"",
          "\"Shake them and listen! Do they sound the same?\"",
          "\"YES! Both rice! Same sound—they match!\"",
          "\"Let's try another one!\""
        ],
        "whatToDo": [
          "Line up 4-6 shakers in front of your baby (include some matches and some that don't match).",
          "Pick up one rice shaker and shake it: 'Listen to THIS one!'",
          "Put it down in front of your baby.",
          "Point to the other shakers: 'Which one sounds like this? Let's find out!'",
          "Hand your baby a different shaker to try.",
          "If it's wrong: 'Hmm, that one sounds different! That's bells—jingle-jingle! We need rice—shhhh!'",
          "If it's right: 'YES! That's the match! Both sound the same—both rice!'",
          "Place matching pairs together.",
          "Make the game easier by giving only 2-3 choices at first.",
          "Repeat until all pairs are matched, or your baby loses interest (that's okay!)."
        ],
        "examples": [
          "You shake rice, baby picks up beans → 'Ooh, that's different! Beans are loud—rattle! Rice is quiet—shhh! Try again!'",
          "You shake bells, baby picks up bells → 'PERFECT! Both jingly! Ring-ring! Match!'",
          "Baby shakes both back and forth to compare → 'Good thinking! You're listening to both!'"
        ],
        "watchFor": [
          "your baby shaking containers before choosing—they're testing to see if sounds match.",
          "Your baby getting matches right more than half the time—that shows they're learning!",
          "Your baby bringing two shakers together when they match—copying what you did earlier.",
          "Your baby taking time to think—pausing and listening is GOOD, even if they pick wrong!"
        ],
        "parentTip": "It's totally fine to help! This is HARD. You can shake both containers together and say 'Listen—are they the same?'",
        "funTwist": "Pretend the shakers are calling to each other: 'Rice shaker is lonely! He's calling for his friend! Shhhhh! Where are you?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Sound Family Groups",
      "duration": "3-4 min",
      "focus": "Sorting by Sound Type",
      "researchBasis": "Booth (2006) - When children actively sort things into groups, they understand categories better than just watching someone else do it.",
      "content": {
        "goal": "Put all the containers into sound families—all the rice shakers together, all the bean shakers together, all the bell shakers together.",
        "whatToSay": [
          "\"Let's make sound families! All the same sounds go together!\"",
          "\"This is the rice family—shhhhh! All the quiet ones!\"",
          "\"This is the bell family—ring-ring! All the jingly ones!\"",
          "\"Which family does this one belong to? Let's shake it and find out!\"",
          "\"You sorted by SOUND! That's amazing!\""
        ],
        "whatToDo": [
          "Create three spots on the floor or table—one for each type of sound.",
          "Put one rice shaker in the first spot: 'This is the rice family spot!'",
          "Put one bell shaker in the second spot: 'This is the bell family spot!'",
          "Put one bean shaker in the third spot: 'This is the bean family spot!'",
          "Pick up a shaker and hand it to your baby: 'Shake it! What does it sound like?'",
          "Help your baby decide: 'Is it quiet like rice? Or jingly like bells? Or loud like beans?'",
          "When they place it, celebrate: 'Yes! That's bells—it goes with the other bells!'",
          "Continue until all shakers are sorted by sound.",
          "After sorting, visit each family: 'Let's shake the whole rice family together! Shhhhh!'",
          "Mix them all up and see if your baby wants to sort again!"
        ],
        "examples": [
          "Baby shakes and correctly places rice shaker with rice family → 'Perfect! All the rice together!'",
          "Baby puts bell shaker in bean spot → gently help: 'Listen—this goes jingle! Beans go rattle! Let's find the jingle family!'",
          "All sorted → shake each family as a group: 'The rice family! The bean family! The bell family!'"
        ],
        "watchFor": [
          "your baby shaking containers to test before placing them—using sound as their guide.",
          "Your baby understanding that containers go with their 'sound family'—showing they get the sorting concept.",
          "Your baby working more independently as they practice—needing less help.",
          "Your baby excited about getting it right—celebrating their own success!"
        ],
        "parentTip": "If your baby just wants to shake everything and not sort, that's FINE! Making and listening to sounds is the real goal here.",
        "funTwist": "Have a sound family concert: 'Now the rice family performs! Shhhhh! Now the bean family! Rattle-rattle! Now everyone together!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You matched sounds without even seeing inside! What smart listening!",
          "You practiced:",
          "✓ Listening carefully to different sounds",
          "✓ Finding containers that sound the same",
          "✓ Sorting things by what you HEAR, not what you SEE",
          "✓ Remembering what each sound is!"
        ],
        "reviewQuestions": [
          "Could your baby tell the difference between the sounds?",
          "Did your baby successfully find matching pairs sometimes?",
          "Did your baby shake containers to test them before deciding?",
          "Which sound did your baby seem to like best?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Matching by sound instead of sight builds listening skills that are critical for reading. Later, your child will need to match the sound 'buh' to the letter 'B,' or hear that 'cat' and 'hat' have matching ending sounds—exactly like matching these shakers!"
        },
        "nextSteps": [
          "Keep the sound shakers for play time—your baby will practice matching on their own!",
          "Next week we explore sounds in your everyday environment and routines.",
          "Play 'guess the sound' in the kitchen: close your eyes while someone stirs, pours, or taps something.",
          "This wraps up Week 7—you've learned same/different, big/little, animal families, color sorting, and sound matching!"
        ]
      }
    }
  ]
},

// ---------- LESSON 36 (Week 8 Day 1) ----------
{
  "id": 36,
  "phase": "1-2",
  "quarter": 1,
  "week": 8,
  "weekIndex": 7,
  "dayInWeek": 0,
  "title": "Lesson 36: \"The Car Goes Vroom!\" — Environmental Sounds",
  "theme": "Sound Revisit in Daily Routines",
  "skillRecurrenceTags": ["environmental-sounds", "sound-imitation", "vocabulary-building"],
  "relatedToPreviousSkills": ["animal-sounds-week1", "sounds-around-us-week2", "sound-awareness"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Environmental Sounds!",
      "duration": "1 min",
      "content": {
        "goal": "Notice and copy the everyday sounds around us—like cars, doors, water, and vacuums. This helps your baby connect sounds to real life and builds vocabulary.",
        "parentGuide": [
          "We're bringing back sound exploration from Week 2, but now your baby has more skills and can understand the connection between sounds and what makes them.",
          "Environmental sounds (vroom, beep, whoosh) are often your baby's first words because they're fun to say and happen all the time.",
          "Connecting sounds to actions (car goes vroom, door goes slam) helps your baby learn that words describe what things DO, not just what things ARE.",
          "This week focuses on putting sound skills into your daily routine—you'll find sound learning opportunities everywhere!",
          "Sound imitation builds the mouth muscles and coordination needed for clear speech later."
        ],
        "materials": ["Toy vehicles (car, truck, airplane, train)", "Optional: pictures of vehicles or books with transportation", "Access to real environmental sounds if possible (outdoors, windows, cars in driveway)"],
        "tip": "Make the sounds BIG and DRAMATIC! Exaggerate the sounds to make them easier to copy and more memorable.",
        "researchNote": "Kuhl (2004) - Children learn words faster when they're connected to actions and sounds they can see and hear in real life, not just from pictures."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Vehicle Sounds",
      "duration": "3-4 min",
      "focus": "Learning Transportation Sounds",
      "researchBasis": "Nelson (1973) - Action-based words (like 'vroom' and 'beep') are often learned before object names because they're more interesting and easier to connect to experiences.",
      "content": {
        "goal": "Show your baby different toy vehicles and teach them the sound each one makes while moving it around.",
        "whatToSay": [
          "\"Look! Here comes the CAR! The car goes VROOM! Vroom-vroom!\"",
          "\"The TRUCK is here! Beep-beep! Honk-honk!\"",
          "\"Here's the AIRPLANE! Whoooosh—it flies! Whoooosh!\"",
          "\"Choo-choo! The TRAIN is coming! Choo-choo-choo!\"",
          "\"Can you say vroom? VROOM!\""
        ],
        "whatToDo": [
          "Hold up a toy car at your baby's eye level.",
          "Make it move toward your baby while saying: 'Vroom! Vroom! The car goes vroom!'",
          "Move the car fast and slow—match the sound to the speed: 'Fast vroom! Slow vroom!'",
          "Hand the car to your baby and encourage them to move it: 'Your turn! Make it go vroom!'",
          "Pick up a truck next: 'Beep-beep! The truck is backing up! Beep-beep!'",
          "Show an airplane (or use your hand): 'Whoosh! Flying high! Whoooosh!'",
          "Use a train: 'Choo-choo! Chug-chug-chug! All aboard!'",
          "After introducing each vehicle, go back through them again quickly: 'Car? VROOM! Truck? BEEP! Airplane? WHOOSH!'",
          "Encourage your baby to make the sounds with you."
        ],
        "examples": [
          "Car: Roll it on the floor saying 'vroom-vroom' and making engine sounds with your lips.",
          "Truck: Back it up with 'beep-beep-beep' warning sounds.",
          "Airplane: Make it soar through the air with 'whoooosh' and dive down with 'neeeeoow!'",
          "Train: Move it in a line and chug: 'Choo-choo! Chug-chug-chug!'"
        ],
        "watchFor": [
          "your baby watching the vehicles move—tracking them with their eyes.",
          "Your baby reaching for vehicles and moving them around—copying your actions.",
          "Your baby making ANY sounds while playing with vehicles—even if they're not the 'right' sound yet!",
          "Your baby's mouth moving like they're trying to say the sounds—silent practice counts!"
        ],
        "parentTip": "Don't worry if your baby just wants to play with the toys instead of copying sounds—playing IS learning at this age!",
        "funTwist": "Make the vehicles talk to each other: 'Car says vroom! Truck says beep! They're saying hello!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Real Sound Hunt",
      "duration": "4-5 min",
      "focus": "Connecting Sounds to Real Life",
      "researchBasis": "Tomasello & Farrar (1986) - Children learn words faster when adults label things the child is already paying attention to in their environment.",
      "content": {
        "goal": "Take your baby to a window, outside, or around the house to listen for and identify real sounds happening around you.",
        "whatToSay": [
          "\"Let's listen! Do you hear that?\"",
          "\"A CAR! Listen—vroom! The car goes vroom!\"",
          "\"The door went SLAM! Slam!\"",
          "\"Water! Hear it? Splash-splash!\"",
          "\"What's that sound? Let's find out!\""
        ],
        "whatToDo": [
          "Go to a window where you might hear cars, birds, or outdoor sounds.",
          "When you hear a sound, stop and point: 'Wait! Listen! Do you hear that?'",
          "Identify the sound: 'That's a car! Vroom-vroom!'",
          "If possible, let your baby see what made the sound: 'Look—there's the car!'",
          "Copy the sound together: 'Let's say it! Vroom-vroom!'",
          "Move around your home listening for sounds: door opening/closing, water running, phone ringing, dog barking.",
          "Each time you hear something, stop and name it with a sound: 'The door goes slam! Water goes splash!'",
          "Encourage your baby to point when they hear something: 'You heard it! What was that?'",
          "Make the sound with your baby even if they don't know the answer yet.",
          "Try to find 4-5 different sounds during this activity."
        ],
        "examples": [
          "Hear a car outside → point to window: 'Car! Vroom!' → look together: 'See the car?'",
          "Dog barks → 'Woof-woof! The dog is talking!' → baby might point or look around.",
          "Sink running → 'Splash! Water sound!' → turn it on and off: 'Splash-splash!'",
          "Door closes → 'Slam! The door closed!' → open and close a door: 'Slam!'"
        ],
        "watchFor": [
          "your baby turning their head toward sounds—showing they can locate where sounds come from.",
          "Your baby pointing or looking excited when they hear familiar sounds.",
          "Your baby trying to copy any of the sounds you're making together.",
          "Your baby starting to anticipate—getting excited before a predictable sound happens (like when you're about to slam a door)."
        ],
        "parentTip": "Pause after saying 'Listen!'—give your baby quiet time to actually hear the sound before you label it.",
        "funTwist": "Play 'Sound Detective': 'I hear a mystery sound! Can we find it? Let's hunt for it!' Make it an adventure!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Sound and Motion Game",
      "duration": "3-4 min",
      "focus": "Connecting Sounds to Body Movements",
      "researchBasis": "Iverson (2010) - Combining sounds with physical movements helps children remember words better because they're using more parts of their brain at once.",
      "content": {
        "goal": "Move your body to match vehicle sounds—zoom like a car, fly like a plane, chug like a train. This makes sound learning active and memorable.",
        "whatToSay": [
          "\"Let's BE a car! Ready? VROOM!\"",
          "\"Run fast—vroom-vroom-vroom! You're a speedy car!\"",
          "\"Now let's fly like an airplane! Whoooosh!\"",
          "\"Arms out! Fly around! Whoooosh!\"",
          "\"Choo-choo! Let's be a train! Chug-chug-chug!\""
        ],
        "whatToDo": [
          "Stand up with your baby (or hold them if they're not walking yet).",
          "Say: 'We're going to BE a car! Hold my hands!'",
          "Start moving around the room saying 'Vroom! Vroom! Vroom!'",
          "Go fast and slow to match the sound: 'Fast vroom! Slow vroom!'",
          "Stop suddenly: 'Red light! Stop! Now green light—vroom!'",
          "Next, spread your arms out like wings: 'Now we're airplanes! Whoosh!'",
          "Run or walk with arms out, making whooshing sounds.",
          "Try being a train: Hold your baby's hands and chug around in a circle: 'Choo-choo!'",
          "Move your arms like train wheels: 'Chug-chug-chug!'",
          "End with everyone lying down: 'The vehicles are sleeping! Shhhhh!'"
        ],
        "examples": [
          "Car: Run around the room holding hands, saying 'vroom!' and stopping at 'red lights' (chairs or doorways).",
          "Airplane: Baby on your hip with arms out, you spin slowly saying 'whoooosh—flying high!'",
          "Train: Make a line (with siblings or stuffed animals) and chug in a chain: 'Choo-choo!'",
          "Truck backing up: Walk backwards together: 'Beep-beep-beep!'"
        ],
        "watchFor": [
          "your baby laughing and enjoying the movement—joy helps learning stick!",
          "Your baby trying to make the sounds while moving—combining sound and action.",
          "Your baby anticipating what comes next—like getting ready to run when you say 'vroom.'",
          "Your baby wanting to do it again and again—repetition is how babies learn best!"
        ],
        "parentTip": "If your baby isn't walking yet, you can do this sitting down! Be a car with your hands, fly stuffed animals, or just rock and make sounds!",
        "funTwist": "Play 'Red Light, Green Light' with sounds: Green light = 'VROOM go!' Red light = 'Stop—shhh!' Yellow light = 'Slow vroom!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned so many sounds from the world around you!",
          "You practiced:",
          "✓ Making vehicle sounds: vroom, beep, whoosh, choo-choo",
          "✓ Listening to real sounds in your house and outside",
          "✓ Moving your body while making sounds",
          "✓ Connecting sounds to what makes them!"
        ],
        "reviewQuestions": [
          "Did your baby try to copy any of the vehicle sounds?",
          "Could your baby hear and notice real sounds in your environment?",
          "Did your baby enjoy the movement activities?",
          "Which sound did your baby seem most interested in?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Learning that sounds represent real things (vroom = car moving) is how your baby starts understanding that sounds have meaning—the foundation of language and eventually reading."
        },
        "nextSteps": [
          "Point out environmental sounds throughout your day: 'Hear that? Vroom! Car outside!'",
          "Tomorrow we'll explore sounds during laundry time—finding rhythm in chores!",
          "When reading books, add sound effects: 'The bus goes beep-beep!'",
          "Play with vehicles at bath time or in a cardboard box 'garage'—and always add the sounds!"
        ]
      }
    }
  ]
},

// ---------- LESSON 37 (Week 8 Day 2) ----------
{
  "id": 37,
  "phase": "1-2",
  "quarter": 1,
  "week": 8,
  "weekIndex": 7,
  "dayInWeek": 1,
  "title": "Lesson 37: \"Laundry Song\" — Rhythm in Routine",
  "theme": "Sound Revisit in Daily Routines",
  "skillRecurrenceTags": ["rhythm", "routine-integration", "vocabulary-building"],
  "relatedToPreviousSkills": ["clap-the-beat-week1", "rhythm-awareness", "descriptive-language"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Laundry Song!",
      "duration": "1 min",
      "content": {
        "goal": "Turn laundry time into a learning activity by adding songs, rhythm, and sound play. This shows your baby that learning happens everywhere, not just during 'lesson time.'",
        "parentGuide": [
          "Connecting learning to daily routines helps your baby understand that words and sounds are part of real life, not just special activities.",
          "Singing during chores makes boring tasks fun for your baby AND makes the time more educational.",
          "Repetitive routines (like laundry every week) give your baby chances to hear the same words over and over, which is how they learn vocabulary.",
          "Adding rhythm to everyday tasks builds your baby's sense of timing and pattern—important for reading fluency later.",
          "This lesson shows you how to make ANY routine into a learning opportunity!"
        ],
        "materials": ["Clean laundry to fold (clothes, towels, socks)", "Laundry basket", "Optional: washing machine or dryer to watch/listen to"],
        "tip": "You don't need to do a whole load of laundry for this lesson—even 5-6 items are enough to practice with!",
        "researchNote": "Hoff (2003) - Children who hear more words during everyday routines (not just during book reading) develop vocabulary faster and have stronger language skills."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: The Washing Machine Song",
      "duration": "3-4 min",
      "focus": "Learning Laundry Sounds and Actions",
      "researchBasis": "Trainor & Corrigall (2010) - Singing simple, repetitive songs helps babies remember words and sequences because music makes patterns easier for the brain to store.",
      "content": {
        "goal": "Sing a simple song about washing clothes while doing the motions. This teaches action words and builds rhythm skills.",
        "whatToSay": [
          "\"Let's sing about the washing machine!\"",
          "\"This is the way we wash our clothes, wash our clothes, wash our clothes!\"",
          "\"This is the way we wash our clothes, early in the morning!\"",
          "\"Swish-swish-swish! The water goes swish!\"",
          "\"Spin-spin-spin! The machine goes spin!\""
        ],
        "whatToDo": [
          "Sit with your baby near the laundry basket or washing machine.",
          "Start singing to the tune of 'Here We Go Round the Mulberry Bush': 'This is the way we wash our clothes, wash our clothes, wash our clothes!'",
          "Do washing motions with your hands—rub them together like you're scrubbing.",
          "Encourage your baby to copy the rubbing motion.",
          "Add verses for different laundry actions: 'This is the way we rinse our clothes' (make splashing motions).",
          "Sing: 'This is the way we spin our clothes' (spin your hands in circles).",
          "If you have a washing machine going, listen to it: 'Hear that? Swish-swish-swish!'",
          "Point to the machine and make the sound together: 'The water goes swish-swish!'",
          "End with: 'This is the way we dry our clothes' (shake your hands)."
        ],
        "examples": [
          "Wash verse: Rub hands together: 'Scrub-scrub-scrub! Wash, wash, wash!'",
          "Rinse verse: Make splashing motions: 'Splash-splash! Rinse the soap away!'",
          "Spin verse: Spin hands or body around: 'Spin-spin-spin! Round and round!'",
          "Dry verse: Shake clothes in the air: 'Shake-shake! Getting dry!'"
        ],
        "watchFor": [
          "your baby copying any of the hand motions—rubbing, splashing, spinning, shaking.",
          "Your baby bouncing or swaying to the rhythm of the song.",
          "Your baby making any sounds along with you—even humming counts!",
          "Your baby looking at the washing machine when you point to it—connecting the song to the real machine."
        ],
        "parentTip": "Keep the tune simple and repeat it several times—babies learn through lots of repetition, not variety!",
        "funTwist": "Make stuffed animals 'do the laundry' by moving their arms: 'Teddy Bear washes his shirt! Scrub-scrub-scrub!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sock Matching Rhythm",
      "duration": "4-5 min",
      "focus": "Combining Matching Skills with Rhythm",
      "researchBasis": "Degé & Schwarzer (2011) - Combining music and movement with learning tasks helps children remember information better and stay engaged longer.",
      "content": {
        "goal": "Match pairs of socks while singing a rhythm pattern. This brings together last week's matching skills with this week's rhythm focus.",
        "whatToSay": [
          "\"Let's find the socks! Where are the socks?\"",
          "\"I found one sock! Clap-clap! Now find its friend!\"",
          "\"Match-match-match! Two socks together!\"",
          "\"Sock pair! Clap-clap! Good job!\"",
          "\"How many sock pairs can we make?\""
        ],
        "whatToDo": [
          "Dump clean socks on the floor in front of your baby.",
          "Pick up one sock: 'Here's a sock! Let's find its match!'",
          "Clap twice: 'Clap-clap—we're looking for a match!'",
          "Look through the pile: 'Is this one the same? Let's check!'",
          "When you find the match: 'We found it! Match-match!' (clap twice).",
          "Put the pair together and clap: 'Sock pair—clap-clap!'",
          "Hand your baby a sock: 'Your turn! Find this sock's friend!'",
          "Help your baby search through the pile.",
          "When they find it (or you help them find it): 'Match! Let's clap! Clap-clap!'",
          "Keep the rhythm going throughout: 'Match-match-clap-clap! Match-match-clap-clap!'",
          "Stack matched pairs in a pile, counting: 'One pair! Two pairs! Three pairs!'"
        ],
        "examples": [
          "Baby picks up a blue sock → you find another blue sock → put them together → clap twice: 'Match! Clap-clap!'",
          "Baby holds two socks that don't match → 'Hmm, not quite! This one has stripes, this one is plain. Let's find the striped friend!'",
          "Every time a pair is complete → sing: 'We found a pair, we found a pair, clap-clap-clap!'"
        ],
        "watchFor": [
          "your baby trying to match socks by looking at colors or patterns—using last week's skills!",
          "Your baby clapping along with the rhythm—even if the timing isn't perfect.",
          "Your baby handing you socks to help match—understanding the game.",
          "Your baby enjoying the repetitive rhythm—relaxing into the predictable pattern."
        ],
        "parentTip": "Don't stress about perfect matches! If your baby puts two random socks together, celebrate it anyway—they're practicing the concept of pairing!",
        "funTwist": "Make the socks dance before matching: 'This sock is looking for his friend! Dance-dance-dance! Oh, there you are!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Folding Rhythm Station",
      "duration": "3-4 min",
      "focus": "Action Words with Beat Patterns",
      "researchBasis": "Longhi (2009) - Rhythmic activities during everyday routines strengthen parent-child connection and help babies predict what comes next in sequences.",
      "content": {
        "goal": "Fold laundry together while chanting action words in rhythm. This teaches verbs (action words) and creates a beat pattern your baby can follow.",
        "whatToSay": [
          "\"Fold-fold-fold the towel!\"",
          "\"Shake-shake-shake the shirt!\"",
          "\"Roll-roll-roll the socks!\"",
          "\"Stack-stack-stack them up!\"",
          "\"All done with the laundry!\""
        ],
        "whatToDo": [
          "Pick up a towel and say: 'Let's fold it! Fold-fold-fold!' while folding it in rhythm (one fold per 'fold').",
          "Let your baby touch the towel or help push it down.",
          "Pick up a shirt and shake it out: 'Shake-shake-shake!' (shake on each word).",
          "Hand it to your baby to shake—encourage three shakes.",
          "Take a pair of socks: 'Roll-roll-roll!' and roll them into a ball, one roll per word.",
          "Let your baby try rolling—help their hands make the rolling motion.",
          "Stack folded items: 'Stack-stack-stack! One, two, three!' (put one down on each count).",
          "Create a rhythm pattern for the whole process: 'Fold-fold! Shake-shake! Roll-roll! Stack-stack!'",
          "Repeat the pattern with different items so your baby hears the same words several times.",
          "End by putting everything in the basket with a final chant: 'In the basket—one, two, three!'"
        ],
        "examples": [
          "Towel: Hold both ends, bring them together in three beats: 'Fold-fold-fold!'",
          "Shirt: Hold shoulders, shake three times: 'Shake-shake-shake!'",
          "Socks: Roll them together in three motions: 'Roll-roll-roll!'",
          "Stack: Put three items in a pile, one at a time: 'Stack-stack-stack!'"
        ],
        "watchFor": [
          "your baby moving in rhythm with your words—bouncing, swaying, or waving arms.",
          "Your baby trying to do the actions—even if they can't actually fold, they might push or pat the clothes.",
          "Your baby anticipating what comes next—getting excited before 'stack' because they know it's coming.",
          "Your baby vocalizing along with the rhythm—saying parts of words or making sounds on the beat."
        ],
        "parentTip": "Keep each action to THREE beats—that's the magic number for babies to follow and remember patterns!",
        "funTwist": "Make different items have different voices: towels get a LOW voice ('FOLD-FOLD-FOLD'), socks get a HIGH voice ('roll-roll-roll!')."
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You turned laundry into a fun learning time!",
          "You practiced:",
          "✓ Singing a laundry song with actions",
          "✓ Matching socks with clapping rhythm",
          "✓ Learning action words: fold, shake, roll, stack",
          "✓ Following rhythm patterns during a routine!"
        ],
        "reviewQuestions": [
          "Did your baby enjoy the laundry song and try the motions?",
          "Could your baby follow the rhythm patterns (clapping, folding, shaking)?",
          "Did your baby help with matching socks?",
          "Did this make laundry time more fun for both of you?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Turning routines into learning opportunities means your baby gets constant language exposure throughout the day. Plus, connecting words to actions (fold, shake, stack) builds verb vocabulary—which research shows predicts reading comprehension later!"
        },
        "nextSteps": [
          "Use the laundry song every time you do laundry—repetition helps your baby remember!",
          "Tomorrow we'll explore sounds in the kitchen—another routine filled with learning!",
          "Try adding rhythm to other chores: 'Sweep-sweep-sweep the floor!' or 'Wipe-wipe-wipe the table!'",
          "Let your baby 'help' with laundry anytime—even just playing in the clean clothes builds positive associations with chores!"
        ]
      }
    }
  ]
},

// ---------- LESSON 38 (Week 8 Day 3) ----------
{
  "id": 38,
  "phase": "1-2",
  "quarter": 1,
  "week": 8,
  "weekIndex": 7,
  "dayInWeek": 2,
  "title": "Lesson 38: \"Kitchen Concert\" — Play with Everyday Objects",
  "theme": "Sound Revisit in Daily Routines",
  "skillRecurrenceTags": ["sound-exploration", "rhythm-patterns", "cause-and-effect"],
  "relatedToPreviousSkills": ["sound-exploration-week2", "clap-the-beat-week1", "rhythm-awareness"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Kitchen Concert!",
      "duration": "1 min",
      "content": {
        "goal": "Explore the different sounds that everyday kitchen items make. Turn your kitchen into a music studio and help your baby discover that ordinary objects can make interesting noises!",
        "parentGuide": [
          "Your kitchen is full of different materials that make unique sounds—metal pots, wooden spoons, plastic containers—each sounds different when tapped or shaken.",
          "Learning that different objects make different sounds builds listening skills and helps your baby notice details in their environment.",
          "Creating rhythms with kitchen items is early music education, which research shows boosts language development and reading skills.",
          "This activity brings back Week 2's sound exploration but with more purpose—now your baby can create patterns, not just random noise.",
          "The best part? This costs nothing and shows your baby that fun and learning don't require fancy toys!"
        ],
        "materials": ["Safe kitchen items: pots, pans, plastic containers, wooden spoons, metal spoons, measuring cups, plastic bowls", "Optional: a muffin tin, whisk, spatula", "Clear floor space or high chair tray"],
        "tip": "Choose items that won't break or hurt—avoid glass, sharp utensils, or anything too heavy for your baby to handle safely.",
        "researchNote": "Trehub (2003) - Babies who engage in musical play (including making sounds with objects) develop better auditory processing skills, which directly supports language learning and reading."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Sound Discovery Station",
      "duration": "3-4 min",
      "focus": "Exploring Different Kitchen Sounds",
      "researchBasis": "Bahrick & Lickliter (2000) - When babies explore objects through multiple senses (seeing, hearing, touching), they learn and remember more about those objects' properties.",
      "content": {
        "goal": "Let your baby tap, shake, and bang different kitchen items to discover what sound each one makes. Compare sounds and describe them with words.",
        "whatToSay": [
          "\"Let's see what sounds we can make!\"",
          "\"Tap the pot! Listen—BONG! Big sound!\"",
          "\"Now tap the bowl. Tap-tap! Different sound!\"",
          "\"Shake this cup—rattle-rattle!\"",
          "\"Which one sounds loud? Which one sounds quiet?\""
        ],
        "whatToDo": [
          "Set out 5-6 kitchen items on the floor or on a high chair tray.",
          "Pick up a metal pot and a wooden spoon.",
          "Tap the pot slowly: 'Listen! Tap... tap... tap... BONG!'",
          "Say what you hear: 'That's LOUD! The pot makes a loud sound!'",
          "Hand the spoon to your baby: 'Your turn! Tap the pot!'",
          "Celebrate when they tap: 'You made a sound! BONG!'",
          "Pick up a plastic container: 'Now try this one. Does it sound the same or different?'",
          "Tap it: 'Tap-tap! Quieter! Softer sound!'",
          "Let your baby try several items, describing each: 'That's metal—ding! That's plastic—thunk! That's wood—knock-knock!'",
          "Compare sounds: 'This one is high! Ting! This one is low! Bong!'",
          "Let your baby explore freely—bang, shake, tap, drop, and listen!"
        ],
        "examples": [
          "Metal pot with spoon: 'BONG! Loud and ringy!'",
          "Plastic bowl with hand: 'Thunk-thunk! Softer and lower!'",
          "Measuring cups shaken: 'Clink-clink! Jingly!'",
          "Wooden spoon on cutting board: 'Knock-knock! Like a door!'"
        ],
        "watchFor": [
          "your baby trying different items to see what sounds they make—showing curiosity and experimentation.",
          "Your baby banging or tapping with purpose—creating sounds intentionally, not just randomly.",
          "Your baby pausing to listen after making a sound—showing they're paying attention to the results.",
          "Your baby showing preferences—going back to items they like the sound of!"
        ],
        "parentTip": "It's okay if it's loud! Learning about sound means experimenting—and kids naturally love making noise!",
        "funTwist": "Play 'guess the sound' with your eyes closed: You tap something, your baby guesses which item it is (with your help)!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Copy My Beat",
      "duration": "4-5 min",
      "focus": "Creating and Imitating Rhythm Patterns",
      "researchBasis": "Kirschner & Tomasello (2010) - Musical activities that involve copying rhythms strengthen babies' ability to synchronize with others, which supports turn-taking in conversation and social bonding.",
      "content": {
        "goal": "Tap out simple rhythm patterns and encourage your baby to copy them. This builds listening skills, memory, and imitation—all important for language learning.",
        "whatToSay": [
          "\"Watch me! I'll make a pattern!\"",
          "\"Tap-tap-tap! Three taps! Can you do it?\"",
          "\"Your turn! Tap-tap-tap!\"",
          "\"Great! You copied my beat!\"",
          "\"Let's try a new one! Tap... tap... tap—slow!\""
        ],
        "whatToDo": [
          "Pick a pot and spoon (or let your baby choose an item).",
          "Tap a simple pattern: three quick taps: 'Tap-tap-tap!'",
          "Say: 'Now you try! Tap-tap-tap!'",
          "Wait for your baby to attempt it—any tapping counts as trying!",
          "Celebrate: 'Yes! You did it! You tapped!'",
          "Try a different pattern: two loud taps: 'BANG-BANG!'",
          "Encourage copying: 'Your turn! BANG-BANG!'",
          "Make it easier by doing it together: 'Let's do it together! Tap-tap-tap!'",
          "Try slow vs. fast: 'Slow taps: tap... tap... tap. Fast taps: taptaptap!'",
          "Let your baby create a pattern and YOU copy it: 'You make a sound, I'll copy YOU!'",
          "Go back and forth several times—this is turn-taking practice!"
        ],
        "examples": [
          "Simple: Tap-tap-tap (three quick taps)",
          "Slow: Tap... tap... tap (three slow taps)",
          "Loud-soft: BANG-tap (one loud, one quiet)",
          "Fast: Taptaptaptap (rapid tapping)",
          "Pattern: Tap-tap-pause-tap (rhythm with a rest)"
        ],
        "watchFor": [
          "your baby watching your hands closely—showing they're paying attention to the pattern.",
          "Your baby attempting to copy—even if they don't get the exact rhythm, trying is what matters!",
          "Your baby understanding turn-taking—waiting for you to finish, then taking their turn.",
          "Your baby creating their own patterns—showing independence and creativity!"
        ],
        "parentTip": "Don't expect perfect copying—at this age, ANY response to your pattern shows they're processing and trying!",
        "funTwist": "Name the patterns silly names: 'This is the Bunny Hop!' (hop-hop-hop) 'This is the Giant Stomp!' (BOOM... BOOM...)"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Kitchen Band Parade",
      "duration": "3-4 min",
      "focus": "Group Rhythm and Music-Making",
      "researchBasis": "Gerry et al. (2012) - Participatory music-making enhances babies' brain responses to music and speech, suggesting that active musical play supports overall communication development.",
      "content": {
        "goal": "March around with your kitchen instruments playing together. This combines rhythm, movement, and sound in a fun, active way.",
        "whatToSay": [
          "\"Let's have a parade! March-march-march!\"",
          "\"Bang your drum! BOOM-BOOM-BOOM!\"",
          "\"We're making music together!\"",
          "\"March around the kitchen! Play your instruments!\"",
          "\"Kitchen band—let's go!\""
        ],
        "whatToDo": [
          "Give your baby a pot and spoon (or let them pick their favorite sound-maker).",
          "You take a pot and spoon too.",
          "Stand up and start marching in place: 'Let's march! Left-right-left-right!'",
          "Bang your pot in rhythm with your steps: 'March-bang-march-bang!'",
          "Encourage your baby to bang while marching (or you carry them and march while they bang).",
          "March around the kitchen: 'Here comes the kitchen band!'",
          "Sing a simple chant: 'We are marching, we are drumming, boom-boom-boom!'",
          "Stop suddenly: 'STOP!' (silence) 'Now go again!'",
          "Try marching fast and slow: 'Fast parade!' (quick steps and banging) 'Slow parade!' (slow steps and banging).",
          "End with a big finale: 'Everyone play together! BOOM-BOOM-BOOM!' then 'All done! Take a bow!'"
        ],
        "examples": [
          "March around the kitchen table in a circle, banging in time with steps.",
          "March to the living room and back—make it a journey!",
          "Invite siblings, pets, or stuffed animals to 'join the band'—the more the merrier!",
          "Stop at different 'stations' (refrigerator, sink) and do a special rhythm at each spot."
        ],
        "watchFor": [
          "your baby banging or shaking their instrument while moving—coordinating two actions at once!",
          "Your baby enjoying the combination of sound and movement—smiling, laughing, engaged.",
          "Your baby responding to 'stop' and 'go'—showing they can follow directions during play.",
          "Your baby wanting to do it again—the ultimate sign they're having fun!"
        ],
        "parentTip": "This can be loud and chaotic—that's the point! Embrace the noise and energy. This is joyful learning!",
        "funTwist": "Assign each family member an instrument and take turns being the 'leader' who sets the rhythm everyone copies!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You created a whole concert from kitchen items! What amazing music!",
          "You practiced:",
          "✓ Discovering different sounds from pots, pans, and spoons",
          "✓ Copying rhythm patterns",
          "✓ Playing music together as a 'band'",
          "✓ Learning that everyday objects can make music!"
        ],
        "reviewQuestions": [
          "Did your baby experiment with different items to hear various sounds?",
          "Could your baby attempt to copy any of your rhythm patterns?",
          "Did your baby enjoy the marching parade activity?",
          "Which kitchen item made your baby's favorite sound?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Creating and copying rhythms builds the timing and pattern-recognition skills your baby needs for language. The rhythm of speech, the beats in words, and eventually the flow of reading all rely on this same rhythmic sense your baby practiced today!"
        },
        "nextSteps": [
          "Leave safe kitchen items accessible for independent music-making during play time.",
          "Tomorrow we'll take sound exploration outside for an outdoor sound walk!",
          "Add kitchen instrument time to your daily routine—before or after meals is perfect!",
          "When cooking, narrate the sounds: 'Hear the water boil? Bubble-bubble! Hear the toast pop? Pop!'"
        ]
      }
    }
  ]
},

// ---------- LESSON 39 (Week 8 Day 4) ----------
{
  "id": 39,
  "phase": "1-2",
  "quarter": 1,
  "week": 8,
  "weekIndex": 7,
  "dayInWeek": 3,
  "title": "Lesson 39: \"Outdoor Sound Walk\" — Listen and Explore",
  "theme": "Sound Revisit in Daily Routines",
  "skillRecurrenceTags": ["environmental-sounds", "listening-skills", "vocabulary-building"],
  "relatedToPreviousSkills": ["sounds-around-us-week2", "environmental-sounds", "attention-focus"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Outdoor Sound Walk!",
      "duration": "1 min",
      "content": {
        "goal": "Take a walk outside (or sit by a window) and listen carefully to all the sounds in nature and the environment. This builds listening skills and connects language to the real world.",
        "parentGuide": [
          "The outdoor environment is rich with sounds your baby might not notice without your help—birds, wind, cars, planes, dogs, leaves crunching.",
          "Teaching your baby to STOP and LISTEN builds attention skills that are crucial for learning anything, especially language and reading.",
          "Outdoor time with a learning focus combines physical activity, fresh air, and brain development—a perfect combination!",
          "This lesson shows your baby that learning isn't just indoors at home—the whole world is a classroom.",
          "Even if you can't go outside, sitting by an open window or door works perfectly for this activity!"
        ],
        "materials": ["Access to outdoors (yard, sidewalk, porch, balcony) or a window with outdoor sounds", "Comfortable clothing for baby", "Optional: stroller or baby carrier if needed"],
        "tip": "Choose a time when outdoor sounds are likely—morning or late afternoon often have more bird songs and neighborhood activity than midday.",
        "researchNote": "Wells & Evans (2003) - Children who spend time in natural environments show better attention, focus, and stress regulation—all skills that support learning and language development."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Stop and Listen Game",
      "duration": "3-4 min",
      "focus": "Teaching Active Listening",
      "researchBasis": "Fernald & Kuhl (1987) - Directing babies' attention to specific sounds in their environment helps them learn to filter important information from background noise—a critical skill for language learning.",
      "content": {
        "goal": "Practice stopping, being quiet, and listening carefully. This teaches your baby that listening is an active skill you do on purpose, not just something that happens.",
        "whatToSay": [
          "\"Let's play the listening game! We stop and listen!\"",
          "\"Shhh... stop... listen! Do you hear something?\"",
          "\"I hear a BIRD! Tweet-tweet! Do you hear it?\"",
          "\"Let's stop again! Shhh... listen!\"",
          "\"I hear a CAR! Vroom! Listen!\""
        ],
        "whatToDo": [
          "Go outside or sit by an open window with your baby.",
          "Say: 'Let's play the STOP and LISTEN game!'",
          "Model stopping: stand still or sit quietly and put your hand by your ear like you're listening hard.",
          "Say: 'Shhh... stop... listen!' in a whisper.",
          "Wait silently for 3-5 seconds—this is the 'listening time.'",
          "When you hear a sound, point toward it: 'I hear something! A bird!'",
          "Make the sound: 'Tweet-tweet! The bird is singing!'",
          "Start walking or moving again for a bit.",
          "Then stop again: 'Stop! Listen! What do you hear now?'",
          "Repeat this stop-listen-identify pattern 5-6 times during your walk.",
          "Encourage your baby to put their hand to their ear too—copying your listening pose.",
          "Even if your baby can't identify sounds yet, modeling this teaches them HOW to listen actively."
        ],
        "examples": [
          "Stop: stand still, hand to ear, whisper 'shhh, listen!' → hear bird → 'Tweet-tweet! Bird singing!'",
          "Stop: sit quietly → hear wind → 'Whoooosh! Wind blowing in the trees!'",
          "Stop: pause walking → hear car → 'Vroom! Car driving by!'",
          "Stop: stand still → hear dog bark → 'Woof-woof! A dog is talking!'"
        ],
        "watchFor": [
          "your baby becoming still when you say 'stop'—showing they understand the game.",
          "Your baby looking around when you say 'listen'—trying to locate the sound source.",
          "Your baby pointing toward sounds—showing they heard something!",
          "Your baby copying your listening pose—hand to ear or turning head to listen better."
        ],
        "parentTip": "In the beginning, YOU will do most of the identifying and naming. That's perfect—your baby is learning by watching and listening to you!",
        "funTwist": "Make it a detective game: 'We're sound detectives! Let's find clues! Listen for clues!' Use a 'spy' voice!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sound Collection Walk",
      "duration": "4-5 min",
      "focus": "Identifying and Categorizing Sounds",
      "researchBasis": "Swingley (2009) - When adults label environmental sounds for babies, it expands their vocabulary beyond object names to include action words and sound words—building richer language.",
      "content": {
        "goal": "As you walk or sit outside, collect and name as many different sounds as you can. Try to 'collect' at least 5-6 different sounds during your time outside.",
        "whatToSay": [
          "\"Let's collect sounds! How many can we find?\"",
          "\"Sound number one: BIRD! Tweet-tweet!\"",
          "\"Sound number two: CAR! Vroom-vroom!\"",
          "\"That's three sounds! We're great listeners!\"",
          "\"Ooh, a new sound! What's that?\""
        ],
        "whatToDo": [
          "Start your walk or outdoor sitting time.",
          "When you hear the first sound, stop and announce: 'Sound number ONE! What is it?'",
          "Identify it: 'A bird! Tweet-tweet!'",
          "Count on your fingers: 'That's ONE sound!'",
          "Continue walking or sitting.",
          "When you hear a second sound: 'Sound number TWO!'",
          "Identify and make the sound: 'A dog! Woof-woof!'",
          "Count: 'Now we have TWO sounds! Bird and dog!'",
          "Keep collecting, counting, and naming sounds.",
          "Try to find: birds, cars, planes, dogs, wind, leaves, people talking, doors, gates, bikes, construction.",
          "After each sound, briefly review: 'We heard birds, cars, and wind! Three sounds!'",
          "Celebrate when you reach 5-6 sounds: 'We collected FIVE sounds! We're sound collectors!'"
        ],
        "examples": [
          "Bird chirping → 'Sound ONE! Bird says tweet-tweet!' (hold up one finger)",
          "Car passing → 'Sound TWO! Car says vroom!' (hold up two fingers)",
          "Wind in trees → 'Sound THREE! Wind says whoooosh!' (hold up three fingers)",
          "Dog barking → 'Sound FOUR! Dog says woof-woof!' (hold up four fingers)",
          "Airplane overhead → 'Sound FIVE! Plane says neeeeow!' (hold up five fingers)"
        ],
        "watchFor": [
          "your baby looking excited when they hear a new sound—showing engagement with the game.",
          "Your baby trying to make any of the sounds with you—tweet, woof, vroom.",
          "Your baby watching your fingers as you count—learning number connection.",
          "Your baby starting to anticipate—looking up when they hear a plane, looking around for a dog when they hear barking."
        ],
        "parentTip": "If you live somewhere quiet, sounds might be less frequent—that's okay! Just take a longer walk or sit longer, and celebrate each sound you DO find!",
        "funTwist": "Give sounds silly categories: 'That bird is a LOUD talker! That car is a FAST mover! That wind is a SOFT whisper!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Copy the Outdoor Sounds",
      "duration": "3-4 min",
      "focus": "Imitation and Sound Memory",
      "researchBasis": "Kuhl (2004) - Imitating sounds helps babies remember them and builds the motor skills needed for speech production—the more practice making sounds, the better!",
      "content": {
        "goal": "After hearing outdoor sounds, practice making them together. This builds your baby's ability to remember sounds and copy them—important for language development.",
        "whatToSay": [
          "\"Let's be all the sounds we heard!\"",
          "\"Can you be a bird? Tweet-tweet-tweet!\"",
          "\"Now let's be a car! Vroom-vroom!\"",
          "\"Let's be the wind! Whoooooosh!\"",
          "\"You're so good at making sounds!\""
        ],
        "whatToDo": [
          "Find a spot to sit or stand for a moment.",
          "Say: 'We heard so many sounds! Let's make them again!'",
          "Start with a bird: 'Let's be birds! Put your arms out like wings!'",
          "Flap arms and say: 'Tweet-tweet-tweet!'",
          "Encourage your baby to flap and tweet with you.",
          "Next, be a car: 'Now we're cars! Hold the steering wheel!'",
          "Pretend to drive and say: 'Vroom-vroom-vroom!'",
          "Be the wind: 'Now we're wind! Blow and sway!'",
          "Sway back and forth saying: 'Whoooooosh!'",
          "Go through 4-5 sounds you heard, doing an action with each.",
          "Make it active: jump like a barking dog, zoom like a plane, stomp like heavy footsteps.",
          "End with: 'We made all those sounds! Great job, sound-maker!'"
        ],
        "examples": [
          "Bird: flap arms + tweet-tweet + hop around",
          "Car: pretend steering wheel + vroom-vroom + walk/run forward",
          "Wind: sway side to side + whoooosh + wave arms",
          "Dog: down on hands/knees (or just crouch) + woof-woof + bark loudly",
          "Plane: arms straight out + neeeow + zoom in a circle"
        ],
        "watchFor": [
          "your baby doing any of the movements—flapping, swaying, zooming.",
          "Your baby making any sounds—even approximations count!",
          "Your baby remembering which sound goes with which animal/object—showing memory!",
          "Your baby laughing and having fun—joy enhances learning!"
        ],
        "parentTip": "Make the movements BIG and exaggerated—this helps your baby's brain connect the sound to the motion and remember it better!",
        "funTwist": "Play 'Sound Freeze Dance': Make sounds and move, then when you say 'FREEZE!' everyone stops silent and still until the next sound starts!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You were an amazing sound detective outside!",
          "You practiced:",
          "✓ Stopping and listening carefully",
          "✓ Collecting and counting different sounds",
          "✓ Making outdoor sounds with your voice and body",
          "✓ Learning that the world is full of interesting noises!"
        ],
        "reviewQuestions": [
          "How many different sounds did you and your baby find?",
          "Could your baby stop and listen when you paused?",
          "Did your baby try to copy any of the outdoor sounds?",
          "Which sound seemed to interest your baby most?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Active listening—stopping to focus on sounds—is a skill that directly transfers to listening for letter sounds in words and paying attention during reading time. Today's outdoor practice builds the foundation for classroom listening skills!"
        },
        "nextSteps": [
          "Make sound walks part of your routine—even 5 minutes of listening during regular walks counts!",
          "Tomorrow we'll practice quiet listening and wind-down sounds for evening time.",
          "Try sound walks in different locations: park, parking lot, quiet street, busy street—different places have different sound collections!",
          "At bedtime, review: 'Today we heard birds, cars, and dogs! What sounds will we hear tomorrow?'"
        ]
      }
    }
  ]
},

// ---------- LESSON 40 (Week 8 Day 5) ----------
{
  "id": 40,
  "phase": "1-2",
  "quarter": 1,
  "week": 8,
  "weekIndex": 7,
  "dayInWeek": 4,
  "title": "Lesson 40: \"Evening Wind-Down\" — Quiet Listening",
  "theme": "Sound Revisit in Daily Routines",
  "skillRecurrenceTags": ["listening-skills", "quiet-sounds", "routine-integration"],
  "relatedToPreviousSkills": ["quiet-time-echoes-week2", "sound-awareness", "attention-focus"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Evening Wind-Down!",
      "duration": "1 min",
      "content": {
        "goal": "Practice listening to very quiet, gentle sounds as part of your evening routine. This teaches your baby that listening isn't just for loud sounds—soft sounds matter too, and quiet time can be peaceful and special.",
        "parentGuide": [
          "This week has been full of LOUD sound activities—today we balance that by practicing quiet listening and calm awareness.",
          "Learning to hear soft sounds is actually harder than hearing loud ones—it requires more focus and attention.",
          "Evening wind-down activities help your baby transition from active play to bedtime, making sleep routines easier.",
          "Quiet listening builds self-regulation skills—the ability to calm down and focus—which helps with behavior and learning.",
          "This lesson wraps up Week 8 by showing that sound exploration can be both exciting AND peaceful!"
        ],
        "materials": ["Quiet, calm space (bedroom, living room with lights dimmed)", "Optional: soft music, white noise machine, or nature sounds", "Comfortable spot to sit or lie down together"],
        "tip": "Do this activity in the evening as part of your bedtime routine—it will help signal to your baby that calm, quiet time is coming.",
        "researchNote": "Bates et al. (2012) - Consistent, calming bedtime routines that include quiet activities improve children's sleep quality and emotional regulation, which supports daytime learning and attention."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Find the Quiet Sounds",
      "duration": "3-4 min",
      "focus": "Listening for Soft, Distant Sounds",
      "researchBasis": "Werner & Bargones (1991) - Training babies to detect quiet sounds sharpens their auditory discrimination skills, which helps them later hear subtle differences between similar speech sounds.",
      "content": {
        "goal": "Sit quietly together and try to hear very soft sounds—faraway sounds, gentle sounds, tiny sounds. This is the opposite of yesterday's loud outdoor activity!",
        "whatToSay": [
          "\"Let's listen for quiet sounds—shhh, very quiet!\"",
          "\"Do you hear that soft sound? So gentle!\"",
          "\"I hear the clock ticking—tick-tick-tick—so quiet!\"",
          "\"Do you hear the wind outside? Soft whoosh!\"",
          "\"Let's whisper—quiet sounds only!\""
        ],
        "whatToDo": [
          "Dim the lights or draw curtains to create a calm atmosphere.",
          "Sit or lie down with your baby in a comfortable position.",
          "Say in a soft voice: 'Let's be very quiet and listen for soft sounds.'",
          "Put your finger to your lips: 'Shhhhh...'",
          "Sit silently for 10-15 seconds—model being still and quiet.",
          "Whisper when you hear something: 'I hear the clock... tick-tick-tick...'",
          "Continue listening together.",
          "Point out soft sounds: 'Faraway car... soft breeze... house settling... quiet music...'",
          "Keep your voice to a whisper the entire time.",
          "If your baby makes noise, gently redirect: 'Let's use quiet voices—whisper with me!'",
          "Try to identify 3-4 quiet sounds during this time."
        ],
        "examples": [
          "Clock ticking: 'Tick... tick... tick... the clock is so quiet!'",
          "Distant car: 'A car far away... soft vroom... it's leaving...'",
          "House creaking: 'The house is talking—creak! So gentle!'",
          "Your own breathing: 'Hear us breathing? In... out... in... out... so soft!'",
          "Fridge humming: 'The refrigerator hums... hmmmmm... quiet sound!'"
        ],
        "watchFor": [
          "your baby becoming still and quiet—trying to match the calm energy.",
          "Your baby's eyes moving around—showing they're trying to locate quiet sounds.",
          "Your baby whispering or using a quieter voice—matching your volume.",
          "Your baby relaxing—shoulders dropping, breathing slowing, body softening."
        ],
        "parentTip": "Some babies find this HARD—it's okay if your baby can't stay quiet for long! Even 30 seconds of practicing calm is valuable.",
        "funTwist": "Pretend you're both mice: 'Shhh! We're little mice! Mice are so quiet! Squeak-squeak!' (whisper the squeaks)."
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Gentle Sound Making",
      "duration": "4-5 min",
      "focus": "Creating Soft Sounds Together",
      "researchBasis": "Malloch & Trevarthen (2009) - Gentle, rhythmic interactions between parent and baby (like soft singing and whispering) build secure attachment and emotional regulation through sound.",
      "content": {
        "goal": "Take turns making very quiet, gentle sounds together—whispers, hums, soft taps. This shows your baby they can control their volume and make sounds that are calm instead of exciting.",
        "whatToSay": [
          "\"Now let's make quiet sounds together!\"",
          "\"I'll hum softly—hmmmmm—so gentle!\"",
          "\"Your turn! Make a soft sound!\"",
          "\"That was so quiet and nice!\"",
          "\"Let's whisper—whisper-whisper!\""
        ],
        "whatToDo": [
          "Say softly: 'Now we'll make our own quiet sounds.'",
          "Start with a soft hum: 'Hmmmmmm...' (hum gently for 3-4 seconds).",
          "Stop and say: 'That was my quiet sound. Now you try!'",
          "Wait for your baby to make any sound—even if it's not that quiet!",
          "Respond: 'Nice! Let's make it even softer—hmmmmm.'",
          "Try different quiet sounds together:",
          "Soft tapping: tap your fingers together gently: 'Tap-tap-tap... so soft!'",
          "Whisper singing: hum or sing a familiar song in the quietest voice possible.",
          "Gentle shushing: 'Shhh-shhh-shhh...' like calming a baby.",
          "Soft kissing sounds: gentle 'mwah-mwah-mwah.'",
          "Quiet breathing: 'Let's breathe together—in... out... in... out...'",
          "Take turns: you make a sound, then your baby tries one.",
          "Keep everything slow, gentle, and whisper-quiet."
        ],
        "examples": [
          "You hum softly → baby makes any sound → you praise: 'Quiet sound! Good job!'",
          "You tap fingers gently → baby taps hand on leg → 'Yes! Soft taps!'",
          "You whisper 'Twinkle Twinkle' → baby listens or hums along quietly",
          "You do gentle 'shhh-shhh' → baby copies the shushing sound"
        ],
        "watchFor": [
          "your baby attempting to make quieter sounds than usual—showing volume control.",
          "Your baby engaging in the turn-taking—waiting for their turn to make sounds.",
          "Your baby becoming calmer as the activity progresses—feeling the peaceful energy.",
          "Your baby trying to copy your soft sounds—showing they can imitate different volumes."
        ],
        "parentTip": "If your baby makes loud sounds, don't scold—just model quieter: 'That was loud! Now let's try super-duper soft—like a mouse!'",
        "funTwist": "Play 'Quietest Sound Contest': 'Can you make the quietest sound ever? Softer than soft? Quieter than a feather?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Goodnight Whisper Walk",
      "duration": "3-4 min",
      "focus": "Bedtime Routine Integration",
      "researchBasis": "Mindell et al. (2009) - Consistent bedtime routines with calming activities help children fall asleep faster and sleep better, supporting brain development and learning consolidation.",
      "content": {
        "goal": "Walk quietly around your home whispering 'goodnight' to different objects and rooms. This creates a peaceful bedtime ritual that your baby can look forward to every night.",
        "whatToSay": [
          "\"Let's say goodnight to everything!\"",
          "\"Goodnight toys! Sleep well, toys!\"",
          "\"Goodnight kitchen! See you tomorrow!\"",
          "\"Goodnight window! Goodnight moon outside!\"",
          "\"Now let's go to your bed—goodnight baby!\""
        ],
        "whatToDo": [
          "Pick up your baby (or hold their hand if walking) and say: 'Let's say goodnight to everything!'",
          "Walk to the first room or area (like the playroom).",
          "Whisper: 'Goodnight toys! Time to sleep!' and wave.",
          "Move to the kitchen: 'Goodnight kitchen! Goodnight dishes!'",
          "Go to the living room: 'Goodnight couch! Goodnight books!'",
          "Look out a window: 'Goodnight trees! Goodnight moon! Goodnight stars!'",
          "Say goodnight to family members: 'Goodnight Daddy! Goodnight doggy!'",
          "Keep your voice soft and slow throughout.",
          "Make the walk gentle and unhurried—this is transition time, not active playtime.",
          "End at your baby's bedroom: 'And goodnight baby's room! Time for baby to sleep!'",
          "Place your baby in their crib or bed: 'Goodnight, sweet baby! I love you!'"
        ],
        "examples": [
          "Living room: 'Goodnight sofa, goodnight pillows, goodnight lamp—sleep tight!'",
          "Bathroom: 'Goodnight bathtub, goodnight toothbrush, goodnight towels!'",
          "Kitchen: 'Goodnight refrigerator, goodnight table, see you at breakfast!'",
          "Hallway: 'Goodnight pictures, goodnight walls, thank you for a good day!'"
        ],
        "watchFor": [
          "your baby waving 'bye-bye' to objects—understanding the ritual.",
          "Your baby becoming drowsy or calm—the routine is working!",
          "Your baby anticipating the next room—learning the pattern of the walk.",
          "Your baby relaxing into your arms or hand—feeling safe and ready for sleep."
        ],
        "parentTip": "Do this EVERY night in the SAME order—predictable routines help babies feel secure and make bedtime easier!",
        "funTwist": "Let your baby hold a favorite stuffed animal and help them 'wave goodnight' to everything: 'Teddy says goodnight to the kitchen!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You practiced quiet listening and made a peaceful bedtime routine!",
          "You practiced:",
          "✓ Listening for very soft, quiet sounds",
          "✓ Making gentle sounds with whispers and hums",
          "✓ Saying goodnight to your home",
          "✓ Using quiet time to get ready for sleep!"
        ],
        "reviewQuestions": [
          "Was your baby able to calm down during the quiet activities?",
          "Did your baby try to make softer sounds when you modeled them?",
          "Did the goodnight walk help with bedtime transition?",
          "Will you keep this as part of your evening routine?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Learning to listen to quiet sounds builds focused attention—babies must concentrate harder to hear soft sounds. This same focused attention will help them notice subtle differences in letter sounds when learning to read!"
        },
        "nextSteps": [
          "Use the 'Goodnight Whisper Walk' every night—consistency makes it powerful!",
          "This wraps up Week 8! Next week we explore rhymes and repetition—sound patterns that are the building blocks of reading.",
          "Try other quiet listening times: before naps, after busy activities, during car rides.",
          "Read goodnight books in a whisper voice as part of your bedtime routine!"
        ]
      }
    }
  ]
},

// ---------- LESSON 41 (Week 9 Day 1) ----------
{
  "id": 41,
  "phase": "1-2",
  "quarter": 1,
  "week": 9,
  "weekIndex": 8,
  "dayInWeek": 0,
  "title": "Lesson 41: \"Pat-a-Cake\" and Friends — Classic Rhymes",
  "theme": "Rhymes & Repetition",
  "skillRecurrenceTags": ["rhyme-awareness", "rhythm", "phonological-awareness"],
  "relatedToPreviousSkills": ["clap-the-beat-week1", "rhythm-patterns-week8", "sound-play"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Rhyme Time!",
      "duration": "1 min",
      "content": {
        "goal": "Learn classic nursery rhymes with clapping and actions. Rhyming helps your baby hear that words can sound similar at the end, which is a key skill for reading.",
        "parentGuide": [
          "Rhyming is one of the most important early literacy skills—children who can hear rhymes learn to read more easily.",
          "Classic rhymes like 'Pat-a-Cake' have been used for generations because they work! The rhythm and repetition make them easy to remember.",
          "At this age, your baby won't understand what 'rhyme' means, but their brain is learning to recognize sound patterns.",
          "Combining rhymes with hand motions (like clapping) helps your baby remember the words better because they're using multiple senses.",
          "This week focuses on phonological awareness—hearing the sounds IN words, not just whole words themselves."
        ],
        "materials": ["Just you and your baby—no materials needed!", "Optional: other family members to join in"],
        "tip": "Do the rhymes at the SAME speed every time—consistency helps your baby learn the rhythm and eventually join in.",
        "researchNote": "Bryant et al. (1990) - Children who know nursery rhymes at age 3 are better readers at age 6, even when controlling for intelligence and family background. Rhyme knowledge is a powerful predictor!"
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Pat-a-Cake Together",
      "duration": "3-4 min",
      "focus": "Learning the Classic Clapping Rhyme",
      "researchBasis": "Maclean et al. (1987) - Knowledge of nursery rhymes is directly related to phonological awareness, which predicts reading success. The more rhymes children know, the better!",
      "content": {
        "goal": "Teach your baby 'Pat-a-Cake' with clapping and motions. This classic rhyme builds rhythm, rhyme awareness, and coordination.",
        "whatToSay": [
          "\"Let's play Pat-a-Cake! Clap with me!\"",
          "\"Pat-a-cake, pat-a-cake, baker's man!\"",
          "\"Bake me a cake as fast as you can!\"",
          "\"Pat it and prick it and mark it with B!\"",
          "\"Put it in the oven for baby and me!\""
        ],
        "whatToDo": [
          "Sit facing your baby so they can see your hands.",
          "Say: 'Let's do Pat-a-Cake! Watch my hands!'",
          "Clap your hands to the rhythm: 'Pat-a-cake, pat-a-cake, baker's man!'",
          "Do the traditional motions: clap together, clap your own hands, clap together again.",
          "For 'Bake me a cake as fast as you can'—clap quickly!",
          "For 'Pat it'—pat your hands together like patting dough.",
          "For 'prick it'—poke the air with your finger.",
          "For 'mark it with B'—trace a 'B' in the air (or use your baby's initial!).",
          "For 'Put it in the oven'—pretend to slide something forward into an oven.",
          "Repeat 3-4 times—the repetition is important!",
          "Help your baby's hands clap if they're not doing it on their own yet.",
          "Each time, emphasize the rhyming words: 'man' and 'can,' 'B' and 'me.'"
        ],
        "examples": [
          "Full version with baby on your lap: help move baby's hands to clap at the right times.",
          "Face-to-face version: clap your hands together, then clap baby's hands, back and forth.",
          "Independent version: baby claps their own hands while you clap yours and say the words.",
          "Fast version: 'As fast as you can!' — speed up the clapping!"
        ],
        "watchFor": [
          "your baby watching your hands and trying to clap—even if timing is off!",
          "Your baby bouncing or moving to the rhythm—feeling the beat.",
          "Your baby smiling or laughing—showing they enjoy the game.",
          "Your baby anticipating the motions—getting ready to clap before you do it."
        ],
        "parentTip": "At first, your baby might not clap on beat—that's normal! They're learning the pattern. Keep doing it the same way every time.",
        "funTwist": "Change the letter: 'Mark it with [baby's initial]!' Say their name: 'For [baby's name] and me!' Make it personal!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: This Little Piggy",
      "duration": "4-5 min",
      "focus": "Toe Play with Rhyming Story",
      "researchBasis": "Goswami & Bryant (1990) - Rhyme awareness develops through playful exposure. Games that highlight rhyming endings help babies notice sound similarities.",
      "content": {
        "goal": "Play 'This Little Piggy' with your baby's toes (or fingers). This rhyme combines touch, counting, and storytelling with rhyme.",
        "whatToSay": [
          "\"Let's play with your piggies—your toes!\"",
          "\"This little piggy went to market!\"",
          "\"This little piggy stayed home!\"",
          "\"This little piggy had roast beef!\"",
          "\"This little piggy had none!\"",
          "\"And this little piggy cried wee-wee-wee all the way home!\""
        ],
        "whatToDo": [
          "Sit your baby down or lay them on their back where you can reach their feet.",
          "Take off one sock: 'Let's find your piggies—your little toes!'",
          "Hold the big toe: 'This little piggy went to market!' (wiggle the toe).",
          "Move to the next toe: 'This little piggy stayed home!' (wiggle it).",
          "Third toe: 'This little piggy had roast beef!' (wiggle).",
          "Fourth toe: 'This little piggy had none!' (wiggle—say 'none' with emphasis).",
          "Pinky toe: 'And this little piggy cried...' (pause for drama).",
          "Then tickle up their leg: 'Wee-wee-wee all the way home!' (tickle to their tummy).",
          "Your baby will likely giggle at the tickle ending—that's the fun part!",
          "Do the other foot: 'Let's do the other piggies!'",
          "Repeat 2-3 times total—babies love repetition and anticipation."
        ],
        "examples": [
          "Big toe gets a gentle wiggle: 'To market!' (you can pretend to walk the toe).",
          "When you get to 'none,' make a sad face: 'No food! Oh no!'",
          "For the ending, draw out the suspense: 'And this little piggy cried... (wait) ...WEE-WEE-WEE!' then tickle!",
          "Try it with fingers instead: 'These are finger piggies!'"
        ],
        "watchFor": [
          "your baby watching their toes as you touch each one—tracking the sequence.",
          "Your baby giggling or smiling during the tickle ending—showing they know it's coming!",
          "Your baby holding their foot up or wiggling toes—participating actively.",
          "Your baby looking excited when you reach for their foot again—wanting to repeat it!"
        ],
        "parentTip": "The anticipation of the tickle is what makes this rhyme special—build suspense before 'wee-wee-wee!' to maximize the fun!",
        "funTwist": "Let your baby do YOUR toes or fingers: 'Now you do Mommy's piggies!' Help them wiggle your toes—it's hilarious for both of you!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Itsy Bitsy Spider",
      "duration": "3-4 min",
      "focus": "Hand Motion Rhyme with Story",
      "researchBasis": "Dunst et al. (2006) - Action songs and finger plays support language development because they engage multiple learning pathways: auditory (hearing), visual (watching), and kinesthetic (doing).",
      "content": {
        "goal": "Sing 'Itsy Bitsy Spider' with hand motions. This rhyme teaches cause and effect (rain washes spider down, sun brings it back) while building rhyme awareness.",
        "whatToSay": [
          "\"Let's sing about a spider! Watch my fingers!\"",
          "\"The itsy bitsy spider went up the water spout!\"",
          "\"Down came the rain and washed the spider out!\"",
          "\"Out came the sun and dried up all the rain!\"",
          "\"And the itsy bitsy spider went up the spout again!\""
        ],
        "whatToDo": [
          "Sit where your baby can see your hands clearly.",
          "Say: 'Let's make a spider with our fingers!'",
          "For 'itsy bitsy spider': touch opposite fingers together and 'walk' them up (thumb to pinky, index to ring finger, alternating).",
          "For 'went up the water spout': move your climbing fingers upward.",
          "For 'Down came the rain': wiggle your fingers downward like falling rain.",
          "For 'washed the spider out': sweep your hands outward like washing away.",
          "For 'Out came the sun': make a big circle with your arms above your head.",
          "For 'dried up all the rain': bring arms down slowly, fingers wiggling.",
          "For 'went up the spout again': climb fingers up again just like the beginning!",
          "Sing it slowly at first so your baby can watch the motions.",
          "Repeat 2-3 times, getting a bit faster each time if your baby seems engaged.",
          "Help your baby's hands make the motions if they're not doing it independently."
        ],
        "examples": [
          "Spider climbing: demonstrate the finger-walking motion slowly and clearly.",
          "Rain falling: shake hands downward with emphasis: 'Down came the RAIN!'",
          "Sun: make a BIG circle—stretch your arms wide!",
          "Repeat: 'Again! The spider goes up AGAIN!' Emphasize 'again' with excitement."
        ],
        "watchFor": [
          "your baby watching your fingers intently—following the spider's journey.",
          "Your baby attempting any of the hand motions—even if they look different from yours!",
          "Your baby moving their hands up and down—showing they get the up/down concept.",
          "Your baby smiling during the sun part—many babies love that big arm circle!"
        ],
        "parentTip": "Don't worry about perfect finger coordination—at this age, any hand movement that follows the song is great!",
        "funTwist": "Make different voices for different parts: tiny squeaky voice for spider, loud voice for rain, happy voice for sun!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned three classic rhymes! You're a rhyme superstar!",
          "You practiced:",
          "✓ Pat-a-Cake with clapping",
          "✓ This Little Piggy with your toes",
          "✓ Itsy Bitsy Spider with finger motions",
          "✓ Hearing words that rhyme and sound similar!"
        ],
        "reviewQuestions": [
          "Which rhyme did your baby seem to enjoy most?",
          "Could your baby do any of the hand motions or clapping?",
          "Did your baby anticipate any parts (like the tickle in This Little Piggy)?",
          "Will you add these rhymes to your daily routine?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Nursery rhymes teach babies that words can sound similar at the end (cake/bake, man/can, out/spout, rain/again). Recognizing these sound patterns is the foundation for learning letter sounds and eventually reading words!"
        },
        "nextSteps": [
          "Do these three rhymes every day this week—at diaper changes, before meals, or during play time.",
          "Tomorrow we'll explore syllables—clapping the beats in words!",
          "Try other classic rhymes: 'Humpty Dumpty,' 'Jack and Jill,' 'Baa Baa Black Sheep.'",
          "Rhyme books make great bedtime reading—look for Mother Goose collections at your library!"
        ]
      }
    }
  ]
},

// ---------- LESSON 42 (Week 9 Day 2) ----------
{
  "id": 42,
  "phase": "1-2",
  "quarter": 1,
  "week": 9,
  "weekIndex": 8,
  "dayInWeek": 1,
  "title": "Lesson 42: \"Clap the Beat\" — Syllable Awareness",
  "theme": "Rhymes & Repetition",
  "skillRecurrenceTags": ["syllable-awareness", "rhythm", "phonological-awareness"],
  "relatedToPreviousSkills": ["clap-the-beat-week1", "rhythm-patterns", "name-recognition"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Syllable Clapping!",
      "duration": "1 min",
      "content": {
        "goal": "Learn that words are made of smaller chunks called syllables, and practice clapping for each syllable in familiar words. This builds awareness that words can be broken into parts.",
        "parentGuide": [
          "Syllables are the 'beats' in words—every word has at least one syllable, and many have more (ba-by = 2, din-o-saur = 3).",
          "Being able to break words into syllables is a key step toward being able to break words into individual sounds (phonemes) later.",
          "At this age, your baby won't understand the concept of syllables, but their brain is learning to hear the rhythm and structure inside words.",
          "Clapping syllables makes an abstract concept concrete—your baby can SEE and FEEL the beats in words.",
          "Starting with your baby's name makes this personal and meaningful—their name is the most important word to them!"
        ],
        "materials": ["Just you and your baby—no materials needed!", "Optional: toys or objects to practice with (ball, teddy bear, book)"],
        "tip": "Exaggerate the syllable breaks at first: 'BA-by' with a clear pause between syllables helps your baby hear the chunks.",
        "researchNote": "Liberman et al. (1974) - Syllable awareness typically develops before phoneme awareness. Children can hear syllables earlier than individual sounds, making this a perfect starting point."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Clap Your Name",
      "duration": "3-4 min",
      "focus": "Introducing Syllables Through Names",
      "researchBasis": "Treiman & Zukowski (1991) - Children learn phonological awareness skills more easily when starting with meaningful, familiar words like their own name.",
      "content": {
        "goal": "Clap the syllables in your baby's name, family members' names, and pet names. Names are perfect for learning syllables because they're emotionally important!",
        "whatToSay": [
          "\"Let's clap your name! Listen!\"",
          "\"Em-i-ly! Em-i-ly! Three claps!\"",
          "\"Clap with me: Em (clap), i (clap), ly (clap)!\"",
          "\"Now Mommy's name: Mom-my! Two claps!\"",
          "\"Let's clap Daddy: Dad-dy! Two claps!\""
        ],
        "whatToDo": [
          "Say: 'We're going to clap the beats in names! Listen to YOUR name!'",
          "Say your baby's name slowly with clear syllables: 'So-phi-a' or 'Jack' or 'Li-am.'",
          "Clap once for each syllable as you say it: 'So (clap) phi (clap) a (clap)!'",
          "Count the claps on your fingers: 'Three beats! So-phi-a has three beats!'",
          "Do it again: 'Let's do it again! So (clap) phi (clap) a (clap)!'",
          "Help your baby clap along—move their hands if needed.",
          "Try family names: 'Now let's clap Daddy's name! Da (clap) vid (clap)! Two beats!'",
          "Do Mom's name, siblings' names, pets' names.",
          "Compare: 'Your name has three claps! Daddy's name has two claps! Different!'",
          "Practice each name 2-3 times so your baby hears the pattern repeatedly."
        ],
        "examples": [
          "One syllable name (Jack, Kate, Sam): 'Jack! (clap) One beat! Short name!'",
          "Two syllable name (E-mma, No-ah, Mi-a): 'E (clap) mma (clap)! Two beats!'",
          "Three syllable name (Em-i-ly, Ben-ja-min, O-li-ver): 'Em (clap) i (clap) ly (clap)! Three beats!'",
          "Four syllable name (E-li-za-beth, Al-ex-an-der): 'E (clap) li (clap) za (clap) beth (clap)! Four beats!'"
        ],
        "watchFor": [
          "your baby watching your hands clap—tracking the motion.",
          "Your baby clapping along—even if not exactly with the syllables yet!",
          "Your baby smiling when you say their name—emotional connection.",
          "Your baby trying to say their name along with the clapping."
        ],
        "parentTip": "If your baby has a long name, it might be too hard right now—use their nickname! 'Liz' is easier than 'E-liz-a-beth.'",
        "funTwist": "Make it a cheer: 'Give me an M! (clap) Give me an I! (clap) Give me an A! (clap) What does that spell? MI-A!' (clap-clap-clap)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Clap the Toys",
      "duration": "4-5 min",
      "focus": "Syllable Practice with Objects",
      "researchBasis": "Adams (1990) - Practicing syllable segmentation with concrete objects helps children understand that ALL words have internal structure, not just names.",
      "content": {
        "goal": "Pick up toys and objects around you and clap their names. This shows your baby that every word has syllables, not just names.",
        "whatToSay": [
          "\"What's this? A BALL! Let's clap it!\"",
          "\"Ball! (clap) One beat! Short word!\"",
          "\"Now this—TED-DY BEAR! Let's clap!\"",
          "\"Ted (clap) dy (clap) bear (clap)! Three beats!\"",
          "\"Every word has beats!\""
        ],
        "whatToDo": [
          "Gather 5-6 toys or household objects your baby knows.",
          "Hold up a ball: 'What's this? A BALL!'",
          "Say it slowly: 'Ball! (clap) One beat!' (clap once).",
          "Count on your finger: 'One!'",
          "Hold up a teddy bear: 'Teddy bear! Let's clap!'",
          "Say: 'Ted (clap) dy (clap) bear (clap)! Three beats!'",
          "Hold up three fingers: 'One, two, three!'",
          "Continue with different objects: book, bottle, diaper, blanket, cup, spoon.",
          "Compare lengths: 'Ball has one beat—short! Teddy bear has three beats—long!'",
          "Let your baby choose an object, then YOU clap it: 'You picked the bottle! Bot (clap) tle (clap)! Two beats!'",
          "Do each object 2-3 times for repetition."
        ],
        "examples": [
          "One syllable: ball, book, cup, spoon, car, shoe → (clap) 'One beat!'",
          "Two syllables: ba-by, bot-tle, blan-ket, dia-per, ted-dy → (clap-clap) 'Two beats!'",
          "Three syllables: tel-e-phone, di-no-saur, el-e-phant → (clap-clap-clap) 'Three beats!'",
          "Four syllables: re-frig-er-a-tor, hel-i-cop-ter → (clap-clap-clap-clap) 'Four beats!'"
        ],
        "watchFor": [
          "your baby starting to clap when you hold up an object—anticipating the activity.",
          "Your baby reaching for the object while you clap—making the word-object connection.",
          "Your baby noticing differences—some words get more claps than others!",
          "Your baby trying to say the word along with the clapping."
        ],
        "parentTip": "Start with short words (1-2 syllables) and only add longer words if your baby seems engaged—too many claps can be confusing!",
        "funTwist": "Play 'Find the Match': 'Can you find something else with TWO beats like bottle? Let's look!' Hunt for two-syllable words together!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Clapping Body Parts",
      "duration": "3-4 min",
      "focus": "Syllables with Touch Connection",
      "researchBasis": "Goswami (2001) - Multi-sensory approaches (combining sound, sight, touch, movement) are most effective for teaching phonological awareness to young children.",
      "content": {
        "goal": "Clap the names of body parts while touching them. This combines syllable awareness with body awareness and adds a touch element to help learning.",
        "whatToSay": [
          "\"Let's clap body parts! Where's your nose?\"",
          "\"Nose! (clap) One beat! Touch your nose!\"",
          "\"Where are your shoulders?\"",
          "\"Shoul (clap) ders (clap)! Two beats! Touch your shoulders!\"",
          "\"Let's find your belly!\""
        ],
        "whatToDo": [
          "Say: 'Let's clap body parts! This will be fun!'",
          "Point to your baby's nose: 'Nose! (clap) One beat!'",
          "Touch their nose gently as you clap.",
          "Do it again: 'Nose! (clap)' and touch.",
          "Try shoulders: 'Shoul (clap) ders (clap)! Two beats!'",
          "Touch both shoulders as you clap twice.",
          "Continue with different body parts: head, eyes, toes, fingers, belly, elbows, knees.",
          "Make it interactive: 'You touch YOUR nose when I clap!'",
          "Try: 'I'll touch MY nose, you touch YOUR nose! Nose! (clap)'",
          "Do 6-8 different body parts.",
          "Make it active: stand up for body parts that require movement!",
          "End with: 'Where's your head?' (Head! clap) 'Where are your shoulders?' (Shoul-ders! clap-clap) Keep going down the body!"
        ],
        "examples": [
          "One syllable: head, nose, toes, arm, leg, chin, mouth, eye → (clap) + touch",
          "Two syllables: shoul-ders, el-bow, fin-gers, bel-ly, an-kle → (clap-clap) + touch",
          "Three syllables: stom-a-ch (if pronounced with 3), eye-brow-es → (clap-clap-clap) + touch",
          "Make combinations: 'Head-shoul-ders-knees-and-toes!' with clapping!"
        ],
        "watchFor": [
          "your baby touching the correct body part when you name it—showing comprehension.",
          "Your baby clapping and touching at the same time—coordinating multiple actions!",
          "Your baby anticipating favorite body parts—getting ready to touch belly or toes.",
          "Your baby laughing—especially for ticklish spots like belly and toes!"
        ],
        "parentTip": "If your baby doesn't clap yet, just touch the body part on each syllable beat—tapping creates the same rhythm awareness!",
        "funTwist": "Sing 'Head, Shoulders, Knees and Toes' but clap each syllable as you touch: 'Head (clap-touch), shoul-ders (clap-clap-touch-touch)!' Super multi-sensory!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You clapped so many syllables! You're learning how words are built!",
          "You practiced:",
          "✓ Clapping the syllables in names",
          "✓ Clapping toy and object names",
          "✓ Clapping and touching body parts",
          "✓ Hearing that words have beats inside them!"
        ],
        "reviewQuestions": [
          "Could your baby clap along with you for any words?",
          "Did your baby show recognition that some words have more claps than others?",
          "Which category was most engaging: names, toys, or body parts?",
          "Did your baby try to say any of the words while clapping?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Syllable awareness is a stepping stone to phoneme awareness (hearing individual sounds). Children who can break words into syllables find it easier to later break them into sounds—which is exactly what they need to do when learning to read and spell!"
        },
        "nextSteps": [
          "Clap syllables throughout the day: during meals ('bro-cco-li!'), during play ('race-car!'), during walks ('dog-gy!').",
          "Tomorrow we'll hunt for rhyming pairs—words that sound the same at the end!",
          "Try clapping sentences: 'I (clap) love (clap) you (clap)!' = three beats!",
          "Clap syllables in favorite songs: 'Twin-kle twin-kle lit-tle star!' (clap each syllable)"
        ]
      }
    }
  ]
},

// ---------- LESSON 43 (Week 9 Day 3) ----------
{
  "id": 43,
  "phase": "1-2",
  "quarter": 1,
  "week": 9,
  "weekIndex": 8,
  "dayInWeek": 2,
  "title": "Lesson 43: \"Rhyme Hunt\" — Find What Sounds the Same",
  "theme": "Rhymes & Repetition",
  "skillRecurrenceTags": ["rhyme-recognition", "matching", "phonological-awareness"],
  "relatedToPreviousSkills": ["same-different-week7", "matching", "sound-awareness"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Rhyme Hunt!",
      "duration": "1 min",
      "content": {
        "goal": "Find and match objects or pictures that rhyme—words that sound the same at the end. This builds your baby's ability to hear sound similarities, which is critical for reading.",
        "parentGuide": [
          "Rhyming is about hearing that the END of words sounds the same (cat/hat, bear/chair, book/hook).",
          "This is harder than it sounds! Your baby has to ignore what the words MEAN and focus only on how they SOUND.",
          "At this age, you're just introducing the concept—don't expect your baby to identify rhymes independently yet.",
          "Using real objects or clear pictures makes this concrete instead of abstract—your baby can SEE and TOUCH the rhyming pairs.",
          "This lesson combines Week 7's matching skills with this week's focus on sound patterns—building on what your baby already knows!"
        ],
        "materials": ["Pairs of objects or pictures that rhyme: cat/hat, box/socks, car/star, bear/chair, book/hook, bee/key", "Optional: print simple pictures if you don't have physical objects", "Basket or area to collect rhyming pairs"],
        "tip": "Choose rhymes with VERY clear endings—avoid near-rhymes or words that are too similar (like 'mat' and 'cat' might be confusing since they're both similar objects too).",
        "researchNote": "Bradley & Bryant (1983) - Training in rhyme recognition significantly improves later reading ability. Children who received rhyme training showed reading gains 3-4 years later compared to control groups."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Rhyming Pairs",
      "duration": "3-4 min",
      "focus": "Introduction to Rhyming Sounds",
      "researchBasis": "Goswami (1986) - Children learn rhyme through explicit teaching and repeated exposure to rhyming pairs. Hearing adults emphasize the matching sounds helps children notice the pattern.",
      "content": {
        "goal": "Show your baby pairs of objects that rhyme and emphasize that they sound the same at the end. This introduces the concept of rhyming.",
        "whatToSay": [
          "\"Look! A CAT and a HAT! They rhyme!\"",
          "\"Cat—hat! Hear that? Cat-hat! The ends sound the same!\"",
          "\"Listen: caaaat... haaaat! -at, -at! Rhyme!\"",
          "\"Here's a BEAR and a CHAIR! They rhyme too!\"",
          "\"Bear-chair! They sound the same at the end!\""
        ],
        "whatToDo": [
          "Hold up a toy cat (or picture) in one hand and a hat in the other.",
          "Say both words clearly: 'Cat! Hat!'",
          "Bring them close together: 'Cat and hat! They rhyme!'",
          "Emphasize the rhyming part: 'Caaaat... haaaat! Hear the -at sound?'",
          "Say them in a sing-song way: 'Cat-hat, cat-hat, they sound the same!'",
          "Put the rhyming pair down together.",
          "Pick up the next pair: bear and chair.",
          "Repeat the process: 'Bear! Chair! Bear-chair! Rhyme!'",
          "Do this with 3-4 rhyming pairs, always emphasizing the matching ending sound.",
          "After showing all pairs, review: 'Cat-hat rhyme! Bear-chair rhyme! Box-socks rhyme!'",
          "Use exaggerated intonation to make the rhymes obvious."
        ],
        "examples": [
          "Cat-hat: Hold cat, say 'cat,' hold hat, say 'hat,' bring together: 'Cat-hat rhyme!'",
          "Box-socks: 'Box... socks! Box-socks! Hear the -ox sound? They rhyme!'",
          "Car-star: 'Car... star! Car-star! Both say -ar at the end! Rhyme!'",
          "Book-hook: 'Book... hook! Book-hook! -ook, -ook! They match!'"
        ],
        "watchFor": [
          "your baby looking between the two objects—showing they're processing the pair.",
          "Your baby reaching for both objects—wanting to hold the rhyming pair together.",
          "Your baby smiling or showing interest when you say the rhyming words—enjoying the sound pattern.",
          "Your baby vocalizing or trying to repeat any of the words—active participation!"
        ],
        "parentTip": "Really stretch out the ending sounds: 'Caaaat... haaaat!' This exaggeration helps your baby's brain notice what's matching!",
        "funTwist": "Make the rhyming pairs 'kiss' or 'high-five' each other: 'Cat and hat are rhyme friends! They give each other high-fives!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Rhyme Matching Game",
      "duration": "4-5 min",
      "focus": "Active Rhyme Recognition",
      "researchBasis": "Fernald et al. (2006) - Active participation in matching games enhances learning more than passive listening. When children manipulate objects, they form stronger memory connections.",
      "content": {
        "goal": "Separate the rhyming pairs and help your baby match them back together by sound. This is practicing rhyme recognition actively.",
        "whatToSay": [
          "\"The rhyme pairs got separated! Let's match them!\"",
          "\"Here's a cat! Where's the word that rhymes with cat?\"",
          "\"Is it... the hat? YES! Cat-hat! They rhyme!\"",
          "\"Now here's a bear! What rhymes with bear?\"",
          "\"The chair! Bear-chair! You found the rhyme!\""
        ],
        "whatToDo": [
          "Separate all the rhyming objects—put all the 'first words' in one group and all the 'rhyming words' in another.",
          "Pick up one object from the first group: 'I have a cat!'",
          "Say it clearly and with emphasis on the ending: 'Cat! Caaaat!'",
          "Look at the second group: 'Which one rhymes with cat? Which one sounds like cat at the end?'",
          "Point to the options: 'Is it the chair? Chair... cat... no, those don't rhyme!'",
          "Point to the hat: 'Is it the hat? Hat... cat... YES! Cat-hat rhyme!'",
          "Let your baby hold both when you find the match: 'You found the rhyme pair!'",
          "Put the pair together in a special spot.",
          "Continue with each object until all pairs are matched.",
          "If your baby chooses wrong, gently redirect: 'Box... chair... hmm, let's listen. Box says -ox. Chair says -air. Different! Let's find -ox!'",
          "Celebrate every correct match enthusiastically!"
        ],
        "examples": [
          "Hold cat → test chair (not a rhyme) → test hat (rhyme!) → 'Cat-hat! Match!'",
          "Hold box → try each option saying both words slowly until you find socks → 'Box-socks! Rhyme!'",
          "Hold car → baby points to star → 'YES! Car-star! Great listening!'",
          "Hold bear → baby picks book → 'Bear... book... not quite! Bear says -ear. Book says -ook. Let's find -ear!' → find chair → 'Chair! Bear-chair!'"
        ],
        "watchFor": [
          "your baby choosing correctly more than randomly (more than 50% of the time).",
          "Your baby pausing to think before choosing—showing they're processing the sounds.",
          "Your baby bringing the matching pair together when found—understanding the matching concept.",
          "Your baby saying or approximating any of the words—active language production!"
        ],
        "parentTip": "If your baby struggles, reduce the choices! Offer just TWO options: 'Does cat rhyme with chair or hat?' Limited choices make success more likely!",
        "funTwist": "Make it a treasure hunt: 'The cat lost his rhyme friend! Can you help find the hat? We need to rescue the rhyme!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Rhyme Family Celebration",
      "duration": "3-4 min",
      "focus": "Reinforcing Rhyme Awareness",
      "researchBasis": "Metsala (1999) - Repeated exposure to rhyming words helps children build phonological representations—mental models of how words sound—which supports later decoding skills.",
      "content": {
        "goal": "Review all the rhyming pairs you found, celebrating each one and creating a 'rhyme family' display. This reinforces the pattern through repetition.",
        "whatToSay": [
          "\"Let's celebrate all our rhyme families!\"",
          "\"Here's the -at family! Cat and hat!\"",
          "\"Here's the -air family! Bear and chair!\"",
          "\"All these words are rhyme friends!\"",
          "\"They sound the same at the end!\""
        ],
        "whatToDo": [
          "Gather all the matched pairs you found.",
          "Pick up the first pair: 'Let's visit the cat and hat family!'",
          "Hold them up together: 'Cat-hat! Cat-hat! They rhyme!'",
          "Put them in a special spot on the floor.",
          "Pick up the next pair: 'Now the bear and chair family!'",
          "Hold them together: 'Bear-chair! Bear-chair! Rhyme!'",
          "Place them next to the first pair.",
          "Continue until all pairs are displayed.",
          "Point to each pair and say them rhythmically: 'Cat-hat! (point) Bear-chair! (point) Box-socks! (point)'",
          "Make it a chant: 'Cat-hat rhyme! Bear-chair rhyme! Box-socks rhyme!'",
          "Let your baby point to pairs while you say them.",
          "Clap for each rhyming pair: 'Cat-hat! (clap-clap) Bear-chair! (clap-clap)'",
          "End with: 'You found ALL the rhymes! Amazing rhyme detective!'"
        ],
        "examples": [
          "Line up all pairs in a row: 'Look at all our rhyme families!'",
          "March past each pair saying the rhyme: 'Cat-hat! Bear-chair!'",
          "Let baby hold one item from each pair while you hold the match: 'You have cat, I have hat—rhyme!'",
          "Take a picture of all the rhyme pairs together: 'Let's remember all these rhymes!'"
        ],
        "watchFor": [
          "your baby showing pride in the collection—smiling at the display.",
          "Your baby pointing to pairs when you name them—recognizing which go together.",
          "Your baby trying to say the rhyming words—showing memory and engagement.",
          "Your baby wanting to do it again—the best sign of learning!"
        ],
        "parentTip": "Keep these rhyming objects together in a basket for future play—your baby will practice matching them independently!",
        "funTwist": "Make each rhyming pair have a special handshake: cat and hat bump together, bear and chair spin around, box and socks jump!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You found rhyming pairs! You're a rhyme hunter!",
          "You practiced:",
          "✓ Hearing that words can sound the same at the end",
          "✓ Matching rhyming objects together",
          "✓ Saying rhyming words: cat-hat, bear-chair, box-socks",
          "✓ Understanding that rhymes are words that match!"
        ],
        "reviewQuestions": [
          "Could your baby successfully match any rhyming pairs?",
          "Did your baby show recognition that the word endings sounded similar?",
          "Which rhyming pair seemed easiest for your baby to understand?",
          "Did your baby try to say any of the rhyming words?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Recognizing rhymes helps your baby understand that words are made of sounds that can be manipulated and compared. This sound awareness is the foundation for learning that letters represent sounds—the key insight needed for reading!"
        },
        "nextSteps": [
          "Point out rhymes in everyday life: 'Your cup and the pup rhyme!' 'Your name and the plane rhyme!'",
          "Tomorrow we'll practice rhymes with pictures during story time!",
          "Make up silly rhyming sentences: 'The cat wore a hat!' 'The bear sat on a chair!'",
          "Read rhyming books at bedtime—Dr. Seuss books are perfect for this!"
        ]
      }
    }
  ]
},

// ---------- LESSON 44 (Week 9 Day 4) ----------
{
  "id": 44,
  "phase": "1-2",
  "quarter": 1,
  "week": 9,
  "weekIndex": 8,
  "dayInWeek": 3,
  "title": "Lesson 44: \"Sing and Point\" — Visual Rhyme Link",
  "theme": "Rhymes & Repetition",
  "skillRecurrenceTags": ["rhyme-awareness", "visual-connection", "vocabulary-building"],
  "relatedToPreviousSkills": ["rhyme-recognition", "picture-identification", "pointing-skills"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Visual Rhyme Time!",
      "duration": "1 min",
      "content": {
        "goal": "Connect rhyming words to pictures by pointing to images while singing or saying rhymes. This links the sound of rhymes to visual representations, building multi-sensory learning.",
        "parentGuide": [
          "Yesterday your baby matched physical objects that rhyme. Today we're adding pictures—a more abstract representation.",
          "Pointing to pictures while saying words helps your baby connect spoken language to visual symbols—the exact skill needed for reading!",
          "Using familiar songs and rhymes with pictures makes learning feel like play, not work.",
          "This activity strengthens the connection between hearing rhymes, seeing pictures, and understanding meaning—three different brain pathways working together.",
          "Picture books with rhymes are powerful learning tools because they engage visual, auditory, and language centers simultaneously."
        ],
        "materials": ["Picture books with rhyming text (Dr. Seuss, nursery rhyme books, or simple rhyming board books)", "Optional: quick drawings of rhyming pairs on paper (sketch a cat and a hat, a moon and a spoon, etc.)", "Your voice and enthusiasm"],
        "tip": "Choose books with BIG, clear pictures where each rhyming word has an obvious illustration—this makes pointing easier and more meaningful.",
        "researchNote": "Bus et al. (1995) - Shared picture book reading with rhymes significantly boosts phonological awareness. Children who experience frequent rhyme-based reading show better pre-reading skills."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Point to the Rhyme Pictures",
      "duration": "3-4 min",
      "focus": "Connecting Rhyming Words to Images",
      "researchBasis": "Sénéchal & LeFevre (2002) - Interactive reading where children actively participate (pointing, labeling) is more effective for literacy development than passive listening.",
      "content": {
        "goal": "Read or sing a rhyme and point to the pictures that match the rhyming words. This shows your baby that words in rhymes connect to things they can see.",
        "whatToSay": [
          "\"Let's read a rhyming book! Listen for the rhymes!\"",
          "\"'The cat in the hat!' Where's the cat? Point to the cat!\"",
          "\"Where's the hat? There it is! Cat-hat rhyme!\"",
          "\"'The cow jumped over the moon!' Point to the moon!\"",
          "\"Moon-spoon! Let's find them both!\""
        ],
        "whatToDo": [
          "Open a rhyming picture book or lay out simple drawings you've sketched.",
          "Point to the first picture: 'Look! Here's a cat!'",
          "Read or say the rhyming text: 'The cat in the hat!'",
          "Point to the cat: 'CAT!' (emphasize the word).",
          "Point to the hat: 'HAT!' (emphasize the word).",
          "Bring your finger back and forth: 'Cat-hat! Cat-hat! They rhyme!'",
          "Help your baby's finger point to the cat: 'You point to the cat!'",
          "Then help them point to the hat: 'Now point to the hat!'",
          "Continue with the next rhyming pair in the book.",
          "Always say both rhyming words clearly and point to both pictures.",
          "Encourage your baby to point with you or independently.",
          "Repeat the rhyming words multiple times while pointing: 'Mouse-house, mouse-house, mouse-house!'",
          "Make it interactive: 'Can YOU find the moon? Where's the spoon?'"
        ],
        "examples": [
          "Cat in the Hat page: Point to cat, say 'cat,' point to hat, say 'hat,' repeat 'cat-hat rhyme!'",
          "Nursery rhyme: 'Hey diddle diddle, the cat and the fiddle!' Point to cat, then fiddle: 'Cat-fiddle!'",
          "Moon/spoon page: 'The cow jumped over the moon with a spoon!' Point to both.",
          "House/mouse: 'There's a mouse in the house!' Point to mouse, point to house, repeat."
        ],
        "watchFor": [
          "your baby's eyes following your pointing finger—tracking the visual connection.",
          "Your baby reaching toward pictures—showing they want to participate.",
          "Your baby pointing (with help or independently) to the correct pictures.",
          "Your baby vocalizing when you point to pictures—trying to name them!"
        ],
        "parentTip": "Use your baby's hand to help them point if they're not doing it themselves yet—the physical motion helps create the connection!",
        "funTwist": "Make the pictures 'talk' to each other: 'Cat says: Hat, you rhyme with me! Hat says: Yes we do, cat! We're rhyme buddies!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sing and Find Rhyme Game",
      "duration": "4-5 min",
      "focus": "Active Rhyme Hunting in Songs",
      "researchBasis": "Gillon (2004) - Combining music with phonological awareness activities enhances learning because melody helps children remember patterns and sequences.",
      "content": {
        "goal": "Sing familiar rhyming songs and have your baby find the pictures of the rhyming words as you sing. This makes rhyme recognition interactive and fun.",
        "whatToSay": [
          "\"Let's sing and find the pictures!\"",
          "\"Twinkle, twinkle, little STAR! Where's the star? Find it!\"",
          "\"Up above the world so HIGH! Where's high? Point up!\"",
          "\"Jack and Jill went up the HILL! Find the hill!\"",
          "\"Can you find the rhymes while we sing?\""
        ],
        "whatToDo": [
          "Lay out 4-6 quick drawings or objects for rhyming items from familiar songs (draw a star, a hill, water, a crown — stick figures are perfect!).",
          "Start singing 'Twinkle, Twinkle': 'Twinkle, twinkle, little star!'",
          "Pause and ask: 'Where's the star? Can you find the star?'",
          "Let your baby point to or pick up the star picture.",
          "Celebrate: 'Yes! Star! There's the star!'",
          "Continue singing: 'Up above the world so high!'",
          "Pause: 'High! Point up high!' (gesture upward).",
          "Even if there's no picture for 'high,' the gesture connects the word to meaning.",
          "Try 'Jack and Jill': 'Jack and Jill went up the hill!'",
          "Pause: 'Find the hill! Where's the hill picture?'",
          "Continue: 'To fetch a pail of water!'",
          "Pause: 'Water! Find the water!'",
          "Do 3-4 different songs or rhymes, pausing to find the pictures.",
          "Make it faster as your baby gets better: sing a full line, then quickly find the rhyme picture!"
        ],
        "examples": [
          "Twinkle Twinkle: pause at 'star' and 'are,' let baby find star picture",
          "Jack and Jill: pause at 'hill/Jill' and 'down/crown,' find relevant pictures",
          "Baa Baa Black Sheep: pause at 'wool/full' and 'bags,' find sheep and bag pictures",
          "Row Row Row Your Boat: pause at 'boat/float' and 'stream/dream,' find boat picture"
        ],
        "watchFor": [
          "your baby recognizing pictures faster as you repeat songs—showing memory!",
          "Your baby reaching for the correct picture before you ask—anticipating the rhyme.",
          "Your baby trying to sing along, especially on the rhyming words.",
          "Your baby showing excitement when they find the right picture—celebrating their success!"
        ],
        "parentTip": "Sing SLOWLY at first, giving your baby time to process and find the picture before you move on to the next line!",
        "funTwist": "Let your baby be the 'DJ': they pick which song to sing next by choosing a drawing! 'You picked the star! Let's sing Twinkle Twinkle!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Make Your Own Rhyme Book",
      "duration": "3-4 min",
      "focus": "Creating Personal Rhyme Connections",
      "researchBasis": "Neuman & Roskos (1993) - Children who create their own literacy materials (books, labels, signs) show stronger engagement with reading and writing activities.",
      "content": {
        "goal": "Create a simple rhyme book together by putting rhyming pictures on pages (or just arranging them). This makes learning personal and memorable.",
        "whatToSay": [
          "\"Let's make our own rhyme book!\"",
          "\"This page has cat and hat! They rhyme!\"",
          "\"Let's put them together on this page!\"",
          "\"Next page: bear and chair! Rhyme page!\"",
          "\"Look at our rhyme book! We made it!\""
        ],
        "whatToDo": [
          "Get 3-4 pieces of paper or use a small notebook.",
          "Say: 'We're going to make a special rhyme book!'",
          "On the first page, place or tape pictures of a rhyming pair (cat and hat).",
          "Say: 'This is the cat-hat page! Cat and hat rhyme!'",
          "Point to each: 'Cat! Hat! Cat-hat!'",
          "Let your baby help place the pictures or touch them on the page.",
          "On the next page, add another rhyming pair (bear and chair).",
          "Say: 'This is the bear-chair page! These rhyme too!'",
          "Continue with 2-3 more rhyming pairs.",
          "On each page, say the rhyme multiple times: 'Box-socks, box-socks, box-socks!'",
          "When finished, 'read' the book together: flip through each page saying the rhymes.",
          "Let your baby turn the pages and point to the pictures.",
          "Keep the book accessible for your baby to 'read' independently later!",
          "Add more pages over time as you discover new rhymes together."
        ],
        "examples": [
          "Page 1: Cat and hat pictures → label it 'Cat-Hat Rhyme!'",
          "Page 2: Moon and spoon pictures → 'Moon-Spoon Rhyme!'",
          "Page 3: Star and car pictures → 'Star-Car Rhyme!'",
          "Back page: Your baby's photo → 'This book belongs to [baby's name]!'"
        ],
        "watchFor": [
          "your baby wanting to help place or arrange the pictures—showing ownership of the project.",
          "Your baby touching the pictures on each page—engaging with the book.",
          "Your baby trying to 'read' the book by pointing and vocalizing—pretend reading!",
          "Your baby returning to the book later on their own—showing it's become meaningful!"
        ],
        "parentTip": "Don't worry about making it perfect or fancy! Even simple pictures taped to paper create a powerful personal learning tool!",
        "funTwist": "Add your baby's photo on the last page with a rhyme about them: 'And here's sweet [Maya], who learned about rhyma!' (even if it's silly!)"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You connected rhymes to pictures! You're a visual rhyme learner!",
          "You practiced:",
          "✓ Pointing to pictures while hearing rhyming words",
          "✓ Finding rhyme pictures during songs",
          "✓ Making your very own rhyme book",
          "✓ Seeing that words and pictures go together!"
        ],
        "reviewQuestions": [
          "Could your baby point to pictures when you said rhyming words?",
          "Did your baby show recognition of familiar rhymes from songs?",
          "Was your baby engaged in creating the rhyme book?",
          "Did your baby try to 'read' the rhyme book after making it?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Connecting spoken words to pictures is the bridge between oral language and reading. When your baby points to a picture while hearing the word, they're learning that symbols (pictures now, letters later) represent spoken language—the fundamental concept of reading!"
        },
        "nextSteps": [
          "Keep the rhyme book in a place where your baby can reach it and 'read' it independently.",
          "Tomorrow we'll create a rhyme box—collecting objects that rhyme!",
          "Point to pictures in ALL books while reading: 'There's the dog! Dog! Woof-woof!'",
          "Add new pages to your rhyme book whenever you discover new rhyming pairs together!"
        ]
      }
    }
  ]
},

// ---------- LESSON 45 (Week 9 Day 5) ----------
{
  "id": 45,
  "phase": "1-2",
  "quarter": 1,
  "week": 9,
  "weekIndex": 8,
  "dayInWeek": 4,
  "title": "Lesson 45: \"Rhyme Box\" — Collect Objects That Rhyme",
  "theme": "Rhymes & Repetition",
  "skillRecurrenceTags": ["rhyme-recognition", "collecting", "categorization"],
  "relatedToPreviousSkills": ["rhyme-matching-week9", "sorting-week7", "object-recognition"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Rhyme Box!",
      "duration": "1 min",
      "content": {
        "goal": "Create a special collection of objects that rhyme with each other. This makes rhymes tangible and gives your baby hands-on materials to practice with all week long.",
        "parentGuide": [
          "Having a permanent rhyme collection gives your baby repeated practice opportunities—they can explore the rhymes independently during play time.",
          "Collecting and organizing things is satisfying for toddlers—it taps into their natural desire to sort and categorize.",
          "This activity wraps up Week 9 by creating a lasting resource you can use for weeks to come.",
          "Physical objects are easier for this age to understand than pictures—they can hold, move, and manipulate rhyming items.",
          "Every time your baby plays with the rhyme box, they're reinforcing phonological awareness without formal 'lesson time'!"
        ],
        "materials": ["A box, basket, or container (this becomes the special 'Rhyme Box')", "Small rhyming objects you can find around the house: toy car/star, small book/hook, box/socks, toy cat/hat, cup/pup (stuffed dog)", "Optional: make simple items (draw a star if you don't have one, use a cup and draw a pup)"],
        "tip": "Start with 4-6 rhyming pairs—you can always add more over time as your baby learns new rhymes!",
        "researchNote": "Lawhon & Cobb (2002) - Children who have access to literacy-rich materials at home show faster development of phonological awareness skills. A rhyme box is a perfect example of bringing literacy into play."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Hunt for Rhyming Objects",
      "duration": "3-4 min",
      "focus": "Collecting Rhyme Pairs Around the House",
      "researchBasis": "Neuman & Roskos (1997) - Treasure hunts and collecting activities engage children's natural curiosity and make abstract concepts like rhyming feel concrete and purposeful.",
      "content": {
        "goal": "Walk around your house together and hunt for objects that rhyme. This makes learning active and adventurous!",
        "whatToSay": [
          "\"We're going on a rhyme hunt! Let's find things that rhyme!\"",
          "\"I need a cat! Can you help me find a toy cat?\"",
          "\"Now we need something that rhymes with cat! A hat!\"",
          "\"You found it! Cat-hat rhyme! Into the rhyme box!\"",
          "\"What else can we find that rhymes?\""
        ],
        "whatToDo": [
          "Show your baby the empty box or basket: 'This is our special RHYME BOX!'",
          "Say: 'Let's fill it with things that rhyme! Come on!'",
          "Walk to your baby's toy area: 'Do you have a toy car?'",
          "Find or help your baby find a toy car: 'Car! We found a car!'",
          "Say: 'Now we need something that rhymes with car! What rhymes with car?'",
          "Look around: 'A STAR! Star-car! Do we have a star?'",
          "Find a star toy, sticker, or even draw one quickly on paper.",
          "Put both in the rhyme box: 'Car and star in the rhyme box!'",
          "Continue hunting: 'Let's find socks! Do you have socks?'",
          "Find socks: 'Now what rhymes with socks? A BOX!'",
          "Find a small box (jewelry box, toy box, or make one from paper).",
          "Add them to the rhyme box: 'Box-socks rhyme!'",
          "Hunt for 4-6 rhyming pairs total, celebrating each find!",
          "Make it exciting: 'We're rhyme detectives! Let's search!'"
        ],
        "examples": [
          "Toy room: Find toy cat and baby's hat → 'Cat-hat! Into the box!'",
          "Kitchen: Find cup and stuffed pup (dog) → 'Cup-pup! Rhyme found!'",
          "Bedroom: Find book and hook (from closet or make paper one) → 'Book-hook!'",
          "Living room: Find rug and mug → 'Rug-mug! They rhyme!'"
        ],
        "watchFor": [
          "your baby actively searching for items—showing engagement in the hunt.",
          "Your baby carrying items to the rhyme box—understanding the collecting concept.",
          "Your baby showing excitement when you find a rhyming pair—celebrating the discovery.",
          "Your baby trying to say the rhyming words as you find them."
        ],
        "parentTip": "If you can't find a perfect object, get creative! Draw a simple picture, use a photo from a magazine, or just pretend an object is something else!",
        "funTwist": "Make it a race: 'Quick! Can we find all the rhymes before the timer beeps?' Set a fun timer for 3 minutes!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Introduce the Rhyme Box Game",
      "duration": "4-5 min",
      "focus": "Learning to Play with Rhyme Pairs",
      "researchBasis": "Christie & Roskos (2006) - Play-based literacy activities where children manipulate materials independently support learning because children can practice at their own pace and interest level.",
      "content": {
        "goal": "Show your baby how to play with the rhyme box—pulling out pairs, matching them, and saying the rhymes together. This teaches the 'rules' of rhyme box play.",
        "whatToSay": [
          "\"Let's see what's in our rhyme box!\"",
          "\"Pull one out! What did you get?\"",
          "\"You got the cat! Now find what rhymes with cat!\"",
          "\"The hat! Yes! Cat-hat rhyme! Good matching!\"",
          "\"Let's try another one!\""
        ],
        "whatToDo": [
          "Sit with your baby and the rhyme box between you.",
          "Say: 'Let's play the rhyme box game!'",
          "Demonstrate first: reach in and pull out one item (the cat).",
          "Hold it up: 'I got a cat! Caaaat!'",
          "Look in the box: 'Now I need to find what rhymes with cat!'",
          "Search through the box: 'Is it the box? Cat-box... no, those don't rhyme!'",
          "Find the hat: 'Here it is! The hat! Cat-hat! They rhyme!'",
          "Put the pair together outside the box.",
          "Say: 'Now your turn! You pick one!'",
          "Let your baby reach in and pull out an object.",
          "Name it: 'You got the car! What rhymes with car?'",
          "Help them search: 'Let's look for something that rhymes with car!'",
          "When they find it (or you help): 'The star! Car-star! You found the rhyme!'",
          "Continue taking turns until all pairs are matched.",
          "Each time, emphasize the rhyming words: 'Box-socks, box-socks, box-socks!'",
          "After all are matched, put them back in the box: 'All done! Let's play again!'"
        ],
        "examples": [
          "Baby pulls out socks → help find box → 'Box-socks! Match!' → place pair together.",
          "You pull out book → ask baby to find hook → 'Book-hook! Rhyme!' → celebrate together.",
          "Baby pulls out cup → searches for pup → finds it themselves → 'YOU found the rhyme! Cup-pup!'",
          "Mix them all up and start over: 'Let's match them all again!'"
        ],
        "watchFor": [
          "your baby understanding the turn-taking structure—waiting for their turn to pull something out.",
          "Your baby successfully finding matching pairs (with or without help).",
          "Your baby wanting to repeat the game—showing enjoyment!",
          "Your baby saying any of the rhyming words—active language use!"
        ],
        "parentTip": "Let your baby succeed! If they're struggling to find the match, guide them: 'Is it this one? Is it this one? YES, this one!' Keep it positive!",
        "funTwist": "Hide rhyme pairs around the room: 'The cat is hiding! Can you find it? Now find its rhyme friend the hat!' Turn it into hide-and-seek!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Rhyme Box Song",
      "duration": "3-4 min",
      "focus": "Creating a Rhyme Box Ritual",
      "researchBasis": "Ritchie & Bates (2013) - Consistent, repeated literacy routines strengthen phonological awareness. When activities become predictable rituals, children engage more deeply.",
      "content": {
        "goal": "Make up a simple song about the rhyme box and sing it every time you play. This creates a special ritual and makes the activity memorable.",
        "whatToSay": [
          "\"Let's sing the rhyme box song!\"",
          "\"What's in the box? What's in the box? Let's find the rhymes, let's find the rhymes!\"",
          "\"Cat and hat! Cat and hat! They rhyme together, they rhyme together!\"",
          "\"Box and socks! Box and socks! They rhyme together, they rhyme together!\"",
          "\"We love rhymes! We love rhymes!\""
        ],
        "whatToDo": [
          "Create a simple song to the tune of 'Frère Jacques' (or any tune you like).",
          "Hold the rhyme box and sing: 'What's in the box? What's in the box? Let's find out! Let's find out!'",
          "Pull out one item while singing: 'Here's a cat! Here's a cat!'",
          "Pull out its match: 'Here's a hat! Here's a hat!'",
          "Bring them together: 'Cat and hat rhyme! Cat and hat rhyme!'",
          "Continue with each pair, singing as you reveal them.",
          "Make the song interactive: 'YOU pull out one! What did you find?'",
          "Sing about what your baby found: 'You found the car! You found the car! Now find the star! Now find the star!'",
          "End the song: 'All the rhymes! All the rhymes! In our box! In our box!'",
          "Clap and cheer: 'We sang the rhyme box song!'",
          "Repeat the song several times—repetition helps your baby learn it!",
          "Use this song every time you play with the rhyme box from now on."
        ],
        "examples": [
          "Opening song: 'Rhyme box time! (clap-clap) Rhyme box time! (clap-clap) Let's find rhymes! Let's find rhymes!'",
          "During play: 'Cat and hat! (hold up both) They sound the same! (bring together) Cat-hat-cat-hat! (alternate holding up each) Rhyming game!'",
          "Closing song: 'Put them back! Put them back! In the box! In the box! We'll play tomorrow! We'll play tomorrow! Rhyme box fun! Rhyme box fun!'",
          "Celebration: 'We found rhymes! (clap) We found rhymes! (clap) We're so smart! (point to baby) We're so smart! (point to self)'"
        ],
        "watchFor": [
          "your baby bouncing or swaying to the song—feeling the rhythm.",
          "Your baby trying to sing along—even just humming or making sounds!",
          "Your baby anticipating parts of the song—getting excited before favorite parts.",
          "Your baby starting to associate the song with the rhyme box—the ritual is forming!"
        ],
        "parentTip": "Sing the same song the same way every time—the predictability helps your baby learn and eventually 'sing' along!",
        "funTwist": "Let your baby 'conduct' the song: they hold a spoon like a baton and wave it while you sing! They're the rhyme box maestro!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You created a rhyme box! You have rhymes to play with anytime!",
          "You practiced:",
          "✓ Hunting for objects that rhyme around your house",
          "✓ Playing the rhyme matching game",
          "✓ Singing the rhyme box song",
          "✓ Building a special collection for learning!"
        ],
        "reviewQuestions": [
          "Did your baby enjoy hunting for rhyming objects?",
          "Could your baby match any rhyme pairs from the box?",
          "Did your baby respond to the rhyme box song?",
          "Will you keep the rhyme box accessible for daily play?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Having hands-on materials for phonological awareness practice means your baby can explore rhymes independently during play. This self-directed practice is powerful—children learn best when they're in control and having fun!"
        },
        "nextSteps": [
          "Keep the rhyme box in an accessible spot and play with it daily this week!",
          "Next week starts Sound in Stories—bringing all these sound skills into storytelling!",
          "Add new rhyme pairs to the box as you discover them: 'Oh! A bee and a key rhyme! Let's add them!'",
          "This completes Week 9—your baby has explored rhymes through games, pictures, songs, and now a hands-on collection!"
        ]
      }
    }
  ]
},

// ---------- LESSON 46 (Week 10 Day 1) ----------
{
  "id": 46,
  "phase": "1-2",
  "quarter": 1,
  "week": 10,
  "weekIndex": 9,
  "dayInWeek": 0,
  "title": "Lesson 46: \"Story Time Echoes\" — Repeat Key Words",
  "theme": "Sound in Stories",
  "skillRecurrenceTags": ["repetition", "vocabulary", "listening-comprehension"],
  "relatedToPreviousSkills": ["echo-play-week3", "quiet-echoes-week2", "story-engagement"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Time Echoes!",
      "duration": "1 min",
      "content": {
        "goal": "Read stories together and pause to let your baby repeat (echo) important words. This builds vocabulary, listening skills, and active participation in reading.",
        "parentGuide": [
          "Interactive reading where children participate is much more effective than just listening passively—it keeps their attention and helps them learn.",
          "Echoing words helps your baby practice pronunciation and builds their speaking vocabulary.",
          "Repetition is KEY at this age—hearing and saying the same word multiple times in context helps it stick in memory.",
          "This week focuses on bringing sound awareness INTO story time—connecting all the skills from previous weeks to books.",
          "Active participation in reading creates positive associations with books, which leads to a lifelong love of reading!"
        ],
        "materials": ["2-3 favorite picture books with repetitive text or clear key words", "Books with simple, engaging pictures", "Comfortable reading spot"],
        "tip": "Choose books with repetitive phrases or predictable patterns—these are perfect for echo reading (like 'Brown Bear, Brown Bear' or 'Dear Zoo').",
        "researchNote": "Whitehurst et al. (1988) - Dialogic reading techniques, where adults pause for children to fill in words, significantly improve vocabulary and comprehension compared to traditional reading."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Pause and Echo Reading",
      "duration": "3-4 min",
      "focus": "Teaching Echo Participation",
      "researchBasis": "Sénéchal et al. (1995) - When parents pause during reading to let children fill in predictable words, children show faster vocabulary growth and better story comprehension.",
      "content": {
        "goal": "Read a familiar book but pause before key words, giving your baby a chance to say them. This teaches that reading is a conversation, not just listening.",
        "whatToSay": [
          "\"Let's read together! You can help me!\"",
          "\"Brown bear, brown bear, what do you see? I see a red... (pause)\"",
          "\"BIRD! Yes! Red bird! You said it!\"",
          "\"The dog says... (pause)\"",
          "\"WOOF! Yes! The dog says woof!\""
        ],
        "whatToDo": [
          "Choose a book your baby knows well—familiarity makes echoing easier.",
          "Sit with your baby where they can see the pictures clearly.",
          "Start reading normally: 'Brown bear, brown bear, what do you see?'",
          "When you get to a predictable word, slow down and pause: 'I see a red...'",
          "Look at your baby expectantly, giving them 3-5 seconds to fill in the word.",
          "If they say it: 'BIRD! Yes! Red bird! Great job!'",
          "If they don't say it, that's okay—YOU say it enthusiastically: 'BIRD! Red bird!'",
          "Continue reading and pause at every key word or repeated phrase.",
          "Point to the picture when you pause: this helps your baby connect word to image.",
          "Accept ANY attempt—if they say 'bir' instead of 'bird,' celebrate it!",
          "Use exaggerated expression to make pauses obvious and exciting.",
          "Read the same book 2-3 times—each time, your baby will participate more!"
        ],
        "examples": [
          "'Dear Zoo' book: 'They sent me a... (pause, point to elephant) ...ELEPHANT!' Wait for baby to say it.",
          "'Goodnight Moon': 'Goodnight... (pause) ...MOON!' Let baby fill in.",
          "Any animal book: 'The cow says... (pause) ...MOO!' Pause for animal sounds.",
          "'The Very Hungry Caterpillar': 'He was still... (pause) ...HUNGRY!' Let baby say 'hungry.'"
        ],
        "watchFor": [
          "your baby watching your mouth as you pause—showing they're anticipating the word.",
          "Your baby making any sound during the pause—attempting to fill in the word!",
          "Your baby saying the word before you pause—they know what's coming!",
          "Your baby pointing to the picture during the pause—showing they know what word belongs there."
        ],
        "parentTip": "The PAUSE is critical! Give your baby enough silence to process and respond. Count to 3 in your head before moving on.",
        "funTwist": "Make mistakes on purpose: 'The dog says... MEOW!' Baby will likely react! 'Oh no! Dogs don't say meow! What DO dogs say? WOOF!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Key Word Collection",
      "duration": "4-5 min",
      "focus": "Identifying and Repeating Important Words",
      "researchBasis": "Hindman et al. (2008) - Explicit focus on vocabulary words during reading (repeating them, defining them, using them) leads to better word learning than reading without this emphasis.",
      "content": {
        "goal": "As you read, stop and highlight 2-3 important words, repeating them multiple times. This builds deep understanding of key vocabulary.",
        "whatToSay": [
          "\"Listen for the special word: HUNGRY!\"",
          "\"The caterpillar is hungry. Hungry, hungry, hungry!\"",
          "\"Can you say hungry? HUN-GRY!\"",
          "\"What else is hungry? Is a bear hungry? Is a bird hungry?\"",
          "\"Let's say it together: HUNGRY!\""
        ],
        "whatToDo": [
          "Before reading, choose 2-3 important words from the story (words that repeat or are central to the story).",
          "Start reading the book.",
          "When you reach the first key word, STOP and make it special: 'Here's our special word!'",
          "Say the word clearly: 'HUNGRY. The caterpillar is HUNGRY.'",
          "Point to the picture: 'See? He's hungry—he wants to eat!'",
          "Say it again with your baby: 'Say it with me: HUN-GRY!'",
          "Clap the syllables: 'Hun (clap) gry (clap)! Two beats!'",
          "Make it meaningful: 'Are YOU ever hungry? Yes! Hungry means you want food!'",
          "Continue reading until the next key word.",
          "Repeat the process with each key word—stop, highlight, repeat, connect to meaning.",
          "At the end of the book, review all the key words: 'Our special words today were: hungry, big, and butterfly!'",
          "Repeat each word 3-4 times throughout the reading."
        ],
        "examples": [
          "Key word: 'BIG' → 'The bear is BIG! BIG! Say big! BIG! What else is big? Daddy is big! The house is big!'",
          "Key word: 'JUMP' → 'The frog can JUMP! JUMP! Let's jump! (stand and jump) JUMP-JUMP!'",
          "Key word: 'NIGHT' → 'It's NIGHT time! NIGHT! Dark outside! The moon comes out at NIGHT!'",
          "Key word: 'LOVE' → 'I LOVE you! LOVE! Mama loves baby! LOVE means care about someone very much!'"
        ],
        "watchFor": [
          "your baby repeating the key words when you emphasize them.",
          "Your baby showing recognition when the word appears again later in the book—pointing or vocalizing.",
          "Your baby using the word in another context after reading—transferring the learning!",
          "Your baby remembering the key words the next time you read the same book."
        ],
        "parentTip": "Choose words that are meaningful and useful—not just random words. Pick words your baby can use in daily life!",
        "funTwist": "Create actions for key words: 'Every time we say HUNGRY, rub your belly! Every time we say JUMP, bounce up and down!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Echo the Last Word",
      "duration": "3-4 min",
      "focus": "Predictable Participation Pattern",
      "researchBasis": "Snow & Ninio (1986) - Predictable reading routines where children know when and how to participate build confidence and encourage more verbal engagement.",
      "content": {
        "goal": "Read sentences or phrases and pause for your baby to echo the very last word. This creates a consistent, predictable participation pattern.",
        "whatToSay": [
          "\"I'll read, you say the last word!\"",
          "\"The dog is... BIG!\"",
          "\"The cat is... SMALL!\"",
          "\"The bird can... FLY!\"",
          "\"You're doing it! You're reading with me!\""
        ],
        "whatToDo": [
          "Choose a simple book or make up your own sentences about the pictures.",
          "Explain the game: 'I'll say most of the words, but YOU say the last word!'",
          "Read slowly: 'The dog is...' (pause and look expectantly at baby).",
          "Emphasize your tone going UP at the end, showing something is coming.",
          "Wait for your baby to say 'big!' (or any attempt).",
          "If they say it: 'BIG! Yes! The dog is big!'",
          "If they don't say it, supply it: 'BIG! The dog is big! Let's try another one!'",
          "Continue with multiple sentences, always pausing at the end.",
          "Use simple descriptive sentences: 'The sun is... (yellow)' 'The apple is... (red)' 'The baby is... (happy)'",
          "Point to the picture to give a visual clue for what word comes next.",
          "After several successful echoes, try a sentence with a tricky ending to keep it interesting.",
          "Celebrate every attempt: 'You're such a good reader! You helped me read the book!'"
        ],
        "examples": [
          "Simple: 'The ball is... (red)' 'The car goes... (fast)' 'The baby is... (happy)'",
          "Actions: 'The dog is... (running)' 'The bird is... (flying)' 'The fish is... (swimming)'",
          "Sounds: 'The cow says... (moo)' 'The car says... (beep)' 'The duck says... (quack)'",
          "Numbers: 'I see... (one) ...apple' 'I see... (two) ...dogs' 'I see... (three) ...birds'"
        ],
        "watchFor": [
          "your baby understanding the pattern—consistently trying to fill in the last word.",
          "Your baby getting faster at responding—showing they're comfortable with the routine.",
          "Your baby saying words more clearly with practice—improving pronunciation!",
          "Your baby starting to anticipate the pause—leaning forward or opening mouth before you finish!"
        ],
        "parentTip": "Make your voice go UP at the pause to signal that your baby should fill in the word—the rising tone is a cue!",
        "funTwist": "Trade roles: 'Now YOU read to ME! You say the sentence, I'll say the last word!' Help them say 'The cat is...' and you say 'orange!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You helped read the story! You're a reading partner!",
          "You practiced:",
          "✓ Echoing words during story time",
          "✓ Learning special key words from books",
          "✓ Filling in the last word of sentences",
          "✓ Being an active reader, not just a listener!"
        ],
        "reviewQuestions": [
          "Did your baby successfully echo any words during reading?",
          "Which key words seemed to stick with your baby?",
          "Could your baby follow the 'last word' pattern?",
          "Did this interactive approach keep your baby engaged longer than usual?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Active participation in reading builds comprehension, vocabulary, and confidence. When your baby helps 'read' the story by filling in words, they're learning that reading is interactive communication—not just passively receiving information—which is exactly the mindset needed for becoming a strong reader!"
        },
        "nextSteps": [
          "Use echo reading every time you read books—make it your standard reading style!",
          "Tomorrow we'll explore using different character voices during reading!",
          "Try echo reading with songs too: 'Twinkle twinkle little... (STAR!)'",
          "Let your baby pick books to read—choice increases engagement and participation!"
        ]
      }
    }
  ]
},

// ---------- LESSON 47 (Week 10 Day 2) ----------
{
  "id": 47,
  "phase": "1-2",
  "quarter": 1,
  "week": 10,
  "weekIndex": 9,
  "dayInWeek": 1,
  "title": "Lesson 47: \"Animal Story Voices\" — Use Distinct Tones for Each Character",
  "theme": "Sound in Stories",
  "skillRecurrenceTags": ["character-voices", "pitch-variation", "story-comprehension"],
  "relatedToPreviousSkills": ["animal-sounds-week1", "pitch-exploration-week3", "story-echoes-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Voices!",
      "duration": "1 min",
      "content": {
        "goal": "Give each animal character a different voice so your baby can follow who is talking in a story.",
        "parentGuide": [
          "When you use high voices for small animals and low voices for big animals, your baby's brain learns patterns.",
          "Different voices help your baby track different speakers—a skill they'll need when reading dialogue later.",
          "This brings back animal sounds from Week 1, but now inside a story. Your baby is ready for this next step.",
          "Kids who hear expressive reading early become better readers. This is real practice for reading skills.",
          "The sillier and more exaggerated your voices, the more your baby will pay attention and remember them."
        ],
        "materials": ["A simple picture book with animal characters", "Your voice (the best tool!)"],
        "tip": "Go big with your voices. High, squeaky, low, grumbly—exaggeration helps babies learn.",
        "researchNote": "Fernald (1989) - Exaggerated tones and pitches grab babies' attention and help them learn sounds faster."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Voice Characters",
      "duration": "3-4 min",
      "focus": "Assigning Different Voices to Different Animals",
      "researchBasis": "DeCasper & Fifer (1980) - Babies notice pitch changes from birth; distinct voices help them follow stories.",
      "content": {
        "goal": "Show your baby animal characters and give each one a special voice.",
        "whatToSay": [
          "\"Look, it's a tiny mouse! Listen: 'Hi! Hi!' (squeaky voice)\"",
          "\"Now look—a big bear! Bears sound like this: 'HELLO! HELLO!' (deep voice)\"",
          "\"A duck goes: 'Quack quack!' (medium voice)\"",
          "\"Each animal sounds different. Can you hear?\"",
          "\"The tiny mouse: 'Hello friends!' (tiny squeak)\"",
          "\"The HUGE bear: 'I'M SO BIG!' (big, booming voice)\""
        ],
        "whatToDo": [
          "Open your picture book and point to the first animal.",
          "Say: 'This animal has a special voice. Listen!'",
          "Use a VERY HIGH, squeaky voice for small animals.",
          "Use a VERY LOW, deep voice for big animals.",
          "Use a MEDIUM, friendly voice for medium animals.",
          "Make your whole body match the voice—shoulders up for high, lean down for low.",
          "Let your baby watch your face as you make each voice.",
          "Repeat each animal's voice 2-3 times so baby hears the pattern clearly.",
          "Ask: 'Can you make the mouse voice?' or 'Show me the bear voice!'",
          "Celebrate any attempt—even a smile is success."
        ],
        "examples": [
          "Mouse: Squeeze your voice super tiny, almost a whisper. 'Eek eek eek!'",
          "Bear: Drop your voice deep and growly. 'ROOOAAARRR! Hello friends!'",
          "Duck: Use a bouncy, quacky middle voice. 'Quack-quack-quack-quack!'"
        ],
        "watchFor": [
          "Your baby turning to look when you change voices.",
          "Your baby smiling or giggling at the silly voices.",
          "Your baby trying to copy the voice (even if it doesn't sound right).",
          "Your baby pointing to animals when you use their voice."
        ],
        "parentTip": "Perfect voices don't matter. The bigger and sillier you are, the more your baby learns.",
        "funTwist": "Have the animals talk directly to your baby: 'Hi baby! I'm the tiny mouse! What's your name?' (in squeaky voice)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Story with Character Voices",
      "duration": "4-5 min",
      "focus": "Reading a Story Using Different Voices",
      "researchBasis": "Bus & van IJzendoorn (1995) - Expressive voices during story reading increase vocabulary and story understanding in toddlers.",
      "content": {
        "goal": "Read a simple animal story, switching voices every time a different animal speaks.",
        "whatToSay": [
          "\"This story has three friends: a mouse, a duck, and a bear. Each one sounds different!\"",
          "\"Listen to the mouse: 'Hello!' (high squeaky voice)\"",
          "\"Now the duck: 'Quack!' (medium quack voice)\"",
          "\"And the bear: 'Welcome, friends!' (deep bear voice)\"",
          "\"Can you hear which animal is talking?\"",
          "\"The mouse talks again: 'I'm so happy!' (squeaky)\"",
          "\"Did you notice the mouse is squeaky? The bear is deep?\""
        ],
        "whatToDo": [
          "Choose a simple picture book with 2-3 animal characters (like 'Dear Zoo' or 'Click, Clack, Moo').",
          "Before starting, show baby each character and remind them of their voice.",
          "As you read, change your voice EVERY TIME a different character speaks.",
          "Don't just read the words—perform! Make your face match the voice.",
          "Pause after each character speaks: 'Did you hear the bear?'",
          "On the second read-through, pause before each character and ask: 'Who's next? Mouse or bear?'",
          "Let your baby guess by sound, not just by picture.",
          "Stop and replay favorite voices: 'Say it again, bear!' (you repeat in bear voice)"
        ],
        "examples": [
          "Story line: 'The mouse said, \"Come play!\"' → Use tiny squeaky voice with high pitch.",
          "Story line: 'The bear roared, \"Look at me!\"' → Drop your voice deep and powerful.",
          "Your baby hears the voice change → point to the right animal: 'That's the bear talking!'"
        ],
        "watchFor": [
          "Your baby looking at the correct animal when they hear that animal's voice.",
          "Your baby getting excited or laughing when voices switch.",
          "Your baby trying to guess who will speak next based on the voice pattern.",
          "Your baby's eyes following your face as you change expressions with voices."
        ],
        "parentTip": "Keep reading short and joyful. If your baby loses interest, stop. Short, happy reading is better than forcing a full book.",
        "funTwist": "After reading, mix up the voices: Use the mouse voice for the bear and watch your baby react! They'll notice!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Copy the Voice Game",
      "duration": "3-4 min",
      "focus": "Active Voice Imitation and Recognition",
      "researchBasis": "Kuhl (2010) - Babies learn language through imitating sounds during the sensitive period; copying voices builds vocal control and sound awareness.",
      "content": {
        "goal": "Play a game where you make an animal voice and your baby copies or guesses the animal.",
        "whatToSay": [
          "\"Now we play a guessing game! I make a voice, you guess who it is!\"",
          "\"Listen: 'Quack-quack-quack!' Who am I?\"",
          "\"That's right—the duck! Now it's your turn!\"",
          "\"You make a voice and I'll guess who you are!\"",
          "\"I hear a squeaky voice—are you the mouse?\"",
          "\"Yes! You're the tiny mouse! Perfect!\""
        ],
        "whatToDo": [
          "Show your baby the animal pictures again to remind them of each voice.",
          "Say: 'I'm going to make an animal voice. You tell me who I am!'",
          "Make a clear voice (mouse, duck, or bear) without saying the name.",
          "Wait for your baby's response—pointing, vocalizing, or looking at a picture.",
          "Celebrate whatever they do: 'You heard the bear! Yes!'",
          "If wrong, gently correct: 'That's a good try! Listen again—it's the squeaky mouse: eek eek!'",
          "Then say: 'Now YOU make a voice and I'll guess who you are!'",
          "Copy or imitate whatever your baby does.",
          "If they make any sound, assign it an animal: 'Oh, you're the duck! Quack-quack!'",
          "Repeat 5-6 times, switching who makes the voice.",
          "End big: 'You're such a great voice actor!'"
        ],
        "examples": [
          "You make deep bear voice → Baby points to bear picture → 'Yes! That's the bear!'",
          "You make mouse squeak → Baby giggles and squirms → 'The mouse makes you giggle!'",
          "Baby makes any sound → You say 'Are you the quacky duck?' and baby smiles → 'Yes!'"
        ],
        "watchFor": [
          "Your baby recognizing voices and pointing to the right animal.",
          "Your baby attempting to copy voices (pitch may be off, but effort shows learning).",
          "Your baby responding faster as the game continues.",
          "Your baby giggling or showing joy—positive emotion helps memory."
        ],
        "parentTip": "There's no wrong answer in this game. Every attempt is practice and success.",
        "funTwist": "Make silly mixed-up voices: 'The mouse with a bear voice! The duck with a teeny voice!' Baby will notice the mismatch and laugh!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're a storyteller with amazing character voices!",
          "You practiced:",
          "✓ Giving each animal a different voice",
          "✓ Listening to voices and finding the right animal",
          "✓ Copying animal voices",
          "✓ Following a story with character voices!"
        ],
        "reviewQuestions": [
          "Did your baby notice when you changed voices?",
          "Could your baby point to or identify animals by their voice?",
          "Did your baby try to copy any of the voices?",
          "What was your baby's favorite animal voice?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "When your baby hears different voices for different characters, they learn to track multiple speakers. Good readers do this automatically when reading dialogue. Your funny voices are training your baby's brain to be a better reader."
        },
        "nextSteps": [
          "Use character voices every time you read books this week.",
          "Point out voices in real life: 'Listen—the dog has a different voice than the cat!'",
          "Let your baby pick which animal voice to use in stories.",
          "Tomorrow we add sound effects to stories—even more fun!"
        ]
      }
    }
  ]
},

// ---------- LESSON 48 (Week 10 Day 3) ----------
{
  "id": 48,
  "phase": "1-2",
  "quarter": 1,
  "week": 10,
  "weekIndex": 9,
  "dayInWeek": 2,
  "title": "Lesson 48: \"Sound Effects for Stories\" — Add 'Moo,' 'Splash,' 'Whoosh' During Story Scenes",
  "theme": "Sound in Stories",
  "skillRecurrenceTags": ["sound-effects", "onomatopoeia", "story-participation"],
  "relatedToPreviousSkills": ["animal-sounds-week1", "environmental-sounds-week8", "character-voices-lesson47"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sound Effects!",
      "duration": "1 min",
      "content": {
        "goal": "Add sound effects to stories so your baby learns that sounds match actions and help tell the story.",
        "parentGuide": [
          "Sound effects make stories come alive and keep your baby engaged.",
          "When your baby hears 'splash' while seeing water, their brain links the sound to the action. This is how language connects to the real world.",
          "Words that sound like what they mean (like 'splash' or 'zoom') are the easiest for babies to learn.",
          "Adding sounds turns passive listening into active participation—your baby is helping tell the story.",
          "This teaches your baby that sounds and words have meaning—a foundation for reading."
        ],
        "materials": ["A picture book with action scenes (animals, water, movement)", "Your voice making silly sounds"],
        "tip": "The weirder and more exaggerated your sound effects, the more your baby will remember them.",
        "researchNote": "Lonigan et al. (2000) - Sound play helps babies notice the individual sounds that make up words. This is crucial for reading."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Meet the Sounds",
      "duration": "3-4 min",
      "focus": "Learning Action Sounds",
      "researchBasis": "Trainor et al. (2005) - Babies learn sounds faster when they see the action at the same time they hear the sound.",
      "content": {
        "goal": "Introduce basic sound effects and connect them to real actions.",
        "whatToSay": [
          "\"Watch what happens when a cow moves—MOOOOO!\"",
          "\"Now the cow jumps in water—SPLASH! Hear that? Splash!\"",
          "\"The wind blows leaves around—WHOOOOSH! Whoosh!\"",
          "\"Baby falls down—THUMP! Big sound!\"",
          "\"Doors close—SLAM! Can you make that sound?\""
        ],
        "whatToDo": [
          "Start with one sound at a time.",
          "Make the sound while acting out the movement: clap hands for 'clap,' jump for 'thump.'",
          "Say the sound and do the action together: 'SPLASH!' while splashing water.",
          "Repeat each sound 3-4 times so your baby hears it clearly.",
          "Make your body big and exaggerated with the sounds.",
          "Let your baby watch your mouth as you make each sound.",
          "Introduce 4-5 action sounds: splash, thump, whoosh, clap, zoom.",
          "After each one, pause: 'Did you hear that sound?'",
          "Invite your baby to copy: 'You say splash!' Celebrate any attempt."
        ],
        "examples": [
          "SPLASH: Make splashing motion with hands while saying 'splaaaaash!' with high energy.",
          "WHOOSH: Wave arm across body like wind, say 'whoooosh!' with your breath.",
          "THUMP: Tap chest or stomp, say 'thump!' with a beat.",
          "ZOOM: Move toy car quickly, say 'zoom zoom zoom!' fast and excited.",
          "CLAP: Clap hands together, say 'clap clap!' in rhythm."
        ],
        "watchFor": [
          "Your baby watching your face and mouth as you make sounds.",
          "Your baby smiling, giggling, or showing excitement.",
          "Your baby trying to make any sounds back—even quiet ones.",
          "Your baby doing the action along with the sound (clapping, splashing)."
        ],
        "parentTip": "Don't worry about perfect sound effects. The bigger and sillier, the better your baby learns.",
        "funTwist": "Make the sounds REALLY big one time, then whisper them the next time. 'SPLASH!' then 'splash' (tiny whisper). Baby will love the contrast!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Sound Effects Story Time",
      "duration": "4-5 min",
      "focus": "Adding Sounds to Story Events",
      "researchBasis": "Bus & van IJzendoorn (1995) - Interactive story reading where children participate with sounds increases engagement and memory for stories.",
      "content": {
        "goal": "Read a story and add sound effects when important actions happen.",
        "whatToSay": [
          "\"This story has lots of fun sounds. When I point to something, we make the sound together!\"",
          "\"Look—the duck is swimming! What sound? SPLASH!\"",
          "\"Now the wind comes—WHOOOOSH! Did you hear it?\"",
          "\"The door closes—SLAM! Let's say it together!\"",
          "\"What happens next? Make a sound with me!\""
        ],
        "whatToDo": [
          "Choose a simple story with action (animals moving, water, doors, weather).",
          "As you read, pause when an action happens and make the sound effect.",
          "Point to the picture: 'The duck jumps in the water—SPLASH!'",
          "On the second pause, invite your baby: 'The bunny hops—can you make the sound? Hop-hop!'",
          "Accept any sound as the right answer.",
          "Make the sound together a few times so they hear it clearly.",
          "Add sound effects at key moments (not every sentence).",
          "Vary the energy: some sounds loud, some quiet, some fast, some slow.",
          "By the end of the story, your baby should anticipate sounds."
        ],
        "examples": [
          "Story: 'The dog ran through the yard'—You say: 'The dog runs—zoom zoom!' (run in place).",
          "Story: 'Rain fell from the sky'—You say: 'Pitter-patter! Pitter-patter!' (tap fingers).",
          "Story: 'The baby fell asleep'—You whisper: 'Shhhhh... quiet.' (soft sound)."
        ],
        "watchFor": [
          "Your baby anticipating sounds—waiting for the 'splash' when they see water.",
          "Your baby trying to make sounds themselves—even imperfect versions.",
          "Your baby staying engaged and interested (less looking away or distraction).",
          "Your baby responding faster to sound cues as the story goes on."
        ],
        "parentTip": "Keep reading fun and short. If your baby gets bored, stop and play the sound games. Quality over quantity!",
        "funTwist": "On the second read-through, YOU pause and let your baby make the sound while you do the action. Flip the roles!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Sound Effects Guessing Game",
      "duration": "3-4 min",
      "focus": "Matching Sounds to Actions",
      "researchBasis": "Saffran et al. (1996) - Babies learn to recognize patterns; connecting sounds to actions repeatedly builds pattern recognition.",
      "content": {
        "goal": "Play a game where you make a sound and your baby guesses the action.",
        "whatToSay": [
          "\"Listen to this sound. What's happening? SPLASH! What made that sound?\"",
          "\"Right—water! Now listen—WHOOOOSH! What's that?\"",
          "\"Wind! You're so smart! One more—ZOOM! What's zooming?\"",
          "\"You got it—a car! Now YOU make a sound and I'll guess!\"",
          "\"Hmm, I hear a sound—are you a dog saying WOOF?\""
        ],
        "whatToDo": [
          "Make one sound effect at a time without showing a picture.",
          "Ask: 'What sound is this? What's happening?'",
          "Wait for your baby's response—pointing, vocalizing, or looking around.",
          "Celebrate all engagement: 'You heard the splash! Good listening!'",
          "Show the action or picture after they guess: 'Yes—it's water splashing!'",
          "Do this with 4-6 sounds you've already learned.",
          "Then reverse: 'Now YOU make a sound and I'll guess what you mean!'",
          "Whatever sound your baby makes, create an action: 'Oh, you're making a 'babababa' sound! Is that a bouncing ball? Boing-boing!'",
          "End with multiple rounds of the sound-action game."
        ],
        "examples": [
          "You make 'pitter-patter' → Baby nods or looks outside at rain → 'Yes! Rain sounds like pitter-patter!'",
          "You make 'meow meow' → Baby crawls or points to cat picture → 'That's right—the cat! Meow meow!'",
          "Baby makes random sounds → You guess: 'Is that a dog? A car? A plane?' and baby reacts to your guesses."
        ],
        "watchFor": [
          "Your baby making louder or more enthusiastic sounds as the game continues.",
          "Your baby laughing and engaging actively.",
          "Your baby attempting to match your sound, even if imperfect.",
          "Your baby requesting sounds or actions (reaching, vocalizing, looking for repeats)."
        ],
        "parentTip": "There are no mistakes here. Every sound your baby makes is perfect. The goal is engagement and joy.",
        "funTwist": "Make up silly wrong guesses: 'I hear 'splash'—is that a dinosaur?' Let your baby correct you and laugh at your silliness!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You made amazing sound effects! You told a story with sounds!",
          "You practiced:",
          "✓ Learning action sounds like splash, whoosh, and thump",
          "✓ Adding sounds to a story when things happen",
          "✓ Guessing what action a sound means",
          "✓ Making your own sounds and actions!"
        ],
        "reviewQuestions": [
          "Did your baby enjoy making and hearing the sound effects?",
          "Could your baby match sounds to actions—like knowing 'splash' means water?",
          "Did your baby try to copy any of the sounds?",
          "What was your baby's favorite sound effect?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Sound effects connect sounds to real-world actions. When your baby reads later, they'll understand that letters and sounds represent real things. This lesson teaches that sounds have meaning—the foundation for reading every word."
        },
        "nextSteps": [
          "Add sound effects to daily routines: 'Splash!' in the bath, 'Whoosh!' with the door, 'Clap!' when playing.",
          "Let your baby pick a sound effect to add to stories.",
          "Tomorrow we explore guessing which character spoke—detective work!",
          "Keep a list of your baby's favorite sounds and bring them back regularly."
        ]
      }
    }
  ]
},

// ---------- LESSON 49 (Week 10 Day 4) ----------
{
  "id": 49,
  "phase": "1-2",
  "quarter": 1,
  "week": 10,
  "weekIndex": 9,
  "dayInWeek": 3,
  "title": "Lesson 49: \"Guess Who Spoke?\" — Character Voice Detective Game",
  "theme": "Sound in Stories",
  "skillRecurrenceTags": ["voice-recognition", "listening-comprehension", "character-tracking"],
  "relatedToPreviousSkills": ["character-voices-lesson47", "sound-hide-and-seek-week3", "listening-attention"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Voice Detective!",
      "duration": "1 min",
      "content": {
        "goal": "Play a listening game where your baby guesses which character spoke based only on their voice. This builds listening skills and character tracking.",
        "parentGuide": [
          "This lesson teaches your baby to listen carefully and remember patterns—skills needed for reading comprehension.",
          "When readers track multiple characters in stories, they're using the same skills your baby practices here.",
          "Listening games build auditory memory, which helps your baby remember letter sounds later.",
          "This combines character voices from Lesson 47 with detective listening from Week 3—your baby is ready!",
          "Playing detective makes learning feel like an adventure, not a task."
        ],
        "materials": ["A picture book with 2-3 characters", "Small toys or finger puppets representing story characters (optional)", "Your detective voice skills!"],
        "tip": "Start with just two very different voices (tiny mouse and big bear) before adding a third character.",
        "researchNote": "DeCasper & Fifer (1980) - Babies recognize voices from birth; practicing voice discrimination strengthens auditory processing skills essential for language development."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Voice Review Game",
      "duration": "3-4 min",
      "focus": "Reviewing Character Voices Before the Detective Game",
      "researchBasis": "Cepeda et al. (2006) - Spaced repetition and review strengthen long-term retention; reviewing voices before the game ensures success and confidence.",
      "content": {
        "goal": "Remind your baby of each character's voice so they can recognize them in the detective game.",
        "whatToSay": [
          "\"Let's remember our character voices! Here's the mouse!\"",
          "\"The mouse talks like this: 'Hello! Hello!' (tiny squeaky voice)\"",
          "\"Now the bear: 'I'm big and strong!' (deep, booming voice)\"",
          "\"And the bunny: 'Hop hop hop!' (medium, bouncy voice)\"",
          "\"Can you remember which voice is which?\"",
          "\"Let's practice! When I use the mouse voice, point to the mouse!\""
        ],
        "whatToDo": [
          "Show your baby pictures or toys of 2-3 story characters (mouse, bear, bunny).",
          "Point to each character and make their voice clearly.",
          "Repeat each voice 2-3 times: 'Mouse: eek eek eek! Bear: ROAR ROAR! Bunny: hop-hop-hop!'",
          "After introducing all voices, test recall: 'Which one says ROAR?'",
          "Wait for your baby to point, look, or vocalize toward the bear.",
          "Celebrate: 'Yes! The bear says ROAR! You remembered!'",
          "Do this with all characters: 'Who says eek-eek? The mouse! Good job!'",
          "Make it playful: hide characters behind your back, make voice, reveal correct one.",
          "Repeat until your baby can match at least one voice to one character consistently.",
          "Keep energy high and positive—this is review, not a test!"
        ],
        "examples": [
          "Show mouse, make squeaky voice 3 times → 'This is mouse voice! Squeak-squeak!'",
          "Show bear, make deep voice 3 times → 'This is bear voice! Grrrr-hello!'",
          "Mix them up: Make bear voice → Baby points to bear → 'You found the bear!'"
        ],
        "watchFor": [
          "Your baby pointing to or looking at the correct character when they hear the voice.",
          "Your baby smiling or showing recognition—they remember the pattern!",
          "Your baby trying to copy any of the voices.",
          "Your baby getting faster at matching voices to characters."
        ],
        "parentTip": "If your baby struggles, reduce to just two characters. Success matters more than complexity.",
        "funTwist": "Let characters 'hide' behind pillows or blankets—make the voice and see if baby can find who spoke!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Detective Listening Game",
      "duration": "4-5 min",
      "focus": "Guessing Who Spoke Based on Voice Alone",
      "researchBasis": "Kuhl et al. (2003) - Babies who practice discriminating between sounds develop stronger language processing abilities; voice detective games strengthen this skill.",
      "content": {
        "goal": "Hide the characters, make a voice, and let your baby guess who spoke. This is pure listening practice!",
        "whatToSay": [
          "\"Time to be a detective! Someone is going to talk, and you have to guess WHO!\"",
          "\"Close your eyes or look away—no peeking!\"",
          "\"Listen carefully... 'Hello friends!' (squeaky mouse voice)\"",
          "\"Who said that? Point to who you think it is!\"",
          "\"YES! It's the mouse! Great detective work!\"",
          "\"Let's try another one!\""
        ],
        "whatToDo": [
          "Place all character pictures or toys in front of your baby where they can see them.",
          "Say: 'I'm going to hide someone and they'll talk. You guess who!'",
          "Turn one character face-down or cover it with your hand (but keep all visible afterward for pointing).",
          "Make one character's voice clearly: 'I like carrots!' (bunny voice).",
          "Look at your baby: 'Who said that?'",
          "Wait 3-5 seconds for your baby to point, look, or vocalize.",
          "If they point to the right character: 'YES! The bunny said it! You're a detective!'",
          "If they point to the wrong one: 'Good try! Listen again—I like carrots! Hop-hop! It's the bunny!'",
          "Reveal the character and celebrate.",
          "Repeat with different characters 5-6 times.",
          "Make voices clear and very different from each other.",
          "As your baby gets better, make the game harder: use similar voices or longer sentences."
        ],
        "examples": [
          "Make mouse voice: 'I'm so small!' → Baby points to mouse → 'Correct! You found me!'",
          "Make bear voice: 'I'm big and strong!' → Baby points to bear → 'Great listening!'",
          "Make bunny voice: 'Hop with me!' → Baby points to wrong one → 'Listen again—hop hop! It's bunny!'"
        ],
        "watchFor": [
          "Your baby listening carefully before pointing—showing they're processing the sound.",
          "Your baby pointing to the correct character more often as the game continues.",
          "Your baby getting excited when they guess correctly.",
          "Your baby starting to anticipate—leaning forward when you start talking."
        ],
        "parentTip": "Celebrate all attempts! Even wrong guesses show your baby is listening and trying.",
        "funTwist": "Let your baby be the detective with a magnifying glass (or pretend one)! 'Detective baby, who spoke?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Story Detective Challenge",
      "duration": "3-4 min",
      "focus": "Identifying Characters in a Real Story",
      "researchBasis": "Whitehurst et al. (1988) - Interactive reading where children identify speakers improves story comprehension and prepares them for tracking dialogue in text.",
      "content": {
        "goal": "Read a short story with character dialogue, and pause to ask your baby who just spoke.",
        "whatToSay": [
          "\"Let's read a story and be detectives! Every time someone talks, we'll guess who it is!\"",
          "\"Listen: 'I want honey!' Who said that?\"",
          "\"Is it the mouse or the bear? Point!\"",
          "\"YES! The bear wants honey! You're such a good detective!\"",
          "\"Let's keep reading and find out who talks next!\""
        ],
        "whatToDo": [
          "Choose a simple story with clear character dialogue (or make up your own).",
          "Read the first line in a character's voice: 'I'm looking for food!' (bear voice).",
          "Stop immediately and ask: 'Who said that? Was it the mouse or the bear?'",
          "Point to both character pictures so your baby can choose.",
          "Wait for your baby's response—pointing, looking, or any vocalization.",
          "Confirm: 'Yes! The bear said it! Listen to the bear voice: ROAR!'",
          "Continue reading, pausing after each piece of dialogue.",
          "Each time, ask: 'Who was that? Can you tell?'",
          "Make the voices VERY different so it's easier to guess.",
          "By the end, your baby should recognize at least one character's voice consistently.",
          "Reread the story and let your baby 'tell you' who's talking before you even ask."
        ],
        "examples": [
          "Story: 'I love cheese!' (mouse voice) → Pause → 'Who loves cheese?' → Baby points to mouse.",
          "Story: 'Let's go to the forest!' (bear voice) → Pause → 'Who wants to go to the forest?' → Baby points to bear.",
          "Story: 'I'll hop there!' (bunny voice) → Pause → 'Who's hopping?' → Baby points to bunny."
        ],
        "watchFor": [
          "Your baby correctly identifying at least one character by voice consistently.",
          "Your baby anticipating pauses—ready to answer 'who spoke?'",
          "Your baby staying engaged throughout the story—not looking away.",
          "Your baby trying to make character voices themselves!"
        ],
        "parentTip": "Keep stories short and simple—3-5 dialogue moments are plenty. Success and fun matter most!",
        "funTwist": "Mix it up: Read a line in the WRONG character voice on purpose. 'I'm tiny!' (in deep bear voice). Your baby will notice and react!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're an amazing voice detective! You found who was talking!",
          "You practiced:",
          "✓ Remembering each character's special voice",
          "✓ Listening carefully to guess who spoke",
          "✓ Tracking characters through a whole story",
          "✓ Being a reading detective!"
        ],
        "reviewQuestions": [
          "Could your baby identify at least one character by voice?",
          "Did your baby enjoy the detective listening game?",
          "Did your baby stay engaged during the story?",
          "Which character voice was easiest for your baby to recognize?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "When your baby tracks who's speaking in a story, they're building comprehension skills they'll need for reading. Good readers automatically track multiple characters and voices. This detective game is teaching your baby to be an active, engaged reader who understands stories deeply."
        },
        "nextSteps": [
          "Play voice detective every time you read books this week!",
          "Point out different voices in real life: 'That's Daddy's voice!' 'That's the dog's voice!'",
          "Tomorrow we create stories using sounds and pictures—putting it all together!",
          "Ask 'who said that?' during puppet play or when reading books—practice everywhere!"
        ]
      }
    }
  ]
},

// ---------- LESSON 50 (Week 10 Day 5) ----------
{
  "id": 50,
  "phase": "1-2",
  "quarter": 1,
  "week": 10,
  "weekIndex": 9,
  "dayInWeek": 4,
  "title": "Lesson 50: \"Make Your Own Story\" — Sounds + Pictures Narration",
  "theme": "Sound in Stories",
  "skillRecurrenceTags": ["narration", "vocabulary-building", "creative-storytelling"],
  "relatedToPreviousSkills": ["sound-effects-lesson48", "object-naming-week5", "story-participation"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Making!",
      "duration": "1 min",
      "content": {
        "goal": "Narrate your baby's play using sound words, creating an instant story together. This shows your baby that stories can come from everyday life.",
        "parentGuide": [
          "When you narrate your baby's actions, you're teaching them that everything has a story—reading will feel natural later.",
          "Adding sound words to play (clap, splash, zoom) makes language concrete and fun.",
          "This combines ALL skills from Week 10: character voices, sound effects, listening, and now creating stories.",
          "Your baby learns that they're the author of their own stories—powerful for confidence and creativity.",
          "Narrating play is one of the most effective ways to build vocabulary naturally."
        ],
        "materials": ["Your baby's toys", "Any household objects", "Your storytelling voice!"],
        "tip": "Follow your baby's lead! Narrate whatever they're doing—don't force them to play a certain way.",
        "researchNote": "Rowe (2012) - Parents who narrate and describe children's actions during play significantly boost vocabulary development and sentence complexity."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Narrate the Play",
      "duration": "3-4 min",
      "focus": "Describing Your Baby's Actions with Sound Words",
      "researchBasis": "Weisleder & Fernald (2013) - The more words children hear describing their immediate actions, the faster their language processing and vocabulary growth.",
      "content": {
        "goal": "Watch your baby play and describe everything they do using exciting language and sound words.",
        "whatToSay": [
          "\"Oh! You picked up the car! Vroom-vroom!\"",
          "\"Now the car is going fast! Zooooom down the table!\"",
          "\"Uh-oh! The car stopped! What happened?\"",
          "\"You're stacking blocks! Stack-stack-stack! So tall!\"",
          "\"And... CRASH! The blocks fell down! Big crash!\"",
          "\"You're making a story with your toys!\""
        ],
        "whatToDo": [
          "Sit near your baby while they play with toys.",
          "Watch what they do—don't direct them.",
          "Describe every action as it happens: 'You picked up the bear! Hello, bear!'",
          "Add sound effects to their actions: 'The bear is walking—stomp-stomp-stomp!'",
          "Use exciting tone and exaggerated expression.",
          "Add simple story structure: 'First the bear walked. Then he found food! Now he's eating!'",
          "If your baby does something, make it dramatic: 'Oh no! The doll fell! Thump! Is she okay?'",
          "Keep sentences short and clear—match your baby's attention span.",
          "Repeat key words: 'Crash! Did you hear the crash? Big crash!'",
          "Follow your baby's interest—if they switch toys, switch your narration.",
          "Make your baby the hero: 'And then baby made the car go ZOOM! So fast!'"
        ],
        "examples": [
          "Baby pushes car → 'Vroom! The car is moving! Where is it going? Zooom across the floor!'",
          "Baby drops toy → 'Uh-oh! The toy fell! Thump! Let's pick it up!'",
          "Baby stacks blocks → 'Stack, stack, stack! You're building! So high! Will it fall? Oh! Crash!'",
          "Baby feeds doll → 'Yum yum yum! The baby is eating! Munch-munch! All done!'"
        ],
        "watchFor": [
          "Your baby pausing to listen to your narration—they're engaged!",
          "Your baby repeating any of your sound words.",
          "Your baby doing actions more intentionally because you're narrating—they're aware you're watching.",
          "Your baby looking at you after actions—checking if you'll narrate what they did."
        ],
        "parentTip": "Don't interrupt play—narrate alongside it. You're the announcer, not the director!",
        "funTwist": "Make your baby the star: 'And now, the amazing baby builds the tallest tower in the world! Will it stand? Let's see!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Picture Book Story Creation",
      "duration": "4-5 min",
      "focus": "Creating Stories from Pictures Using Sound Words",
      "researchBasis": "Snow & Ninio (1986) - Picture-based storytelling where adults expand on images helps children understand narrative structure and builds vocabulary faster than reading alone.",
      "content": {
        "goal": "Look at a wordless picture book or any pictures and make up a story together using sound words.",
        "whatToSay": [
          "\"Let's make up a story about these pictures!\"",
          "\"Look—there's a dog! What's the dog doing? Running! Zoom-zoom!\"",
          "\"Now the dog sees a ball! He wants to play!\"",
          "\"The dog jumps—BOING! High in the air!\"",
          "\"And he catches the ball! Woof-woof! Good dog!\"",
          "\"What happens next? Let's turn the page and see!\""
        ],
        "whatToDo": [
          "Choose a wordless picture book or any book with pictures.",
          "Instead of reading printed words, make up your own story from the images.",
          "Point to the first picture: 'Look at this! What do you see?'",
          "Name what's in the picture: 'I see a cat! A big orange cat!'",
          "Create action: 'The cat is walking—step-step-step. Where is he going?'",
          "Add sound effects: 'He sees a mouse! The mouse says squeak-squeak!'",
          "Ask your baby simple questions: 'What will happen next?'",
          "Accept any response (pointing, vocalizing, looking) as their contribution.",
          "Turn the page: 'Let's find out! Oh! The cat is running—zoom-zoom!'",
          "Keep the story simple and focus on actions with sounds.",
          "Let your baby 'tell' parts of the story by making sounds or pointing.",
          "End with celebration: 'We just made our own story! We're authors!'"
        ],
        "examples": [
          "Picture of bird → 'The bird is flying! Flap-flap-flap! Up in the sky!'",
          "Picture of rain → 'Rain is falling! Pitter-patter! Everything gets wet! Splash!'",
          "Picture of sleeping baby → 'Shhhhh! Baby is sleeping! Quiet time! Zzzzz!'",
          "Picture of truck → 'The truck is so loud! Beep-beep! Vroom-vroom! It's carrying blocks!'"
        ],
        "watchFor": [
          "Your baby pointing to pictures they want you to talk about.",
          "Your baby making sounds during the story.",
          "Your baby 'helping' turn pages—engaged in the story flow.",
          "Your baby anticipating sounds—getting excited before you make them!"
        ],
        "parentTip": "There's no 'right' story! Whatever you make up is perfect. The goal is showing that pictures tell stories.",
        "funTwist": "Let your baby 'direct' the story: 'Point to what happens next! Oh, the bear! Let's talk about the bear!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Sound Story Performance",
      "duration": "3-4 min",
      "focus": "Acting Out a Story with Sounds",
      "researchBasis": "Bruner (1983) - Children learn narrative structure through active participation; physically acting out stories helps children internalize story patterns.",
      "content": {
        "goal": "Act out a simple story together using your bodies and sound effects. This makes storytelling physical and memorable.",
        "whatToSay": [
          "\"Let's act out our story! You be the character!\"",
          "\"Once upon a time, there was a little bunny. Hop-hop-hop!\"",
          "\"The bunny was hungry. What sound does a hungry tummy make? Grumble-grumble!\"",
          "\"The bunny found a carrot! Crunch-crunch-crunch! Yummy!\"",
          "\"Then the bunny was happy! Yay! Hooray! The end!\"",
          "\"Let's do it again!\""
        ],
        "whatToDo": [
          "Create a very simple story with 3-4 actions (wake up, eat, play, sleep).",
          "Say: 'Let's act out a story about a baby (or animal)!'",
          "Start the story: 'Once upon a time, baby woke up! Stretch! Yawn!'",
          "Do the action together—you and baby stretch and yawn.",
          "Add sound effects: 'Yaaaawn! Good morning!'",
          "Continue: 'Baby ate breakfast! Munch-munch-munch!'",
          "Pretend to eat together, making eating sounds.",
          "Next action: 'Then baby played! Clap-clap! Jump-jump!'",
          "Do the actions with big movements.",
          "Final action: 'Baby got tired! Yaaawn! Time for sleep! Shhhhh! Zzzzz!'",
          "Lie down together and pretend to sleep.",
          "End: 'The end! Let's do another story!'",
          "Repeat with different characters or actions—follow baby's interest."
        ],
        "examples": [
          "Story: 'The bird woke up! Chirp-chirp! (flap arms) Flew to find food! Zoom! (run) Ate seeds! Peck-peck! (pretend to peck) Flew home! Flap-flap! (flap arms) The end!'",
          "Story: 'The dog ran outside! Woof-woof! (run in place) Played with a ball! Bounce-bounce! (bounce) Got tired! Pant-pant! (pant like dog) Went to sleep! Zzzzz! (lie down)'",
          "Story: 'Baby played with blocks! Stack-stack! (pretend to stack) Built a tower! So tall! (reach up) Tower fell! CRASH! (clap hands) Built again! Stack-stack! (repeat)'"
        ],
        "watchFor": [
          "Your baby doing actions along with you.",
          "Your baby making any of the sound effects during the story.",
          "Your baby anticipating what comes next—familiar story structure forming!",
          "Your baby wanting to repeat the story—requesting 'again!'"
        ],
        "parentTip": "Keep stories short and repetitive. Babies love hearing the same story over and over!",
        "funTwist": "Let your baby 'write' the story: 'What does the bunny do next? You choose!' Follow their lead completely!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You made your own stories! You're an author and storyteller!",
          "You practiced:",
          "✓ Narrating play with sound words",
          "✓ Creating stories from pictures",
          "✓ Acting out stories with sounds and movements",
          "✓ Understanding that stories are everywhere!"
        ],
        "reviewQuestions": [
          "Did your baby enjoy having their play narrated?",
          "Could your baby participate in making up stories?",
          "Did your baby act out any of the story movements?",
          "What was your baby's favorite story you created together?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "When your baby sees that stories come from real life—their play, pictures, and actions—they learn that reading isn't separate from living. Stories are everywhere! This understanding makes your baby confident that they have stories to tell, which is the heart of literacy."
        },
        "nextSteps": [
          "Narrate your baby's play every day—even just for 2-3 minutes!",
          "Make up bedtime stories using your baby as the main character.",
          "Next week begins Everyday Conversations—bringing all these sound skills into daily talk!",
          "This completes Week 10—your baby has explored sounds IN stories through voices, effects, detection, and creation!"
        ]
      }
    }
  ]
},

// ---------- LESSON 51 (Week 11 Day 1) ----------
{
  "id": 51,
  "phase": "1-2",
  "quarter": 1,
  "week": 11,
  "weekIndex": 10,
  "dayInWeek": 0,
  "title": "Lesson 51: \"Hello and Goodbye\" — Social Words with Gesture",
  "theme": "Everyday Conversations",
  "skillRecurrenceTags": ["social-language", "greetings", "gesture-word-pairing"],
  "relatedToPreviousSkills": ["name-recognition-week1", "turn-taking-week4", "vocabulary-building"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Social Words!",
      "duration": "1 min",
      "content": {
        "goal": "Teach your baby essential social words like 'hello' and 'goodbye' paired with gestures. These are the building blocks of conversation.",
        "parentGuide": [
          "Social words are often a baby's first functional language—they see immediate results when they use them.",
          "Pairing words with gestures (waving, nodding) helps babies understand and remember the words faster.",
          "This week focuses on real-world conversation skills—language your baby will use every single day.",
          "Greetings and farewells teach turn-taking, which is the foundation of all conversation.",
          "When your baby learns these words, they gain social power—they can greet people and say goodbye independently!"
        ],
        "materials": ["Just you, your baby, and family members or toys to greet", "Optional: mirror to practice waving"],
        "tip": "Use these words naturally all day long—every entrance, every exit, every encounter is a teaching moment!",
        "researchNote": "Tomasello (2003) - Social interaction drives language learning; words learned in social contexts (greetings) are retained better than isolated vocabulary."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Hello Practice",
      "duration": "3-4 min",
      "focus": "Teaching 'Hello' with Waving Gesture",
      "researchBasis": "Iverson & Goldin-Meadow (2005) - Gestures paired with words strengthen language learning; babies who use gestures develop verbal language faster.",
      "content": {
        "goal": "Practice saying 'hello' with a clear wave gesture. Repeat many times so your baby connects the word to the action.",
        "whatToSay": [
          "\"Let's learn to say hello!\"",
          "\"When someone comes, we wave and say HELLO!\"",
          "\"Watch me! (wave hand) HELLO!\"",
          "\"Now you try! Wave your hand! HELLO!\"",
          "\"You did it! You said hello!\"",
          "\"Let's practice lots of times!\""
        ],
        "whatToDo": [
          "Sit facing your baby where they can see your face and hands.",
          "Say clearly: 'When we see someone, we say HELLO!'",
          "Wave your hand slowly and deliberately while saying 'HELLO!'",
          "Repeat 3-4 times: wave and say 'Hello' each time.",
          "Make eye contact while waving—show the social connection.",
          "Take your baby's hand and help them wave: 'Let's wave together! Hello!'",
          "Say 'hello' every time you wave—word and gesture always together.",
          "Practice with different people: turn to stuffed animals, 'Hello, bear!'",
          "Walk to a mirror: 'Let's say hello to ourselves! Hello!' (wave at reflection)",
          "Go to the window: 'Let's say hello to the tree! Hello, tree!' (wave outside)",
          "Make it playful: hide behind your hands, pop out and wave: 'Hello!'",
          "Encourage any attempt—if your baby raises their hand even slightly, celebrate!"
        ],
        "examples": [
          "Enter the room → wave immediately → 'Hello, baby! Hello!'",
          "Someone knocks on door → wave toward door → 'Hello! Someone's here!'",
          "Someone visits → wave and greet → 'Hello, Grandma! Can you wave?'",
          "Daddy walks in → 'Look! It's Daddy! Let's say hello!' → wave together"
        ],
        "watchFor": [
          "Your baby watching your hand as you wave—tracking the gesture.",
          "Your baby attempting to lift their hand or move their arm.",
          "Your baby smiling or showing excitement during greetings.",
          "Your baby eventually waving without physical help—independent gesture!"
        ],
        "parentTip": "Some babies wave differently—open-close fist, whole arm movement. Any wave is perfect!",
        "funTwist": "Make silly hellos: 'Robot hello!' (stiff wave), 'Silly hello!' (fast wild wave), 'Tiny hello!' (finger wave)"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Goodbye Practice",
      "duration": "4-5 min",
      "focus": "Teaching 'Goodbye' with Waving Gesture",
      "researchBasis": "Hart & Risley (1995) - Repeated exposure to social words in context accelerates vocabulary; 'goodbye' is one of the most frequently used and early-learned words.",
      "content": {
        "goal": "Practice saying 'goodbye' with a wave, teaching your baby that this word means someone is leaving.",
        "whatToSay": [
          "\"Now let's learn goodbye!\"",
          "\"When someone leaves, we wave and say GOODBYE!\"",
          "\"Watch: (wave) GOODBYE! See you later!\"",
          "\"Your turn! Wave and say goodbye!\"",
          "\"Goodbye, goodbye! Wave-wave!\"",
          "\"You're so good at saying goodbye!\""
        ],
        "whatToDo": [
          "Explain: 'Goodbye means someone is leaving. Let's practice!'",
          "Stand up and pretend to leave the room.",
          "Wave as you walk backward: 'Goodbye! Goodbye!'",
          "Come back and do it again: 'Watch me leave—goodbye!'",
          "This time, help your baby wave as you leave: 'You wave goodbye to me!'",
          "Have your baby watch someone else leave (partner, sibling): 'Wave goodbye!'",
          "Practice with toys: move a toy car away, 'Goodbye, car! Wave!'",
          "Use stuffed animals: walk bear away, 'The bear is leaving! Goodbye, bear!'",
          "Make it real: when you actually leave a room, say 'Goodbye!' and wave.",
          "Go outside and come back in: 'I'm leaving! Goodbye!' then return: 'Hello again!'",
          "Pair goodbye with phrases: 'Goodbye! See you soon! Bye-bye!'",
          "Accept variations: 'bye-bye,' 'bye,' 'night-night'—all count as goodbye words!"
        ],
        "examples": [
          "End of playtime → wave to toys → 'Goodbye, blocks! See you tomorrow!'",
          "Leaving for a walk → wave to house → 'Goodbye, house! We'll be back!'",
          "Visitor leaving → wave goodbye → 'Goodbye, Grandpa! Bye-bye!'",
          "Pet leaving room → wave at dog → 'Goodbye, doggy! Bye-bye!'"
        ],
        "watchFor": [
          "Your baby waving when people or things leave.",
          "Your baby understanding 'goodbye' means departure—showing recognition.",
          "Your baby saying any version of 'bye'—'buh,' 'bye,' 'ba.'",
          "Your baby initiating waves without prompting when someone leaves."
        ],
        "parentTip": "Make goodbyes positive and short—avoid long, sad goodbyes that confuse the concept.",
        "funTwist": "Play 'goodbye parade': line up toys, have each one 'leave' one at a time—'Goodbye, bear! Goodbye, car! Goodbye, ball!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Hello-Goodbye Game",
      "duration": "3-4 min",
      "focus": "Distinguishing Between Hello and Goodbye",
      "researchBasis": "Bruner (1983) - Turn-taking games build conversational skills; practicing social rituals through play solidifies understanding.",
      "content": {
        "goal": "Play a game where you alternate saying hello when arriving and goodbye when leaving. This teaches the difference.",
        "whatToSay": [
          "\"Let's play the hello-goodbye game!\"",
          "\"When I come in, what do we say? HELLO!\"",
          "\"When I leave, what do we say? GOODBYE!\"",
          "\"Watch me! I'm leaving—goodbye! I'm coming back—hello!\"",
          "\"Now you say it with me!\"",
          "\"Hello! Goodbye! Hello! Goodbye!\""
        ],
        "whatToDo": [
          "Start in the middle of the room with your baby.",
          "Say: 'I'm going to leave and come back. Watch what words we use!'",
          "Walk away from your baby, turn around, and wave: 'Goodbye!'",
          "Leave the room (or go far away).",
          "Come back immediately, waving: 'Hello! I'm back!'",
          "Repeat several times: leave saying 'goodbye,' return saying 'hello.'",
          "Make it exaggerated: 'I'm LEAVING! Goodbye! I'm BACK! Hello!'",
          "Now invite your baby: 'You try! Walk away from me and say goodbye!'",
          "Help them walk a few steps away, prompting: 'Goodbye!'",
          "Encourage them to come back: 'Come back and say hello!'",
          "Do this back-and-forth 5-6 times.",
          "Use toys: move toy toward baby 'Hello!' then away 'Goodbye!'",
          "Make it a rhythm: 'Hello-goodbye-hello-goodbye!' with actions."
        ],
        "examples": [
          "You hide behind couch → 'Goodbye!' → pop back out → 'Hello!'",
          "Baby crawls away → you say 'Goodbye!' → baby returns → 'Hello! You're back!'",
          "Toy car drives away → 'Goodbye, car!' → comes back → 'Hello, car!'",
          "Peek-a-boo style: cover face 'Goodbye!' → uncover 'Hello!'"
        ],
        "watchFor": [
          "Your baby waving at the right times—goodbye for leaving, hello for arriving.",
          "Your baby anticipating which word comes next—showing pattern recognition.",
          "Your baby attempting to say either word—'hi,' 'bye,' or any approximation.",
          "Your baby laughing and enjoying the repetition—positive emotional engagement."
        ],
        "parentTip": "If your baby confuses the words, that's normal! Keep playing—repetition will clarify the difference.",
        "funTwist": "Speed round: do hello-goodbye super fast, then super slow. 'HELLO! GOODBYE! HELLO! GOODBYE!' then 'hellooooo... goodbyeeeee...'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned hello and goodbye! You can greet people!",
          "You practiced:",
          "✓ Saying 'hello' with a wave when someone arrives",
          "✓ Saying 'goodbye' with a wave when someone leaves",
          "✓ Playing games to practice both words",
          "✓ Starting real conversations!"
        ],
        "reviewQuestions": [
          "Did your baby wave during hello or goodbye?",
          "Did your baby try to say either word?",
          "Could your baby tell the difference between hello and goodbye situations?",
          "Did your baby enjoy the hello-goodbye game?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Social words like 'hello' and 'goodbye' are your baby's first real conversations. These words give your baby social power—they can greet people, say farewell, and participate in daily life. This is functional language that builds confidence and connection."
        },
        "nextSteps": [
          "Use hello and goodbye every single day—every entrance, every exit!",
          "Point out when others use these words: 'Look! Daddy said goodbye!'",
          "Tomorrow we learn 'please' and 'thank you'—more conversation tools!",
          "Practice with toys, pets, and family—make it part of daily routine!"
        ]
      }
    }
  ]
},

// ---------- LESSON 52 (Week 11 Day 2) ----------
{
  "id": 52,
  "phase": "1-2",
  "quarter": 1,
  "week": 11,
  "weekIndex": 10,
  "dayInWeek": 1,
  "title": "Lesson 52: \"Please and Thank You\" — Courtesy Imitation",
  "theme": "Everyday Conversations",
  "skillRecurrenceTags": ["polite-language", "social-skills", "imitation"],
  "relatedToPreviousSkills": ["echo-play-week3", "turn-taking-week4", "hello-goodbye-lesson51"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Polite Words!",
      "duration": "1 min",
      "content": {
        "goal": "Introduce 'please' and 'thank you' through modeling and imitation. These words help your baby get what they need and show appreciation.",
        "parentGuide": [
          "Polite words are powerful—they help babies communicate needs respectfully and get positive responses from others.",
          "Babies learn these words through imitation, not lectures. Hearing you say them constantly is how they learn.",
          "At this age, any attempt at 'please' or 'thank you' (even gestures) should be celebrated.",
          "These words teach patience—'please' means 'I'm asking nicely' and 'thank you' acknowledges someone helped.",
          "Using courtesy words early builds strong social skills and positive relationships with others."
        ],
        "materials": ["Toys, snacks, or objects to exchange", "Family members to practice with (optional)"],
        "tip": "Model these words in EVERY exchange—with your baby, with others, even when alone. 'Please' and 'thank you' should be constant background words.",
        "researchNote": "Snow (1983) - Children acquire social language through consistent modeling in natural contexts; parents who use polite words frequently raise children who use them earlier and more naturally."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Modeling 'Please'",
      "duration": "3-4 min",
      "focus": "Demonstrating How to Ask Politely",
      "researchBasis": "Rowe (2012) - Children learn functional language fastest when parents narrate social exchanges; modeling 'please' teaches both the word and its purpose.",
      "content": {
        "goal": "Show your baby how to use 'please' when asking for something. Say it every single time before giving them anything.",
        "whatToSay": [
          "\"When we want something, we say PLEASE!\"",
          "\"Can I have the ball? Please!\"",
          "\"Watch me ask nicely: Ball, please!\"",
          "\"Now you try! Say please!\"",
          "\"Good! Here's the ball! You said please!\"",
          "\"Please is how we ask nicely!\""
        ],
        "whatToDo": [
          "Hold a toy your baby wants (but don't give it yet).",
          "Say: 'Do you want the ball? Let's say PLEASE!'",
          "Model clearly: 'Ball, please!' (gesture toward ball)",
          "Wait 2-3 seconds for any response from your baby.",
          "Even if they don't say it, repeat: 'Please! Ball, please!' and hand it over.",
          "Every time you give your baby something, say 'please' first—for them if needed.",
          "Narrate exchanges: 'You want juice? Say please! Juice, please! Here you go!'",
          "Use simple phrases: 'More, please!' 'Toy, please!' 'Book, please!'",
          "Show your baby using 'please' with others: 'Daddy, can I have a hug? Please!'",
          "Encourage any attempt—babbling, reaching with intention, nodding all count!",
          "Pair 'please' with a gesture: hands together in front of chest (sign language 'please').",
          "Practice 10+ times during this activity with different objects."
        ],
        "examples": [
          "Baby reaches for snack → You say: 'Cracker? Say please!' → model 'Please!' → give cracker.",
          "Baby points at toy → You say: 'Car, please!' → wait → 'Please!' → hand over car.",
          "Baby makes any sound → You affirm: 'Yes! Please! Good asking!' → give item.",
          "You ask someone else: 'Can I have the cup? Please!' → baby hears model."
        ],
        "watchFor": [
          "Your baby attempting to make any sound when you prompt 'please.'",
          "Your baby using the 'please' gesture (hands together or reaching gently).",
          "Your baby pausing before grabbing—showing they're learning to ask first.",
          "Your baby looking at you expectantly after asking—waiting for your response."
        ],
        "parentTip": "Don't withhold items harshly if they don't say please—model and give. Positive association is key!",
        "funTwist": "Make a 'please game': hold up several toys, baby says 'please,' you hand them one. Keep going until they have a pile of toys!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Modeling 'Thank You'",
      "duration": "4-5 min",
      "focus": "Demonstrating Gratitude",
      "researchBasis": "Tomasello (2003) - Social gestures and words of gratitude build reciprocal interaction; children who learn 'thank you' early develop stronger social bonds.",
      "content": {
        "goal": "Show your baby how to say 'thank you' whenever they receive something. Build gratitude into every exchange.",
        "whatToSay": [
          "\"When someone gives us something, we say THANK YOU!\"",
          "\"Here's your toy! What do we say? Thank you!\"",
          "\"Watch me: 'Thank you, Daddy!' when Daddy gives me something!\"",
          "\"Now you say it! Thank you!\"",
          "\"You said thank you! That's so polite!\"",
          "\"Thank you shows we're happy someone helped us!\""
        ],
        "whatToDo": [
          "Give your baby a toy and immediately say: 'Here you go! Say thank you!'",
          "Model clearly: 'Thank you! Thank you for the toy!'",
          "Encourage any response—a smile, a nod, any sound.",
          "If no response, that's okay—YOU say thank you for them: 'You say thank you!'",
          "Repeat with different items: 'Here's your cup! Thank you! Here's your book! Thank you!'",
          "Show your baby thanking others: 'Daddy gave me water! Thank you, Daddy!'",
          "Have family members give baby things and prompt: 'What do we say? Thank you!'",
          "Pair 'thank you' with gesture: hand to mouth, then extend outward (sign language 'thank you').",
          "Narrate when others thank baby: 'You gave me the toy! Thank you, baby!'",
          "Practice 10+ exchanges during this activity.",
          "Make eye contact when saying thank you—show it's directed at a person.",
          "Use variations: 'Thanks!' 'Thank you so much!' 'Thanks a lot!'"
        ],
        "examples": [
          "You hand baby a snack → 'Here's your apple! Thank you!' → baby smiles → 'Good thank you!'",
          "Sibling hands baby a toy → You prompt: 'Say thank you to sister!' → baby looks at sister.",
          "Baby hands YOU a toy → You say enthusiastically: 'Thank you! Thank you for sharing!'",
          "Grandparent visits and waves → prompt baby: 'Wave and say thank you, Grandma!'"
        ],
        "watchFor": [
          "Your baby making any sound after receiving something.",
          "Your baby using the 'thank you' gesture (hand motion).",
          "Your baby smiling or showing appreciation—understanding the emotion behind the words.",
          "Your baby attempting to say 'tank-too,' 'tan-too,' or any approximation."
        ],
        "parentTip": "Always model thanking YOUR baby too! 'Thank you for the toy! Thank you for the hug!' It goes both ways!",
        "funTwist": "Exaggerate reactions: 'THANK YOU SO MUCH! THIS IS THE BEST TOY EVER!' Show extreme gratitude for even small exchanges!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Please and Thank You Exchange Game",
      "duration": "3-4 min",
      "focus": "Practicing Full Polite Exchange",
      "researchBasis": "Bruner (1983) - Turn-taking routines scaffold language development; structured exchanges teach conversational flow.",
      "content": {
        "goal": "Practice a complete polite exchange: ask with 'please,' receive item, say 'thank you.' This is a full conversation!",
        "whatToSay": [
          "\"Let's practice our polite words together!\"",
          "\"First, you ask: 'Ball, please!'\"",
          "\"Then I give you the ball!\"",
          "\"Then you say: 'Thank you!'\"",
          "\"That's a complete polite conversation!\"",
          "\"Let's do it again with a different toy!\""
        ],
        "whatToDo": [
          "Set up 5-6 toys in front of you, out of baby's reach.",
          "Point to the toys: 'Which one do you want?'",
          "Wait for baby to point or reach.",
          "Say: 'Say please! Car, please!'",
          "Wait for any attempt (sound, gesture, look).",
          "When they try, hand over the toy: 'Good asking!'",
          "Immediately prompt: 'Now say thank you!'",
          "Model: 'Thank you! You say thank you!'",
          "Accept any response and celebrate.",
          "Repeat with each toy: Want it? → Say please → Get it → Say thank you.",
          "After several rounds, try just prompting: 'What do you say?' without giving the exact word.",
          "Trade roles: baby 'gives' you toys, you model the full exchange.",
          "Make it a game: 'Let's see how many polite exchanges we can do!'",
          "Keep it light and playful—not demanding."
        ],
        "examples": [
          "Baby reaches for block → You: 'Block, please!' → Baby makes sound → You give block → You: 'Thank you!' → Baby smiles.",
          "You hold up two toys → Baby points → You: 'Which one? Say please!' → Baby tries → You give toy → You: 'Thank you!' → Baby attempts.",
          "Baby wants book → You: 'Book, please!' → help baby say it → hand book → prompt 'thank you' → baby nods → 'Great manners!'",
          "Reverse: Baby hands you toy → You: 'Thank you! May I have another? Please!' → baby gives another → You: 'Thank you again!'"
        ],
        "watchFor": [
          "Your baby attempting both words in sequence (please, then thank you).",
          "Your baby understanding the pattern: ask, receive, thank.",
          "Your baby showing excitement during the exchange—positive reinforcement working!",
          "Your baby initiating polite words without prompting—independent use!"
        ],
        "parentTip": "This is advanced! Don't expect perfection. Any part of the exchange baby does is success!",
        "funTwist": "Silly swap: YOU be the baby asking, baby 'gives' you toys, you model exaggerated polite words. Make it hilarious!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned polite words! You can ask nicely and say thank you!",
          "You practiced:",
          "✓ Saying 'please' when asking for something",
          "✓ Saying 'thank you' when receiving something",
          "✓ Doing complete polite exchanges",
          "✓ Being kind and respectful in conversation!"
        ],
        "reviewQuestions": [
          "Did your baby attempt to say or gesture 'please'?",
          "Did your baby respond to 'thank you' prompts?",
          "Could your baby follow the please-receive-thank you pattern?",
          "Did your baby enjoy the exchange game?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Polite words teach your baby that language gets results—and that kind language gets positive results. These words build social skills, teach patience, and help your baby form positive relationships. When your baby says 'please' and 'thank you,' they're participating in social cooperation—the foundation of community."
        },
        "nextSteps": [
          "Model please and thank you in EVERY exchange this week—make it automatic!",
          "Point out when others use polite words: 'Did you hear? Sister said please!'",
          "Tomorrow we learn 'my turn' and 'your turn'—sharing language!",
          "Practice with everyone: grandparents, siblings, pets—polite words for all!"
        ]
      }
    }
  ]
},

// ---------- LESSON 53 (Week 11 Day 3) ----------
{
  "id": 53,
  "phase": "1-2",
  "quarter": 1,
  "week": 11,
  "weekIndex": 10,
  "dayInWeek": 2,
  "title": "Lesson 53: \"Sharing Talk\" — Turn-Taking Language",
  "theme": "Everyday Conversations",
  "skillRecurrenceTags": ["turn-taking", "sharing-language", "patience"],
  "relatedToPreviousSkills": ["turn-taking-week4", "please-thank-you-lesson52", "echo-play-week3"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Sharing Talk!",
      "duration": "1 min",
      "content": {
        "goal": "Teach 'my turn' and 'your turn' phrases to help your baby understand taking turns and sharing. These words prevent frustration and build cooperation.",
        "parentGuide": [
          "Turn-taking language gives your baby words for a hard concept—waiting and sharing.",
          "When babies have words for turn-taking, conflicts decrease because they can communicate instead of grabbing.",
          "This teaches patience—'your turn' means 'I wait,' and 'my turn' means 'now I get to play.'",
          "Turn-taking is the structure of all conversations—learning it now prepares your baby for back-and-forth dialogue.",
          "These phrases help your baby feel in control—they know a turn IS coming, which reduces anxiety."
        ],
        "materials": ["Toys that can be shared or passed back and forth (ball, blocks, toy car)", "Items for turn-taking activities (bubbles, musical instruments)"],
        "tip": "Always follow through on turn promises—if you say 'your turn,' make sure your baby gets their turn immediately!",
        "researchNote": "Brownell et al. (2006) - Toddlers who practice structured turn-taking with verbal cues show better cooperation skills and less aggressive behavior during play."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Learning 'My Turn' and 'Your Turn'",
      "duration": "3-4 min",
      "focus": "Introducing Turn-Taking Language",
      "researchBasis": "Hay et al. (1991) - Verbal labeling of turns during play helps toddlers internalize turn-taking rules and reduces frustration during shared activities.",
      "content": {
        "goal": "Practice saying 'my turn' and 'your turn' while passing a toy back and forth. Make the language match the action.",
        "whatToSay": [
          "\"Let's learn about turns! Watch!\"",
          "\"MY turn! I have the ball! My turn!\"",
          "\"Now YOUR turn! You have the ball! Your turn!\"",
          "\"My turn! Your turn! My turn! Your turn!\"",
          "\"We're taking turns! You wait, then I wait!\"",
          "\"Taking turns is sharing!\""
        ],
        "whatToDo": [
          "Sit facing your baby with a toy between you (ball, block, or toy car).",
          "Pick up the toy and say clearly: 'MY turn! I have the toy! My turn!'",
          "Hold it for 2-3 seconds, showing possession.",
          "Hand it to your baby: 'Now YOUR turn! You have it! Your turn!'",
          "Let them hold it for 2-3 seconds.",
          "Gently take it back: 'Now my turn again! My turn!'",
          "Repeat this pattern 10+ times in quick succession.",
          "Say 'my turn' and 'your turn' EVERY single time the toy changes hands.",
          "Keep the pace quick so turns happen frequently—this prevents frustration.",
          "Point to yourself when saying 'my turn,' point to baby when saying 'your turn.'",
          "Make it rhythmic: 'My turn! Your turn! My turn! Your turn!' like a chant.",
          "Celebrate: 'We're taking turns! Great sharing!'"
        ],
        "examples": [
          "Rolling ball: 'My turn to roll! (roll to baby) Your turn to roll! (baby rolls back)'",
          "Stacking blocks: 'My turn to add a block! (place block) Your turn! (baby adds block)'",
          "Playing instrument: 'My turn to shake! (shake rattle) Your turn to shake! (hand to baby)'",
          "Pushing car: 'My turn—vroom! (push car) Your turn—vroom! (baby pushes)'"
        ],
        "watchFor": [
          "Your baby waiting for their turn—showing they understand the pattern.",
          "Your baby attempting to say 'turn,' 'my,' or any approximation.",
          "Your baby reaching for the toy only when you say 'your turn.'",
          "Your baby handing the toy back when you say 'my turn'—cooperation!"
        ],
        "parentTip": "Keep turns SHORT at first—just a few seconds each. Long waits are hard for babies!",
        "funTwist": "Speed turns: 'My turn-your turn-my turn-your turn!' super fast, then slow: 'Myyyyy tuuuuurn... youuuuur tuuuuurn!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Turn-Taking with Actions",
      "duration": "4-5 min",
      "focus": "Taking Turns with Activities, Not Just Objects",
      "researchBasis": "Eckerman et al. (1989) - Turn-taking in action sequences (clapping, jumping) helps children understand reciprocity beyond object exchange.",
      "content": {
        "goal": "Practice turn-taking with actions like clapping, jumping, or making sounds. This shows that turns aren't just about toys—they're about participation.",
        "whatToSay": [
          "\"Let's take turns doing actions!\"",
          "\"MY turn to clap! (clap clap clap) My turn!\"",
          "\"YOUR turn to clap! Clap-clap-clap! Your turn!\"",
          "\"My turn to jump! Your turn to jump!\"",
          "\"We're taking turns! You do it, then I do it!\"",
          "\"Turn-taking is so much fun!\""
        ],
        "whatToDo": [
          "Stand or sit where your baby can see you clearly.",
          "Say: 'Let's take turns making sounds!'",
          "Start: 'My turn!' then clap three times loudly.",
          "Stop and say: 'Your turn!' and encourage baby to clap.",
          "Even if they clap once, celebrate: 'You took your turn!'",
          "Take your turn again: 'My turn!' (clap three times).",
          "Hand it back: 'Your turn!' (wait for baby).",
          "Try different actions: clapping, stomping, jumping, waving, making sounds.",
          "Always say 'my turn' and 'your turn' with each switch.",
          "Make it playful: 'My turn to make a silly face!' (make face) 'Your turn!' (baby tries).",
          "Practice 5-6 different actions with turn-taking.",
          "If baby takes a turn when it's YOUR turn, gently say: 'Wait! My turn first! Then your turn!'",
          "Build anticipation: 'Get ready... YOUR TURN!' (baby loves the excitement)"
        ],
        "examples": [
          "Clapping: 'My turn—clap-clap-clap! Your turn—clap-clap-clap!'",
          "Stomping: 'My turn—stomp-stomp! Your turn—stomp-stomp!'",
          "Making sounds: 'My turn—ROAR! Your turn—ROAR!'",
          "Dancing: 'My turn to dance! (spin around) Your turn to dance! (baby moves)'"
        ],
        "watchFor": [
          "Your baby waiting for 'your turn' before acting—impulse control!",
          "Your baby attempting the action when prompted.",
          "Your baby getting excited when they hear 'your turn!'",
          "Your baby trying to say 'turn' or 'my' or 'you.'"
        ],
        "parentTip": "If your baby acts during your turn, don't scold—just gently pause and clarify: 'Wait! My turn! Watch me!'",
        "funTwist": "Surprise turns: 'My turn to... tickle you! Your turn to... tickle me!' Make unexpected, silly actions!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Turn-Taking with Waiting Practice",
      "duration": "3-4 min",
      "focus": "Building Patience During Turns",
      "researchBasis": "Diamond (1990) - Practice with delayed gratification (waiting for turns) strengthens prefrontal cortex development and builds self-control.",
      "content": {
        "goal": "Gradually lengthen the time between turns so your baby practices waiting. This builds patience and trust that their turn will come.",
        "whatToSay": [
          "\"My turn is a little longer this time. You're waiting so nicely!\"",
          "\"Wait, wait, wait... YOUR TURN! You waited!\"",
          "\"My turn... I'm still playing... almost done... YOUR turn!\"",
          "\"You waited for your turn! Great patience!\"",
          "\"Waiting is hard, but you did it!\"",
          "\"Your turn is coming!\""
        ],
        "whatToDo": [
          "Start with the ball or toy again.",
          "Say: 'My turn!' and hold the toy for 5 seconds (a bit longer than before).",
          "While holding it, acknowledge baby: 'You're waiting! Good waiting!'",
          "After 5 seconds: 'YOUR turn!' Hand it over enthusiastically.",
          "Let baby have their turn for 5 seconds.",
          "Take it back: 'My turn again!' Hold for 7-8 seconds this time.",
          "During your turn, narrate: 'I'm playing with the car. Vroom-vroom. You're waiting!'",
          "Give it back: 'Thank you for waiting! YOUR turn!'",
          "Gradually increase 'my turn' time to 10 seconds.",
          "If baby gets frustrated, shorten your turn immediately: 'Okay! Your turn right now!'",
          "Praise all waiting: 'You waited so well! That's hard!'",
          "Keep 'your turn' consistently long—don't make baby wait longer than you do.",
          "End on a positive: 'We practiced waiting and taking turns! You're so good at this!'"
        ],
        "examples": [
          "Bubble blowing: 'My turn to blow bubbles! (blow 3 bubbles) Your turn! (hand wand to baby)'",
          "Book reading: 'My turn to turn the page! (turn page slowly) Your turn to turn! (baby turns page)'",
          "Toy phone: 'My turn to talk! (pretend call for 5 seconds) Your turn to talk! (hand phone)'",
          "Musical toy: 'My turn to press! (press buttons) Wait, wait... YOUR turn! (baby presses)'"
        ],
        "watchFor": [
          "Your baby staying calm while waiting—not grabbing or crying.",
          "Your baby showing excitement when 'your turn' is announced—positive anticipation!",
          "Your baby understanding that waiting means a turn IS coming.",
          "Your baby's wait time increasing—building patience over time."
        ],
        "parentTip": "If baby can't wait, that's okay! Shorten turns. Success is more important than long waits right now.",
        "funTwist": "Count down to turns: 'My turn! 3... 2... 1... YOUR TURN!' The countdown builds excitement and helps baby wait!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned to take turns! You can share and wait!",
          "You practiced:",
          "✓ Saying 'my turn' and 'your turn'",
          "✓ Taking turns with toys and actions",
          "✓ Waiting patiently for your turn",
          "✓ Sharing and cooperating!"
        ],
        "reviewQuestions": [
          "Did your baby wait for their turn at least once?",
          "Did your baby hand the toy back during 'my turn'?",
          "Did your baby attempt to say 'turn' or point to themselves?",
          "Did your baby show less frustration with turn-taking language?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Turn-taking language gives your baby control over a hard situation. Instead of confusion and grabbing, they now have words that help them understand waiting and sharing. These phrases reduce conflicts and teach cooperation—essential skills for playing with others and eventually for classroom learning."
        },
        "nextSteps": [
          "Use 'my turn' and 'your turn' during ALL shared activities this week!",
          "Practice with siblings, playmates, or even pets: 'My turn to pet the dog! Your turn!'",
          "Tomorrow we learn emotion words—happy, sad, tired!",
          "Point out turn-taking everywhere: 'Look! The cars are taking turns at the stop sign!'"
        ]
      }
    }
  ]
},

// ---------- LESSON 54 (Week 11 Day 4) ----------
{
  "id": 54,
  "phase": "1-2",
  "quarter": 1,
  "week": 11,
  "weekIndex": 10,
  "dayInWeek": 3,
  "title": "Lesson 54: \"Emotion Words\" — Happy, Sad, Tired",
  "theme": "Everyday Conversations",
  "skillRecurrenceTags": ["emotion-vocabulary", "self-expression", "emotional-literacy"],
  "relatedToPreviousSkills": ["facial-expressions-week3", "naming-week5", "social-words-lesson51"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Feelings Words!",
      "duration": "1 min",
      "content": {
        "goal": "Teach your baby to recognize and name basic emotions—happy, sad, and tired. These words help your baby express feelings instead of just crying.",
        "parentGuide": [
          "Emotion words give your baby power—they can tell you how they feel instead of melting down.",
          "Naming emotions helps babies understand and regulate their feelings—this is emotional intelligence.",
          "When babies can say 'sad' or 'tired,' parents can respond to real needs instead of guessing.",
          "Learning emotion words early predicts better social skills and fewer behavioral problems later.",
          "This connects to facial expressions from Week 3—now we add the WORDS to the faces."
        ],
        "materials": ["Mirror", "Pictures of faces showing emotions (from books, photos, or drawings)", "Your expressive face!"],
        "tip": "Name YOUR emotions throughout the day: 'Mommy is tired!' 'Daddy is happy!' Model using feeling words constantly.",
        "researchNote": "Denham et al. (2003) - Children who learn emotion vocabulary early show better emotional regulation, fewer tantrums, and stronger social relationships in preschool."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Learning 'Happy'",
      "duration": "3-4 min",
      "focus": "Recognizing and Naming Happy Feelings",
      "researchBasis": "Izard et al. (2001) - Teaching children to label positive emotions increases their ability to maintain and return to positive emotional states.",
      "content": {
        "goal": "Teach your baby what 'happy' means by showing happy faces, making happy sounds, and connecting 'happy' to joyful moments.",
        "whatToSay": [
          "\"Let's learn about HAPPY! Happy means feeling good!\"",
          "\"Look at my happy face! I'm smiling! I'm HAPPY!\"",
          "\"When you laugh, you're HAPPY!\"",
          "\"When we play, we're HAPPY!\"",
          "\"Can you show me a happy face? Big smile! HAPPY!\"",
          "\"You look HAPPY! I see your smile!\""
        ],
        "whatToDo": [
          "Sit with your baby facing a mirror or facing you.",
          "Make a BIG smile: 'This is HAPPY! See my happy face?'",
          "Point to your smile: 'Happy! I'm smiling because I'm happy!'",
          "Say the word clearly 3-4 times while smiling.",
          "Help your baby touch your smile: 'Feel my happy smile!'",
          "Now touch baby's mouth: 'Make a happy smile!'",
          "Tickle or do something that makes baby laugh.",
          "When they laugh: 'You're HAPPY! You're laughing! HAPPY!'",
          "Look in mirror together: 'We're both happy! See our happy faces?'",
          "Point out happy moments: 'You got your toy! You're HAPPY!'",
          "Clap and cheer: 'When we clap, we feel HAPPY!'",
          "Repeat 'happy' 10+ times during this activity."
        ],
        "examples": [
          "Baby giggles → 'You're happy! Listen to your happy laugh!'",
          "Show picture of smiling face → 'This baby is HAPPY! See the smile?'",
          "Sing a happy song → 'We're singing! We feel HAPPY when we sing!'",
          "Give baby favorite toy → 'You love this toy! You're HAPPY now!'"
        ],
        "watchFor": [
          "Your baby smiling when you say 'happy.'",
          "Your baby attempting to copy your happy face.",
          "Your baby making sounds that match happy moments.",
          "Your baby associating 'happy' with positive experiences."
        ],
        "parentTip": "Connect 'happy' to things baby loves—their favorite activities, toys, or people. Make it personal!",
        "funTwist": "Happy dance! 'When we feel happy, we can dance! Let's do the happy dance!' Wiggle and dance together!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Learning 'Sad'",
      "duration": "4-5 min",
      "focus": "Recognizing and Naming Sad Feelings",
      "researchBasis": "Brownell et al. (2013) - Teaching toddlers to label negative emotions reduces tantrums and aggressive behavior by giving them words instead of actions.",
      "content": {
        "goal": "Teach your baby what 'sad' means and help them understand that sad feelings are normal and can be expressed with words.",
        "whatToSay": [
          "\"Sometimes we feel SAD. Sad is when we feel not happy.\"",
          "\"Look at my sad face. (frown) I'm SAD.\"",
          "\"When something makes us sad, we can cry or frown.\"",
          "\"It's okay to feel sad! Everyone feels sad sometimes!\"",
          "\"When you're sad, you can tell me! Say 'sad!'\"",
          "\"I can help when you're sad!\""
        ],
        "whatToDo": [
          "Make a sad face—frown, turn down the corners of your mouth.",
          "Say: 'This is SAD. See my sad face? I'm frowning.'",
          "Point to your frown: 'My mouth goes down when I'm sad.'",
          "Make a sad sound: 'When we're sad we might say 'awww' or we might cry.'",
          "Show baby a picture of a sad face: 'This baby is sad. See? No smile.'",
          "Talk about sad moments gently: 'When you fell, you felt sad. You cried.'",
          "Validate the emotion: 'It's okay to be sad! Sad is a real feeling!'",
          "Show transition from sad to happy: 'I was sad, but now I'm happy! My face changed!'",
          "Make it interactive: 'Can you make a sad face? Frown with me!'",
          "Give examples: 'When toy breaks, we feel sad. When we say goodbye, sometimes we're sad.'",
          "Comfort: 'When you're sad, I give you hugs! Hugs help sad feelings!'",
          "Don't dwell too long on sad—keep it brief and informative."
        ],
        "examples": [
          "Show picture of crying baby → 'This baby is SAD. Maybe they want mommy.'",
          "Drop a toy on purpose → Make sad face → 'Oh no! I'm sad! The toy fell! Sad!'",
          "Pretend to hurt finger → 'Ow! That makes me sad! I'm sad my finger hurts!' → then recover → 'But now I'm okay! Happy again!'",
          "Read book with sad character → 'Look! The bear is sad! He lost his friend! Sad!'"
        ],
        "watchFor": [
          "Your baby watching your sad face carefully—studying the expression.",
          "Your baby showing recognition when you name a sad moment they experienced.",
          "Your baby attempting to make a sad face.",
          "Your baby using 'sad' (or attempting it) when upset."
        ],
        "parentTip": "Don't make sad TOO dramatic or scary. Keep it calm and matter-of-fact: 'This is what sad looks like. It's normal.'",
        "funTwist": "Emotion switch game: 'Happy! (big smile) Sad! (frown) Happy! (smile) Sad! (frown)' Switch back and forth quickly!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Learning 'Tired'",
      "duration": "3-4 min",
      "focus": "Recognizing and Naming Tired Feelings",
      "researchBasis": "Scher et al. (2010) - Toddlers who can communicate tiredness show better sleep patterns and less bedtime resistance because parents can respond appropriately.",
      "content": {
        "goal": "Teach your baby to recognize when they feel tired and to use the word 'tired' instead of just getting cranky.",
        "whatToSay": [
          "\"Sometimes our body feels TIRED. Tired means we need rest.\"",
          "\"Look at my tired face. (yawn) I'm TIRED. Yaaaawn!\"",
          "\"When we're tired, we yawn and rub our eyes.\"",
          "\"Babies feel tired after playing a lot!\"",
          "\"When you're tired, tell me! Say 'tired!'\"",
          "\"Then we can rest or take a nap!\""
        ],
        "whatToDo": [
          "Make a tired face: yawn big and slow, rub your eyes.",
          "Say: 'I'm TIRED! Watch me yawn! Yaaaaawn!'",
          "Point to your mouth: 'Tired people yawn. Yaaaawn!'",
          "Rub your eyes: 'When we're tired, our eyes feel heavy.'",
          "Stretch: 'Tired people stretch. Stretch with me!'",
          "Pretend to be tired: move slowly, speak softly, slump a bit.",
          "Say: 'After we play a lot, we get TIRED!'",
          "Name tired moments: 'Before bedtime, you're tired. After the park, you're tired.'",
          "Show tired actions: 'Tired babies might cry or get grumpy. That's okay!'",
          "Offer solutions: 'When we're tired, we rest! We read quiet books or nap.'",
          "Make it gentle and calm—tired is a peaceful feeling, not scary.",
          "Look for baby's tired signs NOW: 'Are you rubbing your eyes? Are you tired?'"
        ],
        "examples": [
          "After active play → Sit down, yawn → 'I'm tired! We played so much! Yaawn! Tired!'",
          "Before naptime → 'Your eyes are sleepy! You look tired! Time to rest!'",
          "Show picture of sleeping baby → 'This baby is TIRED! They're sleeping! Tired means sleep time!'",
          "During story → Yawn together → 'We're both tired! Tired reading together!'"
        ],
        "watchFor": [
          "Your baby yawning when you yawn—imitation!",
          "Your baby rubbing eyes when you talk about tired.",
          "Your baby slowing down or getting quieter—feeling the tired vibe.",
          "Your baby associating 'tired' with rest or sleep time."
        ],
        "parentTip": "Use 'tired' consistently before naps and bedtime: 'You're tired! Time to sleep!' This helps baby understand the connection.",
        "funTwist": "Tired slow-motion game: 'When we're tired, everything goes slow! Walk tired! Talk tired! Move so slow because we're so tired!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned feeling words! You can talk about emotions!",
          "You practiced:",
          "✓ Recognizing and saying 'happy'",
          "✓ Understanding 'sad' feelings",
          "✓ Knowing when you're 'tired'",
          "✓ Expressing how you feel with words!"
        ],
        "reviewQuestions": [
          "Did your baby show recognition of any emotion faces?",
          "Did your baby attempt to say or gesture any emotion words?",
          "Could your baby make emotion faces (happy smile, sad frown)?",
          "Did connecting words to feelings seem to help your baby?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Emotion words are some of the most important vocabulary your baby will ever learn. When they can say 'sad' instead of just crying, or 'tired' instead of melting down, you can respond to their real needs. This is emotional intelligence—the ability to understand and express feelings—which predicts success in school, friendships, and life."
        },
        "nextSteps": [
          "Label emotions ALL DAY: 'You're happy! I'm tired! The dog is excited!'",
          "Point out emotions in books, TV, and real life constantly.",
          "Tomorrow we learn 'I want' language—expressing desires!",
          "Add more emotions gradually: mad, scared, excited, surprised!"
        ]
      }
    }
  ]
},

// ---------- LESSON 55 (Week 11 Day 5) ----------
{
  "id": 55,
  "phase": "1-2",
  "quarter": 1,
  "week": 11,
  "weekIndex": 10,
  "dayInWeek": 4,
  "title": "Lesson 55: \"What Do You Want?\" — Choice and Response",
  "theme": "Everyday Conversations",
  "skillRecurrenceTags": ["expressive-language", "choice-making", "communication-initiation"],
  "relatedToPreviousSkills": ["naming-week5", "social-words-lesson51", "emotion-words-lesson54"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Choice Talk!",
      "duration": "1 min",
      "content": {
        "goal": "Teach your baby to express wants and make choices using simple phrases like 'I want ___.' This gives them voice and control, reducing frustration.",
        "parentGuide": [
          "When babies can say what they want, they cry less—words replace frustration!",
          "Offering choices builds independence and decision-making skills early.",
          "The phrase 'I want' is powerful—it's baby's first sentence structure!",
          "Choice-making teaches cause and effect: I say what I want, and I get it!",
          "This connects to all the naming work from Week 5—now we use those words in sentences."
        ],
        "materials": ["2-3 favorite toys", "2 snack options", "Books or other items baby likes"],
        "tip": "Always offer REAL choices you're okay with—don't offer something you'll take away. Build trust!",
        "researchNote": "Tomasello & Farrar (1986) - When toddlers initiate communication and get responses, their language development accelerates faster than passive language exposure."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Two-Choice Game",
      "duration": "4-5 min",
      "focus": "Choosing Between Two Options",
      "researchBasis": "Koegel et al. (2012) - Offering toddlers choices increases motivation, attention, and language output because they feel agency and control.",
      "content": {
        "goal": "Present two clear options and teach your baby to point, reach, or say which one they want. This is the foundation of expressing preferences.",
        "whatToSay": [
          "\"Do you want the BALL or the CAR? Which one?\"",
          "\"Show me! Point to what you want!\"",
          "\"You want the BALL? Okay! Here's the ball!\"",
          "\"You picked the ball! You said what you want!\"",
          "\"Good choice! You told me what you want!\""
        ],
        "whatToDo": [
          "Hold two toys, one in each hand, at baby's eye level.",
          "Name both clearly: 'Here's the BALL (shake it) and here's the CAR (shake it).'",
          "Ask: 'Which one do you want? Ball or car?'",
          "Wait 5-10 seconds for baby to respond—give them time to process!",
          "Watch for ANY response: reaching, pointing, looking, vocalizing, leaning.",
          "When baby responds: 'You want the BALL! Here you go!'",
          "Hand them the toy immediately—reward their communication!",
          "Celebrate: 'You told me what you want! You picked the ball!'",
          "Play with that toy for 1-2 minutes.",
          "Repeat with different toy pairs: book vs stuffed animal, block vs puzzle piece.",
          "Make it clear: hold toys far apart so choice is obvious.",
          "Accept ANY indication of preference—doesn't have to be perfect pointing!"
        ],
        "examples": [
          "Food choice: 'Do you want BANANA (hold up) or CRACKER (hold up)? Which one?'",
          "Book choice: 'Should we read THIS book or THAT book? You pick!'",
          "Activity choice: 'Do you want to SING (gesture) or DANCE (gesture)?'",
          "Toy choice: 'Bear or ball? Show me which one you want!'"
        ],
        "watchFor": [
          "Your baby looking longer at one option—visual preference!",
          "Your baby reaching toward one toy—clear choice!",
          "Your baby making sounds toward one option—verbal attempt!",
          "Your baby showing frustration if you give the wrong one—they KNOW what they want!"
        ],
        "parentTip": "Start with favorite items baby already knows—makes choosing easier. Graduate to new items later!",
        "funTwist": "Switch hands! After baby picks, switch the toys to opposite hands and offer again: 'Now which one?' See if they track the item!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: 'I Want' Modeling",
      "duration": "4-5 min",
      "focus": "Teaching the 'I Want' Phrase",
      "researchBasis": "Bloom et al. (1975) - Children's first two-word combinations often express desire ('want cookie'), showing this is a natural and important early sentence structure.",
      "content": {
        "goal": "Model the phrase 'I want ___' repeatedly so your baby learns this powerful sentence structure for expressing needs.",
        "whatToSay": [
          "\"I want the ball. Say it with me: I want ball.\"",
          "\"You want the car? Say 'I want car!'\"",
          "\"I... want... ball. Your turn! I want ball!\"",
          "\"Yes! You said 'I want!' You told me what you want!\"",
          "\"I want book! I want book! Let's read!\""
        ],
        "whatToDo": [
          "Hold up a toy baby likes.",
          "Say clearly: 'I want the ball. I WANT the BALL.'",
          "Point to yourself: 'I (point to self) want (gesture want) ball (hold up ball).'",
          "Repeat 3 times: 'I want ball. I want ball. I want ball.'",
          "Now prompt baby: 'You try! Say I want ball!'",
          "Wait for ANY attempt: 'ba' counts, 'I' counts, 'want' counts!",
          "If baby makes any sound: 'Yes! I want ball! Here's the ball!' Give it immediately.",
          "If baby doesn't say it: 'That's okay! I'll say it FOR you: YOU want ball! Here!'",
          "Repeat with different items: 'I want book. I want teddy. I want snack.'",
          "Use gestures: point to self for 'I,' reach out hands for 'want.'",
          "Break it down: Start with just 'want ___' if 'I want' is too much.",
          "Model constantly: When YOU want something, say 'I want water! I want my phone!'"
        ],
        "examples": [
          "Snack time: 'You want more? Say I want more! I... want... more!' (accept any attempt)",
          "Play: 'I want to play! I want this toy! Say it: I want toy!'",
          "Books: 'Which book? I want THIS one! You try: I want book!'",
          "Outside: 'I want to go outside! I want outside! Say: I want outside!'"
        ],
        "watchFor": [
          "Your baby attempting to copy ANY part of the phrase—even just 'want!'",
          "Your baby using 'want' sound consistently when desiring something.",
          "Your baby combining 'want' with object names from Week 5!",
          "Your baby getting excited when you model the phrase—recognizing its power!"
        ],
        "parentTip": "Don't demand perfect pronunciation! 'Wa ba' for 'want ball' is GREAT! Accept approximations and reward effort!",
        "funTwist": "Silly wants: 'I want... socks on my head! I want... to hop like a bunny!' Model 'I want' with silly actions!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Real-Life Choice Practice",
      "duration": "3-4 min",
      "focus": "Using Choice Language Throughout Daily Routines",
      "researchBasis": "Hart & Risley (1995) - The amount of conversational turns between parent and child predicts vocabulary growth; offering choices naturally creates back-and-forth exchanges.",
      "content": {
        "goal": "Integrate choice-making into everyday moments—snack time, getting dressed, playtime—so baby practices expressing wants all day long.",
        "whatToSay": [
          "\"Snack time! Do you want crackers or fruit? Which one?\"",
          "\"Getting dressed! Red shirt or blue shirt? You pick!\"",
          "\"Where should we go? Living room or bedroom? Show me!\"",
          "\"Which song? Twinkle Twinkle or Itsy Bitsy? You choose!\"",
          "\"You wanted the cracker! Here you go! You told me what you want!\""
        ],
        "whatToDo": [
          "During snack time: Hold up two foods. 'Which one? Banana or cheese?'",
          "Wait for response—pointing, reaching, looking, vocalizing.",
          "Give chosen item immediately: 'You picked banana! Here!'",
          "During play: 'Which toy next? Blocks or books?'",
          "Getting dressed: 'These socks or those socks?' Let baby touch one.",
          "Before bath: 'Which toy in the bath? Duck or boat?'",
          "Throughout the day: ALWAYS offer choices when possible.",
          "Honor their choices! If they pick it, they get it (within reason).",
          "Narrate their choice: 'You wanted the banana! I gave you the banana! You TOLD me what you want!'",
          "Make it conversational: 'Oh, you want the blue shirt? Good choice! Here we go!'",
          "Gradually expand: Start with 2 choices, later try 3 (but not yet—that's too much!)",
          "If baby seems overwhelmed, simplify: 'Do you want THIS? Yes or no?'"
        ],
        "examples": [
          "Mealtime: 'Milk or water? Which cup?' Wait for choice. 'Milk! Okay, here's milk!'",
          "Bedtime: 'Which book tonight? This one or this one?' Baby points. 'Good pick!'",
          "Diaper change: 'Which toy to hold? Bear or rattle?' Give baby control over something!",
          "Car ride: 'Music or quiet? Should we sing?' Wait for response, follow their lead!"
        ],
        "watchFor": [
          "Your baby responding faster to choices as they learn the routine.",
          "Your baby getting upset if you don't offer a choice they expected!",
          "Your baby attempting words along with pointing—combining skills!",
          "Your baby starting to initiate requests without being asked!"
        ],
        "parentTip": "Choice-making reduces tantrums! When baby feels some control, they're happier and more cooperative!",
        "funTwist": "Surprise choice: 'Do you want SOCKS... or BANANAS? (silly!) Oh, bananas! Of course! Let's eat!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You learned to tell us what you want! You have a voice!",
          "You practiced:",
          "✓ Choosing between two options",
          "✓ Learning 'I want' language",
          "✓ Making real choices all day long",
          "✓ Expressing your preferences instead of guessing!"
        ],
        "reviewQuestions": [
          "Did your baby clearly choose between two options?",
          "Did your baby attempt to say or gesture 'want' or 'I want'?",
          "Did offering choices seem to reduce frustration?",
          "Could you see your baby understanding their power to communicate?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Learning to express wants is a HUGE milestone. When your baby can tell you what they need—even with a point or single word—they're taking control of their world. This reduces crying, builds confidence, and creates real communication. The phrase 'I want' is often one of the first sentence structures babies master, and it opens the door to all future language!"
        },
        "nextSteps": [
          "Offer choices EVERYWHERE: meals, clothes, toys, activities!",
          "Always follow through—honor their choices to build trust!",
          "Next week (Week 12) we combine sound, stories, and movement!",
          "Keep modeling 'I want' constantly—they'll start saying it soon!"
        ]
      }
    }
  ]
},

// ---------- LESSON 56 (Week 12 Day 1) ----------
{
  "id": 56,
  "phase": "1-2",
  "quarter": 1,
  "week": 12,
  "weekIndex": 11,
  "dayInWeek": 0,
  "title": "Lesson 56: \"Rhythm Retell\" — Story with Claps",
  "theme": "Sound and Story Integration",
  "skillRecurrenceTags": ["rhythm", "story-comprehension", "phonological-awareness"],
  "relatedToPreviousSkills": ["clapping-week1", "rhythm-awareness-week4", "story-echoes-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Rhythm Stories!",
      "duration": "1 min",
      "content": {
        "goal": "Retell familiar stories or rhymes using ONLY rhythm and clapping patterns—no words! This strengthens memory, pattern recognition, and phonological awareness.",
        "parentGuide": [
          "Rhythm is the skeleton of language—hearing rhythm patterns helps babies learn syllables and word boundaries.",
          "Retelling stories through rhythm strengthens memory and comprehension without needing words.",
          "This integrates all our sound work (Weeks 1-3) with story time (Week 10)—bringing skills together!",
          "Clapping stories builds anticipation and active participation in reading.",
          "This week focuses on combining everything we've learned—sound, movement, and stories become ONE!"
        ],
        "materials": ["Familiar rhyme or story (Twinkle Twinkle, Pat-a-Cake, Itsy Bitsy Spider)", "Your hands for clapping!", "Optional: small drum or shaker"],
        "tip": "Choose rhymes baby knows VERY well—familiarity makes the rhythm version easier to recognize!",
        "researchNote": "Trainor & Corrigall (2010) - Rhythm training in infancy improves both musical and linguistic processing, including syllable awareness and later reading skills."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Clap the Rhyme",
      "duration": "4-5 min",
      "focus": "Clapping Rhythm of Familiar Rhymes",
      "researchBasis": "Anvari et al. (2002) - Musical rhythm activities in early childhood correlate with phonological awareness and reading readiness in kindergarten.",
      "content": {
        "goal": "Take a rhyme baby knows and clap out its rhythm without saying the words. Baby learns to recognize stories by their sound patterns alone.",
        "whatToSay": [
          "\"Let's clap Twinkle Twinkle! No words—just claps!\"",
          "\"Listen! (clap the rhythm) Can you guess what song that is?\"",
          "\"Yes! Twinkle Twinkle! You knew it from the claps!\"",
          "\"Now let's clap it TOGETHER!\"",
          "\"Every clap is one sound in the song!\""
        ],
        "whatToDo": [
          "Choose a rhyme baby knows well: 'Twinkle Twinkle Little Star.'",
          "First, sing it normally while clapping the beat: 'Twin-kle twin-kle lit-tle star' (7 claps).",
          "Now, clap ONLY—no words: (clap clap clap clap clap clap clap) in the rhythm.",
          "Ask: 'What song was that? It's Twinkle Twinkle!'",
          "Sing it again, this time having baby clap WITH you.",
          "Try again: clap-only version. See if baby recognizes it!",
          "Do this with multiple familiar rhymes: Pat-a-Cake, Itsy Bitsy Spider.",
          "Clap the rhythm slowly at first, then faster.",
          "Emphasize the beat: make some claps louder for stressed syllables.",
          "If baby doesn't recognize it, give hints: hum the tune while clapping.",
          "Celebrate when they connect the claps to the rhyme: 'You KNEW it!'",
          "Let baby TRY to clap the rhythm—doesn't need to be perfect!"
        ],
        "examples": [
          "'Pat-a-Cake': (clap clap clap clap / clap clap clap) → 'Pat-a-cake, pat-a-cake, baker's man!'",
          "'Row Row Row Your Boat': (clap clap clap-clap clap) → Row row row your boat!",
          "'Itsy Bitsy Spider': (clap-clap clap-clap clap-clap clap) → The itsy bitsy spider!",
          "'Old MacDonald': (clap clap clap-clap clap clap clap) → Old MacDonald had a farm!"
        ],
        "watchFor": [
          "Your baby bobbing their head or body to the rhythm—feeling the beat!",
          "Your baby attempting to clap along—rhythm participation!",
          "Your baby showing recognition—lighting up when they realize which rhyme it is!",
          "Your baby clapping at the right tempo—matching your speed!"
        ],
        "parentTip": "Start with the MOST familiar rhyme—the one you sing every day! Success builds confidence!",
        "funTwist": "Speed challenge: Clap Twinkle Twinkle SLOW... now FAST! Which is easier to recognize?"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Rhythm Guessing Game",
      "duration": "4-5 min",
      "focus": "Identifying Stories by Rhythm Alone",
      "researchBasis": "Goswami et al. (2013) - Rhythm perception in infancy predicts phonological skills and reading development—babies who are better at rhythm tasks show stronger language skills later.",
      "content": {
        "goal": "Clap multiple different rhyme rhythms and see if baby can identify which one you're performing. This is memory + pattern recognition!",
        "whatToSay": [
          "\"I'm going to clap a song! You guess which one!\"",
          "\"Listen carefully! (clap rhythm) What song is that?\"",
          "\"Is it Twinkle Twinkle? Is it Pat-a-Cake? Which one?\"",
          "\"YES! It's Twinkle Twinkle! You guessed it!\"",
          "\"Let's try another one!\""
        ],
        "whatToDo": [
          "Sit facing baby.",
          "Say: 'I'm going to clap a rhyme. You guess which one!'",
          "Clap the rhythm of 'Twinkle Twinkle' (no words).",
          "Wait for ANY response—pointing to a book, vocalizing, bouncing.",
          "After a few seconds: 'It was Twinkle Twinkle! Let's sing it to check!' Sing it.",
          "Try another: Clap 'Pat-a-Cake' rhythm.",
          "If baby seems confused, give hints: 'We make a cake in this one!'",
          "When baby responds (even just looking interested): 'Yes! Pat-a-Cake! You're so smart!'",
          "Do 3-4 different rhymes: make it a game!",
          "If baby can't guess, that's OKAY! You're building the skill!",
          "Name the rhyme after clapping: 'That was Itsy Bitsy Spider! Let's sing it!'",
          "Let baby CLAP one and you guess: 'What song are YOU clapping? Show me!'"
        ],
        "examples": [
          "Clap 'Baa Baa Black Sheep': (clap clap clap clap clap) → 'It's Baa Baa Black Sheep!'",
          "Clap 'Mary Had a Little Lamb': (clap-clap-clap clap clap) → 'Mary Had a Little Lamb!'",
          "Clap 'Wheels on the Bus': (clap clap clap clap clap clap clap) → 'The wheels on the bus!'",
          "Clap 'Head Shoulders Knees and Toes': (clap clap clap-clap clap) → 'Head shoulders knees and toes!'"
        ],
        "watchFor": [
          "Your baby showing excitement when they recognize a rhythm!",
          "Your baby moving or bouncing when a familiar rhythm plays—body recognition!",
          "Your baby attempting to sing or vocalize when they know which rhyme it is!",
          "Your baby waiting for you to reveal the answer—engaged in the game!"
        ],
        "parentTip": "Use 3-4 rhymes total—not too many! Too many choices = overwhelm. Keep it simple!",
        "funTwist": "Wrong guess game: Clap Twinkle Twinkle. Pretend to guess: 'Is it... Old MacDonald? No! Is it Twinkle Twinkle? YES!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Story Rhythm March",
      "duration": "3-4 min",
      "focus": "Moving to Story Rhythms",
      "researchBasis": "Phillips-Silver & Trainor (2005) - Infants who move rhythmically during auditory tasks show better encoding of rhythm patterns and improved beat perception.",
      "content": {
        "goal": "March, stomp, or sway to the rhythm of a familiar story or rhyme. This connects rhythm to whole-body movement, deepening the learning.",
        "whatToSay": [
          "\"Let's MARCH to Twinkle Twinkle!\"",
          "\"One step for each word! Twin-kle (step step)!\"",
          "\"Feel the rhythm with your feet!\"",
          "\"Stomp stomp stomp to the song!\"",
          "\"Can you move with the rhythm?\""
        ],
        "whatToDo": [
          "Stand up with baby (or sit if baby can't walk yet—bounce on lap instead).",
          "Choose a rhyme: 'Row Row Row Your Boat.'",
          "Say: 'We're going to MARCH to the rhythm!'",
          "Start marching in place (or bouncing baby) while singing: 'Row row row your boat' (march with each word).",
          "Keep marching through the whole song: march = rhythm!",
          "Stop singing, just march/stomp in the rhythm—no words!",
          "Ask: 'What song are we marching to? Row Row!'",
          "Try different movements: march, stomp, sway, bounce, clap-walk.",
          "Switch rhymes: 'Now let's march to Pat-a-Cake!'",
          "Change speed: March slow, march fast!",
          "If baby can't walk yet: bounce on your knees to the rhythm, sway side to side, tap baby's feet.",
          "Make it playful: 'We're in a rhythm parade!'"
        ],
        "examples": [
          "'Twinkle Twinkle': March around the room to the beat, one step per syllable.",
          "'Itsy Bitsy Spider': Crawl slowly on the floor to the rhythm (spider crawl!).",
          "'Wheels on the Bus': Walk in a circle like you're driving, stepping to the beat.",
          "'If You're Happy': Stomp when you sing 'stomp your feet,' march during verses."
        ],
        "watchFor": [
          "Your baby moving in time with the rhythm—coordinating movement and beat!",
          "Your baby anticipating the beat—stepping just before the next syllable!",
          "Your baby enjoying the whole-body experience—smiling, laughing!",
          "Your baby trying to sing or hum along while moving!"
        ],
        "parentTip": "Moving to rhythm helps babies FEEL the beat in their bodies, not just hear it! This is multi-sensory learning!",
        "funTwist": "Freeze dance: March to rhythm, then STOP when the music stops! Start again when it starts!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You told stories with rhythm! You recognized songs from clapping!",
          "You practiced:",
          "✓ Clapping out familiar rhymes",
          "✓ Guessing songs from rhythm patterns",
          "✓ Marching and moving to story rhythms",
          "✓ Connecting sound, movement, and memory!"
        ],
        "reviewQuestions": [
          "Could your baby recognize any rhymes from clapping alone?",
          "Did your baby try to clap or move along with the rhythms?",
          "Which rhyme was easiest for your baby to identify?",
          "Did combining rhythm and movement engage your baby more than usual?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Rhythm is the foundation of language! When babies learn to hear and feel rhythm patterns, they're developing phonological awareness—the understanding that language is made of sound units (syllables, words, beats). This skill directly predicts reading success! Children who are good at rhythm activities often become strong readers because they understand that words have patterns and structures."
        },
        "nextSteps": [
          "Clap out rhythms during any song or story—make it your habit!",
          "Tomorrow we match rhymes to pictures—visual + sound together!",
          "Try rhythm games with everyday sounds: clap the rhythm of 'time for dinner!'",
          "Keep combining all our skills—Week 12 is about INTEGRATION!"
        ]
      }
    }
  ]
},

// ---------- LESSON 57 (Week 12 Day 2) ----------
{
  "id": 57,
  "phase": "1-2",
  "quarter": 1,
  "week": 12,
  "weekIndex": 11,
  "dayInWeek": 1,
  "title": "Lesson 57: \"Rhyme and Draw\" — Match Picture to Sound",
  "theme": "Sound and Story Integration",
  "skillRecurrenceTags": ["rhyme-recognition", "visual-auditory-connection", "vocabulary"],
  "relatedToPreviousSkills": ["rhyme-hunt-lesson43", "naming-week5", "story-echoes-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Picture Rhymes!",
      "duration": "1 min",
      "content": {
        "goal": "Connect rhyming sounds to pictures, helping your baby understand that words that sound alike can represent different objects. This builds phonological awareness through visual pairing.",
        "parentGuide": [
          "Matching pictures to rhyming sounds strengthens the connection between what we hear and what we see.",
          "This is pre-reading skill! Later, children will match sounds to letters—this is the foundation.",
          "Rhyme awareness is one of the strongest predictors of reading success in kindergarten.",
          "Visual + auditory learning together creates stronger memory pathways than either alone.",
          "This integrates Week 9 rhyme work with Week 5 object naming—bringing skills together!"
        ],
        "materials": ["Pictures of rhyming pairs (cat/hat, ball/wall, dog/frog)", "Books with rhyming illustrations", "Simple drawings you make, or printed images"],
        "tip": "Use REAL photos or clear drawings—baby needs to recognize the objects easily before focusing on rhyme!",
        "researchNote": "Bradley & Bryant (1983) - Children trained in rhyme recognition showed significant reading advantages years later compared to control groups—rhyme skills transfer directly to literacy."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Picture Pair Rhymes",
      "duration": "4-5 min",
      "focus": "Matching Two Rhyming Pictures",
      "researchBasis": "Layton et al. (1996) - Visual support during rhyme activities helps toddlers form stronger sound-meaning connections than auditory-only rhyme practice.",
      "content": {
        "goal": "Show two pictures that rhyme and emphasize how the words sound the same at the end. Baby learns that different things can share similar sounds.",
        "whatToSay": [
          "\"Look! Here's a CAT! (show picture) And here's a HAT! (show picture)\"",
          "\"Listen! CAT... HAT! They rhyme!\"",
          "\"Cat and hat sound the same at the end! -AT -AT!\"",
          "\"They both say -AT! CAT, HAT!\"",
          "\"Point to the cat! Point to the hat! They rhyme!\""
        ],
        "whatToDo": [
          "Lay out two pictures side by side: cat and hat.",
          "Point to the cat: 'This is a CAT! Say cat! CAT!'",
          "Point to the hat: 'This is a HAT! Say hat! HAT!'",
          "Now emphasize the rhyme: 'CAT... HAT! Listen! CAT, HAT!'",
          "Exaggerate the ending sound: 'C-AT, H-AT! They both say AT!'",
          "Point to each picture as you say the word again: 'CAT (point), HAT (point).'",
          "Ask baby to point: 'Show me the cat! Show me the hat!'",
          "Celebrate: 'Cat and hat RHYME! They sound the same!'",
          "Repeat with another rhyme pair: dog and frog.",
          "Say: 'DOG... FROG! They rhyme! DOG, FROG! -OG -OG!'",
          "Do 3-4 rhyme pairs total: cat/hat, dog/frog, ball/wall, bee/tree.",
          "Keep each pair simple—just two pictures, clear rhyme!"
        ],
        "examples": [
          "Cat/Hat: 'The CAT wears a HAT! Cat-hat, cat-hat! They rhyme!'",
          "Dog/Frog: 'The DOG sees a FROG! Dog-frog! They sound the same!'",
          "Ball/Wall: 'The BALL hits the WALL! Ball-wall! Rhyme!'",
          "Tree/Bee: 'The BEE is in the TREE! Bee-tree! They rhyme!'"
        ],
        "watchFor": [
          "Your baby looking back and forth between the pictures—comparing them!",
          "Your baby pointing when you name the objects—recognition!",
          "Your baby attempting to say either word—participating!",
          "Your baby showing excitement when pictures appear—engagement!"
        ],
        "parentTip": "Point to your mouth when you say the rhyming words—help baby SEE the sound coming from your mouth!",
        "funTwist": "Mix-up game: 'Where's the CAT? Is it THIS one? (point to hat) No! That's the HAT! CAT is here! They rhyme!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Rhyme Sorting",
      "duration": "4-5 min",
      "focus": "Grouping Pictures by Rhyme",
      "researchBasis": "Goswami (2001) - Children who practice categorizing by sound patterns (rhyme sorting) develop stronger phonological awareness, which directly supports decoding skills in reading.",
      "content": {
        "goal": "Give baby several pictures and help them sort which ones rhyme together. This teaches active categorization by sound.",
        "whatToSay": [
          "\"Let's find the words that sound the same!\"",
          "\"Here's a CAT! Does HAT rhyme with cat? YES!\"",
          "\"Put hat with cat! They go together—they rhyme!\"",
          "\"Does DOG rhyme with cat? No! Dog goes with frog!\"",
          "\"You're sorting by rhyme! Great job!\""
        ],
        "whatToDo": [
          "Lay out 4-6 pictures: cat, hat, dog, frog (2 rhyme pairs).",
          "Pick up cat: 'Here's the CAT! Let's find what rhymes with cat!'",
          "Hold up hat: 'Does HAT rhyme with cat? CAT... HAT! Yes! They rhyme!'",
          "Put cat and hat together in a pile: 'These go together! They rhyme!'",
          "Pick up dog: 'Now the DOG! What rhymes with dog?'",
          "Hold up frog: 'FROG! DOG... FROG! Yes! Rhyme!'",
          "Put dog and frog together: 'These rhyme too! Dog-frog!'",
          "Let baby help: 'You pick one! Does this rhyme with cat or dog?'",
          "Guide baby's hand to sort pictures into rhyme piles.",
          "Repeat the rhymes each time: 'Cat-hat pile! Dog-frog pile!'",
          "Try with 3 pairs (6 pictures total) if baby is ready.",
          "Keep it playful—this is a game, not a test!"
        ],
        "examples": [
          "Pictures: cat, hat, bat → 'All these rhyme! CAT, HAT, BAT! They all say -AT!'",
          "Pictures: ball, wall, doll → 'BALL, WALL, DOLL! They rhyme! -ALL sound!'",
          "Pictures: star, car, jar → 'STAR, CAR, JAR! All rhyme! -AR at the end!'",
          "Pictures: sun, run, fun → 'SUN, RUN, FUN! Rhyme family! -UN!'"
        ],
        "watchFor": [
          "Your baby reaching for the correct rhyming picture—understanding the task!",
          "Your baby grouping pictures together—categorization!",
          "Your baby repeating the rhyming words—auditory practice!",
          "Your baby correcting you if you sort wrong—they KNOW the rhyme!"
        ],
        "parentTip": "Start with just 2 pairs (4 pictures). Too many at once = confusion. Keep it simple!",
        "funTwist": "Mystery rhyme: 'I'm thinking of something that rhymes with CAT and you wear it on your head! What is it? HAT!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Draw Your Own Rhymes",
      "duration": "3-4 min",
      "focus": "Creating Visual Rhyme Stories",
      "researchBasis": "Dickinson & Tabors (2001) - Multi-modal learning (seeing, hearing, and creating) strengthens vocabulary and phonological skills more than single-mode instruction.",
      "content": {
        "goal": "Draw simple pictures of rhyming pairs together and tell a little story about them. This makes rhymes personal and memorable.",
        "whatToSay": [
          "\"Let's draw! I'll draw a CAT!\"",
          "\"What rhymes with cat? HAT! Let's draw a hat on the cat!\"",
          "\"The CAT wears a HAT! Cat-hat! They rhyme!\"",
          "\"What else rhymes? BAT! I'll draw a bat flying!\"",
          "\"Cat, hat, bat! All rhyme! Our rhyme picture!\""
        ],
        "whatToDo": [
          "Get paper and crayons or markers (washable!).",
          "Say: 'Let's draw rhymes! I'll draw a cat!'",
          "Draw a very simple cat (doesn't need to be perfect!).",
          "Say: 'CAT! Now what rhymes with cat?'",
          "If baby doesn't answer: 'HAT rhymes with cat! Let's draw a hat!'",
          "Draw a simple hat on the cat's head.",
          "Point to your drawing: 'Look! CAT in a HAT! Cat-hat rhyme!'",
          "Let baby scribble or draw on the page too—participation!",
          "Draw another rhyme pair: dog and frog.",
          "Say: 'Here's a DOG! (draw) What rhymes? FROG! (draw frog next to dog)'",
          "Tell a silly story: 'The dog plays with the frog! Dog-frog rhyme!'",
          "Keep drawings VERY simple—stick figures are fine!",
          "Focus on the SOUNDS, not artistic skill!"
        ],
        "examples": [
          "Draw sun, draw bun: 'The SUN shines on the BUN! Sun-bun rhyme!'",
          "Draw fox, draw box: 'The FOX is in the BOX! Fox-box!'",
          "Draw king, draw ring: 'The KING wears a RING! King-ring!'",
          "Draw moon, draw spoon: 'The MOON and a SPOON! Moon-spoon!'"
        ],
        "watchFor": [
          "Your baby watching you draw—visual attention!",
          "Your baby trying to add marks or scribbles—creative participation!",
          "Your baby pointing to the drawings when you say the words!",
          "Your baby showing excitement about the silly rhyme combinations!"
        ],
        "parentTip": "Your drawing doesn't need to be good! Simple shapes work perfectly—circle for sun, stick figure for person!",
        "funTwist": "Backwards game: YOU draw, baby guesses! 'I'm drawing something that rhymes with CAT! What is it? (draw hat)'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You matched pictures and rhymes! You see AND hear rhymes!",
          "You practiced:",
          "✓ Pairing rhyming pictures together",
          "✓ Sorting objects by rhyming sounds",
          "✓ Drawing your own rhyme pictures",
          "✓ Connecting what you see with what you hear!"
        ],
        "reviewQuestions": [
          "Did your baby recognize any rhyming pairs?",
          "Could your baby sort or group rhyming pictures?",
          "Did drawing the rhymes help make them more memorable?",
          "Which rhyme pair was your baby's favorite?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Matching pictures to rhyming sounds builds the bridge between auditory and visual processing—exactly what reading requires! When children eventually learn that letters represent sounds, they'll already understand that what they see and what they hear are connected. Rhyme awareness is one of the best predictors of reading success, and visual rhymes make this skill concrete and memorable!"
        },
        "nextSteps": [
          "Look for rhymes in books: 'Cat! Hat! They rhyme! See the pictures?'",
          "Tomorrow we listen for specific sounds INSIDE stories!",
          "Create a rhyme poster: draw multiple rhyme families to review daily!",
          "Point out rhymes everywhere: 'Your name rhymes with ___!'"
        ]
      }
    }
  ]
},

// ---------- LESSON 58 (Week 12 Day 3) ----------
{
  "id": 58,
  "phase": "1-2",
  "quarter": 1,
  "week": 12,
  "weekIndex": 11,
  "dayInWeek": 2,
  "title": "Lesson 58: \"What's That Sound in the Story?\" — Listen for Specific Sounds",
  "theme": "Sound and Story Integration",
  "skillRecurrenceTags": ["listening-comprehension", "sound-discrimination", "active-reading"],
  "relatedToPreviousSkills": ["sound-hide-and-seek-lesson14", "story-echoes-lesson46", "key-word-collection-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Sound Hunters!",
      "duration": "1 min",
      "content": {
        "goal": "Read stories together and pause to identify specific sounds within the words—animal sounds, action sounds, and beginning letter sounds. This builds focused listening and phonemic awareness.",
        "parentGuide": [
          "Listening FOR specific sounds in stories teaches attention and discrimination—not just passive hearing.",
          "This is early phonemic awareness—noticing sounds INSIDE words, which is crucial for reading.",
          "When babies learn to hear individual sounds (phonemes), they're preparing to decode words later.",
          "Active listening during stories improves comprehension and memory more than passive listening.",
          "This combines Week 2 sound awareness with Week 10 story time—integrated learning!"
        ],
        "materials": ["Picture books with animal characters or action words", "Books with repetitive sounds (moo, splash, boom)", "Your voice for sound effects!"],
        "tip": "Choose books with LOTS of sounds—'Moo, Baa, La La La!' or 'Mr. Brown Can Moo!' are perfect!",
        "researchNote": "Lonigan et al. (2009) - Phonological awareness activities embedded in shared book reading significantly improve children's sound awareness skills and predict later reading ability."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Animal Sound Hunt",
      "duration": "4-5 min",
      "focus": "Finding and Emphasizing Animal Sounds in Stories",
      "researchBasis": "Justice & Ezell (2002) - When parents draw explicit attention to sounds during reading (emphasizing, repeating, isolating), children show faster phonological development.",
      "content": {
        "goal": "Read a story with animals and STOP each time you encounter an animal sound. Make the sound together and emphasize that it's a special sound to notice.",
        "whatToSay": [
          "\"Let's find all the animal sounds in this story!\"",
          "\"Listen! Here's a cow! The cow says... MOO!\"",
          "\"Did you hear that? MOO! Let's say it together! MOO!\"",
          "\"That's the sound we're listening for! MOO!\"",
          "\"How many MOOs will we hear in this book?\""
        ],
        "whatToDo": [
          "Choose a book with animal sounds (like 'Old MacDonald' or any farm book).",
          "Before reading: 'Today we're listening for animal sounds! When you hear one, we'll stop!'",
          "Start reading: 'Old MacDonald had a farm...'",
          "When you reach an animal: STOP! 'Here's a COW! What sound does a cow make?'",
          "Make the sound together: 'MOO! Let's say it loud! MOOOOO!'",
          "Emphasize the sound: 'That's the COW sound! MOO! M-O-O!'",
          "Continue reading until the next animal.",
          "Stop again: 'A PIG! The pig says... OINK! Say oink! OINK!'",
          "Make it a game: 'How many times did we hear MOO? Let's count!'",
          "Point to your ear: 'We're using our listening ears to find sounds!'",
          "After each sound, exaggerate it: 'Moo is a loud sound! MOO!'",
          "Celebrate every sound you find: 'We found another sound!'"
        ],
        "examples": [
          "'Brown Bear' book: 'Listen! The BIRD goes TWEET-TWEET! Let's say it! TWEET-TWEET!'",
          "'Dear Zoo' book: 'The LION goes ROAR! Big sound! ROAAAR! Can you roar?'",
          "'The Gruffalo': 'The MOUSE goes SQUEAK! Tiny sound! Squeak squeak!'",
          "Any farm book: 'The SHEEP goes BAA! Listen: BAA-BAA! You say it!'"
        ],
        "watchFor": [
          "Your baby making the animal sounds with you—participation!",
          "Your baby anticipating sounds before you say them—memory!",
          "Your baby pointing to animals when you make their sounds—connecting sound to image!",
          "Your baby showing excitement when a new sound appears!"
        ],
        "parentTip": "Make sounds BIG and exaggerated! The more dramatic you are, the more memorable the sounds become!",
        "funTwist": "Sound swap: 'What if the cow said WOOF? No! That's silly! The DOG says woof! The cow says MOO!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Action Sound Search",
      "duration": "4-5 min",
      "focus": "Identifying Action Sounds in Stories",
      "researchBasis": "Yopp & Yopp (2000) - When children learn to isolate and identify sounds in words (like action sounds), they develop phoneme segmentation skills—the ability to break words into individual sounds.",
      "content": {
        "goal": "Find action sounds in stories—splash, boom, crash, zoom—and emphasize how these sounds describe what's happening. This connects sound to meaning.",
        "whatToSay": [
          "\"Let's find action sounds! Sounds that tell us what's happening!\"",
          "\"The ball goes... BOUNCE! That's an action sound!\"",
          "\"BOUNCE! Can you say bounce? BOUNCE!\"",
          "\"Every time something happens, there's a sound!\"",
          "\"Listen for SPLASH, CRASH, ZOOM, BANG!\""
        ],
        "whatToDo": [
          "Choose a book with action words (like 'The Very Busy Spider' or any adventure story).",
          "Before reading: 'We're listening for action sounds—sounds that show movement!'",
          "Start reading.",
          "When you reach an action: 'The ball BOUNCES! BOUNCE! That's our action sound!'",
          "Act it out: 'BOUNCE! (bounce your hand) We can show it AND say it!'",
          "Continue: 'The water goes SPLASH! Let's say it! SPLASH!'",
          "Make the sound dramatic: 'SPLAAAASH! Hear the water?'",
          "Find multiple action sounds: splash, crash, bang, zoom, jump, stomp.",
          "Stop at each one: 'That's an action sound! Let's remember it!'",
          "At the end: 'How many action sounds did we find? Let's count!'",
          "List them: 'Bounce, splash, crash! We found three!'",
          "Act out each sound with movement!"
        ],
        "examples": [
          "Water book: 'The water goes SPLASH! (clap hands together) SPLASH!'",
          "Cars: 'The car goes ZOOM! (move hand fast) ZOOOOOM!'",
          "Building: 'The blocks go CRASH! (clap) CRASH! They fell down!'",
          "Jumping: 'The frog goes JUMP! (bounce up) JUMP-JUMP!'"
        ],
        "watchFor": [
          "Your baby moving their body when action sounds happen—embodying the sound!",
          "Your baby attempting to say action words—verbal practice!",
          "Your baby making sound effects during play later—transferring learning!",
          "Your baby anticipating action sounds in familiar books!"
        ],
        "parentTip": "Action sounds are everywhere! Even if the book doesn't say 'splash,' YOU add it: 'The duck goes in the water—SPLASH!'",
        "funTwist": "Sound effects track: Re-read a page using ONLY action sounds, no other words! 'SPLASH! ZOOM! CRASH! BOOM!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: First Sound Focus",
      "duration": "3-4 min",
      "focus": "Listening for Beginning Sounds in Story Words",
      "researchBasis": "Ball & Blachman (1991) - Phoneme awareness training, especially focusing on initial sounds, significantly improves reading readiness and early decoding skills.",
      "content": {
        "goal": "Choose 2-3 key words from the story and emphasize their FIRST sound. This is early phonemic awareness—hearing the beginning of words.",
        "whatToSay": [
          "\"Let's listen to the first sound in BALL! B-B-BALL!\"",
          "\"It starts with B! B-B-B! Say it with me: B-B-BALL!\"",
          "\"Now CAT! C-C-CAT! Hear that C sound?\"",
          "\"B-B-BALL and C-C-CAT! Different first sounds!\"",
          "\"You're hearing the sounds at the start of words!\""
        ],
        "whatToDo": [
          "During story time, pick 2-3 important words.",
          "Say the word normally first: 'BALL! Here's a ball!'",
          "Now emphasize the first sound: 'B-B-BALL! Hear the B at the beginning?'",
          "Exaggerate it: 'BBBBBALL! The B sound starts the word!'",
          "Touch your lips: 'Feel my lips make B! B-B-B!'",
          "Let baby touch your lips while you say: 'B-B-BALL!'",
          "Try another word: 'CAT! C-C-CAT! Hear the C?'",
          "Compare: 'BALL starts with B! CAT starts with C! Different sounds!'",
          "Do this with 2-3 words per story—don't overdo it!",
          "Keep it playful: 'Let's stretch the first sound! MMMMMOON!'",
          "Point to the picture when you emphasize the sound.",
          "This is advanced—don't worry if baby doesn't repeat yet!"
        ],
        "examples": [
          "'Dog': 'D-D-DOG! Hear the D? D-D-D-DOG!'",
          "'Sun': 'S-S-SUN! The S sound! Sssssun!'",
          "'Moon': 'M-M-MOON! Feel your lips close for M! Mmmoon!'",
          "'Fish': 'F-F-FISH! The F sound! Ffffff-ish!'"
        ],
        "watchFor": [
          "Your baby watching your mouth carefully—visual focus on articulation!",
          "Your baby attempting to copy the exaggerated first sound!",
          "Your baby showing recognition when you emphasize familiar words!",
          "Your baby noticing when two words start with the same sound!"
        ],
        "parentTip": "This is HARD for 1-2 year olds! The goal is exposure, not mastery. Just hearing you emphasize first sounds is enough!",
        "funTwist": "Silly stretch: 'Let's stretch the first sound REALLY long! SSSSSSSSSUUUUUN! How long can we make it?'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You found sounds hiding in the story! You're a sound detective!",
          "You practiced:",
          "✓ Finding animal sounds in books",
          "✓ Hearing action sounds (splash, crash, zoom!)",
          "✓ Listening for first sounds in words",
          "✓ Paying attention to specific sounds in stories!"
        ],
        "reviewQuestions": [
          "Did your baby make animal sounds when you stopped?",
          "Did your baby recognize action sounds during reading?",
          "Could you see your baby focusing on your mouth during first sounds?",
          "Which type of sound (animal, action, or first sound) engaged your baby most?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Learning to listen FOR specific sounds—not just hear them passively—is the foundation of phonemic awareness, which is THE strongest predictor of reading success. When children can isolate and identify sounds in words, they're preparing to decode written language. Sound hunting in stories makes this critical skill fun and natural!"
        },
        "nextSteps": [
          "Sound hunt EVERY story: 'Let's find the sounds in this book!'",
          "Tomorrow we act out stories with movement and sound together!",
          "Make everyday life a sound hunt: 'What sound does the door make? CREAK!'",
          "Keep emphasizing first sounds in daily talk: 'B-B-BANANA! T-T-TRUCK!'"
        ]
      }
    }
  ]
},

// ---------- LESSON 59 (Week 12 Day 4) ----------
{
  "id": 59,
  "phase": "1-2",
  "quarter": 1,
  "week": 12,
  "weekIndex": 11,
  "dayInWeek": 3,
  "title": "Lesson 59: \"Act It Out\" — Story Through Movement",
  "theme": "Sound and Story Integration",
  "skillRecurrenceTags": ["comprehension", "physical-engagement", "story-retelling"],
  "relatedToPreviousSkills": ["rhythm-march-lesson56", "emotion-words-lesson54", "story-echoes-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Acting!",
      "duration": "1 min",
      "content": {
        "goal": "Act out story scenes with your baby using movement, sounds, and simple actions. This makes stories come alive through the body, deepening comprehension and memory.",
        "parentGuide": [
          "Acting out stories helps babies understand and remember them better—movement creates stronger memory!",
          "When babies physically experience story actions, they connect meaning to words in powerful ways.",
          "This is embodied learning—using the whole body to understand concepts, not just the brain.",
          "Movement during stories keeps attention longer and makes reading more engaging for active toddlers.",
          "This integrates ALL our skills: sounds (Weeks 1-3), rhythm (Week 9), emotions (Week 11), and stories (Week 10)!"
        ],
        "materials": ["Favorite story book with clear actions", "Space to move around safely", "Optional: simple props (blanket, stuffed animals)"],
        "tip": "Choose stories with lots of action: jumping, sleeping, eating, running! Active verbs are easiest to act out!",
        "researchNote": "Glenberg et al. (2004) - Children who physically manipulate objects or act out story events show significantly better comprehension and recall than children who just listen to stories."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Simple Action Acting",
      "duration": "4-5 min",
      "focus": "Acting Out Individual Story Actions",
      "researchBasis": "Rubin (1980) - Motor enactment during story learning creates stronger memory traces than verbal repetition alone—children remember stories they've acted out better than stories they've only heard.",
      "content": {
        "goal": "Read a story and pause to act out each action verb with your baby. Jump when characters jump, sleep when they sleep, eat when they eat!",
        "whatToSay": [
          "\"The bunny JUMPS! Let's jump like the bunny!\"",
          "\"Jump, jump, jump! (jumping together) We're jumping!\"",
          "\"Now the bunny SLEEPS! Let's sleep! Close your eyes!\"",
          "\"Shhh, sleeping! (pretend to sleep together)\"",
          "\"You're acting out the story! You're IN the story!\""
        ],
        "whatToDo": [
          "Choose a book with clear actions: 'From Head to Toe' or 'We're Going on a Bear Hunt.'",
          "Sit or stand where you have room to move.",
          "Start reading: 'The bunny hops through the garden.'",
          "STOP at 'hops': 'The bunny HOPS! Let's hop! Hop hop hop!'",
          "Hop together—exaggerate the movement!",
          "Continue reading: 'The bunny eats a carrot.'",
          "STOP: 'EATS! Let's pretend to eat! Munch munch munch!'",
          "Mime eating together.",
          "Keep going: 'The bunny sleeps under a tree.'",
          "STOP: 'SLEEPS! Let's sleep! Lay down! Close your eyes! Shhh!'",
          "Pretend to sleep together for a moment.",
          "Do this with EVERY action verb in the story.",
          "Make movements big and clear—help baby understand what to do!",
          "Celebrate: 'You're acting just like the bunny!'"
        ],
        "examples": [
          "'The Very Hungry Caterpillar': 'He EATS an apple! (munch munch) He EATS a pear! (munch munch)'",
          "'Where the Wild Things Are': 'Max STOMPS around! (stomp stomp stomp together!)'",
          "'Brown Bear': 'The bird FLIES! (flap arms like wings) Fly, fly, fly!'",
          "'Goodnight Moon': 'Say goodnight! (wave) Now sleep! (lay down and close eyes)'"
        ],
        "watchFor": [
          "Your baby copying your movements—motor imitation!",
          "Your baby starting to act before you do—anticipating the action!",
          "Your baby making sounds while acting—combining verbal and physical!",
          "Your baby showing understanding of what actions mean!"
        ],
        "parentTip": "Don't worry about finishing the whole story—focus on acting out 5-6 key actions really well!",
        "funTwist": "Freeze dance version: Act out the action, then FREEZE when you stop reading! 'Jumping... FREEZE! Now eating... FREEZE!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Scene Reenactment",
      "duration": "4-5 min",
      "focus": "Acting Out Complete Story Scenes",
      "researchBasis": "Pellegrini & Galda (1982) - Dramatic play based on stories enhances children's narrative understanding, sequencing skills, and ability to retell stories independently.",
      "content": {
        "goal": "Pick a favorite story scene and act out the whole sequence together—not just individual actions, but the entire mini-story with beginning, middle, and end.",
        "whatToSay": [
          "\"Let's act out the whole scene! You're the bear!\"",
          "\"First, the bear wakes up! (stretch and yawn) Good morning!\"",
          "\"Then the bear is HUNGRY! (rub belly) So hungry!\"",
          "\"The bear looks for food! (look around) Where's the honey?\"",
          "\"The bear finds honey! (pretend to grab) Yum! (pretend to eat)\"",
          "\"You did the whole scene! You told the story with your body!\""
        ],
        "whatToDo": [
  "Choose a simple scene: 'Goldilocks trying the three chairs' or 'The Three Little Pigs building houses.'",
  "Set the scene: 'You're Goldilocks! Here are three chairs!' (use cushions or furniture).",
  "Guide through the sequence: 'First, try this chair! (sit) Too hard! Stand up!'",
  "Continue: 'Now try THIS chair! (sit) Too soft! Stand up!'",
  "Finish: 'Now THIS chair! (sit) Just right! Stay sitting!'",
  "Act out the whole sequence in order.",
  "Use simple props if helpful: blankets, pillows, stuffed animals.",
  "Repeat the scene 2-3 times—repetition helps memory!",
  "Let baby lead the second time: 'Now YOU show me! Which chair first?'",
  "Add dialogue: 'Say \\\"too hard!\\\" Say \\\"too soft!\\\" Say \\\"just right!\\\"'",
  "Emphasize sequence: 'First this, then this, then this!'",
  "Celebrate completion: 'You acted out the whole story!'"
],

        "examples": [
          "'Five Little Monkeys': Jump on bed (couch), fall off (gentle fall), call doctor (pretend phone), no more jumping! (shake finger)",
          "'Bear Hunt': Going through grass (swish swish), mud (squelch squelch), cave (tiptoe tiptoe), SEE BEAR (gasp!), run home! (run in place)",
          "'Little Red Hen': Plant seeds (digging motion), water (pour motion), harvest (picking motion), bake bread (stirring motion), eat! (munch)",
          "'Three Billy Goats': Small goat crosses (tiptoe), medium goat crosses (walk normal), big goat crosses (stomp stomp), troll falls! (fall down)"
        ],
        "watchFor": [
          "Your baby following the sequence correctly—understanding story order!",
          "Your baby remembering what comes next—narrative memory!",
          "Your baby adding their own sounds or words—creative engagement!",
          "Your baby wanting to repeat the scene—enjoyment and mastery!"
        ],
        "parentTip": "Keep scenes SHORT—just 3-4 actions in sequence. Too long = baby loses the thread!",
        "funTwist": "Speed versions: Act out the scene SLOWLY... now FAST! Which is funnier?"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Story Movement Retell",
      "duration": "3-4 min",
      "focus": "Retelling Story Using Movement Without Words",
      "researchBasis": "Nicolopoulou & Richner (2007) - Nonverbal story retelling through movement helps toddlers organize narrative structure and strengthens comprehension before they have full verbal abilities.",
      "content": {
        "goal": "After reading a story, retell it using ONLY movement and sounds—no words! This shows if baby truly understands what happened.",
        "whatToSay": [
          "\"Can we tell the story again using just our bodies? No words—just actions!\"",
          "\"First what happened? (wait for baby to move) Yes! The bear woke up!\"",
          "\"Then what? Show me! (baby acts) He walked!\"",
          "\"What happened next? You show me!\"",
          "\"You told the whole story without talking! You remembered everything!\""
        ],
        "whatToDo": [
          "After reading a story once with acting, close the book.",
          "Say: 'Can you show me the story again? Just actions!'",
          "Prompt: 'What happened first? Show me with your body!'",
          "Wait for baby to make any movement.",
          "Narrate what they do: 'Oh! The bunny hopped! Yes! That was first!'",
          "Ask: 'What happened next? Show me!'",
          "Let baby act out the next part.",
          "Continue through the whole story sequence.",
          "If baby forgets: give hints! 'What did the bunny do after hopping? Did he eat?'",
          "Baby acts it out: 'Yes! Eating! Munch munch!'",
          "String together: 'First hop, then eat, then sleep! You told the whole story!'",
          "High five: 'You remembered the whole story with your body!'",
          "Try it again—see if baby can do it with fewer prompts!"
        ],
        "examples": [
          "'The Very Hungry Caterpillar': Baby crawls (caterpillar), eats (munch munch), sleeps in cocoon (curl up), becomes butterfly (arms flap)!",
          "'Going on a Bear Hunt': Baby walks through grass (swish), through mud (squelch), finds bear (gasp!), runs home (run)!",
          "'Five Little Ducks': Baby waddles like duck, swims (arms moving), comes back (waddle back)!",
          "'Where's Spot?': Baby looks behind door (peek), under bed (look down), in basket (look in), FOUND! (point and smile)"
        ],
        "watchFor": [
          "Your baby moving through the sequence in the correct order!",
          "Your baby remembering key actions without heavy prompting!",
          "Your baby adding emotional expressions—scared, happy, tired!",
          "Your baby wanting to 'perform' the story again and again!"
        ],
        "parentTip": "If baby struggles, do it WITH them first: 'Let's do it together!' Then let them try solo!",
        "funTwist": "Switch roles: 'You act out the story, I'll guess what it is! Is it... Bear Hunt? Yes!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You became the story! You acted it out with your whole body!",
          "You practiced:",
          "✓ Acting out individual story actions",
          "✓ Performing complete story scenes",
          "✓ Retelling stories through movement",
          "✓ Understanding stories by living them!"
        ],
        "reviewQuestions": [
          "Did your baby successfully act out story actions?",
          "Could your baby remember and sequence a story scene?",
          "Did your baby retell any part of the story through movement?",
          "Did acting make your baby more engaged with the story?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Acting out stories transforms passive listening into active learning. When children move their bodies to represent story events, they're creating powerful memory connections and deep comprehension. This embodied learning helps them understand cause and effect, sequence, and story structure—all critical pre-reading skills. Plus, it keeps wiggly toddlers engaged with books!"
        },
        "nextSteps": [
          "Act out EVERY story from now on—make it your reading style!",
          "Tomorrow we create a sound storybook of everything we've learned!",
          "Act out daily routines too: 'First we wake up! Then we eat! Then we play!'",
          "Encourage baby to act out stories independently—narrate for them!"
        ]
      }
    }
  ]
},


// ---------- LESSON 60 (Week 12 Day 5) ----------
{
  "id": 60,
  "phase": "1-2",
  "quarter": 1,
  "week": 12,
  "weekIndex": 11,
  "dayInWeek": 4,
  "title": "Lesson 60: \"Favorite Sounds Storybook\" — Review All Skills",
  "theme": "Sound and Story Integration",
  "skillRecurrenceTags": ["skill-integration", "creative-expression", "vocabulary-consolidation"],
  "relatedToPreviousSkills": ["all-previous-weeks", "sound-show-lesson20", "story-echoes-lesson46"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Our Sound Book!",
      "duration": "1 min",
      "content": {
        "goal": "Create a personalized picture book featuring all the sounds, words, and skills your baby has learned over the past 12 weeks. This celebrates progress and creates a keepsake!",
        "parentGuide": [
          "Creating a personalized book gives your baby ownership over their learning—it's THEIR story!",
          "Reviewing all learned skills in one place consolidates learning and shows visible progress.",
          "Homemade books are more meaningful than store-bought because they reflect YOUR child's experiences.",
          "This activity integrates EVERYTHING: animal sounds (Week 1), object names (Week 5), rhymes (Week 9), emotions (Week 11)!",
          "You'll use this book over and over—it becomes a favorite because it's about THEM!"
        ],
        "materials": ["Blank paper or small notebook", "Crayons, markers, or stickers", "Photos of baby or drawings", "Tape or stapler"],
        "tip": "Keep it simple! Stick figures and basic drawings are perfect—the content matters more than artistic skill!",
        "researchNote": "Reese & Cox (1999) - Personalized books that reflect children's own experiences lead to more engagement, repeated readings, and better vocabulary retention than generic books."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Sound Collection Pages",
      "duration": "4-5 min",
      "focus": "Creating Pages for Learned Sounds",
      "researchBasis": "Kaderavek & Justice (2005) - When children participate in creating literacy materials, they show increased motivation to engage with books and stronger print awareness.",
      "content": {
        "goal": "Make pages featuring all the sounds baby has learned—animal sounds, environmental sounds, action sounds. Each page has a picture and the sound word.",
        "whatToSay": [
          "\"Let's make YOUR sound book! What sounds do you know?\"",
          "\"You know MOO! The cow says moo! Let's draw a cow!\"",
          "\"This page is the COW page! MOO! (write 'MOO' on page)\"",
          "\"What other sounds? WOOF! Let's make a dog page!\"",
          "\"This is YOUR book with YOUR sounds!\""
        ],
        "whatToDo": [
          "Get 5-10 blank pages (folded paper works great!).",
          "Say: 'We're making a book of all YOUR sounds!'",
          "Pick the first sound: 'What sound do you love? Moo? Okay!'",
          "Draw a simple cow (or paste a picture/sticker of a cow).",
          "Write 'MOO' in big letters under the picture.",
          "Say together: 'COW says MOO! This is the moo page!'",
          "Let baby scribble or add marks to the page—participation!",
          "Make another page: 'What's another sound? WOOF?'",
          "Draw dog, write 'WOOF.'",
          "Continue with 5-8 sounds baby knows well.",
          "Include: animal sounds (moo, woof, quack), environmental sounds (beep, splash, boom), and baby's favorites.",
          "Keep each page simple: one picture, one sound word.",
          "Make it colorful—use bright markers or crayons!"
        ],
        "examples": [
          "Page 1: Cow picture + 'MOO' written large",
          "Page 2: Dog picture + 'WOOF'",
          "Page 3: Car picture + 'BEEP'",
          "Page 4: Water picture + 'SPLASH'",
          "Page 5: Duck picture + 'QUACK'",
          "Page 6: Drum picture + 'BOOM'",
          "Page 7: Baby jumping + 'JUMP'",
          "Page 8: Baby's photo + 'YAY!'"
        ],
        "watchFor": [
          "Your baby pointing to pictures and making the sounds—connection!",
          "Your baby excited to see familiar sounds represented—recognition!",
          "Your baby wanting to add to pages—creative participation!",
          "Your baby saying sounds as you create each page!"
        ],
        "parentTip": "Use pictures of REAL items from your home if possible—'OUR dog,' 'OUR car'—makes it even more personal!",
        "funTwist": "Add a photo of baby making the sound! Picture of baby saying 'moo' on the cow page!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Word and Rhyme Pages",
      "duration": "4-5 min",
      "focus": "Adding Vocabulary and Rhyme Pages",
      "researchBasis": "Senechal & LeFevre (2002) - Children who create their own books or literacy materials show accelerated vocabulary growth and earlier interest in reading independently.",
      "content": {
        "goal": "Create pages featuring important words baby has learned (colors, objects, emotions) and include a rhyme page with rhyming pairs.",
        "whatToSay": [
          "\"You know so many WORDS now! Let's put them in your book!\"",
          "\"BALL! You can say ball! Let's draw a ball!\"",
          "\"HAPPY! You know happy! Let's draw a happy face!\"",
          "\"CAT and HAT rhyme! Let's make a rhyme page!\"",
          "\"This book shows everything you've learned!\""
        ],
        "whatToDo": [
          "Create pages for key vocabulary categories.",
          "Objects page: Draw ball, car, teddy bear—things baby can name.",
          "Write the word under each object: 'BALL,' 'CAR,' 'BEAR.'",
          "Say each word as you draw: 'BALL! You know ball! Say it! Ball!'",
          "Colors page: Draw something red, something blue, something yellow.",
          "Write: 'RED,' 'BLUE,' 'YELLOW.'",
          "Point and say: 'This is RED! You know red!'",
          "Emotions page: Draw happy face, sad face, tired face.",
          "Write: 'HAPPY,' 'SAD,' 'TIRED.'",
          "Make faces together: 'This is happy! (smile) You make happy face!'",
          "Rhyme page: Draw cat next to hat, dog next to frog.",
          "Write: 'CAT - HAT' and 'DOG - FROG.'",
          "Say: 'Cat and hat rhyme! CAT, HAT! They sound the same!'",
          "Include 3-4 vocabulary pages total."
        ],
        "examples": [
          "Objects page: ball, book, cup, spoon (things baby uses daily)",
          "Family page: Mama, Dada, baby, dog (baby's people and pets)",
          "Actions page: jump, clap, wave, sleep (things baby does)",
          "Rhyme page: sun/fun, bee/tree, fox/box (illustrated rhyme pairs)"
        ],
        "watchFor": [
          "Your baby pointing to and naming objects on the pages!",
          "Your baby making emotion faces when you review emotion page!",
          "Your baby recognizing rhyme pairs—saying both words!",
          "Your baby showing pride—'I know these words!'"
        ],
        "parentTip": "Only include words baby TRULY knows and uses—this is a celebration of mastery, not new learning!",
        "funTwist": "Mystery page: Leave one page blank for baby to fill in later: 'What should we add next time?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Read Your Book Together!",
      "duration": "3-4 min",
      "focus": "Reading and Celebrating the Completed Book",
      "researchBasis": "Morrow (1990) - Repeated readings of personally meaningful books lead to increased print awareness, story structure understanding, and voluntary engagement with literacy.",
      "content": {
        "goal": "Staple or bind the pages together and read the completed book! Celebrate every sound, word, and skill represented.",
        "whatToSay": [
          "\"Look! It's YOUR book! The book YOU made!\"",
          "\"Let's read it together! Turn the page!\"",
          "\"The cow says MOO! You know that! Say it! MOO!\"",
          "\"Here's happy! Can you make a happy face?\"",
          "\"You learned ALL of these things! I'm so proud of you!\""
        ],
        "whatToDo": [
          "Staple the pages together on the left side (or use tape to bind).",
          "Make a cover: 'Baby's Sound Book' with your baby's name!",
          "Decorate the cover together—stickers, scribbles, whatever!",
          "Sit down with baby: 'Let's read YOUR special book!'",
          "Start with the cover: 'This is YOUR book! You made this!'",
          "Turn to page 1: 'The cow! What does the cow say? MOO! Yes!'",
          "Continue through each page, pausing for baby to participate.",
          "Encourage baby to make sounds, point, name objects, make faces.",
          "Turn pages slowly: 'You turn the page! What's next?'",
          "At each page: 'You KNOW this! You learned this!'",
          "Read the whole book 2-3 times right now.",
          "End with celebration: 'This is everything you learned! You're amazing!'",
          "Put book in a special place where baby can access it."
        ],
        "examples": [
          "Page with cow: 'What's this? (point) COW! What does it say? MOO! You're reading your book!'",
          "Rhyme page: 'Cat... hat! They rhyme! Say them! Cat-hat!'",
          "Emotion page: 'Show me happy! (baby smiles) Yes! That's happy!'",
          "Action page: 'JUMP! Can you jump? (baby jumps) You're doing what the book says!'"
        ],
        "watchFor": [
          "Your baby treating the book like a 'real' book—turning pages, looking carefully!",
          "Your baby participating MORE with their own book than with other books!",
          "Your baby wanting to read it again immediately—engagement!",
          "Your baby bringing the book to you later—seeking repeated readings!"
        ],
        "parentTip": "This book will become a FAVORITE! Read it daily for the next few weeks—repetition strengthens all these skills!",
        "funTwist": "Book tour: 'Let's show your book to everyone! Read it to Daddy! Read it to Grandma! You're the teacher!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You made YOUR OWN book! You're an author and illustrator!",
          "You practiced:",
          "✓ Creating pages with sounds you know",
          "✓ Adding words and rhymes you've learned",
          "✓ Reading your personal book together",
          "✓ Celebrating ALL your learning from 12 weeks!"
        ],
        "reviewQuestions": [
          "Did your baby participate in creating the book?",
          "Did your baby recognize and respond to familiar sounds and words?",
          "How many sounds/words could your baby produce independently?",
          "Does your baby seem proud of their creation?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Creating a personalized book is powerful because it shows your baby that THEY are capable of making books—not just reading them! This book is a tangible record of their learning journey and will be treasured for years. Reading it together reinforces every skill from the past 12 weeks and builds confidence. Week 13 next week is our Quarter Review celebration—get ready to show off everything you've learned!"
        },
        "nextSteps": [
          "Read your baby's book EVERY day this week!",
          "Add new pages as baby learns new sounds and words!",
          "Next week (Week 13) is QUARTER REVIEW—we celebrate everything!",
          "Make more books together—'Our Family Book,' 'Our Day Book,' etc.!"
        ]
      }
    }
  ]
},

// ---------- LESSON 61 (Week 13 Day 1) ----------
{
  "id": 61,
  "phase": "1-2",
  "quarter": 1,
  "week": 13,
  "weekIndex": 12,
  "dayInWeek": 0,
  "title": "Lesson 61: \"Sound Parade\" — Revisit Animal & Home Noises",
  "theme": "Our Favorite Sounds!",
  "skillRecurrenceTags": ["skill-integration", "review-celebration", "sound-mastery"],
  "relatedToPreviousSkills": ["week1-animal-sounds", "week2-everyday-sounds", "week3-echo-play", "week10-story-sounds"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Quarter Review Week!",
      "duration": "1 min",
      "content": {
        "goal": "Celebrate 13 weeks of learning by creating a 'Sound Parade' featuring ALL the sounds your baby has mastered—animal sounds, environmental sounds, and action sounds!",
        "parentGuide": [
          "This is CELEBRATION week—not new learning! We're reviewing everything your baby has accomplished over the past 12 weeks.",
          "Review strengthens memory—revisiting learned skills ensures they become permanent parts of your baby's repertoire.",
          "Combining multiple skills in one activity (like a parade) shows how far your baby has come from Week 1!",
          "This parade format makes review FUN rather than repetitive—it's a celebration, not a test!",
          "By showcasing skills, you're building your baby's confidence and pride in their accomplishments!"
        ],
        "materials": ["Toy animals from Week 1", "Household objects that make sounds (keys, cups, spoons)", "Space to march or move around", "Optional: musical instruments from Week 4", "Camera to capture memories!"],
        "tip": "Don't stress about perfection! Some sounds your baby will remember easily, others might need prompting—both are totally normal!",
        "researchNote": "Bjork & Bjork (1992) - Distributed practice and spaced review sessions lead to better long-term retention than massed practice. Reviewing skills after time has passed strengthens memory more effectively than continuous repetition."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Animal Sound Parade",
      "duration": "4-5 min",
      "focus": "Reviewing Week 1 Animal Sounds",
      "researchBasis": "Roediger & Butler (2011) - Retrieval practice (actively recalling information) produces stronger learning than simply re-exposure to material. Having children produce sounds strengthens neural pathways more than hearing them.",
      "content": {
        "goal": "March around the room with toy animals, making each animal's sound as you go. This reviews all the animal sounds from Week 1 in a fun, active way!",
        "whatToSay": [
          "\"Let's have a Sound Parade! All your animals are coming!\"",
          "\"Here comes the COW! What does the cow say?\"",
          "\"MOO! Yes! The cow says moo! March with the cow!\"",
          "\"Now the DOG is coming! What does the dog say?\"",
          "\"WOOF! Good! The dog says woof! Keep marching!\""
        ],
        "whatToDo": [
          "Gather all toy animals your baby knows (from Week 1 and beyond).",
          "Start with your baby's FAVORITE animal—guaranteed success!",
          "Hold up the first animal: 'Here comes the cow in the parade!'",
          "Ask: 'What does the cow say?' Give baby 3-5 seconds to respond.",
          "If they say it: 'MOO! Perfect! Let's march!' Start marching around the room.",
          "If they don't say it, prompt: 'The cow says... moo! Say moo!' Then march.",
          "March for 15-20 seconds while saying 'moo, moo, moo!' together.",
          "Stop and grab the next animal: 'Now the dog is joining our parade!'",
          "Repeat: ask for the sound, celebrate the response, march together.",
          "Continue through 5-8 animals your baby has learned.",
          "Include animals from Week 1 (cow, dog, cat, duck, sheep) and any others learned since.",
          "Make it theatrical: stomp like elephants, hop like bunnies, flap arms like birds!",
          "End with ALL animals together: 'The whole parade! All the sounds at once! Chaos!'"
        ],
        "examples": [
          "Cow: 'Moo, moo, moo!' (stomp heavily like a cow)",
          "Dog: 'Woof, woof, woof!' (march on all fours if baby can)",
          "Cat: 'Meow, meow, meow!' (tiptoe quietly like a sneaky cat)",
          "Duck: 'Quack, quack, quack!' (waddle side to side)",
          "Bird: 'Tweet, tweet, tweet!' (flap arms like wings)",
          "Sheep: 'Baa, baa, baa!' (jump gently like a hopping sheep)",
          "Pig: 'Oink, oink, oink!' (roll around playfully)",
          "Horse: 'Neigh, neigh, neigh!' (gallop around the room)"
        ],
        "watchFor": [
          "Your baby remembering sounds independently without prompting—mastery!",
          "Your baby making sounds BEFORE you ask—anticipation and confidence!",
          "Your baby moving in character with each animal—full engagement!",
          "Your baby showing excitement about animals they particularly love—emotional connection to learning!"
        ],
        "parentTip": "If your baby doesn't remember a sound, don't worry! Just remind them and move on. The goal is celebration, not testing!",
        "funTwist": "Surprise sound: Add a new animal your baby hasn't learned yet: 'Here comes a lion! ROAR! Can you roar?' Expand the parade!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Home Sound Parade",
      "duration": "4-5 min",
      "focus": "Reviewing Week 2 Environmental Sounds",
      "researchBasis": "Craik & Lockhart (1972) - Deeper processing of information (like connecting sounds to actions and contexts) leads to better memory than shallow processing. Acting out sounds creates richer memory traces.",
      "content": {
        "goal": "Now add everyday sounds to your parade! March around making environmental sounds like 'beep,' 'splash,' 'ring,' and 'knock.'",
        "whatToSay": [
          "\"Now let's add house sounds to our parade!\"",
          "\"The car is coming! What does the car say?\"",
          "\"BEEP BEEP! Yes! Beep beep! Let's drive!\"",
          "\"Now we're at the sink! What does water say?\"",
          "\"SPLASH! Great! Splash splash splash!\""
        ],
        "whatToDo": [
          "Gather everyday objects that make sounds (keys, cups, spoons, toy phone).",
          "Continue the parade format from Step 1.",
          "Hold up keys: 'The keys are in the parade! What do keys say?'",
          "Shake keys: 'Jingle jingle! Say jingle!'",
          "March while shaking keys and saying 'jingle, jingle, jingle!'",
          "Grab the next object: toy car or picture of car.",
          "'The car is here! What does the car say?'",
          "Wait for 'beep!' then pretend to drive while saying 'beep beep!'",
          "Continue with 5-6 household sounds baby has learned.",
          "Include sounds from Week 2 and Week 8 (beep, splash, knock, ring, tick-tock, whoosh).",
          "Act out each sound: splash hands for water, knock on door, ring pretend phone.",
          "Make connections: 'Remember when we learned this? You love this sound!'",
          "Combine sounds: 'The car goes beep AND the horn goes honk!'"
        ],
        "examples": [
          "Keys: 'Jingle, jingle!' (shake keys while marching)",
          "Car: 'Beep, beep!' (pretend to steer while marching)",
          "Water: 'Splash, splash!' (pretend to pour or splash)",
          "Door: 'Knock, knock!' (knock on imaginary door)",
          "Phone: 'Ring, ring!' (hold hand to ear)",
          "Clock: 'Tick, tock!' (swing arm like pendulum)",
          "Wind: 'Whoooosh!' (wave arms overhead)",
          "Drum: 'Boom, boom!' (march and clap hands)"
        ],
        "watchFor": [
          "Your baby connecting sounds to real objects—'Oh yes, that's what THIS does!'",
          "Your baby performing actions associated with sounds (driving, splashing, knocking)—embodied learning!",
          "Your baby remembering sounds from weeks ago—long-term retention!",
          "Your baby getting MORE energetic as the parade continues—building momentum!"
        ],
        "parentTip": "Use REAL objects when possible—shake actual keys, tap actual spoons. Tangible objects make sounds more memorable!",
        "funTwist": "Sound mix-up: 'Does the car say splash? No! Does the water say beep? No! What's the RIGHT sound?' Make it silly!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Action Sound Parade",
      "duration": "3-4 min",
      "focus": "Reviewing Action Sounds and Body Movements",
      "researchBasis": "Goldin-Meadow (2011) - Gesture and movement during learning strengthen memory and comprehension. Children who act out concepts retain them better than those who only verbalize.",
      "content": {
        "goal": "Finish the parade with ACTION sounds! Jump, clap, stomp, and make the sounds that go with physical movements!",
        "whatToSay": [
          "\"Now let's add what YOUR body can do!\"",
          "\"Can you jump? Let's jump!\"",
          "\"JUMP! JUMP! JUMP! Say it while you jump!\"",
          "\"What about clap? Can you clap?\"",
          "\"CLAP! CLAP! CLAP! You're doing it!\""
        ],
        "whatToDo": [
          "This part of the parade focuses on words and actions baby's BODY makes.",
          "Start with jumping: 'Let's JUMP in our parade!'",
          "Jump together while saying 'Jump! Jump! Jump!'",
          "Even if baby doesn't jump yet, encourage bouncing or hopping.",
          "Next action: 'Now let's CLAP!'",
          "Clap hands together: 'Clap! Clap! Clap!'",
          "Continue with 4-5 action words baby knows.",
          "Include: jump, clap, stomp, wave, spin, march, dance, bounce.",
          "Say the action word WITH the action: 'STOMP!' (stomp feet)",
          "Make it a sequence: 'Jump, clap, stomp! Do it all!'",
          "End with free dance: 'Now dance however you want! Your parade!'",
          "Celebrate: 'You did the WHOLE parade! All the sounds you know!'"
        ],
        "examples": [
          "Jump: 'Jump, jump, jump!' (bounce or hop)",
          "Clap: 'Clap, clap, clap!' (hands together)",
          "Stomp: 'Stomp, stomp, stomp!' (march heavily)",
          "Wave: 'Wave, wave, wave!' (wave hands)",
          "Spin: 'Spin, spin, spin!' (turn in circle)",
          "Wiggle: 'Wiggle, wiggle, wiggle!' (shake body)",
          "March: 'March, march, march!' (walk with high knees)",
          "Dance: 'Dance, dance, dance!' (free movement)"
        ],
        "watchFor": [
          "Your baby coordinating sound with movement—saying 'jump' WHILE jumping!",
          "Your baby remembering action words independently—spontaneous recall!",
          "Your baby trying new movements or combining actions—creativity!",
          "Your baby LAUGHING and showing pure joy—the best sign of learning!"
        ],
        "parentTip": "If baby is too young for some actions (like jumping), modify: 'You bounce! That's your jump!' All attempts count!",
        "funTwist": "Grand finale: 'Do ALL the sounds at once! Animal sounds, house sounds, actions—everything together! CHAOS PARADE!'"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You had a SOUND PARADE with ALL your sounds!",
          "You reviewed:",
          "✓ Animal sounds from Week 1 (cow, dog, cat, duck, and more!)",
          "✓ Everyday sounds from Week 2 and Week 8 (beep, splash, jingle!)",
          "✓ Action words with movements (jump, clap, stomp!)",
          "✓ Everything you've learned in 13 weeks—you're AMAZING!"
        ],
        "reviewQuestions": [
          "How many sounds could your baby produce independently?",
          "Which sounds were your baby's favorites during the parade?",
          "Did your baby remember sounds from early weeks without prompting?",
          "How did your baby's energy and engagement compare to Week 1?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "This Sound Parade shows you and your baby how much has been accomplished over the past 13 weeks! From the first 'moo' in Week 1 to confidently producing dozens of sounds while moving and playing—this is HUGE progress! Review activities like this strengthen memory and build confidence. Your baby now has a foundation of sounds, words, and listening skills that will support all future literacy learning!"
        },
        "nextSteps": [
          "Keep doing sound parades whenever you want to review—it's always fun!",
          "Tomorrow we'll explore rhythm patterns and predictable beats!",
          "Celebrate today's parade—you'll treasure seeing this progress!",
          "This week is all celebration—enjoy every moment of showing off skills!"
        ]
      }
    }
  ]
},

// ---------- LESSON 62 (Week 13 Day 2) ----------
{
  "id": 62,
  "phase": "1-2",
  "quarter": 1,
  "week": 13,
  "weekIndex": 12,
  "dayInWeek": 1,
  "title": "Lesson 62: \"Rhythm Circle\" — Predict the Beat",
  "theme": "Our Favorite Sounds!",
  "skillRecurrenceTags": ["rhythm-awareness", "pattern-recognition", "predictability"],
  "relatedToPreviousSkills": ["week1-rhythm-clapping", "week4-sound-band", "week9-clap-beat", "week12-rhythm-retell"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Rhythm Circle!",
      "duration": "1 min",
      "content": {
        "goal": "Review all the rhythm skills from Quarter 1 by creating predictable patterns that your baby can anticipate and join. This builds pattern recognition and early math skills!",
        "parentGuide": [
          "Rhythm and pattern recognition are foundational for both language and math—they teach your baby that things happen in predictable sequences.",
          "When babies learn to PREDICT what comes next in a pattern, they're building critical thinking skills.",
          "This lesson brings together ALL rhythm activities from the past 13 weeks—clapping, drumming, marching, syllable counting.",
          "Predictability is comforting for babies—knowing what comes next builds confidence and reduces anxiety.",
          "Rhythm is one of the earliest forms of communication babies understand—it's universal and deeply engaging!"
        ],
        "materials": ["Your hands for clapping", "Homemade instruments from Week 4 (pots, spoons, shakers)", "Space to sit in a circle or face-to-face", "Optional: recorded music with clear beats"],
        "tip": "Start with simple patterns (clap-clap-pause) and gradually make them more complex as baby succeeds!",
        "researchNote": "Trainor & Corrigall (2010) - Musical rhythm training in infancy enhances not only musical ability but also language processing, pattern recognition, and mathematical reasoning later in childhood."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Clap and Predict",
      "duration": "4-5 min",
      "focus": "Simple Rhythm Patterns",
      "researchBasis": "Phillips-Silver & Trainor (2005) - When infants experience rhythmic patterns repeatedly, they develop expectation for those patterns and can anticipate upcoming beats, showing early pattern learning.",
      "content": {
        "goal": "Create simple clapping patterns and pause before the last clap, letting your baby fill it in. This teaches prediction and active participation!",
        "whatToSay": [
          "\"Let's make a clapping pattern! Listen!\"",
          "\"Clap-clap-clap! Now you try!\"",
          "\"Clap-clap... (pause)\"",
          "\"You did it! You knew the next clap was coming!\"",
          "\"Let's try a new pattern!\""
        ],
        "whatToDo": [
          "Sit facing your baby where you can both see each other's hands.",
          "Start with the simplest pattern: three claps.",
          "Clap: 'Clap-clap-clap!' while saying the words.",
          "Say: 'Let's do it together!' Clap three times together.",
          "Repeat the pattern 3-4 times so baby learns it.",
          "Now pause before the third clap: 'Clap-clap... (wait)'",
          "Look expectantly at baby—will they clap the third time?",
          "If they do: 'YES! You knew it was clap! You predicted it!'",
          "If they don't: 'CLAP! (do the third clap) Let's try again!'",
          "Once baby can predict the three-clap pattern, try a new one.",
          "Four claps: 'Clap-clap-clap-clap!' Practice, then pause before fourth.",
          "Alternating: 'Clap-pause-clap-pause-clap... (pause before last)'",
          "Fast-slow: 'Fast-fast-sloooow! Fast-fast... (pause before slow)'",
          "Celebrate every successful prediction!"
        ],
        "examples": [
          "Simple: Clap-clap-clap, pause before third",
          "Four-beat: Clap-clap-clap-clap, pause before fourth",
          "Alternating: Clap-pause-clap-pause-clap, pause before last clap",
          "Pattern sequence: Clap-clap-pause, clap-clap-pause, clap-clap... (pause before last set)",
          "Body percussion: Clap-stomp-clap, pause before final clap",
          "Speed variation: Slow-slow-FAST, pause before fast clap"
        ],
        "watchFor": [
          "Your baby clapping at the right moment during the pause—they're predicting!",
          "Your baby starting to clap BEFORE you finish the pattern—anticipation!",
          "Your baby showing excitement when they get it right—pride in mastery!",
          "Your baby creating their OWN patterns and pausing for you—role reversal!"
        ],
        "parentTip": "The pause is everything! Hold it long enough (2-3 seconds) that baby has time to realize they should clap. Don't rush it!",
        "funTwist": "Switch roles: 'YOU make a pattern, I'll predict it!' Help baby clap a pattern, then YOU fill in the missing beat!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Instrument Pattern Game",
      "duration": "4-5 min",
      "focus": "Multi-Sensory Rhythm Patterns",
      "researchBasis": "Kirschner & Tomasello (2010) - Joint music-making with instruments enhances cooperation, turn-taking, and social bonding between parents and young children more than non-musical joint activities.",
      "content": {
        "goal": "Use instruments (pots, spoons, shakers) to create rhythm patterns. Take turns being the leader and the follower!",
        "whatToSay": [
          "\"Now let's use our instruments! I'll make a pattern!\"",
          "\"Tap-tap-tap! Can you copy me?\"",
          "\"Shake-shake-pause-shake! You try!\"",
          "\"Now YOU make a pattern! I'll copy YOU!\"",
          "\"We're making music together!\""
        ],
        "whatToDo": [
          "Get out the instruments from Week 4 (or any safe household items).",
          "You take one instrument, baby takes another (or matching pair).",
          "Start simple: 'I'll tap my spoon three times. Watch!'",
          "Tap spoon on pot: 'Tap-tap-tap!'",
          "Say: 'Now you do it! Tap-tap-tap!'",
          "Baby copies your pattern on their instrument.",
          "Celebrate: 'Perfect! Same pattern!'",
          "Now try a pattern with a pause: 'Tap-tap-pause-tap!'",
          "Repeat it several times so baby learns it.",
          "Then start the pattern but STOP: 'Tap-tap-pause... (wait for baby to tap)'",
          "If baby taps: 'Yes! You finished the pattern!'",
          "Try different instruments: shakers, drums, bells.",
          "Make patterns with two instruments: 'Tap-shake-tap-shake!'",
          "Switch roles: 'Now YOU be the leader! Make a pattern for ME!'",
          "Help baby create a simple pattern, then you follow it.",
          "Celebrate role reversal: 'You're the music teacher now!'"
        ],
        "examples": [
          "Drum pattern: Boom-boom-boom, pause before third boom",
          "Shaker pattern: Shake-shake-pause-shake, pause before final shake",
          "Two instruments: Tap(spoon)-shake(bells)-tap-shake, pause before last action",
          "Speed pattern: Fast tap-fast tap-slow tap, pause before slow",
          "Volume pattern: Loud-loud-soft, pause before soft tap",
          "Complex: Tap-tap-shake-pause-tap, pause before final tap"
        ],
        "watchFor": [
          "Your baby accurately copying your rhythms—imitation skill!",
          "Your baby predicting the missing beat with the correct instrument—connection!",
          "Your baby creating their own patterns independently—creativity!",
          "Your baby enjoying being the leader—confidence and autonomy!"
        ],
        "parentTip": "Keep patterns SHORT—3 to 5 beats maximum. Long patterns are too hard for babies to remember and predict!",
        "funTwist": "Mystery pattern: Make a pattern behind your back (so baby can only HEAR it), then reveal the instrument: 'What was making that sound?'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Name Rhythm Review",
      "duration": "3-4 min",
      "focus": "Syllable Awareness Through Names",
      "researchBasis": "Liberman et al. (1974) - Syllable awareness develops before phoneme awareness and is a critical stepping stone to reading. Children who can clap syllables show better reading readiness.",
      "content": {
        "goal": "Review syllable clapping from Week 9 by clapping the rhythm of family names, favorite words, and common objects. This connects rhythm to language!",
        "whatToSay": [
          "\"Let's clap your name! Listen!\"",
          "\"Lu-ci-a (clap-clap-clap)! Three claps for Lucia!\"",
          "\"Now clap Ma-ma! How many claps?\"",
          "\"Two! Ma (clap) ma (clap)!\"",
          "\"Let's clap all our favorite words!\""
        ],
        "whatToDo": [
          "Start with your baby's name: 'Your name is [baby's name]!'",
          "Clap each syllable: 'So-phi-a' = clap-clap-clap (3 claps).",
          "Say: 'Three claps for Sophia! Clap with me!'",
          "Clap your baby's name together 3-4 times.",
          "Now try family members: 'Let's clap Mama!'",
          "Ma-ma = clap-clap (two claps).",
          "Try Dada, sister's/brother's name, pet's name.",
          "Move to object words: 'Let's clap things you know!'",
          "Ball = one clap. 'Ball! Just one clap!'",
          "Ba-nan-a = three claps. 'Banana! Three claps!'",
          "Elephant = three claps: 'El-e-phant!'",
          "Continue with 5-6 familiar words.",
          "Make it a game: 'I'm thinking of something with two claps... it says woof! DOG! Yes!'",
          "Reverse: 'Can you think of something with one clap? Cat! Yes! One clap!'"
        ],
        "examples": [
          "One syllable: cat, dog, ball, car, cup, book (one clap)",
          "Two syllables: Ma-ma, Da-da, ba-by, ap-ple, wa-ter (two claps)",
          "Three syllables: ba-na-na, el-e-phant, di-no-saur, to-ma-to (three claps)",
          "Four syllables: hip-po-pot-a-mus, wa-ter-mel-on (four claps)",
          "Names: Em-i-ly (3), Jack (1), Dan-iel (2), Sam-an-tha (3)"
        ],
        "watchFor": [
          "Your baby clapping the correct number of syllables—phonological awareness!",
          "Your baby recognizing that longer words have more claps—length awareness!",
          "Your baby offering their own words to clap—active participation!",
          "Your baby enjoying the game aspect—learning through play!"
        ],
        "parentTip": "If baby doesn't clap correctly, don't correct! Just model it again: 'Let's try together!' Clap WITH them rather than asking them to perform alone.",
        "funTwist": "Body syllables: Instead of clapping, use different body parts—stomp feet, tap head, touch nose—one action per syllable!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You predicted rhythms! You're a pattern master!",
          "You reviewed:",
          "✓ Clapping patterns and predicting the missing beat",
          "✓ Creating patterns with instruments and taking turns",
          "✓ Clapping syllables in names and favorite words",
          "✓ All your rhythm skills from 13 weeks of learning!"
        ],
        "reviewQuestions": [
          "Could your baby successfully predict missing beats in patterns?",
          "Did your baby enjoy being the leader and creating patterns?",
          "How many syllables can your baby clap accurately?",
          "Does your baby show excitement when they predict correctly?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Pattern recognition and prediction are foundational cognitive skills! When your baby learns to anticipate what comes next in a rhythm, they're developing critical thinking, sequencing, and problem-solving abilities. These same skills will help them predict what comes next in stories, understand cause-and-effect, and eventually recognize letter patterns in words. Rhythm is language practice in disguise!"
        },
        "nextSteps": [
          "Play rhythm prediction games during daily routines—brushing teeth, putting on shoes!",
          "Tomorrow we'll revisit favorite stories and retell them together!",
          "Make up silly rhythm patterns during meals: fork-tap, cup-tap, spoon-tap!",
          "Keep clapping syllables whenever you say new words—it's a lifelong skill!"
        ]
      }
    }
  ]
},

// ---------- LESSON 63 (Week 13 Day 3) ----------
{
  "id": 63,
  "phase": "1-2",
  "quarter": 1,
  "week": 13,
  "weekIndex": 12,
  "dayInWeek": 2,
  "title": "Lesson 63: \"Story Time Remix\" — Choose and Retell",
  "theme": "Our Favorite Sounds!",
  "skillRecurrenceTags": ["story-comprehension", "memory-recall", "narrative-skills"],
  "relatedToPreviousSkills": ["week10-story-echoes", "week10-animal-voices", "week12-act-it-out", "lesson60-sound-storybook"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Story Time Remix!",
      "duration": "1 min",
      "content": {
        "goal": "Let your baby CHOOSE their favorite story from the past 13 weeks, then retell it together using all the skills learned—sounds, echoes, voices, and actions!",
        "parentGuide": [
          "Giving children choice increases engagement dramatically—when they pick the story, they're more invested in participating.",
          "Retelling stories (rather than just reading them) strengthens comprehension and memory—it's active learning!",
          "This lesson integrates EVERYTHING: sound effects from Week 10, character voices from Lesson 47, actions from Lesson 59, and echo reading from Lesson 46.",
          "When babies help tell stories, they learn narrative structure—beginning, middle, end—which is foundational for literacy.",
          "Retelling familiar stories builds confidence—your baby KNOWS this story and can be an expert!"
        ],
        "materials": ["3-4 of baby's favorite books from the past weeks", "Baby's personalized sound book from Lesson 60", "Props or toys related to stories (optional)", "Enthusiasm and silly voices!"],
        "tip": "Don't worry about telling the story 'correctly'—the goal is participation and engagement, not perfect retelling!",
        "researchNote": "Morrow (1985) - Children who are encouraged to retell stories show significantly better story comprehension, vocabulary retention, and narrative structure understanding than children who only listen to stories being read to them."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Story Selection Celebration",
      "duration": "3-4 min",
      "focus": "Choosing and Previewing the Story",
      "researchBasis": "Patall et al. (2008) - Providing choices to young children increases intrinsic motivation, task engagement, and performance. Children who choose their activities show more sustained attention and deeper learning.",
      "content": {
        "goal": "Let your baby choose which story to retell from 2-3 options. Preview the story together by looking at pictures and remembering key parts.",
        "whatToSay": [
          "\"Which story should we tell today? YOU pick!\"",
          "\"Do you want the animal story or the silly story?\"",
          "\"You chose THIS one! Great choice!\"",
          "\"Let's remember what happens! Look at the pictures!\"",
          "\"Oh yes! This is when the dog says WOOF!\""
        ],
        "whatToDo": [
          "Lay out 2-3 favorite books in front of your baby.",
          "Say: 'Which story do you want to tell? You pick!'",
          "Give baby time to look at options—30 seconds to 1 minute.",
          "Watch for pointing, reaching, or vocalizing toward one book.",
          "If baby doesn't choose, offer two: 'This one or this one?'",
          "Once baby indicates a choice: 'THIS one! Perfect! You chose [book name]!'",
          "Pick up the chosen book and celebrate: 'I love this story!'",
          "Now preview the story WITHOUT reading all the words.",
          "Flip through pages: 'Look! Let's remember what happens!'",
          "Point to pictures: 'Here's the cow! What does the cow say?'",
          "Baby says 'moo!' or you prompt: 'Moo! Right!'",
          "Continue through key pages: 'And here's when the dog comes! And here's the big surprise!'",
          "Refresh baby's memory of the story sequence.",
          "Say: 'Now let's TELL the story TOGETHER! You can help!'"
        ],
        "examples": [
          "Baby points to 'Brown Bear, Brown Bear': 'You want the color animal book! Let's remember all the animals!'",
          "Baby reaches for 'Dear Zoo': 'The zoo book! Yes! They send so many animals!'",
          "Baby touches their personal sound book: 'YOUR book! The one YOU made! Let's read it!'",
          "Baby looks at animal story: 'The farm book! With all the sounds you know!'"
        ],
        "watchFor": [
          "Your baby clearly indicating a choice—pointing, reaching, saying a word!",
          "Your baby showing excitement when you pick up their chosen book—ownership!",
          "Your baby making sounds or words while previewing pictures—remembering!",
          "Your baby turning pages themselves during preview—active engagement!"
        ],
        "parentTip": "If baby chooses the same book every time, that's PERFECT! Repetition builds mastery. Don't push for variety—let them be the expert on their favorite!",
        "funTwist": "Mystery book: Cover books with blankets, let baby pick one without seeing it: 'Surprise story! Let's see what we got!'"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Interactive Retelling",
      "duration": "5-6 min",
      "focus": "Telling the Story Together with Full Participation",
      "researchBasis": "Lever & Sénéchal (2011) - When children actively participate in storytelling (making sounds, filling in words, acting out parts), they show better narrative comprehension and vocabulary learning than passive listening.",
      "content": {
        "goal": "Retell the story page by page, but pause constantly for baby to add sounds, words, actions, and predictions. Make baby a CO-STORYTELLER!",
        "whatToSay": [
          "\"Once upon a time there was a... (pause, point to picture)\"",
          "\"COW! Yes! And what does the cow say?\"",
          "\"MOO! Perfect! The cow said moo!\"",
          "\"Then along came a... (pause)\"",
          "\"DOG! And the dog said... WOOF!\""
        ],
        "whatToDo": [
          "Start at the beginning of the story.",
          "Instead of reading the actual text, TELL the story in your own simple words.",
          "Use the echo technique from Lesson 46: pause before key words.",
          "'Once upon a time, there was a big... (pause, point to cow)'",
          "Wait for baby to say 'cow!' or fill it in yourself: 'Cow! Yes!'",
          "Then prompt for the sound: 'What does the cow say?'",
          "Baby says 'moo!' (or you prompt: 'Moo! The cow says moo!')",
          "Continue to next page using the same pattern.",
          "Add character voices from Lesson 47: make cow voice deep, mouse voice squeaky.",
          "Add actions from Lesson 59: 'The cow was eating grass!' (pretend to chew)",
          "Encourage baby to act out parts: 'Show me how the dog runs!'",
          "For repeated phrases, let baby say the WHOLE phrase: 'Brown bear, brown bear, what do you see?'",
          "Make it conversational: 'What do YOU think happens next?'",
          "Celebrate every contribution: 'You're telling this story with me! You're a storyteller!'"
        ],
        "examples": [
          "'Brown Bear' retelling: 'There was a brown... (bear!). He saw a red... (bird!). The red bird could... (fly!)'",
          "'Dear Zoo' retelling: 'I wanted a pet. They sent me an... (elephant!). Too... (big!). So they sent me a... (monkey!)'",
          "Personal sound book: 'This is YOUR book! Here's the page with the... (cow!). The cow says... (moo!). And here's the... (dog!).'",
          "Any animal story: 'The farm had a cow who said... (moo!), a pig who said... (oink!), and a duck who said... (quack!)'"
        ],
        "watchFor": [
          "Your baby filling in words before you finish sentences—prediction!",
          "Your baby making appropriate sounds without prompting—internalized learning!",
          "Your baby acting out story parts spontaneously—embodied understanding!",
          "Your baby correcting YOU if you make a mistake—they know the story!"
        ],
        "parentTip": "Don't worry about reading every word from the book—paraphrase! Focus on participation, not accuracy. The pictures guide you!",
        "funTwist": "Wrong version: Tell the story wrong on purpose: 'The cow said MEOW!' Baby will likely react! 'Oh no! What DOES the cow say? MOO! You're right!'"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Story Show Performance",
      "duration": "2-3 min",
      "focus": "Performing the Story for an Audience",
      "researchBasis": "Pellegrini & Galda (1982) - When children perform or 'present' stories to others, they show increased confidence, better oral language skills, and stronger story structure understanding than in private retellings.",
      "content": {
        "goal": "Now perform the story for a family member or stuffed animal! This celebrates baby's storytelling skills!",
        "whatToSay": [
          "\"Let's tell our story to Daddy! You're the star!\"",
          "\"We'll read it together and YOU do the sounds!\"",
          "\"When we get to the cow, YOU say moo!\"",
          "\"You're such a good storyteller! Everyone wants to hear!\"",
          "\"Take a bow! You did it!\""
        ],
        "whatToDo": [
          "Find an audience: parent, sibling, grandparent, or stuffed animal.",
          "Announce the performance: 'We're going to tell you a story!'",
          "Sit with baby holding the book (or let baby hold it).",
          "Say to the audience: 'Baby will help me tell this story!'",
          "Begin retelling the story just like in Step 2.",
          "Emphasize baby's contributions: 'And now Baby will tell you what the cow says!'",
          "Pause and let baby make the sound or say the word.",
          "If baby is shy, that's okay—YOU make the sound and say: 'Baby taught me that!'",
          "Audience should react enthusiastically: 'Wow! You know that sound!'",
          "Continue through the whole story with baby participating.",
          "At the end: 'Let's clap for our storyteller!' Everyone claps.",
          "Say to baby: 'You DID it! You told the story!'",
          "Clap and cheer: 'Look! You told a story! Amazing!'"
        ],
        "examples": [
          "To Daddy: 'Sit down, Daddy! We have a story for you! Baby knows all the sounds!'",
          "To stuffed bear: 'Mr. Bear, listen to Baby's story! Baby will teach you animal sounds!'",
          "To Grandma when she visits: 'Grandma, Baby learned a story! Watch!'",
          "'Let's tell this story again! You can tell it to anyone you want!'"
        ],
        "watchFor": [
          "Your baby showing pride when performing—smiling, confident posture!",
          "Your baby performing even BETTER with an audience—motivated by attention!",
          "Your baby wanting to perform again—'Again! Again!'—building confidence!",
          "Your baby pointing out their contributions: pointing to themselves when they make sounds!"
        ],
        "parentTip": "If baby is shy or hesitant, don't force it. You can perform together as a team: 'We'll BOTH tell the story!' Reduce pressure!",
        "funTwist": "Role swap: 'Now YOU hold the book and I'll be the audience! You tell ME the story!' Help baby turn pages and prompt them to tell it!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You're a STORYTELLER! You told a whole story!",
          "You practiced:",
          "✓ Choosing your own story—making decisions!",
          "✓ Retelling the story with sounds, words, and actions",
          "✓ Being an active participant, not just a listener",
          "✓ Performing your story for others—confidence!"
        ],
        "reviewQuestions": [
          "Did your baby choose a story independently?",
          "How many words or sounds did your baby contribute during retelling?",
          "Was your baby comfortable performing for others?",
          "Did retelling feel more engaging than regular reading?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Retelling stories is one of the most powerful literacy activities you can do! It shows your baby that stories aren't just something adults read—they're something WE create and share together. When your baby helps tell a story, they're learning narrative structure (beginning, middle, end), practicing vocabulary, and building the confidence to be a communicator. This is the foundation for eventually reading independently and creating their own stories!"
        },
        "nextSteps": [
          "Retell stories every time you read—make it your regular reading style!",
          "Tomorrow we'll sing all our favorite songs together—musical celebration!",
          "Try retelling everyday events as stories: 'Tell Daddy what we did today!'",
          "Let baby 'read' books to their toys independently—they'll retell in their own way!"
        ]
      }
    }
  ]
},

// ---------- LESSON 64 (Week 13 Day 4) ----------
{
  "id": 64,
  "phase": "1-2",
  "quarter": 1,
  "week": 13,
  "weekIndex": 12,
  "dayInWeek": 3,
  "title": "Lesson 64: \"Family Showcase\" — Sing Our Favorite Songs",
  "theme": "Our Favorite Sounds!",
  "skillRecurrenceTags": ["music-integration", "vocabulary-review", "social-celebration"],
  "relatedToPreviousSkills": ["week4-sing-along", "week4-sound-show", "week9-rhyme-rhythm", "all-music-activities"],
  "duration": "10-15 min",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "Welcome to Family Showcase!",
      "duration": "1 min",
      "content": {
        "goal": "Gather family members to showcase ALL the songs, rhymes, and musical activities your baby has learned over 13 weeks. This is a celebration concert!",
        "parentGuide": [
          "Performing for others builds confidence and shows baby that their learning is valued and celebrated by the whole family.",
          "Music is the ultimate memory tool—songs learned weeks ago will come back instantly when you start singing!",
          "Sharing accomplishments with family strengthens bonds and creates lasting memories of this learning journey.",
          "This showcase format gives you a tangible way to SEE how much your baby has learned—it's easy to forget progress without reflection!",
          "Celebrating learning creates positive associations—baby learns that learning is fun, worthy of celebration, and makes people happy!"
        ],
        "materials": ["Family members as audience", "Instruments from Week 4 (optional)", "Song list from past 13 weeks (see examples below)", "Clapping hands and enthusiasm!"],
        "tip": "Don't pressure perfection! This is about participation and joy, not performing perfectly. Celebrate every attempt!",
        "researchNote": "Trehub (2003) - Musical experiences shared with family members create stronger emotional bonds, better memory encoding, and increased motivation for continued learning compared to solo musical activities."
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Opening Number—Our Favorite Song",
      "duration": "3-4 min",
      "focus": "Starting with Baby's Favorite Song",
      "researchBasis": "Custodero (2005) - When children lead musical activities or choose songs, they show greater engagement, more spontaneous singing, and better attention than when adults control all musical choices.",
      "content": {
        "goal": "Let your baby choose the opening song for the showcase! Sing it together with full participation—actions, sounds, and movements included!",
        "whatToSay": [
          "\"Welcome to Baby's concert! What song should we sing first?\"",
          "\"You want Old MacDonald? Perfect! That's YOUR favorite!\"",
          "\"Everyone sing with us! Baby knows all the animals!\"",
          "\"Baby, what animal should we sing about first?\"",
          "\"COW! Yes! Old MacDonald had a cow!\""
        ],
        "whatToDo": [
          "Gather your audience—family members sit in a circle.",
          "Announce the showcase: 'Welcome to Baby's Song Showcase! Baby has learned so many songs!'",
          "Ask baby: 'What song should we start with? What's your favorite?'",
          "Offer 2-3 choices if needed: 'Twinkle Twinkle or Old MacDonald?'",
          "Once baby indicates (pointing, vocalizing, or you picking their favorite): 'Old MacDonald it is!'",
          "Say to audience: 'This is Baby's favorite song! Baby knows all the animal sounds!'",
          "Start singing: 'Old MacDonald had a farm, E-I-E-I-O!'",
          "Pause at key moments: 'And on that farm he had a... (look at baby)'",
          "Let baby say the animal: 'COW! Yes! A cow!'",
          "Continue: 'With a moo moo here...' and let baby make the sounds.",
          "Do hand motions, clap, bounce—full physical engagement.",
          "Sing through 3-4 verses with different animals baby knows.",
          "End with applause: 'Let's clap for Baby! You sang that so well!'",
          "Audience claps enthusiastically!"
        ],
        "examples": [
          "Old MacDonald: Let baby choose animals, make sounds for each",
          "Twinkle Twinkle: Do hand motions (twinkle fingers), let baby fill in 'star'",
          "Wheels on the Bus: Act out all the movements (wheels go round, wipers swish)",
          "If You're Happy: Clap hands, stomp feet, shout hooray—all the actions",
          "Itsy Bitsy Spider: Finger crawling motions, rain falling, sun coming out",
          "Head Shoulders Knees and Toes: Touch body parts together"
        ],
        "watchFor": [
          "Your baby choosing a song independently—showing preference!",
          "Your baby participating MORE with an audience—motivation from attention!",
          "Your baby making sounds or words without prompting—confidence!",
          "Your baby smiling or showing pride—positive association with performing!"
        ],
        "parentTip": "If baby is shy at first, YOU lead enthusiastically and they'll often join in once comfortable. Model joy and they'll follow!",
        "funTwist": "Concert tickets: Make pretend tickets for family members: 'You need a ticket to see this show!' Adds excitement and formality!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Song Medley—All Our Songs!",
      "duration": "5-6 min",
      "focus": "Singing Multiple Songs in a Row",
      "researchBasis": "Barrett (2011) - Musical performances that include audience participation increase children's vocal confidence, memory for lyrics, and willingness to engage in future musical activities.",
      "content": {
        "goal": "Sing through 5-7 songs baby knows, one after another! Keep each song short (one verse) and encourage audience participation. This shows the RANGE of learning!",
        "whatToSay": [
          "\"Now let's sing ALL the songs Baby knows!\"",
          "\"First, The Wheels on the Bus!\"",
          "\"Now, If You're Happy and You Know It!\"",
          "\"Everyone clap! Everyone stomp!\"",
          "\"Baby knows SO many songs!\""
        ],
        "whatToDo": [
          "Announce: 'Now we'll sing lots of Baby's songs—a medley!'",
          "Keep each song SHORT—just one verse or the chorus.",
          "Sing energetically, moving quickly from one to the next.",
          "Start with action songs that get everyone moving.",
          "Song 1: 'The Wheels on the Bus' - one verse, everyone does motions.",
          "Quick transition: 'Now this one!'",
          "Song 2: 'If You're Happy' - clap hands, stomp feet, shout hooray.",
          "Continue through 5-7 familiar songs.",
          "Encourage audience: 'Everyone join in! Sing with Baby!'",
          "For each song, highlight baby's participation.",
          "'Listen to Baby sing this part!' (pause for baby to contribute)",
          "Include songs from different weeks: animal songs, rhyming songs, action songs.",
          "Make it interactive: 'Baby, show Grandma how we stomp!'",
          "Between songs: 'Baby learned that one in Week 4!'",
          "End on a high-energy note with everyone singing together!"
        ],
        "examples": [
          "Song list for medley:",
          "1. The Wheels on the Bus (Week 4)",
          "2. If You're Happy and You Know It (Week 11)",
          "3. Twinkle Twinkle Little Star (Week 1)",
          "4. Old MacDonald (Week 1)",
          "5. Itsy Bitsy Spider (Week 9)",
          "6. Head Shoulders Knees and Toes (Week 3)",
          "7. Ring Around the Rosie (Week 9)",
          "Alternative: Pat-a-Cake, Baa Baa Black Sheep, Row Row Row Your Boat"
        ],
        "watchFor": [
          "Your baby remembering songs from weeks ago—long-term memory!",
          "Your baby doing actions independently without prompting—mastery!",
          "Your baby singing words or phrases spontaneously—language development!",
          "Your baby enjoying the performance atmosphere—confidence building!"
        ],
        "parentTip": "If baby gets tired or overwhelmed, it's okay to stop early! Follow their energy level. You can always sing more songs later!",
        "funTwist": "Request time: Let audience members request songs! 'Grandma, which song do you want to hear Baby sing?' Gives variety and control!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Grand Finale—Family Sing-Along",
      "duration": "2-3 min",
      "focus": "Everyone Singing Together",
      "researchBasis": "Kirschner & Tomasello (2010) - Joint singing activities strengthen social bonds, increase prosocial behavior, and enhance cooperation between children and adults more effectively than non-musical joint activities.",
      "content": {
        "goal": "End with ONE final song where EVERYONE sings together—baby, parents, siblings, grandparents. This creates a powerful bonding moment!",
        "whatToSay": [
          "\"For our grand finale, EVERYONE sings together!\"",
          "\"This is Baby's favorite—let's all sing!\"",
          "\"Ready? One, two, three...!\"",
          "\"Louder! Let Baby hear everyone singing!\"",
          "\"Beautiful! Let's give Baby a big round of applause!\""
        ],
        "whatToDo": [
          "Announce the finale: 'For our last song, everyone joins in!'",
          "Choose a simple song everyone knows—ideally baby's absolute favorite.",
          "Good choices: Happy Birthday (even if not birthday!), Twinkle Twinkle, Old MacDonald.",
          "Everyone stands or sits in a circle with baby in the center.",
          "Say: 'On three, we ALL sing together! One, two, three!'",
          "Start singing loudly and enthusiastically.",
          "Make sure baby can SEE everyone singing—eye contact, smiles!",
          "Do actions together so baby is mirroring the whole group.",
          "Sing through the entire song, full verses.",
          "Build energy toward the end—sing louder, move more!",
          "At the very end, everyone claps and cheers for baby.",
          "'Baby's showcase! Let's give Baby a standing ovation!'",
          "Everyone claps, cheers, says 'Bravo!'",
          "Give baby a big hug: 'You did it! You sang all your songs!'",
          "Cheer and celebrate together as a group!"
        ],
        "examples": [
          "Twinkle Twinkle: Everyone does twinkle finger motions together, swaying",
          "Old MacDonald: Everyone makes animal sounds, baby chooses animals",
          "If You're Happy: Everyone claps, stomps, shouts hooray together",
          "Happy Birthday: Sing to baby (even if not their birthday!) as celebration of learning",
          "You Are My Sunshine: Sweet, slow song everyone sings warmly to baby"
        ],
        "watchFor": [
          "Your baby looking around at everyone singing—social awareness!",
          "Your baby singing or vocalizing more with group support—social motivation!",
          "Your baby smiling or laughing at the group energy—joy in community!",
          "Your baby seeming proud or happy at the applause—positive self-concept!"
        ],
        "parentTip": "This moment is about CONNECTION, not performance. If baby doesn't sing at all but just watches and smiles, that's perfect! They're experiencing community celebration!",
        "funTwist": "Encore: After applause, audience chants 'Encore! Encore!' Baby 'performs' one more short song—makes them feel like a star!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "Activity Complete!",
      "duration": "1 min",
      "content": {
        "celebration": [
          "🎉 You put on a SHOWCASE! You performed for your family!",
          "You celebrated:",
          "✓ Choosing and singing your favorite songs",
          "✓ Performing a medley of all learned songs",
          "✓ Singing together with your whole family",
          "✓ Showing everyone how much you've learned in 13 weeks!"
        ],
        "reviewQuestions": [
          "How many songs did your baby participate in?",
          "Did your baby show confidence performing for family?",
          "Which songs did your baby remember best?",
          "Did family members notice how much baby has learned?"
        ],
        "parentTakeaway": {
          "title": "Why This Matters",
          "content": "Performing for family isn't just cute—it's a powerful confidence builder! When your baby's learning is celebrated by loved ones, they learn that their accomplishments matter and that learning brings joy to others. This showcase also helps YOU see how far baby has come from Week 1 to now. All those daily 10-minute lessons added up to real, measurable skills! Music ties everything together—rhythm, vocabulary, memory, social bonding—making it the perfect way to celebrate the quarter!"
        },
        "nextSteps": [
          "Have mini family showcases regularly—they don't need to be formal!",
          "Tomorrow is our FINAL lesson of Quarter 1—Milestone Moments reflection!",
          "Repeat today's showcase throughout the week—baby will love performing their favorite parts!",
          "Start thinking about favorite moments from the past 13 weeks for tomorrow's reflection!"
        ]
      }
    }
  ]
},

// ---------- LESSON 65 (Week 13 Day 5) ----------
{
  "id": 65,
  "phase": "1-2",
  "quarter": 1,
  "week": 13,
  "weekIndex": 12,
  "dayInWeek": 4,
  "title": "Lesson 65: \"Milestone Moments\" — Quarter 1 Celebration & Reflection",
  "theme": "Our Favorite Sounds! 🎊 QUARTER 1 COMPLETE! 🎊",
  "skillRecurrenceTags": ["milestone-celebration", "progress-reflection", "parent-empowerment", "learning-legacy"],
  "relatedToPreviousSkills": ["ALL-QUARTER-1-SKILLS", "weeks-1-through-13", "every-single-lesson"],
  "duration": "15-20 min (SPECIAL EXTENDED CELEBRATION)",
  "steps": [
    {
      "id": 0,
      "type": "intro",
      "title": "🎉 WELCOME TO YOUR QUARTER 1 GRADUATION! 🎉",
      "duration": "2 min",
      "content": {
        "goal": "CELEBRATE the completion of 13 weeks and 65 lessons! Today is about reflection, recognition, and pure joy. You and your baby have built an incredible foundation for lifelong literacy, this is HUGE!",
        "parentGuide": [
          "YOU DID IT! Thirteen weeks of consistent, intentional learning with your baby. That's 65 lessons—over 10 hours of dedicated literacy time!",
          "Your baby's brain has grown measurably during this quarter. Neural pathways for language, sound recognition, and social communication are stronger today than 13 weeks ago!",
          "Taking time to reflect on progress isn't just nice, it's motivating! Seeing how far you've come fuels your commitment to continue.",
          "This lesson is different: it's slower, more emotional, and deeply personal. You'll look back at where you started and celebrate where you are now.",
          "Today you're not just a parent, you're a teacher who has given your baby the gift of early literacy. Be proud!"
        ],
        "materials": [
          "Artwork and creations from Week 1 and throughout the quarter",
          "Baby's personalized sound book from Lesson 60",
          "All toys, books, and instruments used over 13 weeks",
          "Paper and pen for recording milestones",
          "Special treat for baby (favorite snack or small toy)",
          "Camera for capturing this milestone moment",
          "Optional: Balloons, streamers, or simple decorations to mark the occasion",
          "Optional: Certificate template (you'll create one today!)",
          "Tissues (you might get emotional—that's beautiful!)"
        ],
        "tip": "Make this feel SPECIAL! Even small touches like a balloon or singing 'Congratulations' create lasting memories. This isn't just another lesson—it's a MILESTONE!",
        "researchNote": "Dweck (2006) - Celebrating effort and progress (not just innate ability) builds growth mindset in children. When parents explicitly recognize improvement and hard work, children develop persistence, resilience, and intrinsic motivation to continue learning. Today's celebration teaches baby that learning journeys are valuable and worthy of recognition!"
      }
    },
    {
      "id": 1,
      "type": "activity",
      "title": "Step 1: Memory Lane Walk — Then vs. Now",
      "duration": "5-6 min",
      "focus": "Reflecting on Transformation and Growth",
      "researchBasis": "Schacter et al. (2007) - Episodic memory retrieval (remembering specific past experiences) strengthens neural connections and helps consolidate learning. Reflecting on past lessons helps both parent and child solidify the skills learned and recognize patterns of growth.",
      "content": {
        "goal": "Look back at Week 1 and compare to today. Notice EVERYTHING that has changed—baby's sounds, attention span, vocabulary, confidence, and your own teaching skills!",
        "whatToSay": [
          "\"Let's look at pictures from when we started! Look how little you were!\"",
          "\"This is Week 1! You were just learning about 'moo!'\"",
          "\"Now listen to you! You can say SO many sounds!\"",
          "\"Remember when we first tried this? Now you're an expert!\"",
          "\"Look how much we both learned together!\""
        ],
        "whatToDo": [
          "Sit comfortably with baby and think back to Week 1.",
          "Start with the very first lesson: 'This is us on Day 1! Lesson 1!'",
          "Watch or look at early attempts: baby's first 'moo,' first clapping game, first book reading.",
          "Narrate what you see: 'Look! You were learning animal sounds for the first time!'",
          "Point out specific changes: 'You couldn't say moo yet, but now you can!'",
          "Show progression: Week 1 photos, then Week 4, then Week 8, then Week 13.",
          "For each milestone, celebrate: 'This was when you learned colors! And now you know so many!'",
          "Compare attention span: 'In Week 1, you focused for 2 minutes. Now you can do 15-minute lessons!'",
          "Notice confidence: 'See how shy you were at first? Now you perform for everyone!'",
          "Celebrate YOUR growth too: 'I was nervous at first! Now I know exactly how to teach you!'",
          "Get emotional—it's okay! 'I'm so proud of us. We did this TOGETHER.'",
          "Let baby touch pictures: 'That's YOU! You were learning!'",
          "End this step with a specific observation: 'The biggest change I see is [specific skill/confidence/engagement].'"
        ],
        "examples": [
          "Week 1 memory: Baby hearing 'moo' for first time, uncertain face → Now: Baby confidently saying 'moo' and making cow gestures",
          "Week 3 photo: Baby attempting first echo game, barely responding → Now: Baby echoing multiple words in sequence",
          "Week 6 picture: Baby touching one texture → Now: Baby naming colors and textures independently",
          "Week 9 memory: Baby clapping randomly → Now: Baby clapping exact syllable counts",
          "Week 10 photo: Baby listening to story passively → Now: Baby participating, filling in words, acting out scenes",
          "Parent transformation: 'I read instructions nervously in Week 1 → Now I improvise and know what works!'"
        ],
        "watchFor": [
          "Your baby showing recognition of earlier photos—'That's me!'—self-awareness!",
          "Your baby demonstrating learned skills confidently—'I can do that now!'",
          "YOUR OWN emotional response—pride, maybe tears—that's real connection!",
          "Clear, visible differences between then and now—tangible proof of growth!"
        ],
        "parentTip": "If you didn't take many photos, that's okay! Reflect verbally: 'Remember when we started? You barely made sounds. Now listen to you!' Memory works too!",
        "specialTouch": "Create a 'Then & Now' photo collage: Print Week 1 photo and today's photo side-by-side. Write: 'Quarter 1: We learned together!' Frame it or save it!"
      }
    },
    {
      "id": 2,
      "type": "activity",
      "title": "Step 2: Skills Showcase — The Grand Review",
      "duration": "6-8 min",
      "focus": "Demonstrating Mastery Through Play",
      "researchBasis": "Brown et al. (2014) - Demonstrating learned skills in a playful, pressure-free context strengthens retention and builds self-efficacy. Children who 'show off' their skills internalize their competence more deeply than those who only practice privately.",
      "content": {
        "goal": "Go through major skill categories and let baby SHOW what they can do! This isn't a test—it's a celebration where baby gets to be the expert and demonstrate their superpowers!",
        "whatToSay": [
          "\"Let's show off everything you learned! You're the teacher now!\"",
          "\"Show me your animal sounds! What does the cow say?\"",
          "\"Amazing! Now show me you can clap syllables!\"",
          "\"You're teaching ME now! You know so much!\"",
          "\"Every time you do something, we'll put a star on your chart!\""
        ],
        "whatToDo": [
          "Create a simple 'Skills Chart' on paper with categories: Animal Sounds, Object Names, Colors, Rhymes, Actions, Story Skills.",
          "Tell baby: 'We're going to play ALL your favorite games and I'll give you stars!'",
          "Start with ANIMAL SOUNDS (Week 1 skill):",
          "Bring out toy animals: 'What does the cow say? The dog? The duck?'",
          "For each sound baby makes, draw a big star on the chart!",
          "Celebrate LOUDLY: 'YES! Star for you! You know animal sounds!'",
          "Move to OBJECT NAMING (Week 5 skill):",
          "Hold up common objects: 'What's this? Ball! What's this? Cup!'",
          "Count how many objects baby can name: 'You know 8 objects! Amazing!'",
          "Add stars to the chart!",
          "Continue through COLORS (Week 6): 'Find something red! Find something blue!'",
          "RHYMES (Week 9): 'What rhymes with cat? HAT! Yes!'",
          "ACTIONS (Week 11): 'Can you jump? Clap? Wave?'",
          "STORY SKILLS (Week 10): 'Let's read a book—you fill in the words!'",
          "For each category, make it playful and low-pressure.",
          "If baby doesn't remember something, that's FINE: 'We'll practice more in Quarter 2!'",
          "Count total stars at the end: 'You earned 25 stars! You're a literacy superstar!'",
          "Stick the chart on the wall or fridge: 'Everyone can see how smart you are!'"
        ],
        "examples": [
          "Animal Sounds: Cow-moo, Dog-woof, Cat-meow, Duck-quack, Sheep-baa (5 stars!)",
          "Object Names: Ball, book, cup, car, teddy, spoon, hat, shoe (8 stars!)",
          "Colors: Red, blue, yellow, green (4 stars!)",
          "Rhymes: Cat/hat, dog/frog, sun/fun (3 stars!)",
          "Actions: Jump, clap, wave, spin, stomp (5 stars!)",
          "Story Skills: Echo words, make sounds, turn pages, act out scenes (4 stars!)",
          "BONUS category: 'Special Skills' — anything unique baby learned (funny voice, special song, etc.)"
        ],
        "watchFor": [
          "Your baby LIGHTING UP when they successfully demonstrate a skill—intrinsic pride!",
          "Your baby remembering skills from early weeks—long-term retention!",
          "Your baby showing preferences—some skills they LOVE showing off!",
          "Your baby wanting to keep going—'More! More!'—motivated by success!"
        ],
        "parentTip": "Focus on what baby CAN do, not what they can't. If they only get 60% of skills, that's AMAZING for this age! Celebrate it all!",
        "specialTouch": "Create a 'Quarter 1 Graduate Certificate': Write your baby's name, date, and list their top skills. Sign it 'Teacher: [Your name]' and 'Graduate: [Baby's name]!' Decorate with stickers!"
      }
    },
    {
      "id": 3,
      "type": "activity",
      "title": "Step 3: Time Capsule — Preserving the Memories",
      "duration": "4-5 min",
      "focus": "Creating a Keepsake for the Future",
      "researchBasis": "Nelson & Fivush (2004) - Creating narrative memories with young children (especially documenting milestones) helps form autobiographical memory. Children who have documented early experiences develop stronger self-identity and family connection.",
      "content": {
        "goal": "Create a 'Quarter 1 Time Capsule' with items representing this learning journey. Seal it to open in 1 year (or when baby starts kindergarten!). This creates a tangible legacy of this precious time!",
        "whatToSay": [
          "\"Let's make a treasure box of everything you learned!\"",
          "\"This is YOUR sound book—it goes in the box!\"",
          "\"Here's a picture of us from Week 1!\"",
          "\"We'll hide this box and open it when you're bigger!\"",
          "\"Future you will see how smart baby you was!\""
        ],
        "whatToDo": [
          "Find a box, envelope, or container—decorate it together!",
          "Write on the outside: 'Baby's Quarter 1 Time Capsule — Open [Date One Year From Now]!'",
          "Gather items to include:",
          "→ Baby's personalized sound book from Lesson 60",
          "→ The skills chart with stars from Step 2",
          "→ 2-3 favorite photos from throughout the quarter",
          "→ A list you write: 'Baby's favorite sounds, words, songs, and activities'",
          "→ Baby's hand print or foot print (stamp with paint!)",
          "→ A letter YOU write to future baby: 'Dear [Baby], when you were [age], you learned...'",
          "→ A small toy or object baby loved during these lessons",
          "As you add each item, explain to baby: 'This is your sound book! You made this!'",
          "Let baby help put items in: 'You put the book in! Good job!'",
          "Write the letter while baby plays: Tell future baby about favorite moments, challenges overcome, your proudest moment, how it felt teaching them.",
          "Include specifics: 'Your first word was [word], your favorite sound was [sound], you laughed the most during [activity].'",
          "Seal the box with tape and write: 'DO NOT OPEN UNTIL [Date]!'",
          "Put it somewhere safe: 'This box is magic. We'll open it together when you're bigger!'",
          "Take a photo of baby holding the sealed box!"
        ],
        "examples": [
          "Letter snippet: 'Dear Sofia, right now you are 18 months old. We just finished 13 weeks of learning together. Your favorite sound is 'moo' and you say it whenever you see a cow—real or toy! You learned to clap syllables and you're so proud when you get it right. My favorite memory is Week 10 when you \"read\" a whole book to your teddy bear using all the sounds you learned. I cried happy tears. You're going to do amazing things. Love, Mama'",
          "Items list: 'Baby's favorites: Cow (moo!), the color blue, jumping, Old MacDonald song, reading \"Dear Zoo,\" playing drums on pots, making silly faces in the mirror'",
          "Skills list: 'Sounds learned: 12 animal sounds, 8 environmental sounds, 6 action words. Words: 25+ object names, 4 colors, 5 emotion words. Songs: 10 full songs memorized!'"
        ],
        "watchFor": [
          "Your baby showing interest in the time capsule process—what goes in?",
          "Your baby wanting to keep touching items—attachment to learning materials!",
          "YOUR emotional response while writing the letter—this is powerful!",
          "The act of sealing it feeling significant—closure on this chapter!"
        ],
        "parentTip": "Take your time with the letter! This is for YOU as much as baby. Years from now, you'll treasure reading your own words about this precious time.",
        "specialTouch": "Special moment: Take a minute with baby to reflect on what you learned together and how you feel. You've both grown so much!"
      }
    },
    {
      "id": 4,
      "type": "activity",
      "title": "Step 4: Celebration Party — You Are AMAZING!",
      "duration": "3-4 min",
      "focus": "Pure Joy and Celebration",
      "researchBasis": "Fredrickson (2001) - Positive emotions, especially pride and joy, broaden cognitive capacity and build lasting psychological resources. Celebrating achievements creates an 'upward spiral' of well-being and motivation for future learning.",
      "content": {
        "goal": "PARTY TIME! Sing, dance, eat a treat, give awards, and celebrate with pure unbridled joy! This is about creating a peak positive experience that baby (and you!) will remember emotionally forever!",
        "whatToSay": [
          "\"IT'S PARTY TIME! You finished Quarter 1!\"",
          "\"Let's dance and celebrate! You're a GRADUATE!\"",
          "\"Here's your special treat for working so hard!\"",
          "\"You are SO smart, SO amazing, SO wonderful!\"",
          "\"I am so proud to be YOUR parent and YOUR teacher!\""
        ],
        "whatToDo": [
          "Put on baby's favorite music—loud and happy!",
          "DANCE like nobody's watching! Pick baby up, spin, jump, be silly!",
          "Sing a celebration song: 'For she's a jolly good learner!' (to 'Jolly Good Fellow' tune)",
          "Give baby their special treat: favorite snack, small toy, or extra playtime!",
          "Present the 'certificate' from Step 2 (if you made one): 'This is for YOU! You graduated Quarter 1!'",
          "Take a GRADUATION PHOTO: Baby holding certificate, wearing a party hat (if you have one), giving thumbs up!",
          "Call family members: 'Guess what? Baby finished Quarter 1! 65 lessons! Tell baby congratulations!'",
          "Let grandparents, siblings, and friends congratulate baby when they visit!",
          "Do a final 'Victory Lap': March around the house chanting 'We did it! We did it!'",
          "Make noise: Clap, cheer, bang pots, shake instruments—CELEBRATE LOUDLY!",
          "Blow bubbles, throw confetti (if you have any), or just throw soft toys in the air!",
          "End with a big, long HUG: 'I love learning with you. You make me so proud every single day.'",
          "Take a moment to be still and just FEEL the accomplishment.",
          "Look baby in the eyes: 'You did something really special. We both did.'"
        ],
        "examples": [
          "Celebration song (to 'Happy Birthday' tune): '🎵 Congratulations to you! Congratulations to you! You finished Quarter 1! Congratulations to you! 🎵'",
          "Victory chant: 'We did it! Clap clap! We did it! Clap clap! Quarter 1! Clap clap! We're so smart! Clap clap!'",
          "Family call: 'Grandma! Baby learned 12 animal sounds, 25 words, and 10 songs in 13 weeks! Can you believe it?!'",
          "Photo poses: Baby pointing to skills chart, baby 'reading' their sound book, baby making animal sounds, parent and baby cheek-to-cheek smiling"
        ],
        "watchFor": [
          "Your baby's HUGE smiles and laughter—pure joy!",
          "Your baby feeding off your energy—mirroring your excitement!",
          "Your baby seeming to understand this is SPECIAL—milestone awareness!",
          "YOUR tears of joy (totally normal!)—this is real pride and love!"
        ],
        "parentTip": "Let yourself be emotional! This IS a big deal! You committed to 13 weeks of consistent learning. That's dedication. That's love. That's transformative parenting!",
        "specialTouch": "Create a 'Quarter 1 Playlist': Compile all the songs you sang together over 13 weeks into one playlist. Play it during the party and randomly throughout Quarter 2 to maintain connection!"
      }
    },
    {
      "id": 5,
      "type": "conclusion",
      "title": "🎊 QUARTER 1 COMPLETE! YOU ARE A LITERACY SUPERHERO! 🎊",
      "duration": "2 min",
      "content": {
        "celebration": [
          "🏆 YOU DID IT! 13 WEEKS! 65 LESSONS! OVER 10 HOURS OF LEARNING!",
          "🌟 Your baby has grown EXPONENTIALLY:",
          "✓ From zero organized sound awareness to 12+ animal sounds mastered",
          "✓ From minimal vocabulary to 25+ named objects and concepts",
          "✓ From passive listening to ACTIVE story participation",
          "✓ From simple responses to complex patterns and predictions",
          "✓ From short attention (2-3 min) to sustained engagement (10-15 min)",
          "✓ From hesitant attempts to CONFIDENT demonstrations!",
          "",
          "🎯 Skills Mastered This Quarter:",
          "→ Animal sounds and environmental sound recognition",
          "→ Echo play and call-and-response communication",
          "→ Object naming and vocabulary building (25+ words)",
          "→ Color and texture identification",
          "→ Sorting, matching, and pattern recognition",
          "→ Rhyme awareness and syllable clapping",
          "→ Interactive story reading and participation",
          "→ Social language (greetings, courtesy words, emotions)",
          "→ Rhythm patterns and musical integration",
          "→ Memory, sequencing, and prediction skills",
          "",
          "💪 YOU grew too, parent!",
          "✓ You learned how to be your baby's first teacher",
          "✓ You discovered what engages YOUR unique child",
          "✓ You built consistency and follow-through (13 weeks!)",
          "✓ You created a learning-rich environment",
          "✓ You became confident in literacy instruction!"
        ],
        "reviewQuestions": [
          "What was your favorite lesson or activity from Quarter 1?",
          "What surprised you most about your baby's learning?",
          "Which skill did baby master faster than expected?",
          "Which skill needs more practice in Quarter 2?",
          "How did YOU grow as a teacher over these 13 weeks?",
          "What will you remember most about this quarter?",
          "How does baby respond to learning activities now vs. Week 1?",
          "What's your proudest moment from the past 13 weeks?"
        ],
        "parentTakeaway": {
          "title": "🎓 Why This Quarter Changed Everything",
          "content": "Quarter 1 laid the foundation for EVERY literacy skill your child will ever need. Sound awareness leads to phonics. Vocabulary leads to comprehension. Story participation leads to reading fluency. Rhythm leads to decoding. You didn't just teach sounds and words—you built neural pathways, created positive associations with learning, established routines, and modeled the joy of discovery. Your baby now knows that learning is fun, books are engaging, and YOU are their trusted guide. This is MORE than early literacy—this is the beginning of a lifelong learning relationship between you and your child. And you showed up. Every week. Every lesson. That consistency is the greatest gift you could give. The skills matter, but the MESSAGE matters more: 'Learning is something we do together, with joy, and you are capable of amazing things.' THAT is what your baby learned this quarter. Everything else is just bonus."
        },
        "quarter1Statistics": {
          "title": "📊 Your Quarter 1 By The Numbers:",
          "stats": [
            "✦ 13 weeks of consistent learning",
            "✦ 65 complete lessons delivered",
            "✦ 10+ hours of focused literacy instruction",
            "✦ 12+ animal sounds introduced and practiced",
            "✦ 25+ vocabulary words taught and reinforced",
            "✦ 10+ songs and rhymes learned",
            "✦ 8+ story reading sessions with participation",
            "✦ Hundreds of positive parent-child interactions",
            "✦ Countless moments of joy, laughter, and connection",
            "✦ One transformed relationship with your child!",
            "✦ ONE INCREDIBLE ACHIEVEMENT! 🎉"
          ]
        },
        "lookingAhead": {
          "title": "🔮 What's Next? Quarter 2 Preview!",
          "content": "Quarter 2 (Weeks 14-26) builds on EVERYTHING you accomplished! We'll deepen phonological awareness with letter sounds, expand vocabulary into categories and concepts, introduce early writing readiness, develop narrative skills through storytelling, and begin pre-reading activities. But you're not starting from scratch—you have a FOUNDATION now. Your baby knows how to learn, you know how to teach, and together you're unstoppable. Take a few days off if you need to rest and celebrate, then dive into Quarter 2 with confidence. You've got this!",
          "comingInQuarter2": [
            "Letter sound exploration (building toward alphabet knowledge)",
            "Category vocabulary (animals, foods, vehicles, etc.)",
            "Early writing readiness (scribbling, mark-making, pre-writing strokes)",
            "Expanded storytelling (creating original stories together)",
            "Pre-reading skills (print awareness, directionality, word recognition)",
            "More complex rhyming and word play",
            "Social-emotional vocabulary expansion",
            "Advanced rhythm and pattern work",
            "Multi-step sequences and memory games",
            "Parent-child co-reading mastery!"
          ]
        },
        "finalWords": {
          "title": "💝 A Letter to You, Parent:",
          "message": "You just did something extraordinary. In a world full of distractions, obligations, and chaos, you carved out 10+ hours over 13 weeks to intentionally teach your child. You showed up when you were tired. You played when you had a thousand other things to do. You sang silly songs, made animal noises, clapped rhythms, and read books over and over and over. You didn't just 'do activities'—you built a relationship. You weren't just 'teaching literacy'—you were shaping a brain, forming neural connections, creating memories, and modeling what it means to invest in someone you love. Your baby won't remember these specific lessons when they're older, but they will FEEL the impact forever. They'll approach books with confidence because you made reading fun. They'll try new things without fear because you celebrated every attempt. They'll believe in themselves because YOU believed in them first. This quarter wasn't just about sounds and words—it was about love made visible through consistent, patient, joyful teaching. And you did it. You actually did it. Be proud. Be amazed. Be ready to do it all over again in Quarter 2—because now you KNOW you can. You're not just a parent. You're an educator, a guide, a champion, and your child's first and most important teacher. And you're absolutely crushing it. Congratulations, graduate. You earned this celebration just as much as your baby did. 🎓❤️"
        },
        "nextSteps": [
          "🎉 CELEBRATE! Take the rest of today (and this weekend!) to bask in this accomplishment!",
          "📸 Review artwork and creations from the entire quarter—make a scrapbook or display!",
          "📝 Journal about your experience: What worked? What surprised you? What will you do differently in Q2?",
          "🎁 Give yourself a reward! You deserve something special for this commitment!",
          "📞 Share your success with friends and family—let them celebrate you!",
          "🗓️ Decide when to start Quarter 2 (take a break if needed, or jump right in!)",
          "📚 Keep practicing Quarter 1 skills casually—they're now part of your daily routine!",
          "💌 Read your time capsule letter again in private—let yourself feel ALL the feelings!",
          "🌟 Set an intention for Quarter 2: What do you hope to achieve? How will you grow?",
          "🎊 Most importantly: HUG your baby and whisper 'We did it. You and me. Together.' ❤️",
          "",
          "🚀 WHEN YOU'RE READY: Quarter 2, Lesson 66 awaits! New adventures in literacy are coming!",
          "",
          "But for today? Just celebrate. You've earned it! 🎉🎊🏆👏💕"
        ]
      }
    }
  ]
}
    
  ]
};

